import React,{useState,useEffect,useRef,useMemo,useCallback,memo}from"react";
import QRCode from "qrcode";

// PLAN — défini à la compilation via REACT_APP_PLAN
// 3 applis distinctes : "Standard" (couturier base) · "Premium" (couturier pro) · "Grossiste" (tissus)
const PLAN = (process.env.REACT_APP_PLAN || "Standard");
const IS_PREMIUM = PLAN === "Premium";
const IS_MAISON = PLAN === "Maison";
const IS_GROSSISTE = PLAN === "Grossiste";
const IS_PRO = IS_PREMIUM || IS_MAISON || IS_GROSSISTE; // fonctions avancées (Stock, Bilans, QR...)

// ─── LES FORMULES ───
// Chaque formule a un rang ; une fonction est disponible à partir du rang indiqué.
// Ce qui est verrouillé n'est jamais caché : on montre une vitrine qui donne envie.
const RANG = { Standard: 1, Premium: 2, Grossiste: 2, Maison: 3 };
const RANG_ACTUEL = RANG[PLAN] || 1;
const REQUIS = {
  collection: 2,   // catalogue de modèles
  stock:      2,   // stock et fournisseurs
  bilans:     2,   // histogramme et bilans détaillés
  photos:     2,   // photos de tissu et de modèle sur les commandes
  impression: 3,   // photos en qualité imprimable
  cataloguePdf: 3, // catalogue papier à imprimer
  mannequin:  3,   // mannequin virtuel (sur ordinateur)
  employes:   3,   // comptes employés
};
// dispo("collection") → cette formule y a-t-elle droit ?
// GrossistePro n'a qu'une formule, à 25 000 F : tout ce qui existe est compris.
// Rien à verrouiller, donc aucune vitrine d'abonnement à lui montrer.
const dispo = (fonction) => IS_GROSSISTE || RANG_ACTUEL >= (REQUIS[fonction] || 1);
// Nom de la formule qui débloque une fonction
const FORMULE_DE = { 2: "Premium", 3: "Maison de couture" };
const debloquePar = (fonction) => FORMULE_DE[REQUIS[fonction]] || "Premium";
// Nombre de modèles autorisés dans le catalogue
// GrossistePro n'a AUCUNE limite : a 25 000 F, le vendeur doit pouvoir enregistrer
// la totalite des tissus qu'il vend — son catalogue EST sa boutique.
const LIMITE_COLLECTION = (IS_GROSSISTE || RANG_ACTUEL >= 3) ? Infinity : RANG_ACTUEL >= 2 ? 20 : 0;
// Identité visuelle par appli
const APP_NAME = IS_GROSSISTE ? "Tissi-App" : "CouturePro";
const APP_BADGE = IS_GROSSISTE ? "" : IS_MAISON ? "MAISON" : IS_PREMIUM ? "PREMIUM" : "";
const APP_SUBTITLE = IS_GROSSISTE ? "GESTION DE TISSUS" : IS_MAISON ? "MAISON DE COUTURE" : IS_PREMIUM ? "ÉDITION PREMIUM" : "GESTION COUTURIER";

// Pont vers le stockage fichier Electron — ou IndexedDB en mode mobile/web (PWA, Capacitor)
// IndexedDB : quota de centaines de Mo (vs ~5 Mo localStorage) → photos HD OK sur téléphone
const idb={
  db:null,
  open(){return new Promise((res,rej)=>{if(idb.db)return res(idb.db);const rq=indexedDB.open("toby-store",1);rq.onupgradeneeded=()=>rq.result.createObjectStore("kv");rq.onsuccess=()=>{idb.db=rq.result;res(idb.db);};rq.onerror=()=>rej(rq.error);});},
  async get(k){const db=await idb.open();return new Promise((res,rej)=>{const rq=db.transaction("kv").objectStore("kv").get(k);rq.onsuccess=()=>res(rq.result);rq.onerror=()=>rej(rq.error);});},
  async set(k,v){const db=await idb.open();return new Promise((res,rej)=>{const rq=db.transaction("kv","readwrite").objectStore("kv").put(v,k);rq.onsuccess=()=>res();rq.onerror=()=>rej(rq.error);});},
  async del(k){const db=await idb.open();return new Promise((res,rej)=>{const rq=db.transaction("kv","readwrite").objectStore("kv").delete(k);rq.onsuccess=()=>res();rq.onerror=()=>rej(rq.error);});},
  async keys(){const db=await idb.open();return new Promise((res,rej)=>{const rq=db.transaction("kv").objectStore("kv").getAllKeys();rq.onsuccess=()=>res(rq.result||[]);rq.onerror=()=>rej(rq.error);});},
};
window.storage = window.electronAPI?.storage ?? {
  get:    async k   => { const v=await idb.get(k); return v!==undefined&&v!==null?{value:v}:null; },
  set:    async (k,v) => { await idb.set(k,String(v)); return {value:v}; },
  delete: async k   => { await idb.del(k); return {deleted:true}; },
  list:   async ()  => ({ keys:await idb.keys() }),
};
// Mode mobile/web (pas d'Electron) : détection responsive
const IS_WEB=!window.electronAPI;
// Le pont QR ne sert qu'aux appareils sans appareil photo à portée : sur un
// téléphone, on photographie directement la balle.
const EST_TACTILE = typeof window!=="undefined" && ("ontouchstart" in window || (navigator.maxTouchPoints||0)>0);

// ICÔNES DU MENU — dessinées au trait plutôt que des émojis : elles prennent la
// couleur du menu (or quand la rubrique est ouverte, gris sinon), restent nettes
// à toutes les tailles et ne dépendent pas de la police d'émojis du téléphone,
// qui change d'un appareil à l'autre.
function Icone({nom,t=22}){
  const c={width:t,height:t,viewBox:"0 0 24 24",fill:"none",stroke:"currentColor",strokeWidth:1.8,strokeLinecap:"round",strokeLinejoin:"round",style:{display:"block",flexShrink:0}};
  switch(nom){
    case "dashboard": return(<svg {...c}><path d="M3 20h18"/><rect x="5" y="11" width="3.4" height="6"/><rect x="10.3" y="7" width="3.4" height="10"/><rect x="15.6" y="13" width="3.4" height="4"/></svg>);
    case "clients":   return(<svg {...c}><circle cx="9" cy="8" r="3.2"/><path d="M3.5 19c0-3 2.5-4.8 5.5-4.8s5.5 1.8 5.5 4.8"/><path d="M16.2 6.3a3 3 0 0 1 0 5.6"/><path d="M17.6 14.6c1.9.6 3 2.1 3 4.4"/></svg>);
    case "ventes":    return(<svg {...c}><path d="M6 3h12v18l-2-1.4L14 21l-2-1.4L10 21l-2-1.4L6 21z"/><path d="M9 8h6"/><path d="M9 12h6"/></svg>);
    case "collection":return(<svg {...c}><rect x="3" y="4" width="18" height="14" rx="2"/><circle cx="8.2" cy="9" r="1.4"/><path d="M3.6 15.5l4.6-4 3.4 3 2.8-2.3 5.9 5"/></svg>);
    case "stock":     return(<svg {...c}><path d="M3.6 7.6L12 3.4l8.4 4.2v8.8L12 20.6 3.6 16.4z"/><path d="M3.6 7.6L12 11.8l8.4-4.2"/><path d="M12 11.8v8.8"/></svg>);
    case "caisse":    return(<svg {...c}><rect x="2.6" y="6" width="18.8" height="12" rx="2.4"/><circle cx="12" cy="12" r="2.6"/><path d="M6 10v4"/><path d="M18 10v4"/></svg>);
    default: return null;
  }
}

// ─── LOGO TISSI-APP ───
// Le nom tel qu'il est dessiné sur la marque : « Tissi- » en bleu nuit, « APP » en
// or, penché, souligné d'un trait doré qui s'affine aux deux bouts. Dessiné en
// formes et non en image : net à toutes les tailles, et 1 Ko au lieu de 87.
// On le règle par sa HAUTEUR, la largeur suit toute seule.
function LogoTissi({h=20}){
  return(<svg viewBox="0 0 560 170" height={h} width={Math.round(h*560/170)} role="img" aria-label="Tissi-App" style={{display:"block"}}>
    <g transform="skewX(-9)">
      <text x="54" y="112" fontFamily="'Arial Black','Segoe UI Black','Trebuchet MS',Impact,sans-serif" fontWeight="900" fontSize="104" letterSpacing="-1">
        <tspan fill={D}>Tissi-</tspan><tspan fill={G}>APP</tspan>
      </text>
    </g>
    <path d="M20 143 C 196 118, 380 114, 540 130 C 380 132, 196 138, 20 153 Z" fill={G}/>
  </svg>);
}
// Le menu et le bandeau du téléphone sont bleu nuit : le logo y serait invisible.
// On le pose donc sur une pastille blanche, comme une étiquette cousue sur le tissu.
// La marge et l'arrondi suivent la hauteur : une seule valeur a regler.
function LogoPastille({h=20}){
  return(<span style={{background:"#fff",borderRadius:Math.round(h*0.45),padding:Math.round(h*0.3)+"px "+Math.round(h*0.55)+"px",display:"inline-flex",alignItems:"center",boxShadow:"0 1px 5px rgba(0,0,0,0.28)"}}><LogoTissi h={h}/></span>);
}
// Les icônes des rubriques sont de vraies images choisies par le patron.
// Elles s'affichent à 19 px ; les fichiers font 76 px pour rester nets sur les
// écrans de téléphone, qui serrent trois à quatre points dans chaque pixel.
function IconeRubrique({src,t=19}){
  return <img src={src} alt="" style={{width:t,height:t,borderRadius:4,objectFit:"cover",display:"block"}}/>;
}

// ─── TOBY CLOUD — sauvegarde en ligne + licence à distance (Premium) ───
// L'adresse du serveur. À changer si tu déploies sous un autre nom :
//   REACT_APP_CLOUD_URL=https://mon-serveur.workers.dev npm run build:premium
const CLOUD_URL=(process.env.REACT_APP_CLOUD_URL||"https://toby-cloud.papemodou11012000.workers.dev").replace(/\/+$/,"");

// Appel au serveur. Renvoie {ok, data} ou {ok:false, erreur} — ne lève jamais d'exception.
async function cloudAppel(chemin,{methode="GET",corps=null,jeton=null,brut=false}={}){
  try{
    const entetes={};
    if(jeton)entetes.Authorization="Bearer "+jeton;
    if(corps&&!brut)entetes["Content-Type"]="application/json";
    const r=await fetch(CLOUD_URL+chemin,{
      method:methode,
      headers:entetes,
      body:corps?(brut?corps:JSON.stringify(corps)):undefined,
    });
    const txt=await r.text();
    let d={};try{d=txt?JSON.parse(txt):{};}catch{d={brut:txt};}
    if(!r.ok)return{ok:false,erreur:d.erreur||("Erreur serveur ("+r.status+")"),statut:r.status,data:d};
    return{ok:true,data:d};
  }catch{
    return{ok:false,erreur:"Pas de connexion internet",horsLigne:true};
  }
}

// À QUI SONT LES DONNÉES DE CET APPAREIL — se connecter ouvre un COMPTE, pas les
// données qui se trouvent déjà sur le téléphone. `cp_proprio` retient la clé du
// compte auquel appartiennent les données locales ; tant qu'il ne correspond pas au
// compte connecté, rien ne part en ligne. Sans cela, un téléphone d'avant les comptes
// remplaçait la sauvegarde d'une boutique par celle d'une autre.
// Ces clés décrivent l'appareil, pas la boutique : jamais sauvegardées, jamais restaurées.
const PROPRES_APPAREIL=["cp_appareil","cp_cloud_token","cp_derniere_verif","cp_proprio","cp_cloud_version","cp_a_envoyer","cp_cloud_last","cp_photos_envoyees","cp_photos_vues"];
// La version en ligne que cet appareil a vue en dernier (date de la sauvegarde qu'il
// a envoyée ou reçue). Plus récente en ligne = l'autre appareil a travaillé depuis.
async function versionCloud(){try{const v=await window.storage.get("cp_cloud_version");return v?v.value:"";}catch{return "";}}
// Compression gzip du navigateur quand il la connaît (Chrome, Android, iPhone iOS 16.4+) :
// la sauvegarde part 6 à 10 fois plus légère. Sinon elle part telle quelle.
async function compresser(texte){
  try{
    if(typeof CompressionStream==="undefined")return{corps:texte,gz:false};
    const flux=new Blob([texte]).stream().pipeThrough(new CompressionStream("gzip"));
    return{corps:await new Response(flux).arrayBuffer(),gz:true};
  }catch{return{corps:texte,gz:false};}
}
// … et celles-ci restent quand on vide l'appareil pour y ouvrir un autre compte.
const GARDEES_AU_CHANGEMENT=[...PROPRES_APPAREIL,"cp_lic","cp_cloud_info","cp_trial_start","cp_licence"];
async function viderDonneesLocales(){
  const l=await window.storage.list();
  for(const k of(l.keys||[])){
    if(GARDEES_AU_CHANGEMENT.includes(k)||k.startsWith("cp_p_"))continue; // une photo ne s'affiche que si une donnée la cite
    try{await window.storage.delete(k);}catch{}
  }
}
// Ce que contient l'appareil, lu dans le stockage : l'état React est encore vide au démarrage.
async function contenuLocal(){
  const nb=async k=>{try{const v=await window.storage.get(k);return v?(JSON.parse(v.value)||[]).length:0;}catch{return 0;}};
  let nom="";try{const a=await window.storage.get("cp_atelier");if(a)nom=(JSON.parse(a.value)||{}).nom||"";}catch{}
  return{nom,commandes:await nb("cp_v5"),clients:await nb("cp_clients"),stock:await nb("cp_stock")};
}

// Sauvegarde « légère » : SANS les photos (trop lourdes pour la data mobile au Sénégal)
// et sans le jeton de connexion. Les photos partent séparément, à la demande, en WiFi.
async function buildLightBackup(){
  const l=await window.storage.list();
  const data={};
  for(const k of(l.keys||[])){
    if(k.startsWith("cp_p_"))continue;   // photos exclues
    if(PROPRES_APPAREIL.includes(k))continue; // le jeton et l'identité de l'appareil ne partent jamais
    try{const v=await window.storage.get(k);if(v)data[k]=v.value;}catch{}
  }
  return{version:2,data,photos:{},source:"cloud-auto"};
}

// ─── PHOTOS EN LIGNE (stockage R2) ───
// Les photos sont trop lourdes pour la sauvegarde automatique : elles partent
// séparément, à la demande, et l'envoi reprend là où il s'est arrêté.

// Identifiants des photos présentes sur cet appareil
// Identifiant de la photo d'un article de stock. Préfixé pour ne jamais entrer en
// collision avec les photos de la Collection, qui utilisent l'identifiant nu.
const photoStockId = id => "stk-" + id;

async function photosLocales(){
  try{
    // Sur ordinateur, les photos sont des fichiers dans le dossier de l appli
    if(window.electronAPI?.photos?.list){
      const r=await window.electronAPI.photos.list();
      return Array.isArray(r)?r:(r&&r.ids)||[];
    }
    const l=await window.storage.list();
    return (l.keys||[]).filter(k=>k.startsWith("cp_p_")).map(k=>k.slice(5));
  }catch{return[];}
}

// Lit une photo, quel que soit l appareil
async function lirePhotoLocale(id){
  try{
    if(window.electronAPI?.photos?.get)return await window.electronAPI.photos.get(id);
    const v=await window.storage.get("cp_p_"+id);
    return v?v.value:null;
  }catch{return null;}
}

// Enregistre une photo, quel que soit l appareil
async function ecrirePhotoLocale(id,dataURL){
  if(window.electronAPI?.photos?.save)return await window.electronAPI.photos.save(id,dataURL);
  return await window.storage.set("cp_p_"+id,dataURL);
}

// Poids total des photos, pour prévenir le client avant tout envoi
async function poidsPhotosLocales(){
  let o=0;
  for(const id of await photosLocales()){
    try{const v=await lirePhotoLocale(id);if(v)o+=v.length*0.75;}catch{}
  }
  return o; // approximation : le format base64 pèse ~4/3 de la taille réelle
}

async function dataURLversBlob(d){const r=await fetch(d);return await r.blob();}
function blobVersDataURL(b){return new Promise(res=>{const f=new FileReader();f.onload=()=>res(f.result);f.readAsDataURL(b);});}

// Trois transferts à la fois : sur une connexion mobile, c'est l'attente entre deux
// photos qui coûte, pas leur poids. Au-delà, le réseau sature sans rien gagner.
async function parLots(liste,combien,travail){
  let i=0;
  const ouvrier=async()=>{while(i<liste.length){const x=liste[i++];try{await travail(x);}catch{}}};
  await Promise.all(Array.from({length:Math.min(combien,liste.length)},ouvrier));
}

// Envoie les photos absentes du serveur, trois à la fois
async function cloudEnvoyerPhotos(jeton,avancement){
  const r=await cloudAppel("/api/photos",{jeton});
  if(!r.ok)return r;
  const distantes=new Set(r.data.photos||[]);
  const aEnvoyer=(await photosLocales()).filter(id=>!distantes.has(id));
  let n=0,octets=0;
  await parLots(aEnvoyer,3,async id=>{
    try{
      const v=await lirePhotoLocale(id);
      if(!v)return;
      const blob=await dataURLversBlob(v);
      const rep=await fetch(CLOUD_URL+"/api/photo/"+encodeURIComponent(id),{
        method:"PUT",
        headers:{Authorization:"Bearer "+jeton,"Content-Type":blob.type||"image/jpeg"},
        body:blob});
      if(rep.ok){n++;octets+=blob.size;}
    }catch{}
    avancement&&avancement(n,aEnvoyer.length);
  });
  // Ménage : les photos d'un article supprimé restaient en ligne pour toujours.
  // On dit au serveur quelles photos sont encore citées quelque part ; il efface
  // les autres. La liste vient des DONNÉES, pas des fichiers présents sur cet
  // appareil : un deuxième téléphone qui n'a pas encore téléchargé les images
  // connaît quand même les articles qui les citent.
  let effacees=0;
  try{
    const ids=await photosReferencees();
    if(ids.length){
      const m=await cloudAppel("/api/photos-menage",{methode:"POST",jeton,corps:{ids}});
      if(m.ok)effacees=m.data.effacees||0;
    }
  }catch{}
  return{ok:true,envoyees:n,total:aEnvoyer.length,octets,effacees};
}

// Tous les identifiants de photo encore cités par une donnée : le stock, la
// collection, les ventes — et la corbeille, dont le contenu revit 30 jours.
async function photosReferencees(){
  const lire=async k=>{try{const r=await window.storage.get(k);const v=r?JSON.parse(r.value):[];return Array.isArray(v)?v:[];}catch{return [];}};
  const ids=new Set();
  const lignes=t=>{if(t&&t.photoTissu)ids.add(String(t.photoTissu));if(t&&t.photoModele)ids.add(String(t.photoModele));};
  for(const s of await lire("cp_stock"))if(s&&s.id!=null)ids.add(photoStockId(s.id));
  for(const c of await lire("cp_col"))if(c&&c.id!=null)ids.add(String(c.id));
  for(const o of await lire("cp_v5"))(o&&o.tissus||[]).forEach(lignes);
  for(const e of await lire("cp_trash")){
    const s=e&&e.snap;if(!s)continue;
    if(s.id!=null){ids.add(photoStockId(s.id));ids.add(String(s.id));}
    (s.tissus||[]).forEach(lignes);
  }
  return [...ids];
}

// Récupère les photos manquantes sur cet appareil (nouveau téléphone)
async function cloudRecupererPhotos(jeton,avancement){
  const r=await cloudAppel("/api/photos",{jeton});
  if(!r.ok)return r;
  const locales=new Set(await photosLocales());
  const aPrendre=(r.data.photos||[]).filter(id=>!locales.has(id));
  let n=0;
  await parLots(aPrendre,3,async id=>{
    try{
      const rep=await fetch(CLOUD_URL+"/api/photo/"+encodeURIComponent(id),{headers:{Authorization:"Bearer "+jeton}});
      if(!rep.ok)return;
      await ecrirePhotoLocale(id,await blobVersDataURL(await rep.blob()));
      n++;
    }catch{}
    avancement&&avancement(n,aPrendre.length);
  });
  return{ok:true,recuperees:n,total:aPrendre.length};
}

// ─── SAUVEGARDE (helpers partagés App + Réglages) ───
// Collecte TOUT le stockage (données + photos web sous cp_p_) dans un objet importable.
async function buildWebBackup(){
  const l=await window.storage.list();
  const data={};
  for(const k of(l.keys||[])){try{const v=await window.storage.get(k);if(v)data[k]=v.value;}catch{}}
  return{version:2,data,photos:{},source:"mobile"};
}
function backupFileName(prefix){return `${prefix}-${new Date().toISOString().slice(0,10)}.json`;}
function downloadJSON(obj,filename){
  const blob=new Blob([JSON.stringify(obj)],{type:"application/json"});
  const url=URL.createObjectURL(blob);
  const a=document.createElement("a");a.href=url;a.download=filename;
  document.body.appendChild(a);a.click();document.body.removeChild(a);
  setTimeout(()=>URL.revokeObjectURL(url),5000);
}
// Mémorise la date de dernière sauvegarde (pour le rappel 7 jours)
async function markBackupDone(){try{await window.storage.set("cp_last_backup",String(Date.now()));}catch{}}
// Partage le fichier de sauvegarde (WhatsApp, Drive, email…) si l'appareil le permet,
// sinon le télécharge. Renvoie "share" | "download" | "cancel".
async function shareOrDownloadBackup(prefix){
  const backup=await buildWebBackup();
  const filename=backupFileName(prefix);
  const json=JSON.stringify(backup);
  try{
    if(navigator.canShare){
      const file=new File([json],filename,{type:"application/json"});
      if(navigator.canShare({files:[file]})){
        await navigator.share({files:[file],title:"Sauvegarde TOBY",text:"Ma sauvegarde TOBY — à garder précieusement (ne pas supprimer)."});
        await markBackupDone();
        return"share";
      }
    }
  }catch(e){if(e&&e.name==="AbortError")return"cancel";} // partage annulé → pas de sauvegarde effective
  downloadJSON(backup,filename);
  await markBackupDone();
  return"download";
}
// ─── PAIEMENT DE L'ABONNEMENT (Wave / Orange Money) ───
// Le client paie TOBY, puis prévient par WhatsApp ; TOBY ajoute les jours dans l'admin.
// ⚙️ À AJUSTER ICI quand tu veux : ton lien Wave marchand, tes prix.
// Grille tarifaire fixée le 2026-08-28 (voir la veille concurrentielle).
// Annuel = 9 mois payés, soit 3 mois offerts.
const TARIFS={
  Standard :{mois:4000,  an:36000},
  Premium  :{mois:15000, an:135000},
  Maison   :{mois:35000, an:315000},
  Grossiste:{mois:25000, an:225000},
};
const TARIF=TARIFS[PLAN]||TARIFS.Standard;
const fcfa=n=>n.toLocaleString("fr-FR")+" F";
// Les trois formules, telles que le client les voit.
// `cle` correspond a REACT_APP_PLAN pour reperer la formule en cours.
const OFFRES=[
  {cle:"Grossiste", nom:"Tissi-App", pour:"Pour le grossiste et le vendeur de tissus",
   mois:TARIFS.Grossiste.mois, an:TARIFS.Grossiste.an, couleur:"#b45309", phare:true,
   points:["Clients et ventes","Crédit : qui vous doit quoi, et depuis quand",
           "Stock sans limite, au mètre ou à la pièce","Photo de chaque balle",
           "Catalogue papier à imprimer","Caisse, dépenses et bilans",
           "Factures à votre nom","Sauvegarde en ligne chez TOBY","Fonctionne sans connexion"]},
  {cle:"Standard", nom:"Standard", pour:"Pour le couturier qui démarre",
   mois:TARIFS.Standard.mois, an:TARIFS.Standard.an, couleur:"#0f766e",
   points:["Clientes, commandes et mesures","Reçus et factures","Corbeille 30 jours",
           "Sauvegarde en ligne chez TOBY","Fonctionne sans connexion"]},
  {cle:"Premium", nom:"Premium", pour:"Pour l'atelier qui vit de son métier",
   mois:TARIFS.Premium.mois, an:TARIFS.Premium.an, couleur:"#b8860b", phare:true,
   points:["Tout le Standard","Photos du tissu et du modèle","Catalogue de modèles et Mode Boutique",
           "Stock et fournisseurs","Caisse, dépenses et bilans","Code secret pour la caisse",
           "Votre logo sur les reçus","Version ordinateur Windows"]},
  {cle:"Maison", nom:"Maison de couture", pour:"Pour l'atelier qui emploie",
   mois:35000, an:315000, couleur:"#1e3a8a", bientot:true,
   points:["Tout le Premium","Un compte par employé","Chacun ses autorisations",
           "Qui a fait quoi, et quand","Plusieurs postes en même temps","Assistance prioritaire"]},
];

const PAIEMENT={
  whatsapp:"221777533153",        // même numéro, format international, sans +
  // Lien de paiement Wave marchand, genere depuis le compte Wave de TOBY.
  waveLien:"https://pay.wave.com/m/M_sn_F1TzQ28Sj9iD/c/sn/",
  // (Les prix sont dans TARIFS ci-dessus.)

};

// Les trois formules, avec le passage payant d'une formule a l'autre.
function OffresBloc({cle,atelier,onFermer}){
  const actuelle=PLAN;
  const demande=(o)=>encodeURIComponent(
    "Bonjour TOBY, je souhaite passer à la formule "+o.nom+".\n\n"+
    "Atelier : "+(atelier||"(non renseigné)")+"\n"+
    "Clé : "+(cle||"(inconnue)")+"\n\n"+
    "Tarif : "+fcfa(o.mois)+" par mois ou "+fcfa(o.an)+" par an.\n"+
    (o.bientot?"Cette formule arrive bientôt — prévenez-moi dès qu'elle est prête.":"Merci de me dire comment procéder.")
  );
  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:600,display:"flex",alignItems:"flex-start",justifyContent:"center",padding:"14px 12px",overflowY:"auto"}} onClick={e=>{if(e.target===e.currentTarget)onFermer();}}>
      <div style={{background:"#f7f4ee",borderRadius:16,maxWidth:460,width:"100%",padding:"18px 16px 20px",marginTop:"env(safe-area-inset-top)"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
          <div style={{fontSize:17,fontWeight:900,color:D}}>{IS_GROSSISTE?"Votre formule Tissi-App":"Les formules CouturePro"}</div>
          <button onClick={onFermer} aria-label="Fermer" style={{background:"#eae5db",border:"none",borderRadius:9,width:34,height:34,cursor:"pointer",fontSize:18,fontFamily:"inherit",color:"#666"}}>×</button>
        </div>
        <div style={{fontSize:11.5,color:"#8a7f66",marginBottom:14,lineHeight:1.6}}>Payez à l'année et vous économisez 3 mois.</div>
        {OFFRES.filter(o=>IS_GROSSISTE?o.cle==="Grossiste":o.cle!=="Grossiste").map(o=>{
          const ici=o.cle===actuelle;
          return(<div key={o.cle} style={{background:"#fff",border:ici?("2px solid "+o.couleur):"1px solid #e8e2d6",borderRadius:13,padding:"14px 15px",marginBottom:11,opacity:o.bientot?0.92:1}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10,marginBottom:2}}>
              <div>
                <div style={{fontSize:15.5,fontWeight:900,color:o.couleur,display:"flex",alignItems:"center",gap:7,flexWrap:"wrap"}}>
                  {o.nom}
                  {ici&&<span style={{background:o.couleur,color:"#fff",fontSize:9.5,fontWeight:800,padding:"2px 7px",borderRadius:20,letterSpacing:0.5}}>VOTRE FORMULE</span>}
                  {o.bientot&&<span style={{background:"#eef2ff",color:"#3730a3",fontSize:9.5,fontWeight:800,padding:"2px 7px",borderRadius:20,letterSpacing:0.5}}>BIENTÔT</span>}
                </div>
                <div style={{fontSize:11.5,color:"#999",marginTop:2}}>{o.pour}</div>
              </div>
              <div style={{textAlign:"right",flexShrink:0}}>
                <div style={{fontSize:16,fontWeight:900,color:D,whiteSpace:"nowrap"}}>{fcfa(o.mois)}</div>
                <div style={{fontSize:9.5,color:"#aaa",fontWeight:700}}>PAR MOIS</div>
              </div>
            </div>
            <div style={{fontSize:11,color:"#15803d",fontWeight:700,marginBottom:9}}>ou {fcfa(o.an)} par an · 3 mois offerts</div>
            <ul style={{margin:"0 0 11px",padding:0,listStyle:"none",display:"flex",flexDirection:"column",gap:5}}>
              {o.points.map(p=>(<li key={p} style={{fontSize:12.5,color:"#555",display:"flex",gap:7,lineHeight:1.5}}><span style={{color:o.couleur,fontWeight:900,flexShrink:0}}>✓</span>{p}</li>))}
            </ul>
            {ici
              ?<div style={{textAlign:"center",fontSize:11.5,color:"#aaa",fontWeight:700,padding:"9px"}}>C'est votre formule actuelle</div>
              :<a href={"https://wa.me/"+PAIEMENT.whatsapp+"?text="+demande(o)} target="_blank" rel="noreferrer" style={{display:"block",textAlign:"center",background:o.bientot?"#eef2ff":o.couleur,color:o.bientot?"#3730a3":"#fff",padding:"11px",borderRadius:9,fontWeight:800,fontSize:12.5,fontFamily:"inherit",textDecoration:"none"}}>{o.bientot?"Me prévenir à la sortie":"Passer à "+o.nom}</a>}
          </div>);
        })}
        <div style={{fontSize:10.5,color:"#a99c88",textAlign:"center",lineHeight:1.6,marginTop:4}}>Vos données et votre clé restent les mêmes en changeant de formule.</div>
      </div>
    </div>
  );
}

// Bloc « Payer mon abonnement » — affiché sur l'écran d'expiration et dans les Réglages.
function PaiementBloc({cle,atelier,compact}){
  const [offres,setOffres]=useState(false);
  const msg=encodeURIComponent(
    "Bonjour TOBY, je viens de payer mon abonnement "+APP_NAME+".\n\n"+
    "Boutique : "+(atelier||"(non renseignée)")+"\n"+
    "Clé : "+(cle||"(inconnue)")+"\n\n"+
    "Formule souhaitée : ____ (mois "+fcfa(TARIF.mois)+" / an "+fcfa(TARIF.an)+")\n\n"+
    "Merci de réactiver mon accès."
  );
  const bouton={border:"none",borderRadius:9,cursor:"pointer",fontWeight:800,fontSize:13,fontFamily:"inherit",padding:"12px",width:"100%",display:"block",textAlign:"center",textDecoration:"none"};
  return(
    <div style={{background:"#fff",border:"1px solid #e5ddd0",borderRadius:12,padding:compact?"13px 14px":"16px 18px",textAlign:"left"}}>
      <div style={{fontSize:13,fontWeight:900,color:D,marginBottom:3}}>💳 Payer mon abonnement</div>
      <div style={{fontSize:11,color:"#888",lineHeight:1.6,marginBottom:12}}>

        Payez sur le numéro TOBY, puis prévenez-nous : votre accès est réactivé dans la foulée.
      </div>

      {/* Les deux formules */}
      <div style={{display:"flex",gap:8,marginBottom:11}}>
        <div style={{flex:1,background:"#f9f7f0",border:"1px solid #e5ddd0",borderRadius:9,padding:"10px 11px",textAlign:"center"}}>
          <div style={{fontSize:15,fontWeight:900,color:D}}>{fcfa(TARIF.mois)}</div>
          <div style={{fontSize:9.5,color:"#aaa",fontWeight:700,marginTop:2}}>PAR MOIS</div>
        </div>
        <div style={{flex:1,background:"#f0fdf4",border:"1.5px solid #86efac",borderRadius:9,padding:"10px 11px",textAlign:"center"}}>
          <div style={{fontSize:15,fontWeight:900,color:"#166534"}}>{fcfa(TARIF.an)}</div>
          <div style={{fontSize:9.5,color:"#15803d",fontWeight:700,marginTop:2}}>PAR AN · 3 MOIS OFFERTS</div>
        </div>
      </div>
      {/* Wave */}
      {PAIEMENT.waveLien
        ?<a href={PAIEMENT.waveLien} target="_blank" rel="noreferrer" style={{...bouton,background:"#4CC9F0",color:"#04212b",marginBottom:8,display:"flex",alignItems:"center",justifyContent:"center",gap:9}}>
          <img src="wave.png" alt="" style={{width:26,height:26,borderRadius:6,display:"block"}}/>
          Payer avec Wave
        </a>
        :<div style={{background:"#e8f8fd",border:"1px solid #a5e4f5",borderRadius:9,padding:"10px 12px",marginBottom:8,fontSize:11,color:"#0c5d75",lineHeight:1.6}}>
          <strong>🌊 Avec Wave :</strong> ouvrez Wave → <em>Envoyer</em> → collez le numéro ci-dessus.
        </div>}

      {/* Orange Money */}
      <a href={"tel:"+encodeURIComponent("#144#")} style={{...bouton,background:"#FF7900",color:"#fff",marginBottom:8}}>📱 Orange Money — composer #144#</a>

      {/* Confirmation WhatsApp : TOBY reçoit la clé, donc peut ajouter les jours tout de suite */}
      <a href={"https://wa.me/"+PAIEMENT.whatsapp+"?text="+msg} target="_blank" rel="noreferrer" style={{...bouton,background:"#25D366",color:"#fff"}}>💬 J'ai payé — prévenir TOBY</a>
      <button onClick={()=>setOffres(true)} style={{background:"transparent",border:"none",color:"#2563eb",fontWeight:700,fontSize:12,fontFamily:"inherit",cursor:"pointer",width:"100%",padding:"11px 0 4px",textDecoration:"underline"}}>Voir toutes les formules</button>
      {offres&&<OffresBloc cle={cle} atelier={atelier} onFermer={()=>setOffres(false)}/>}
      <div style={{fontSize:9.5,color:"#bbb",marginTop:9,lineHeight:1.5,textAlign:"center"}}>
        Le message WhatsApp contient déjà votre clé : TOBY réactive votre accès sans rien vous redemander.
      </div>
    </div>
  );
}

// Ce qu'on montre quand une fonction est verrouillée : jamais un mur, une vitrine.
const VITRINE = {
  collection: { emoji:"👗", titre:"Collection",
    phrase:"Constituez votre catalogue. Vos clientes commandent en regardant vos modèles.",
    points:["Vos modèles en photos","Vos clientes choisissent sur catalogue","Prix affichés automatiquement","Partage par WhatsApp"] },
  stock: { emoji:"📦", titre:"Stock et fournisseurs",
    phrase:"Sachez ce qu'il vous reste, et ne soyez plus jamais à court de tissu.",
    points:["Métrage restant par tissu","Alerte quand le stock est bas","Vos fournisseurs et leurs prix","Valeur totale de votre stock"] },
  bilans: { emoji:"📊", titre:"Bilans et bénéfices",
    phrase:"Sachez enfin ce que vous gagnez vraiment, mois par mois.",
    points:["Bénéfice net réel","Évolution mois par mois","Dépenses par catégorie","Bilan imprimable"] },
  photos: { emoji:"📷", titre:"Photos des commandes",
    phrase:"Photographiez le tissu apporté et le modèle demandé. Fini les malentendus.",
    points:["Photo du tissu de la cliente","Photo du modèle voulu","Preuve en cas de litige","Zoom sur les détails"] },
  cataloguePdf: { emoji:"📖", titre:"Catalogue papier",
    phrase:"Un vrai book à poser sur votre table, que vos clientes feuillettent.",
    points:["Catalogue prêt à imprimer","Votre logo et vos coordonnées","Vos prix affichés","Qualité impression"] },
  mannequin: { emoji:"👤", titre:"Mannequin virtuel",
    phrase:"Montrez le tissu déjà confectionné, avant même de couper.",
    points:["Le tissu sur une silhouette","Visuel prêt à envoyer","Sur ordinateur, pour la qualité"] },
  employes: { emoji:"👥", titre:"Comptes employés",
    phrase:"Chaque employé son compte. Vous savez qui a fait quoi.",
    points:["Un accès par employé","Chacun ses permissions","Qui a saisi quelle commande","Plusieurs postes à la fois"] },
};

// Écran affiché à la place d'une page verrouillée : on montre ce que la
// fonction apporte, son prix, et le bouton pour y accéder. Jamais un mur gris.
function Vitrine({ fonction, onVoirOffres }) {
  const v = VITRINE[fonction];
  if (!v) return null;
  const formule = debloquePar(fonction);
  const cle = REQUIS[fonction] === 3 ? "Maison" : "Premium";
  const t = TARIFS[cle] || TARIFS.Premium;
  return (
    <div style={{ maxWidth: 460, margin: "0 auto", padding: "18px 0" }}>
      <div style={{ background: "#fff", borderRadius: 16, padding: "26px 22px", textAlign: "center",
                    boxShadow: "0 3px 18px rgba(0,0,0,0.07)", border: "1px solid #f0ece4" }}>
        <div style={{ fontSize: 52 }}>{v.emoji}</div>
        <div style={{ fontSize: 20, fontWeight: 900, color: D, marginTop: 6 }}>{v.titre}</div>
        <div style={{ fontSize: 13, color: "#777", lineHeight: 1.7, marginTop: 8 }}>{v.phrase}</div>
        <div style={{ textAlign: "left", margin: "18px 0 4px" }}>
          {v.points.map(p => (
            <div key={p} style={{ display: "flex", gap: 9, alignItems: "flex-start", padding: "7px 0",
                                  borderBottom: "1px solid #f7f4ee" }}>
              <span style={{ color: "#10b981", fontWeight: 900, flexShrink: 0 }}>✓</span>
              <span style={{ fontSize: 13, color: "#444" }}>{p}</span>
            </div>
          ))}
        </div>
        <div style={{ background: "#f9f7f0", borderRadius: 11, padding: "13px 15px", margin: "16px 0 14px" }}>
          <div style={{ fontSize: 10.5, color: "#aaa", fontWeight: 700, letterSpacing: 0.8 }}>DISPONIBLE EN</div>
          <div style={{ fontSize: 17, fontWeight: 900, color: D, marginTop: 3 }}>{APP_NAME} {formule}</div>
          <div style={{ fontSize: 13, color: "#777", marginTop: 4 }}>
            {fcfa(t.mois)} par mois <span style={{ color: "#ccc" }}>·</span> {fcfa(t.an)} par an
          </div>
        </div>
        <button onClick={onVoirOffres}
          style={{ background: G, color: D, border: "none", padding: "13px", borderRadius: 10,
                   cursor: "pointer", fontWeight: 900, fontSize: 14, fontFamily: "inherit", width: "100%" }}>
          Voir les formules
        </button>
      </div>
    </div>
  );
}

// ─── CODE CAISSE ───
// Hache le code secret : le PIN n'est jamais stocké en clair (ni dans les sauvegardes).
// Note : un code à 4 chiffres protège des regards indiscrets (employés), pas d'un pirate.
async function hashPin(pin){
  const txt="toby-pin:"+String(pin);
  try{
    if(window.crypto?.subtle){
      const buf=await window.crypto.subtle.digest("SHA-256",new TextEncoder().encode(txt));
      return[...new Uint8Array(buf)].map(b=>b.toString(16).padStart(2,"0")).join("");
    }
  }catch{}
  let h=0;for(let i=0;i<txt.length;i++){h=((h<<5)-h+txt.charCodeAt(i))|0;}
  return"s"+(h>>>0).toString(16);
}

// Demande au navigateur de NE PAS effacer les données (cause n°1 de pertes sur mobile).
// À rappeler à chaque démarrage et à l'ouverture des Réglages (idempotent).
async function requestPersistentStorage(){
  try{
    if(window.electronAPI)return true; // Electron = données sur disque, déjà persistantes
    if(!navigator.storage?.persist)return null; // API non dispo (vieux navigateur)
    if(await navigator.storage.persisted())return true;
    return await navigator.storage.persist();
  }catch{return null;}
}

const STATUTS=["En attente","En cours","Prêt","Livré"];
// Statuts grossiste : binaires, dérivés du paiement (pas modifiables manuellement)
const SC_GROS={"Soldé":"#10b981","Reste à régler":"#f59e0b"};
// Au marché, le client paye et repart avec le tissu : la vente se règle sur place.
// « Livraison en retard » n'a aucun sens ici — le seul suivi qui compte est celui
// du paiement (Soldé / Reste à régler, voir statutGrossiste).
const SUIVI_LIVRAISON = !IS_GROSSISTE;
// Calcule le statut effectif d'une vente en mode grossiste depuis les finances
function statutGrossiste(o){
  const pt=parseFloat(o.prixTotal)||0;
  const av=parseFloat(o.avanceRecue)||0;
  return(pt>0&&av>=pt)?"Soldé":"Reste à régler";
}
// L'article du stock d'une ligne de vente : celui qu'on a choisi (stockId) s'il
// correspond toujours au tissu et à la couleur de la ligne, sinon le premier qui correspond.
function articleDeLigne(t,stock){
  const nom=x=>String((x.type==="Autre"?x.custom:x.type)||"").trim().toLowerCase();
  const pareil=s=>nom(s)===nom(t)&&(s.couleur||"").toLowerCase()===(t.couleur||"").toLowerCase();
  if(!nom(t)||!stock)return null;
  if(t.stockId){const s=stock.find(x=>String(x.id)===String(t.stockId));if(s&&pareil(s))return s;}
  return stock.find(pareil)||null;
}
// Prix au mètre d'une ligne : celui retenu le jour de la vente, sinon celui du Stock.
function prixLigne(t,stock){
  const p=parseFloat(t.prixM);if(p>0)return p;
  const s=articleDeLigne(t,stock);return s?(parseFloat(s.prixVente)||0):0;
}
// Changer le tissu ou la couleur d'une ligne efface le prix retenu : il sera repris du Stock.
function sansPrixRetenu(t,champs){
  if(!champs.some(k=>["type","custom","couleur","stockId"].includes(k)))return t;
  const n={...t};delete n.prixM;return n;
}
// CRÉDIT — l'image de la rubrique : le mot sous la loupe, « on regarde qui doit quoi ».
// Dessinée ici plutôt qu'une photo : nette à toutes les tailles, rien à télécharger.
const POLICE_CREDIT="'Arial Black','Segoe UI Black',Arial,sans-serif";
function IconeCredit({t=18}){
  return(<svg width={t} height={t} viewBox="0 0 32 32" style={{display:"inline-block",verticalAlign:"middle",flexShrink:0}} aria-hidden="true">
    <text x="4.5" y="26.5" fontSize="27" fontWeight="900" fontFamily={POLICE_CREDIT} fill="#0d47a1">C</text>
    <text x="2.5" y="24.5" fontSize="27" fontWeight="900" fontFamily={POLICE_CREDIT} fill="#2196f3">C</text>
    <circle cx="20" cy="13" r="7.5" fill="rgba(225,245,254,0.6)" stroke="#90a4ae" strokeWidth="2.4"/>
    <line x1="25.3" y1="18.3" x2="30" y2="23" stroke="#212121" strokeWidth="3.6" strokeLinecap="round"/>
  </svg>);
}
function LogoCredit({h=46}){
  return(<svg height={h} viewBox="0 0 230 84" style={{display:"block",maxWidth:"100%"}} role="img" aria-label="Crédit">
    <text x="7" y="68" fontSize="56" fontWeight="900" fontFamily={POLICE_CREDIT} fill="#0d47a1" textLength="200" lengthAdjust="spacingAndGlyphs">CRÉDIT</text>
    <text x="3" y="64" fontSize="56" fontWeight="900" fontFamily={POLICE_CREDIT} fill="#2196f3" textLength="200" lengthAdjust="spacingAndGlyphs">CRÉDIT</text>
    <circle cx="128" cy="38" r="28" fill="rgba(225,245,254,0.55)" stroke="#b0bec5" strokeWidth="5"/>
    <circle cx="128" cy="38" r="24.5" fill="none" stroke="#fff" strokeWidth="1.5" opacity="0.8"/>
    <line x1="148" y1="58" x2="170" y2="80" stroke="#212121" strokeWidth="10" strokeLinecap="round"/>
  </svg>);
}
// HISTOGRAMME PAR MOIS — le montant de chaque barre est écrit au-dessus d'elle, au bout
// d'un petit trait. Deux séries (le bilan net) : les deux montants s'empilent, chiffre
// d'affaires puis dépenses, chacun dans sa couleur. Au-dessus plutôt qu'à côté, les mois
// se serrent : le graphique tient en deux écrans de téléphone au lieu de neuf.
// Les mois vides du début et de la fin de l'année sont retirés ; ceux du milieu restent,
// car un trou dans l'activité doit se voir. Sous chaque mois, la différence.
function HistoMois({mois,barres,hauteur=140,net}){
  const max=Math.max(1,...mois.flatMap(m=>barres.map(b=>m[b.cle]||0)));
  const lib=k=>{const [y,m]=k.split("-");return new Date(+y,+m-1,1).toLocaleDateString("fr-FR",{month:"short"});};
  const double=barres.length>1;
  const AXE=74;
  const paliers=[1,0.75,0.5,0.25,0];
  const zone=useRef(null);
  const [deborde,setDeborde]=useState(false);
  useEffect(()=>{
    const el=zone.current;if(!el)return;
    const voir=()=>setDeborde(el.scrollWidth>el.clientWidth+2);
    voir();
    const ro=typeof ResizeObserver!=="undefined"?new ResizeObserver(voir):null;
    if(ro)ro.observe(el);
    window.addEventListener("resize",voir);
    return()=>{if(ro)ro.disconnect();window.removeEventListener("resize",voir);};
  });
  const molette=e=>{
    const el=zone.current;if(!el||el.scrollWidth<=el.clientWidth+2)return;
    if(Math.abs(e.deltaY)<=Math.abs(e.deltaX))return;
    el.scrollLeft+=e.deltaY;
  };
  const hautBarre=m=>Math.max(...barres.map(b=>{const v=m[b.cle]||0;return v>0?Math.max(Math.round(v/max*hauteur),4):0;}));
  // Les montants empilés laissent moins de hauteur aux barres : on réserve la place.
  const marge=double?18:32;
  // Largeur d’un mois : celle du plus long montant, et pas un pixel de plus.
  const PAS=Math.max(54,Math.round(fmt(Math.round(max)).length*5.8)+8);
  return(<><div ref={zone} onWheel={molette} style={{overflowX:"auto",WebkitOverflowScrolling:"touch",paddingBottom:4}}>
    <div style={{minWidth:AXE+mois.length*PAS,paddingTop:marge,paddingRight:14,boxSizing:"content-box"}}>
      <div style={{display:"flex"}}>
        <div style={{width:AXE,flexShrink:0,position:"relative",height:hauteur}}>
          {paliers.map(r=>(
            <div key={r} style={{position:"absolute",right:0,top:(1-r)*hauteur,transform:"translateY(-50%)",display:"flex",alignItems:"center",gap:4}}>
              <span style={{fontSize:10,color:"#999",fontWeight:700,whiteSpace:"nowrap"}}>{fmt(Math.round(max*r))}</span>
              <span style={{width:5,height:1,background:"#ccc",display:"inline-block"}}/>
            </div>
          ))}
        </div>
        <div style={{flex:1,position:"relative",height:hauteur,borderLeft:"1px solid #ddd"}}>
          {paliers.map(r=><div key={r} style={{position:"absolute",left:0,right:0,top:(1-r)*hauteur,borderTop:r===0?"1px solid #ddd":"1px dashed #eee"}}/>)}
          <div style={{position:"absolute",inset:0,display:"flex",gap:6}}>
            {mois.map(m=>(<div key={m.key} style={{flex:1,position:"relative",display:"flex",justifyContent:"center",alignItems:"flex-end",gap:4}}>
              {barres.map(b=>{
                const v=m[b.cle]||0;
                const px=v>0?Math.max(Math.round(v/max*hauteur),4):0;
                return<div key={b.cle} title={b.label+" : "+fmt(v)+" F"} style={{width:double?"38%":"62%",maxWidth:double?28:42,height:px,background:v>0?b.grad:"transparent",borderRadius:"4px 4px 0 0"}}/>;
              })}
              {/* Le ou les montants, posés au-dessus de la plus haute barre du mois */}
              {!double&&barres.some(b=>(m[b.cle]||0)>0)&&<div style={{position:"absolute",bottom:hautBarre(m)+5,left:"50%",transform:"translateX(-50%)",display:"flex",flexDirection:"column",alignItems:"center",whiteSpace:"nowrap",pointerEvents:"none"}}>
                {barres.map(b=>{const v=m[b.cle]||0;return v>0?<span key={b.cle} style={{fontSize:11,fontWeight:800,color:b.couleur,lineHeight:1.3}}>{fmt(v)}</span>:null;})}
                <span style={{width:1,height:5,background:"#cbd5e1",marginTop:1}}/>
              </div>}
            </div>))}
          </div>
        </div>
      </div>
      <div style={{display:"flex",marginTop:5}}>
        <div style={{width:AXE,flexShrink:0}}/>
        <div style={{flex:1,display:"flex",gap:6}}>
          {mois.map(m=>(<div key={m.key} style={{flex:1,minWidth:0,textAlign:"center"}}>
            {net&&<div style={{fontSize:11,fontWeight:800,color:(m[net]||0)===0?"#d4d4d4":(m[net]>0?"#059669":"#dc2626"),whiteSpace:"nowrap",lineHeight:1.5}}>{(m[net]||0)===0?"—":((m[net]>0?"+":"")+fmt(m[net]))}</div>}
            <div style={{fontSize:11,color:"#777",textTransform:"capitalize",fontWeight:800,marginTop:2}}>{lib(m.key)}</div>
          </div>))}
        </div>
      </div>
    </div>
  </div>
  {deborde&&<div style={{fontSize:10.5,color:"#aaa",textAlign:"center",marginTop:4}}>↔ Faites glisser le graphique pour voir toute l’année</div>}
  </>);
}
// REMONTER EN HAUT — dans une longue liste, les boutons sont tout en haut : une flèche
// apparaît dès qu'on a bien défilé, et ramène en haut d'un geste.
// « fenetre » : écouter aussi la page entière (selon l'écran, c'est elle ou la zone qui défile).
function FlecheHaut({cible,fenetre,bas,z=120}){
  const [voir,setVoir]=useState(false);
  useEffect(()=>{
    const el=cible&&cible.current;
    const avecFenetre=fenetre||!el;
    const lire=()=>setVoir(Math.max(el?el.scrollTop:0,avecFenetre?window.scrollY:0)>450);
    if(el)el.addEventListener("scroll",lire,{passive:true});
    if(avecFenetre)window.addEventListener("scroll",lire,{passive:true});
    lire();
    return()=>{if(el)el.removeEventListener("scroll",lire);if(avecFenetre)window.removeEventListener("scroll",lire);};
  },[cible,fenetre]);
  if(!voir)return null;
  const remonter=()=>{
    const el=cible&&cible.current,avecFenetre=fenetre||!el;
    if(el)el.scrollTo({top:0,behavior:"smooth"});
    if(avecFenetre)window.scrollTo({top:0,behavior:"smooth"});
    // Filet : un téléphone qui ne sait pas faire le défilement doux remonte d'un coup.
    setTimeout(()=>{if(el&&el.scrollTop>0)el.scrollTop=0;if(avecFenetre&&window.scrollY>0)window.scrollTo(0,0);},1200);
  };
  return(<button type="button" aria-label="Remonter en haut" title="Remonter en haut" onClick={remonter} style={{position:"fixed",right:20,bottom:bas||24,zIndex:z,width:46,height:46,borderRadius:"50%",background:"#fff",border:"1.5px solid #cbd5e1",boxShadow:"0 4px 16px rgba(0,0,0,0.16)",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",padding:0}}>
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M12 16V8M8 12l4-4 4 4"/></svg>
  </button>);
}
// CRÉDIT — un client peut devoir sur plusieurs ventes. On le reconnaît à sa fiche,
// sinon à son téléphone, sinon au nom saisi sur la vente.
function cleCredit(o){
  if(o.clientId)return"c"+o.clientId;
  const t=(o.telephone||"").replace(/\D/g,"");
  if(t)return"t"+t;
  return"n"+((o.prenom||"")+" "+(o.nom||"")).trim().toLowerCase();
}
const CONV_YD=0.9144; // 1 yard
const uniteDe=t=>t.unite==="yd"?"yd":"m";
// Ce que le vendeur a tapé, dans son unité (le stock, lui, reste en mètres)
const qteDe=t=>{
  if(t.saisie!==undefined&&t.saisie!=="")return t.saisie;
  const m=parseFloat(t.metres);
  if(!(m>0))return t.metres||"";
  return t.unite==="yd"?String(+(m/CONV_YD).toFixed(2)):t.metres;
};
const resteDe=o=>Math.max(0,(parseFloat(o.prixTotal)||0)-(parseFloat(o.avanceRecue)||0));
const dateCourte=d=>{if(!d)return"";const x=new Date(d+"T00:00:00");return isNaN(x)?d:x.toLocaleDateString("fr-FR",{day:"numeric",month:"short"});};
const dateLongue=d=>{if(!d)return"";const x=new Date(d+"T00:00:00");return isNaN(x)?d:x.toLocaleDateString("fr-FR",{day:"numeric",month:"long",year:"numeric"});};
const joursEntre=(a,b)=>Math.round((new Date(b+"T00:00:00Z")-new Date(a+"T00:00:00Z"))/86400000);
// Relance WhatsApp : polie et chiffrée vente par vente, le client sait exactement quoi payer.
function messageRelance(g,info,auj){
  const at=info?.nom||"Notre boutique";
  let m=`Bonjour ${g.nom},\n`;
  m+=`petit rappel de ${at} : il reste *${fmt(g.reste)} F* à régler pour vos achats de tissus.\n`;
  m+=g.ventes.map(o=>`• ${o.cmdNum||"Vente"} du ${dateCourte(o.dateDepot)} : ${fmt(resteDe(o))} F`).join("\n")+"\n";
  if(g.echeance)m+=g.echeance<auj?`Le paiement était prévu le ${dateLongue(g.echeance)}.\n`:g.echeance===auj?`Le paiement est prévu pour aujourd'hui.\n`:`Le paiement est prévu le ${dateLongue(g.echeance)}.\n`;
  m+=`\nMerci et à très bientôt 🙏\n${at}`;
  if(info?.tel)m+=`\n📞 ${info.tel}`;
  return encodeURIComponent(m);
}
// Confirmation au client après un paiement : ce qu'il a versé, ce qu'il lui reste.
function messagePaiement(p,info){
  const at=info?.nom||"Notre boutique";
  let m=`Bonjour ${p.nom},\nnous avons bien reçu votre paiement de *${fmt(p.montant)} F* le ${dateLongue(p.date)}${p.mode?` (${p.mode})`:""}. Merci 🙏\n`;
  m+=p.resteApres>0?`Il reste ${fmt(p.resteApres)} F à régler${p.echeance?`, prévu le ${dateLongue(p.echeance)}`:""}.\n`:`Votre compte est à jour ✅\n`;
  m+=`\n${at}`;
  if(info?.tel)m+=`\n📞 ${info.tel}`;
  return encodeURIComponent(m);
}
const CATEGORIES=["Chemise","Ensemble","Robe","Pantalon","Costume complet"];
const PRIORITES=["Normale","Urgente"];
const G="#d4a017",D="#1a1a2e";
// Marge sous la barre des rubriques, sur téléphone. L'iPhone réserve 34 px en bas
// pour sa barre de gestes : tout respecter laisse un gros vide sous les libellés.
// On en rend 16 px à l'écran et on garde de quoi ne pas coller à la barre.
const BAS_BARRE="max(4px, calc(env(safe-area-inset-bottom) - 16px))";
const SC={"En attente":"#f59e0b","En cours":"#3b82f6","Prêt":"#10b981","Livré":"#6b7280"};
const si={width:"100%",padding:"9px 12px",borderRadius:8,border:"1.5px solid #e8e8e8",fontSize:13,boxSizing:"border-box",outline:"none",fontFamily:"inherit",background:"#fff"};
const sl={display:"block",fontSize:10,fontWeight:700,color:"#aaa",marginBottom:4,textTransform:"uppercase",letterSpacing:"0.8px"};

// Multicolore (wax, imprimés) : une roue de couleurs dans la pastille, pas une teinte unique.
const MULTICOLORE="conic-gradient(from 30deg,#e53935,#fb8c00,#fdd835,#43a047,#1e88e5,#8e24aa,#e53935)";
const COULEURS=[{n:"Blanc",h:"#FFFFFF"},{n:"Crème",h:"#FFFDD0"},{n:"Beige",h:"#F5F5DC"},{n:"Gris clair",h:"#D3D3D3"},{n:"Gris",h:"#808080"},{n:"Noir",h:"#222222"},{n:"Rouge",h:"#E53935"},{n:"Bordeaux",h:"#800020"},{n:"Rose",h:"#F48FB1"},{n:"Rose foncé",h:"#E91E8C"},{n:"Orange",h:"#FF7043"},{n:"Jaune",h:"#FDD835"},{n:"Or",h:"#FFD700"},{n:"Caramel",h:"#C68642"},{n:"Marron",h:"#6D4C41"},{n:"Kaki",h:"#8D9440"},{n:"Vert clair",h:"#81C784"},{n:"Vert",h:"#388E3C"},{n:"Vert foncé",h:"#1B5E20"},{n:"Turquoise",h:"#26C6DA"},{n:"Bleu ciel",h:"#64B5F6"},{n:"Bleu",h:"#1E88E5"},{n:"Bleu marine",h:"#0D1B6E"},{n:"Indigo",h:"#3949AB"},{n:"Violet",h:"#8E24AA"},{n:"Mauve",h:"#CE93D8"},{n:"Lavande",h:"#9FA8DA"},{n:"Corail",h:"#FF6B6B"},{n:"Pêche",h:"#FFCBA4"},{n:"Argent",h:"#C0C0C0"},{n:"Multicolore",h:MULTICOLORE},{n:"Autre",h:"#999999"}];
// TISSUS — liste élargie d'après enquêtes grossistes Sandaga / Sénégal
// Format : { n: nom affiché, cat: catégorie pour regroupement visuel }
const TISSUS_LIST=[
  // 🎯 Bazins (le plus populaire au Sénégal — Thioup et Ndiakhass sont des dérivés Bazin)
  {n:"Bazin",cat:"Bazin"},
  {n:"Bazin riche",cat:"Bazin"},
  {n:"Thioup",cat:"Bazin"},
  {n:"Ndiakhass",cat:"Bazin"},
  // 👑 Getzner (gamme premium)
  {n:"Getzner",cat:"Getzner"},
  // 🌺 Wax & Imprimés africains
  {n:"Wax",cat:"Wax & Imprimés"},
  {n:"Pentissé",cat:"Wax & Imprimés"},
  // 🧵 Brodés & Dentelles
  {n:"Brodé",cat:"Brodés & Dentelles"},
  {n:"Brodé Lafaya",cat:"Brodés & Dentelles"},
  {n:"Cotonate brodé",cat:"Brodés & Dentelles"},
  {n:"Dentelle",cat:"Brodés & Dentelles"},
  // 🌬️ Tissus légers
  {n:"Voile",cat:"Tissus légers"},
  {n:"Vale",cat:"Tissus légers"},
  {n:"Dra",cat:"Tissus légers"},
  // 🧶 Coton & Synthétiques
  {n:"Coton",cat:"Coton & Synthétiques"},
  {n:"Nylon",cat:"Coton & Synthétiques"},
  // ✨ Spéciaux (luxe, perlage)
  {n:"Tissu perlage",cat:"Tissus spéciaux"},
  // 🧣 Accessoires habillement
  {n:"Col",cat:"Accessoires habillement"},
  {n:"Foulard",cat:"Accessoires habillement"},
  // 👔 Tissus pour costume / tailleur (lin, soie, etc.)
  {n:"Lin",cat:"Tissus costume"},
  {n:"Fil-à-fil",cat:"Tissus costume"},
  {n:"Soie",cat:"Tissus costume"},
  {n:"Super 100",cat:"Tissus costume"},
  {n:"Velours",cat:"Tissus costume"},
  // ❓ Autre (saisie libre)
  {n:"Autre",cat:"Autre"}
];
const TISSUS=TISSUS_LIST.map(t=>t.n);
// Catégories de la Collection : types de TISSUS pour GrossistePro, types de VÊTEMENTS pour CouturePro
const TISSU_CATS=[...new Set(TISSUS_LIST.map(t=>t.cat))].filter(c=>c!=="Autre");
const COLLECTION_CATS=IS_GROSSISTE?TISSU_CATS:CATEGORIES;
// À quelle famille appartient chaque tissu — sert à ranger la vitrine par onglets
const CAT_DE_TISSU=TISSUS_LIST.reduce((m,t)=>{m[t.n]=t.cat;return m;},{});
const TISSU_CAT_BY_NAME=Object.fromEntries(TISSUS_LIST.map(t=>[t.n,t.cat]));
// Catégorisation pour le dropdown
const TISSUS_BY_CAT=TISSUS_LIST.reduce((acc,t)=>{(acc[t.cat]=acc[t.cat]||[]).push(t.n);return acc;},{});
// Famille d'un article du stock. Un tissu que le vendeur a nommé lui-même forme sa
// propre famille : tous les inconnus rangés dans « Autre » deviendraient illisibles.
function familleStock(x){return(x.type==="Autre"||!x.type)?((x.custom||"").trim()||"Sans nom"):(TISSU_CAT_BY_NAME[x.type]||"Autre");}
// Les familles connues dans l'ordre habituel, puis celles du vendeur par ordre alphabétique.
function ordreFamilles(noms){
  const connues=[...new Set(TISSUS_LIST.map(t=>t.cat))].filter(c=>noms.includes(c));
  return[...connues,...noms.filter(c=>!connues.includes(c)).sort((a,b)=>a.localeCompare(b,"fr"))];
}
const VILLES=["Dakar-Plateau","Médina","Biscuiterie","Grand Dakar","Fann-Point E","Mermoz-Sacré-Cœur","Ouakam","Ngor","Yoff","Parcelles Assainies","Patte d'Oie","HLM","Sicap-Liberté","Almadies","Castors","Liberté 1","Liberté 2","Liberté 3","Liberté 4","Liberté 5","Liberté 6","Fass","Colobane","Pikine","Guédiawaye","Keur Massar","Malika","Sangalkam","Rufisque","Bargny","Diamniadio","Thiaroye","Yeumbeul","Mbao","Tivaouane Peulh","Golf Sud","Thiès","Saint-Louis","Ziguinchor","Kaolack","Touba","Mbour","Diourbel","Louga","Tambacounda","Kolda","Fatick","Kédougou","Matam","Richard Toll","Tivaouane","Mbacké","Bakel","Vélingara","Bignona","Saly","Joal-Fadiouth","Paris","Lyon","Marseille","New York","Abidjan","Bamako","Conakry","Banjul","Bissau","Nouakchott","Casablanca","Lagos","Accra","Douala"];
const PAYS=["Sénégal","Côte d'Ivoire","Mali","Guinée","Guinée-Bissau","Gambie","Mauritanie","Maroc","Algérie","Nigeria","Ghana","Cameroun","Burkina Faso","Niger","Bénin","Togo","Cap-Vert","France","Espagne","Italie","Portugal","Belgique","Suisse","Royaume-Uni","Allemagne","États-Unis","Canada","Autre"];

function b36(s){let n=0;for(const c of s.toUpperCase())n=n*36+(c>='0'&&c<='9'?+c:c.charCodeAt(0)-55);return n;}
// Le préfixe d'une clé dit à quel logiciel elle donne droit. GrossistePro
// n'ouvre qu'avec GPRO : une clé CouturePro n'y entre pas, et inversement.
// Sans cela, un client payait un abonnement et ouvrait les deux logiciels.
const PREFIXE_CLE="GPRO";
function validKey(raw){const k=raw.toUpperCase().replace(/[s-]/g,"");if(k.length!==16||k.slice(0,4)!==PREFIXE_CLE)return false;const g1=k.slice(4,8),g2=k.slice(8,12),g3=k.slice(12,16);if(!/^[A-Z0-9]{12}$/.test(g1+g2+g3))return false;return(b36(g1)+b36(g2))%1679616===b36(g3);}
function fmtTel(v){const d=v.replace(/\D/g,"").slice(0,9);if(d.length<=2)return d;if(d.length<=5)return d.slice(0,2)+" "+d.slice(2);if(d.length<=7)return d.slice(0,2)+" "+d.slice(2,5)+" "+d.slice(5);return d.slice(0,2)+" "+d.slice(2,5)+" "+d.slice(5,7)+" "+d.slice(7);}
const fmt=n=>(parseFloat(n)||0).toLocaleString("fr-FR");

async function shrinkImg(file){return new Promise(res=>{const fr=new FileReader();fr.onload=e=>{const img=new Image();img.onload=()=>{const MAX=1200;const r=Math.min(MAX/img.width,MAX/img.height,1);const cv=document.createElement('canvas');cv.width=Math.round(img.width*r);cv.height=Math.round(img.height*r);const ctx=cv.getContext('2d');ctx.imageSmoothingEnabled=true;ctx.imageSmoothingQuality='high';ctx.drawImage(img,0,0,cv.width,cv.height);res(cv.toDataURL('image/jpeg',0.82));};img.src=e.target.result;};fr.readAsDataURL(file);});}

// Teinte dominante d'une photo de tissu. On ne regarde que le centre de l'image :
// le pourtour cadre souvent une table, une main ou un mur, qui fausseraient tout.
function teinteDominante(dataURL){
  return new Promise(res=>{
    try{
      const img=new Image();
      img.onerror=()=>res(null);
      img.onload=()=>{
        try{
          const T=40,cv=document.createElement('canvas');
          cv.width=T;cv.height=T;
          const c=cv.getContext('2d');
          c.drawImage(img,0,0,T,T);
          const d=c.getImageData(0,0,T,T).data;
          let r=0,g=0,b=0,n=0;
          for(let y=Math.round(T*0.25);y<Math.round(T*0.75);y++){
            for(let x=Math.round(T*0.25);x<Math.round(T*0.75);x++){
              const i=(y*T+x)*4;
              if(d[i+3]<128)continue;
              r+=d[i];g+=d[i+1];b+=d[i+2];n++;
            }
          }
          res(n?{r:Math.round(r/n),g:Math.round(g/n),b:Math.round(b/n)}:null);
        }catch{res(null);}
      };
      img.src=dataURL;
    }catch{res(null);}
  });
}
const hexVersRgb=h=>{const m=/^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(String(h||""));return m?{r:parseInt(m[1],16),g:parseInt(m[2],16),b:parseInt(m[3],16)}:null;};
const rgbVersHex=t=>"#"+[t.r,t.g,t.b].map(v=>Math.max(0,Math.min(255,v)).toString(16).padStart(2,"0")).join("");
// La couleur du nuancier la plus proche d'une teinte mesurée
function couleurLaPlusProche(teinte,palette){
  let best=null,dist=Infinity;
  for(const c of palette){
    const t=hexVersRgb(c.h);
    if(!t)continue;
    const d=(t.r-teinte.r)**2+(t.g-teinte.g)**2+(t.b-teinte.b)**2;
    if(d<dist){dist=d;best=c;}
  }
  return best;
}

// Stockage photo par id — disque userData/photos sur Electron, clés cp_p_ en IndexedDB sur web/mobile
// (même mécanisme que les photos de la Collection → inclus dans les sauvegardes existantes)
// Identifiant de CET appareil — tiré au sort à la première utilisation et gardé.
// Il ne dit rien de personnel : il sert seulement à compter les appareils d'un
// abonnement, pour distinguer le patron qui a un téléphone et un ordinateur du
// compte prêté à trois voisins.
async function idAppareil(){
  try{const r=await window.storage.get("cp_appareil");if(r&&r.value)return r.value;}catch{}
  const brut=(window.crypto&&window.crypto.randomUUID)?window.crypto.randomUUID():(String(Date.now())+Math.random().toString(36).slice(2));
  const id=brut.replace(/[^A-Za-z0-9]/g,"").slice(0,32);
  try{await window.storage.set("cp_appareil",id);}catch{}
  return id;
}
const nomAppareil=()=>window.electronAPI?"Ordinateur":(/Android|iPhone|iPad/i.test(navigator.userAgent)?"Téléphone":"Navigateur");

const photoStore={
  async save(id,data){if(window.electronAPI?.photos){await window.electronAPI.photos.save(id,data);}else{await window.storage.set("cp_p_"+id,data);}},
  async get(id){
    if(!id)return null;
    if(window.electronAPI?.photos?.get){try{return await window.electronAPI.photos.get(id);}catch{return null;}}
    try{const r=await window.storage.get("cp_p_"+id);return r?r.value:null;}catch{return null;}
  },
  async del(id){
    if(!id)return;
    if(window.electronAPI?.photos){try{await window.electronAPI.photos.delete(id);}catch{}}
    else{try{await window.storage.delete("cp_p_"+id);}catch{}}
  }
};

function migrateTissus(o){
  if(o.tissus&&Array.isArray(o.tissus))return{...o,tissus:o.tissus.map(t=>({type:t.type||"",custom:t.custom||"",metres:t.metres||"",couleur:t.couleur||"",modele:t.modele||"",source:t.source||"client",photoTissu:t.photoTissu||"",photoModele:t.photoModele||""}))};
  const t={type:o.typeTissu||"",custom:o.tissuCustom||"",metres:o.nombreMetres||"",couleur:o.couleur||"",modele:o.modele||"",source:"client",photoTissu:"",photoModele:""};
  return{...o,tissus:(t.type||t.metres||t.couleur||t.modele)?[t]:[{type:"",custom:"",metres:"",couleur:"",modele:"",source:"client",photoTissu:"",photoModele:""}]};
}
function tissuLabel(t){return t.type==="Autre"?(t.custom||"Autre"):(t.type||"—");}

// ── RESPONSIVE : détection mobile/tablette (bascule la mise en page) ──
function useIsMobile(){
  const[m,setM]=useState(typeof window!=="undefined"&&window.innerWidth<768);
  useEffect(()=>{
    const h=()=>setM(window.innerWidth<768);
    window.addEventListener("resize",h);
    return()=>window.removeEventListener("resize",h);
  },[]);
  return m;
}

// ── CATALOGUE HTML (Pro) — page web autonome avec sélection ❤️ → WhatsApp ──
// ── GÉNÉRATEUR DE LOGOS (monogrammes pro pour clients Premium) ──────
const LOGO_STYLES=[
  {id:"or",l:"Cercle Or"},
  {id:"nuit",l:"Cercle Nuit"},
  {id:"moderne",l:"Carré Moderne"},
  // Le modèle « Ciseaux » n'est proposé qu'aux ateliers de couture.
  ...(IS_GROSSISTE?[]:[{id:"ciseaux",l:"Ciseaux"}]),
  {id:"anneau",l:"Double Anneau"},
  {id:"minimal",l:"Minimal"}
];
// 6 palettes × 6 styles = 36 combinaisons, chacune unique par les initiales du client
const LOGO_PALETTES=[
  {id:"classique",l:"Or & Nuit",c1:"#f5d060",c2:"#c8951a",c3:"#1a1a2e"},
  {id:"emeraude",l:"Émeraude",c1:"#34d399",c2:"#065f46",c3:"#ffffff"},
  {id:"royal",l:"Bleu Royal",c1:"#60a5fa",c2:"#1e3a8a",c3:"#ffffff"},
  {id:"rose",l:"Rose Chic",c1:"#f9a8d4",c2:"#9d174d",c3:"#ffffff"},
  {id:"terre",l:"Terre Cuite",c1:"#fbbf24",c2:"#7c2d12",c3:"#ffffff"},
  {id:"ardoise",l:"Ardoise",c1:"#94a3b8",c2:"#0f172a",c3:"#ffffff"}
];
function logoDataURL(nom,style,paletteId){
  const initials=((nom||"").trim().split(/\s+/).map(w=>w[0]||"").join("").slice(0,2).toUpperCase())||"AT";
  const pal=LOGO_PALETTES.find(p=>p.id===paletteId)||LOGO_PALETTES[0];
  const{c1,c2,c3}=pal;
  const S=240;
  const cv=document.createElement("canvas");cv.width=S;cv.height=S;
  const x=cv.getContext("2d");
  const cx=S/2,cy=S/2;
  x.textAlign="center";x.textBaseline="middle";
  if(style==="or"){
    const g=x.createLinearGradient(0,0,S,S);g.addColorStop(0,c1);g.addColorStop(1,c2);
    x.fillStyle=g;x.beginPath();x.arc(cx,cy,112,0,7);x.fill();
    x.fillStyle=c3;x.font="900 92px Georgia, serif";x.fillText(initials,cx,cy+6);
  }else if(style==="nuit"){
    x.fillStyle=c2;x.beginPath();x.arc(cx,cy,112,0,7);x.fill();
    x.strokeStyle=c1;x.lineWidth=5;x.beginPath();x.arc(cx,cy,100,0,7);x.stroke();
    x.fillStyle=c1;x.font="900 88px Georgia, serif";x.fillText(initials,cx,cy+6);
  }else if(style==="moderne"){
    const g=x.createLinearGradient(0,0,S,S);g.addColorStop(0,c2);g.addColorStop(1,c1);
    x.fillStyle=g;const r=44;x.beginPath();x.moveTo(20+r,20);x.arcTo(S-20,20,S-20,S-20,r);x.arcTo(S-20,S-20,20,S-20,r);x.arcTo(20,S-20,20,20,r);x.arcTo(20,20,S-20,20,r);x.fill();
    x.fillStyle=c3;x.font="800 90px Segoe UI, Arial";x.fillText(initials,cx,cy+6);
  }else if(style==="ciseaux"){
    x.fillStyle="#f9f7f0";x.beginPath();x.arc(cx,cy,112,0,7);x.fill();
    x.strokeStyle=c2;x.lineWidth=4;x.beginPath();x.arc(cx,cy,108,0,7);x.stroke();
    x.font="52px serif";x.fillText("✂️",cx,cy-48);
    x.fillStyle=c2;x.font="900 68px Georgia, serif";x.fillText(initials,cx,cy+36);
  }else if(style==="anneau"){
    x.fillStyle="#fff";x.beginPath();x.arc(cx,cy,114,0,7);x.fill();
    x.strokeStyle=c1;x.lineWidth=4;x.beginPath();x.arc(cx,cy,110,0,7);x.stroke();
    x.lineWidth=2;x.beginPath();x.arc(cx,cy,96,0,7);x.stroke();
    x.fillStyle=c2;x.font="900 80px Georgia, serif";x.fillText(initials,cx,cy+5);
  }else{ // minimal
    x.fillStyle="#fff";x.fillRect(0,0,S,S);
    x.fillStyle=c2;x.font="900 104px Segoe UI, Arial";x.fillText(initials,cx,cy-10);
    x.fillStyle=c1;x.fillRect(cx-52,cy+58,104,10);
  }
  return cv.toDataURL("image/png");
}

function buildCatalogHTML(colMeta,colPhotos,atelierInfo){
  const nom=atelierInfo?.nom||"Notre Boutique";
  const adr=atelierInfo?.adresse||"";
  const tel=atelierInfo?.tel||"";
  const email=atelierInfo?.email||"";
  const waNum=tel.replace(/\D/g,"");
  const logo=atelierInfo?.logoImage||(atelierInfo?.logoStyle?logoDataURL(nom,atelierInfo.logoStyle,atelierInfo.logoPalette):"");
  const esc=s=>(s||"").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/"/g,"&quot;");
  const cats=[...new Set(colMeta.map(c=>c.categorie).filter(Boolean))];
  const now=Date.now();
  const timeAgo=id=>{const d=now-(parseInt(id)||now);const j=Math.floor(d/86400000);if(j<1)return"aujourd'hui";if(j===1)return"hier";if(j<30)return"il y a "+j+" j";const m=Math.floor(j/30);if(m<12)return"il y a "+m+" mois";return"il y a "+Math.floor(m/12)+" an"+(Math.floor(m/12)>1?"s":"");};
  // Données produits pour le panier (id → titre, prix numérique)
  const products={};
  colMeta.forEach(c=>{products[c.id]={t:c.titre||"Sans titre",p:Math.round(parseFloat(c.prixIndicatif)||0)};});
  const productsJSON=JSON.stringify(products).replace(/</g,"\\u003c");
  const cards=colMeta.map(c=>{
    const img=colPhotos[c.id]||"";
    const titre=esc(c.titre||"Sans titre");
    const cat=esc(c.categorie||"");
    const desc=esc(c.description||"");
    const prixNum=Math.round(parseFloat(c.prixIndicatif)||0);
    return `<article class="card" data-cat="${cat}" data-t="${titre.toLowerCase()}">
      <div class="ph" onclick="openZoom(this)">${img?`<img src="${img}" alt="${titre}" loading="lazy">`:`<div class="np">🧵</div>`}</div>
      <div class="ci">
        <h3>${titre}</h3>
        ${prixNum>0?`<div class="pr">${prixNum.toLocaleString("fr-FR")} FCFA${IS_GROSSISTE?"/m":""}</div>`:`<div class="pr" style="color:#94a3b8;font-size:13px">Prix sur demande</div>`}
        <div class="ago">${timeAgo(c.id)}${cat?" · "+cat:""}</div>
        ${desc?`<div class="dsc">${desc}</div>`:""}
        <div class="rowb">
          <button class="order" onclick="buyNow('${c.id}')"><svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor"><path d="M12 2C6.5 2 2 6.3 2 11.6c0 1.9.6 3.7 1.6 5.2L2.5 21l4.4-1.1c1.5.8 3.2 1.3 5.1 1.3 5.5 0 10-4.3 10-9.6S17.5 2 12 2z"/></svg> Commander</button>
          <button class="addc" onclick="addCart('${c.id}',1,true)" title="Ajouter au panier">🛒</button>
        </div>
      </div>
    </article>`;
  }).join("\n");
  return `<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1"><title>${esc(nom)} — Boutique en ligne</title>
<meta name="description" content="Boutique en ligne de ${esc(nom)} — commandez directement sur WhatsApp.">
<meta name="theme-color" content="#1a1a2e">
<style>
*{margin:0;padding:0;box-sizing:border-box;-webkit-tap-highlight-color:transparent}
body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;background:#f1f5f9;color:#0f172a;min-height:100vh}
.hdr{background:linear-gradient(160deg,#1a1a2e,#23234a);padding:26px 16px 58px;text-align:center;color:#fff;position:relative}
.hdr .lg{width:78px;height:78px;border-radius:50%;margin:0 auto 10px;background:rgba(255,255,255,0.1);display:flex;align-items:center;justify-content:center;font-size:34px;border:2px solid rgba(212,160,23,0.6);overflow:hidden}
.hdr .lg img{width:100%;height:100%;object-fit:cover}
.hdr h1{font-size:26px;font-weight:900;letter-spacing:-0.5px}
.hdr .sub{color:#9ca3af;font-size:12px;margin-top:4px}
.srch{max-width:640px;margin:-30px auto 0;padding:0 16px;position:relative;z-index:5}
.srch .bx{background:#fff;border-radius:16px;box-shadow:0 8px 26px rgba(2,6,23,0.12);display:flex;align-items:center;padding:4px 8px 4px 18px}
.srch input{flex:1;border:none;outline:none;padding:13px 6px;font-size:15px;background:transparent}
.srch .ic{color:#94a3b8;font-size:17px}
.pills{display:flex;gap:8px;overflow-x:auto;padding:18px 16px 6px;max-width:1150px;margin:0 auto;-webkit-overflow-scrolling:touch;scrollbar-width:none}
.pills::-webkit-scrollbar{display:none}
.pill{flex-shrink:0;background:#fff;color:#475569;border:1.5px solid #e2e8f0;padding:9px 18px;border-radius:24px;font-size:13px;font-weight:700;cursor:pointer;font-family:inherit;transition:all .15s}
.pill.on{background:#1a1a2e;color:#d4a017;border-color:#1a1a2e}
.wrap{max-width:1150px;margin:0 auto;padding:12px 14px 120px}
.grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(165px,1fr));gap:13px}
.card{background:#fff;border-radius:18px;overflow:hidden;box-shadow:0 2px 10px rgba(2,6,23,0.06);display:flex;flex-direction:column}
.ph{aspect-ratio:1/1;background:linear-gradient(135deg,#f8fafc,#e2e8f0);overflow:hidden;cursor:zoom-in}
.ph img{width:100%;height:100%;object-fit:cover;display:block}
.np{width:100%;height:100%;display:flex;align-items:center;justify-content:center;font-size:64px}
.ci{padding:12px 13px 13px;display:flex;flex-direction:column;flex:1}
.ci h3{font-size:14.5px;font-weight:800;color:#0f172a;margin-bottom:4px;line-height:1.3}
.pr{font-size:16px;font-weight:900;color:#b8860b;margin-bottom:2px}
.ago{font-size:11px;color:#94a3b8;margin-bottom:8px}
.dsc{font-size:11.5px;color:#64748b;line-height:1.5;margin-bottom:9px;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
.rowb{display:flex;gap:7px;margin-top:auto}
.order{flex:1;background:#25D366;color:#fff;border:none;padding:11px 8px;border-radius:12px;font-weight:800;font-size:12.5px;cursor:pointer;font-family:inherit;display:flex;align-items:center;justify-content:center;gap:6px}
.addc{background:#f1f5f9;border:1.5px solid #e2e8f0;border-radius:12px;width:44px;cursor:pointer;font-size:16px}
.empty{text-align:center;padding:70px 20px;color:#94a3b8}
.cartfab{position:fixed;bottom:18px;left:50%;transform:translateX(-50%) translateY(110px);background:#1a1a2e;color:#fff;padding:14px 24px;border-radius:40px;box-shadow:0 12px 32px rgba(2,6,23,0.35);display:flex;align-items:center;gap:12px;cursor:pointer;font-weight:800;z-index:90;transition:transform .3s;border:none;font-family:inherit;font-size:14px}
.cartfab.show{transform:translateX(-50%) translateY(0)}
.cartfab .n{background:#d4a017;color:#1a1a2e;border-radius:50%;min-width:26px;height:26px;display:inline-flex;align-items:center;justify-content:center;font-weight:900;font-size:13px;padding:0 6px}
.ov{position:fixed;inset:0;background:rgba(15,23,42,0.5);z-index:100;display:none;align-items:flex-end;justify-content:center}
.ov.show{display:flex}
.sheet{background:#fff;width:100%;max-width:560px;max-height:92vh;border-radius:22px 22px 0 0;display:flex;flex-direction:column;overflow:hidden;animation:up .25s ease-out}
@keyframes up{from{transform:translateY(60px);opacity:.4}to{transform:translateY(0);opacity:1}}
.sh{padding:18px 20px 10px;flex-shrink:0}
.sh .step{color:#b8860b;font-size:11px;font-weight:900;letter-spacing:1.5px}
.sh h2{font-size:21px;font-weight:900;margin-top:3px}
.sh .x{position:absolute;right:16px;top:16px;background:#f1f5f9;border:none;width:36px;height:36px;border-radius:10px;font-size:17px;cursor:pointer}
.sb{overflow-y:auto;padding:6px 20px 16px;flex:1}
.sf{padding:14px 20px calc(16px + env(safe-area-inset-bottom));border-top:1px solid #f1f5f9;flex-shrink:0;background:#fff}
.tot{display:flex;justify-content:space-between;align-items:center;margin-bottom:12px}
.tot .l{color:#64748b;font-size:14px}.tot .v{font-size:21px;font-weight:900}
.big{width:100%;background:#1a1a2e;color:#fff;border:none;padding:16px;border-radius:14px;font-weight:800;font-size:15px;cursor:pointer;font-family:inherit}
.big:disabled{background:#cbd5e1;cursor:default}
.big.green{background:#25D366}
.it{display:flex;align-items:center;gap:12px;background:#f0fdf4;border:1px solid #dcfce7;border-radius:14px;padding:10px 12px;margin-bottom:9px}
.it .t{flex:1;min-width:0}
.it .t b{font-size:13.5px;display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.it .t span{font-size:12px;color:#64748b}
.q{display:flex;align-items:center;gap:2px;background:#fff;border:1.5px solid #e2e8f0;border-radius:22px;padding:3px}
.q button{width:30px;height:30px;border:none;background:transparent;font-size:17px;cursor:pointer;color:#475569;font-family:inherit}
.q span{min-width:22px;text-align:center;font-weight:800;font-size:14px}
label.fl{display:block;font-size:13px;font-weight:800;margin:13px 0 6px}
label.fl em{color:#dc2626;font-style:normal}
label.fl small{color:#94a3b8;font-weight:500}
.in{width:100%;border:1.5px solid #e2e8f0;border-radius:12px;padding:13px 14px;font-size:15px;font-family:inherit;outline:none;background:#f8fafc}
.in:focus{border-color:#1a1a2e;background:#fff}
textarea.in{resize:vertical;min-height:64px}
.telrow{display:flex;gap:8px}
.telrow .cc{width:86px;flex-shrink:0}
.pay{display:flex;align-items:center;gap:12px;border:1.5px solid #e2e8f0;border-radius:14px;padding:14px;margin-bottom:9px;cursor:pointer;background:#fff}
.pay.on{border-color:#25D366;background:#f0fdf4}
.pay .pi{font-size:22px}
.plogo{width:42px;height:42px;border-radius:11px;display:flex;align-items:center;justify-content:center;flex-shrink:0;box-shadow:0 2px 7px rgba(0,0,0,0.14);color:#fff;font-family:Arial,sans-serif}
.pay .pt{flex:1}
.pay .pt b{font-size:14px;display:block}
.pay .pt span{font-size:11.5px;color:#64748b}
.pay .chk{width:22px;height:22px;border-radius:50%;border:2px solid #cbd5e1;display:flex;align-items:center;justify-content:center;color:#fff;font-size:12px}
.pay.on .chk{background:#25D366;border-color:#25D366}
.done{text-align:center;padding:26px 10px}
.done .ok{width:74px;height:74px;background:#25D366;border-radius:50%;display:flex;align-items:center;justify-content:center;color:#fff;font-size:36px;margin:0 auto 16px}
.done h3{font-size:18px;font-weight:900;margin-bottom:8px}
.done p{color:#64748b;font-size:13.5px;line-height:1.6}
.zoom{position:fixed;inset:0;background:rgba(2,6,23,0.94);z-index:200;display:none;align-items:center;justify-content:center;cursor:zoom-out}
.zoom.show{display:flex}
.zoom img{max-width:96vw;max-height:88vh;object-fit:contain;border-radius:10px}
.foot{background:#1a1a2e;color:#fff;padding:32px 18px calc(28px + env(safe-area-inset-bottom));text-align:center;margin-top:26px}
.foot .fl2{display:flex;flex-wrap:wrap;gap:9px;justify-content:center;margin:14px 0}
.foot a{color:#fff;text-decoration:none;font-size:12.5px;background:rgba(255,255,255,0.08);padding:10px 16px;border-radius:10px;font-weight:600}
.foot a.wa{background:#25D366;font-weight:800}
.foot .tb{color:#64748b;font-size:10.5px;margin-top:14px}
.foot .tb b{color:#d4a017}
.wafloat{position:fixed;bottom:18px;right:14px;width:54px;height:54px;background:#25D366;border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:0 8px 22px rgba(37,211,102,0.45);z-index:80;text-decoration:none}
.wafloat svg{width:28px;height:28px;fill:#fff}
.sm{max-width:640px;margin:30px auto 0;padding:0 14px}
.smc{background:#fff;border-radius:20px;padding:26px 20px;text-align:center;box-shadow:0 2px 12px rgba(2,6,23,0.07)}
.smc h2{font-size:19px;font-weight:900;margin-bottom:6px}
.smc>p{color:#64748b;font-size:13px;line-height:1.6;margin-bottom:16px}
.smf{text-align:left;display:flex;flex-direction:column;gap:10px}
.smr{display:flex;gap:8px;flex-wrap:wrap}
.smr label{flex:1;min-width:150px;display:flex;align-items:center;gap:8px;border:1.5px solid #e2e8f0;border-radius:12px;padding:12px;font-size:13px;font-weight:700;cursor:pointer;background:#f8fafc}
.smr label:has(input:checked){border-color:#1a1a2e;background:#fff}
@media(min-width:700px){.grid{grid-template-columns:repeat(auto-fill,minmax(215px,1fr))}.ov{align-items:center}.sheet{border-radius:22px}}
</style>
</head><body>

<header class="hdr">
  <div class="lg">${logo?`<img src="${logo}" alt="">`:"🏬"}</div>
  <h1>${esc(nom)}</h1>
  <div class="sub">${adr?"📍 "+esc(adr):"Boutique en ligne"}</div>
</header>

<div class="srch"><div class="bx"><span class="ic">🔍</span><input id="q" type="search" placeholder="Rechercher un produit..." oninput="applyFilters()"></div></div>

<div class="pills" id="pills">
  <button class="pill on" data-c="" onclick="setCat(this)">Tout</button>
  ${cats.map(c=>`<button class="pill" data-c="${esc(c)}" onclick="setCat(this)">${esc(c)}</button>`).join("")}
</div>

<main class="wrap">
  ${colMeta.length>0?`<div class="grid" id="grid">${cards}</div>`:`<div class="empty"><div style="font-size:56px;margin-bottom:12px">🧵</div>Boutique en cours de préparation</div>`}
  <div class="empty" id="noRes" style="display:none"><div style="font-size:42px;margin-bottom:10px">🔍</div>Aucun produit trouvé</div>
</main>

${IS_GROSSISTE?"":`
<!-- SUR-MESURE : le client envoie ses mesures ou demande un rendez-vous -->
<section class="sm">
  <div class="smc">
    <div style="font-size:36px;margin-bottom:8px">📏</div>
    <h2>Vous voulez du sur-mesure ?</h2>
    <p>Envoyez-nous vos mesures ou prenez rendez-vous à l'atelier — nous créons la tenue de vos rêves.</p>
    <div class="smf">
      <input class="in" id="mNom" placeholder="Votre nom">
      <input class="in" id="mTel" type="tel" placeholder="Votre téléphone (77 000 00 00)">
      <select class="in" id="mTissu">
        <option value="">Type de tissu souhaité (optionnel)</option>
        ${TISSUS.filter(t=>t!=="Autre").map(t=>`<option>${t}</option>`).join("")}
      </select>
      <div class="smr">
        <label><input type="radio" name="mMode" value="mesures" checked> 📏 J'envoie mes mesures</label>
        <label><input type="radio" name="mMode" value="rdv"> 📅 Je préfère un rendez-vous</label>
      </div>
      <textarea class="in" id="mMsg" placeholder="Vos mesures (épaule, poitrine, longueur, taille...) ou vos disponibilités pour le rendez-vous"></textarea>
      <button class="big green" onclick="sendMesure()" style="margin-top:6px">💬 Envoyer sur WhatsApp</button>
    </div>
  </div>
</section>`}
<footer class="foot">
  <div style="font-size:19px;font-weight:900;color:#d4a017">${esc(nom)}</div>
  ${adr?`<div style="font-size:12px;color:#9ca3af;margin-top:5px">📍 ${esc(adr)}</div>`:""}
  <div class="fl2">
    ${tel?`<a href="tel:+221${waNum}">📞 ${esc(tel)}</a>`:""}
    ${waNum?`<a class="wa" href="https://wa.me/221${waNum}" target="_blank">💬 WhatsApp</a>`:""}
    ${email?`<a href="mailto:${esc(email)}">✉️ Email</a>`:""}
  </div>
  <div class="tb">Boutique propulsée par <b>TOBY</b></div>
</footer>

${waNum?`<a class="wafloat" href="https://wa.me/221${waNum}" target="_blank" aria-label="WhatsApp"><svg viewBox="0 0 24 24"><path d="M12 2C6.5 2 2 6.3 2 11.6c0 1.9.6 3.7 1.6 5.2L2.5 21l4.4-1.1c1.5.8 3.2 1.3 5.1 1.3 5.5 0 10-4.3 10-9.6S17.5 2 12 2zm5 13.4c-.2.6-1.2 1.2-1.7 1.2-.4.1-1 .1-1.6-.1-.4-.1-.9-.3-1.5-.5-2.6-1.1-4.3-3.7-4.4-3.9-.1-.2-1.1-1.4-1.1-2.7 0-1.3.7-1.9.9-2.2.2-.3.5-.3.7-.3h.5c.2 0 .4 0 .6.4.2.5.7 1.8.8 1.9.1.1.1.3 0 .5-.1.2-.1.3-.3.5l-.4.5c-.1.1-.3.3-.1.6.2.3.8 1.3 1.7 2.1 1.2 1 2.1 1.4 2.4 1.5.3.1.5.1.6-.1.2-.2.7-.8.9-1.1.2-.3.4-.2.6-.1.3.1 1.6.8 1.9.9.3.2.5.2.5.4.1.1.1.7-.1 1.3z"/></svg></a>`:""}

<button class="cartfab" id="cartFab" onclick="openCart()">🛒 Voir le panier <span class="n" id="cartN">0</span></button>

<!-- PANIER / COMMANDE -->
<div class="ov" id="ov">
  <div class="sheet" style="position:relative">
    <button class="x sh-x" style="position:absolute;right:16px;top:16px;background:#f1f5f9;border:none;width:36px;height:36px;border-radius:10px;font-size:17px;cursor:pointer;z-index:2" onclick="closeOv()">✕</button>
    <!-- ÉTAPE PANIER -->
    <div id="stepCart">
      <div class="sh"><div class="step">MON PANIER</div><h2>Vos articles</h2></div>
      <div class="sb" id="cartList"></div>
      <div class="sf"><div class="tot"><span class="l">Total</span><span class="v" id="tot1">0 FCFA</span></div><button class="big" onclick="goStep('stepForm')">Continuer</button></div>
    </div>
    <!-- ÉTAPE COORDONNÉES -->
    <div id="stepForm" style="display:none">
      <div class="sh"><div class="step">ÉTAPE 1/2</div><h2>Vos coordonnées</h2></div>
      <div class="sb">
        <label class="fl">Téléphone <em>*</em></label>
        <div class="telrow"><input class="in cc" value="+221" readonly><input class="in" id="fTel" type="tel" placeholder="77 000 00 00" oninput="checkForm()"></div>
        <label class="fl">Adresse de livraison <em>*</em></label>
        <textarea class="in" id="fAdr" placeholder="Quartier, rue, point de repère, ville" oninput="checkForm()"></textarea>
        <label class="fl">Nom <small>(optionnel)</small></label>
        <input class="in" id="fNom" placeholder="Ex : Awa Diallo">
        <label class="fl">Note pour la boutique <small>(optionnel)</small></label>
        <textarea class="in" id="fNote" placeholder="Ex : couleur préférée, livraison après 18h..."></textarea>
      </div>
      <div class="sf"><div class="tot"><span class="l">Total à payer</span><span class="v" id="tot2">0 FCFA</span></div><button class="big" id="btnForm" disabled onclick="goStep('stepPay')">Continuer</button></div>
    </div>
    <!-- ÉTAPE PAIEMENT -->
    <div id="stepPay" style="display:none">
      <div class="sh"><div class="step">ÉTAPE 2/2</div><h2>Choisir le paiement</h2></div>
      <div class="sb" id="payList">
        <div class="pay on" data-p="Wave" onclick="setPay(this)"><span class="plogo" style="background:#00b9f1"><svg viewBox="0 0 24 24" width="26" height="26"><path d="M3 14.5c2.4-4.6 4.4-4.6 6.2-.6s3.8 4 6.2-.6 3.4-3.6 5.6-1.6" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round"/></svg></span><div class="pt"><b>Wave</b><span>Paiement mobile</span></div><span class="chk">✓</span></div>
        <div class="pay" data-p="Orange Money" onclick="setPay(this)"><span class="plogo" style="background:#ff7900;font-weight:900;font-size:14px;letter-spacing:-0.5px">OM</span><div class="pt"><b>Orange Money</b><span>Paiement mobile</span></div><span class="chk">✓</span></div>
        <div class="pay" data-p="Free Money" onclick="setPay(this)"><span class="plogo" style="background:#cd1126;font-weight:900;font-size:17px;font-style:italic">free</span><div class="pt"><b>Free Money</b><span>Paiement mobile</span></div><span class="chk">✓</span></div>
        <div class="pay" data-p="À la livraison" onclick="setPay(this)"><span class="plogo" style="background:#f1f5f9;font-size:19px">🕐</span><div class="pt"><b>Payer plus tard</b><span>La boutique vous contacte pour finaliser</span></div><span class="chk">✓</span></div>
      </div>
      <div class="sf"><div class="tot"><span class="l">Total à payer</span><span class="v" id="tot3">0 FCFA</span></div><button class="big green" onclick="sendOrder()">Envoyer la commande</button></div>
    </div>
    <!-- ÉTAPE MERCI -->
    <div id="stepDone" style="display:none">
      <div class="sh"><div class="step">COMMANDE ENVOYÉE</div><h2>Merci !</h2></div>
      <div class="sb"><div class="done">
        <div class="ok">✓</div>
        <h3>Votre commande a bien été envoyée</h3>
        <p>Votre commande est partie sur le WhatsApp de la boutique.<br>Nous vous contacterons très prochainement pour finaliser le paiement et la livraison.</p>
      </div></div>
      <div class="sf"><button class="big" onclick="closeOv()">Fermer</button></div>
    </div>
  </div>
</div>

<div class="zoom" id="zoom" onclick="this.classList.remove('show')"><img id="zoomImg" src="" alt=""></div>

<script>
var PRODUCTS=${productsJSON};
var SHOP_TEL='${waNum?"221"+waNum:""}';
var SHOP_NAME=${JSON.stringify(nom).replace(/</g,"\\u003c")};
var cart={};
var curCat='';
var payMode='Wave';
function fmt(n){return (n||0).toLocaleString('fr-FR');}
function cartCount(){var n=0;for(var k in cart)n+=cart[k];return n;}
function cartTotal(){var t=0;for(var k in cart){var p=PRODUCTS[k];if(p&&p.p>0)t+=p.p*cart[k];}return t;}
function refreshFab(){var n=cartCount();var f=document.getElementById('cartFab');document.getElementById('cartN').textContent=n;if(n>0)f.classList.add('show');else f.classList.remove('show');}
function addCart(id,q,silent){cart[id]=(cart[id]||0)+(q||1);refreshFab();if(!silent)openCart();}
function buyNow(id){if(!cart[id])cart[id]=1;refreshFab();openCart();}
function setQty(id,d){cart[id]=(cart[id]||0)+d;if(cart[id]<=0)delete cart[id];renderCart();refreshFab();}
function renderCart(){
  var el=document.getElementById('cartList');var h='';
  for(var k in cart){var p=PRODUCTS[k];if(!p)continue;
    h+='<div class="it"><div class="t"><b>'+p.t+'</b><span>'+(p.p>0?fmt(p.p)+' FCFA × '+cart[k]:'Prix sur demande')+'</span></div>'
      +'<div class="q"><button onclick="setQty(\\''+k+'\\',-1)">−</button><span>'+cart[k]+'</span><button onclick="setQty(\\''+k+'\\',1)">+</button></div></div>';
  }
  if(!h)h='<div class="empty" style="padding:34px 10px">Votre panier est vide</div>';
  el.innerHTML=h;
  var t=fmt(cartTotal())+' FCFA';
  document.getElementById('tot1').textContent=t;
  document.getElementById('tot2').textContent=t;
  document.getElementById('tot3').textContent=t;
}
function openCart(){renderCart();showOv();goStep('stepCart');}
function showOv(){document.getElementById('ov').classList.add('show');document.body.style.overflow='hidden';}
function closeOv(){document.getElementById('ov').classList.remove('show');document.body.style.overflow='';goStep('stepCart');}
function goStep(s){['stepCart','stepForm','stepPay','stepDone'].forEach(function(x){document.getElementById(x).style.display=(x===s)?'':'none';});}
function checkForm(){
  var ok=document.getElementById('fTel').value.replace(/\\D/g,'').length>=9&&document.getElementById('fAdr').value.trim().length>=4;
  document.getElementById('btnForm').disabled=!ok;
}
function setPay(el){payMode=el.getAttribute('data-p');var ps=document.querySelectorAll('.pay');for(var i=0;i<ps.length;i++)ps[i].classList.remove('on');el.classList.add('on');}
function sendOrder(){
  if(!SHOP_TEL){alert('Numéro WhatsApp de la boutique non configuré.');return;}
  var lines='🛍️ NOUVELLE COMMANDE — '+SHOP_NAME+'\\n━━━━━━━━━━━━\\n';
  for(var k in cart){var p=PRODUCTS[k];if(!p)continue;
    lines+='• '+p.t+' × '+cart[k]+(p.p>0?' — '+fmt(p.p*cart[k])+' F':'')+'\\n';
  }
  lines+='━━━━━━━━━━━━\\n💰 TOTAL : '+fmt(cartTotal())+' FCFA\\n\\n';
  var nomC=document.getElementById('fNom').value.trim();
  var note=document.getElementById('fNote').value.trim();
  lines+='👤 '+(nomC||'Client')+'\\n';
  lines+='📞 +221 '+document.getElementById('fTel').value.trim()+'\\n';
  lines+='📍 '+document.getElementById('fAdr').value.trim()+'\\n';
  if(note)lines+='📝 '+note+'\\n';
  lines+='💳 Paiement : '+payMode;
  window.open('https://wa.me/'+SHOP_TEL+'?text='+encodeURIComponent(lines),'_blank');
  goStep('stepDone');
  cart={};refreshFab();
}
function sendMesure(){
  if(!SHOP_TEL){alert('Numéro WhatsApp de la boutique non configuré.');return;}
  var mm=document.querySelector('input[name=mMode]:checked');
  var mode=mm?mm.value:'mesures';
  var nomC=document.getElementById('mNom').value.trim();
  var telC=document.getElementById('mTel').value.trim();
  var tissu=document.getElementById('mTissu').value;
  var msg=document.getElementById('mMsg').value.trim();
  var lines=(mode==='rdv'?'📅 DEMANDE DE RENDEZ-VOUS':'📏 MES MESURES POUR DU SUR-MESURE')+' — '+SHOP_NAME+'\n━━━━━━━━━━━━\n';
  lines+='👤 '+(nomC||'Client')+'\n';
  if(telC)lines+='📞 '+telC+'\n';
  if(tissu)lines+='🧵 Tissu souhaité : '+tissu+'\n';
  if(msg)lines+='\n'+msg;
  window.open('https://wa.me/'+SHOP_TEL+'?text='+encodeURIComponent(lines),'_blank');
}
function setCat(el){
  curCat=el.getAttribute('data-c');
  var ps=document.querySelectorAll('.pill');for(var i=0;i<ps.length;i++)ps[i].classList.remove('on');
  el.classList.add('on');applyFilters();
}
function applyFilters(){
  var q=(document.getElementById('q').value||'').toLowerCase();
  var cards=document.querySelectorAll('.card');var vis=0;
  for(var i=0;i<cards.length;i++){
    var c=cards[i];
    var okCat=!curCat||c.getAttribute('data-cat')===curCat;
    var okQ=!q||c.getAttribute('data-t').indexOf(q)>=0;
    var show=okCat&&okQ;
    c.style.display=show?'':'none';
    if(show)vis++;
  }
  document.getElementById('noRes').style.display=vis===0?'':'none';
}
function openZoom(el){
  var img=el.querySelector('img');
  if(img){document.getElementById('zoomImg').src=img.src;document.getElementById('zoom').classList.add('show');}
}
</script>
</body></html>`;
}

// ── BILAN FINANCIER HTML (Pro) ─────────────────────────────────────
// CATALOGUE PAPIER — Maison de couture.
// Produit une page A4 prête à imprimer : couverture, puis 4 modèles par page.
// Le client passe par « Imprimer » et choisit « Enregistrer en PDF ».
function buildCatalogueHTML(modeles, photos, atelierInfo) {
  const nom = (atelierInfo && atelierInfo.nom) || "Ma maison de couture";
  const annee = new Date().getFullYear();
  const logo = (atelierInfo && atelierInfo.logoImage)
    || (atelierInfo && atelierInfo.logoStyle ? logoDataURL(nom, atelierInfo.logoStyle, atelierInfo.logoPalette) : "");
  const esc = (t) => String(t == null ? "" : t)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

  // 4 modèles par page
  const pages = [];
  for (let i = 0; i < modeles.length; i += 4) pages.push(modeles.slice(i, i + 4));

  const carte = (m) => {
    const ph = photos[m.id];
    const img = ph
      ? `<div class="ph"><img src="${ph}" alt=""></div>`
      : `<div class="ph vide">👗</div>`;
    const prix = m.prixIndicatif
      ? `<div class="pr">${(parseFloat(m.prixIndicatif)||0).toLocaleString("fr-FR")} F</div>`
      : "";
    const desc = m.description
      ? `<div class="de">${esc(m.description).slice(0,90)}</div>`
      : "";
    return `<div class="md">${img}<div class="in"><div class="ti">${esc(m.titre)||"Sans titre"}</div>
      <div class="ca">${esc(m.categorie)}</div>${desc}${prix}</div></div>`;
  };

  const corps = pages.map((p) => `<div class="pg"><div class="gr">${p.map(carte).join("")}</div>
    <div class="pd">${esc(nom)} · ${esc((atelierInfo&&atelierInfo.tel)||"")}</div></div>`).join("");

  return `<!doctype html><html lang="fr"><head><meta charset="utf-8">
<title>Catalogue ${esc(nom)}</title><style>
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:Georgia,"Times New Roman",serif;background:#e8e4dc;color:#1a1a2e}
.pg{width:210mm;min-height:297mm;background:#fff;margin:10mm auto;padding:14mm;
    box-shadow:0 4px 24px rgba(0,0,0,.13);position:relative;display:flex;flex-direction:column}
.cv{justify-content:center;align-items:center;text-align:center;background:#faf4ea}
.cv .lg{width:54mm;height:54mm;object-fit:contain;margin-bottom:12mm}
.cv .nm{font-size:34pt;font-weight:700;color:#1e3a5f;letter-spacing:1px;line-height:1.15}
.cv .tr{width:44mm;height:2px;background:#c9a227;margin:9mm auto}
.cv .co{font-size:15pt;color:#c9a227;letter-spacing:5px;text-transform:uppercase}
.cv .an{font-size:12pt;color:#8a8578;margin-top:3mm}
.cv .ct{margin-top:16mm;font-size:10.5pt;color:#6b6659;line-height:1.9}
.gr{display:grid;grid-template-columns:1fr 1fr;gap:9mm;flex:1;align-content:start}
.md{border:1px solid #ece7dc;border-radius:3mm;overflow:hidden;background:#fff;
     break-inside:avoid;page-break-inside:avoid}
.ph{height:74mm;background:#f7f4ee;display:flex;align-items:center;justify-content:center;overflow:hidden}
.ph img{width:100%;height:100%;object-fit:cover}
.ph.vide{font-size:40pt;color:#d8d2c4}
.in{padding:5mm}
.ti{font-size:13pt;font-weight:700;color:#1e3a5f;line-height:1.25}
.ca{font-size:9pt;color:#a09a8c;margin-top:1.5mm;text-transform:uppercase;letter-spacing:1px}
.de{font-size:9.5pt;color:#6b6659;margin-top:2.5mm;line-height:1.5}
.pr{font-size:15pt;font-weight:700;color:#c9a227;margin-top:3.5mm}
.pd{text-align:center;font-size:9pt;color:#b5afa2;padding-top:7mm;border-top:1px solid #f0ece4;margin-top:7mm}
@media print{body{background:#fff}.pg{margin:0;box-shadow:none;page-break-after:always}
  @page{size:A4;margin:0}}
</style></head><body>
<div class="pg cv">
  ${logo ? `<img class="lg" src="${logo}" alt="">` : ""}
  <div class="co">Collection</div>
  <div class="tr"></div>
  <div class="nm">${esc(nom)}</div>
  <div class="an">${annee}</div>
  <div class="ct">
    ${esc((atelierInfo&&atelierInfo.adresse)||"")}<br>
    ${esc((atelierInfo&&atelierInfo.tel)||"")}
  </div>
</div>
${corps}
</body></html>`;
}

function buildBilanHTML(periode,orders,clients,atelierInfo,type){
  // type = "mensuel" | "annuel" — bilan d'une BOUTIQUE DE TISSUS : des ventes, des
  // mètres, des types de tissu. Ni commandes, ni pièces, ni « Ensemble ».
  const inf=atelierInfo||{};
  const nom=inf.nom||"Ma boutique";
  const logo=inf.logoImage||(inf.logoStyle?logoDataURL(nom,inf.logoStyle,inf.logoPalette):"");
  const adr=inf.adresse||"";
  const tel=inf.tel||"";
  const today=new Date().toLocaleDateString("fr-FR",{day:"2-digit",month:"long",year:"numeric"});
  const fmtF=n=>Math.round(n).toLocaleString("fr-FR");
  const fmtM=n=>(Math.round(n*100)/100).toLocaleString("fr-FR");
  const pluriel=(n,mot)=>n+" "+mot+(n>1?"s":"");
  // Totaux
  let tCA=0,tEnc=0,tMetres=0,nbSoldees=0,nbReste=0;
  const clientsSet=new Set();
  const parTissu={}; // type de tissu → { metres, ventes }
  orders.forEach(o=>{
    const prix=parseFloat(o.prixTotal)||0,verse=parseFloat(o.avanceRecue)||0;
    tCA+=prix;tEnc+=verse;
    if(o.telephone)clientsSet.add(o.telephone);
    if(prix>0&&verse>=prix)nbSoldees++;else nbReste++;
    const vus=new Set();
    (o.tissus||[]).forEach(t=>{
      if(!(t.type||t.custom))return;
      const m=parseFloat(t.metres)||0;tMetres+=m;
      const k=tissuLabel(t);
      parTissu[k]=parTissu[k]||{metres:0,ventes:0};
      parTissu[k].metres+=m;
      if(!vus.has(k)){parTissu[k].ventes++;vus.add(k);}
    });
  });
  const tRest=tCA-tEnc;
  const topTissus=Object.entries(parTissu).sort((a,b)=>b[1].metres-a[1].metres).slice(0,5);
  const tauxEnc=tCA>0?(tEnc/tCA*100).toFixed(1):0;
  const cliCA={};
  orders.forEach(o=>{const k=`${o.prenom||""} ${o.nom||""}`.trim()||"—";cliCA[k]=(cliCA[k]||0)+(parseFloat(o.prixTotal)||0);});
  const topClients=Object.entries(cliCA).sort((a,b)=>b[1]-a[1]).slice(0,5);
  // Lignes : de la plus ancienne à la plus récente, comme un livre de caisse
  const num=o=>parseInt((o.cmdNum||"").replace(/[^0-9]/g,""))||0;
  const rows=orders.slice().sort((a,b)=>(a.dateDepot||"").localeCompare(b.dateDepot||"")||num(a)-num(b)).map(o=>{
    const prix=parseFloat(o.prixTotal)||0,verse=parseFloat(o.avanceRecue)||0,r=prix-verse;
    const tissus=(o.tissus||[]).filter(t=>t.type||t.custom).map(t=>tissuLabel(t)+(t.couleur?" "+t.couleur:"")+(t.metres?" · "+fmtM(parseFloat(t.metres)||0)+" m":"")).join(", ")||"—";
    return`<tr><td style="padding:6px 8px;font-weight:700;color:#d4a017;white-space:nowrap">${o.cmdNum||""}</td><td style="padding:6px 8px">${(o.prenom||"")+" "+(o.nom||"")}</td><td style="padding:6px 8px;font-size:10px;color:#666">${tissus}</td><td style="padding:6px 8px;text-align:right;font-weight:600;white-space:nowrap">${fmtF(prix)} F</td><td style="padding:6px 8px;text-align:right;color:#10b981;white-space:nowrap">${fmtF(verse)} F</td><td style="padding:6px 8px;text-align:right;color:${r>0?"#ef4444":"#10b981"};font-weight:700;white-space:nowrap">${r>0?fmtF(r)+" F":"Soldée"}</td></tr>`;
  }).join("");
  return`<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8"><title>Bilan ${type} ${periode} — ${nom}</title>
<style>*{margin:0;padding:0;box-sizing:border-box}
body{font-family:-apple-system,'Segoe UI',sans-serif;background:#f0f0f0;color:#1a1a2e;font-size:12px}
.pg{max-width:850px;margin:16px auto;background:#fff;border-radius:12px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,0.1)}
.bar{height:6px;background:linear-gradient(90deg,#d4a017,#f5d060,#d4a017)}
.hdr{background:#1a1a2e;padding:24px 30px;display:flex;justify-content:space-between;align-items:flex-start;gap:16px;color:#fff}
.id{display:flex;gap:14px;align-items:center}
.logo{width:58px;height:58px;border-radius:12px;object-fit:cover;background:#fff;flex-shrink:0}
.bn{font-size:22px;font-weight:900;color:#fff}
.bs{font-size:9px;letter-spacing:3px;color:#d4a017;font-weight:700;margin-top:4px}
.bi{font-size:11px;color:#aaa;margin-top:6px;line-height:1.7}
.dr{text-align:right}
.badge{display:inline-block;background:#d4a017;color:#1a1a2e;padding:5px 14px;border-radius:20px;font-size:10px;font-weight:900;letter-spacing:2px;margin-bottom:7px}
.dn{font-size:22px;font-weight:900;color:#d4a017}
.dd{font-size:11px;color:#aaa;margin-top:4px}
.bd{padding:24px 30px}
.kpi{display:grid;grid-template-columns:repeat(4,1fr);gap:9px;margin-bottom:20px}
.kpi-i{background:linear-gradient(135deg,#1a1a2e,#2a2a4a);color:#fff;border-radius:9px;padding:13px;text-align:center}
.kpi-l{font-size:9px;color:#888;letter-spacing:1px;margin-bottom:5px;text-transform:uppercase}
.kpi-v{font-size:18px;font-weight:900;color:#d4a017}
.kpi-v.green{color:#34d399}.kpi-v.red{color:#fca5a5}
.sec{margin-bottom:20px}
.st{font-size:11px;font-weight:800;color:#d4a017;text-transform:uppercase;letter-spacing:1.5px;margin-bottom:10px;padding-bottom:6px;border-bottom:2px solid #f0ece4}
table{width:100%;border-collapse:collapse;font-size:11px}
thead tr{background:#f9f7f0;color:#1a1a2e}
th{padding:8px;text-align:left;font-size:9px;font-weight:800;text-transform:uppercase;letter-spacing:.5px;border-bottom:2px solid #d4a017}
td{border-bottom:1px solid #f5f5f5;vertical-align:top}
tr:last-child td{border:none}
tbody tr:nth-child(even){background:#fafafa}
.g2{display:grid;grid-template-columns:1fr 1fr;gap:14px}
.box{background:#f9f7f0;border-radius:9px;padding:13px;border:1px solid #f0ece4}
.row-pair{display:flex;justify-content:space-between;gap:10px;padding:4px 0;border-bottom:1px solid #f0ece4;font-size:11px}
.row-pair:last-child{border:none}
.taux{background:#f0fdf4;border:1px solid #86efac;border-radius:9px;padding:13px;text-align:center;margin-bottom:18px}
.taux-v{font-size:26px;font-weight:900;color:#10b981}.taux-l{font-size:10px;color:#16a34a;margin-top:4px}
.sgs{display:grid;grid-template-columns:1fr 1fr;gap:60px;margin-top:30px;padding:0 30px 16px}
.sg{text-align:center}.sgl{border-top:1.5px solid #1a1a2e;padding-top:8px;margin-top:50px;font-size:10px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:1px}
.pied{text-align:center;font-size:9px;color:#bbb;padding:0 30px 18px}
@media print{body{background:#fff}.pg{box-shadow:none;margin:0;border-radius:0}@page{size:A4;margin:8mm}}</style>
</head><body><div class="pg">
<div class="bar"></div>
<div class="hdr">
  <div class="id">
    ${logo?`<img class="logo" src="${logo}" alt="">`:""}
    <div>
      <div class="bn">${nom}</div>
      <div class="bs">BILAN FINANCIER ${type.toUpperCase()}</div>
      <div class="bi">${adr?`📍 ${adr}`:""}${adr&&tel?"<br>":""}${tel?`📞 ${tel}`:""}</div>
    </div>
  </div>
  <div class="dr">
    <div class="badge">BILAN ${type.toUpperCase()}</div>
    <div class="dn">${periode}</div>
    <div class="dd">Établi le ${today}</div>
  </div>
</div>
<div class="bd">

<div class="sec">
  <div class="st">📊 Indicateurs clés</div>
  <div class="kpi">
    <div class="kpi-i"><div class="kpi-l">Chiffre d'affaires</div><div class="kpi-v">${fmtF(tCA)} F</div></div>
    <div class="kpi-i"><div class="kpi-l">Encaissé</div><div class="kpi-v green">${fmtF(tEnc)} F</div></div>
    <div class="kpi-i"><div class="kpi-l">À percevoir</div><div class="kpi-v red">${fmtF(tRest)} F</div></div>
    <div class="kpi-i"><div class="kpi-l">Mètres vendus</div><div class="kpi-v">${fmtM(tMetres)} m</div></div>
  </div>
  <div class="taux">
    <div class="taux-v">${tauxEnc}%</div>
    <div class="taux-l">Taux d'encaissement</div>
  </div>
</div>

<div class="sec">
  <div class="st">🧾 Détail des ventes (${orders.length})</div>
  <table>
    <thead><tr><th>N°</th><th>Client</th><th>Tissus vendus</th><th style="text-align:right">Total</th><th style="text-align:right">Versé</th><th style="text-align:right">Reste</th></tr></thead>
    <tbody>${rows||'<tr><td colspan="6" style="text-align:center;padding:14px;color:#bbb">Aucune vente</td></tr>'}</tbody>
  </table>
</div>

<div class="sec">
  <div class="g2">
    <div class="box">
      <div class="st" style="border-bottom:1px solid #f0ece4">🏆 Top 5 clients</div>
      ${topClients.length?topClients.map(([n,ca],i)=>`<div class="row-pair"><span style="color:#888">${i+1}. ${n}</span><span style="font-weight:700;color:#d4a017">${fmtF(ca)} F</span></div>`).join(""):'<div style="color:#bbb;font-size:11px">—</div>'}
    </div>
    <div class="box">
      <div class="st" style="border-bottom:1px solid #f0ece4">🧵 Top 5 tissus</div>
      ${topTissus.length?topTissus.map(([t,v],i)=>`<div class="row-pair"><span style="color:#888">${i+1}. ${t}</span><span style="font-weight:700;color:#1a1a2e">${fmtM(v.metres)} m · ${pluriel(v.ventes,"vente")}</span></div>`).join(""):'<div style="color:#bbb;font-size:11px">—</div>'}
    </div>
  </div>
</div>

<div class="sec">
  <div class="st">💳 Paiement</div>
  <div class="g2">
    <div class="box">
      <div class="row-pair"><span style="color:#888">Ventes soldées</span><span style="font-weight:700">${pluriel(nbSoldees,"vente")}</span></div>
      <div class="row-pair"><span style="color:#888">Reste à régler</span><span style="font-weight:700;color:${nbReste?"#ef4444":"#1a1a2e"}">${pluriel(nbReste,"vente")}</span></div>
    </div>
    <div class="box">
      <div class="row-pair"><span style="color:#888">Total des ventes</span><span style="font-weight:800;color:#d4a017">${orders.length}</span></div>
      <div class="row-pair"><span style="color:#888">Mètres vendus</span><span style="font-weight:800;color:#d4a017">${fmtM(tMetres)} m</span></div>
      <div class="row-pair"><span style="color:#888">Clients uniques</span><span style="font-weight:800;color:#d4a017">${clientsSet.size}</span></div>
      <div class="row-pair"><span style="color:#888">Prix moyen au mètre</span><span style="font-weight:800;color:#d4a017">${tMetres>0?fmtF(tCA/tMetres):0} F</span></div>
    </div>
  </div>
</div>

</div>
<div class="sgs">
  <div class="sg"><div class="sgl">Signature du responsable</div></div>
  <div class="sg"><div class="sgl">Cachet de la boutique</div></div>
</div>
<div class="pied">Établi avec ${APP_NAME}</div>
</div></body></html>`;
}

// ── MESSAGE WHATSAPP INTELLIGENT (Pro) ─────────────────────────────
function buildWaMessage(o,atelierInfo){
  const nom=`${o.prenom||""} ${o.nom||""}`.trim()||"cher client";
  const restant=(parseFloat(o.prixTotal)||0)-(parseFloat(o.avanceRecue)||0);
  const at=atelierInfo?.nom||"Notre Atelier";
  // Si un template personnalisé est défini → l'utiliser avec variables
  if(atelierInfo?.waTemplate&&atelierInfo.waTemplate.trim()){
    const tpl=atelierInfo.waTemplate
      .replace(/\{client\}/g,nom)
      .replace(/\{cmdNum\}/g,o.cmdNum||"")
      .replace(/\{categorie\}/g,o.categorie||"")
      .replace(/\{restant\}/g,restant.toLocaleString("fr-FR"))
      .replace(/\{total\}/g,(parseFloat(o.prixTotal)||0).toLocaleString("fr-FR"))
      .replace(/\{atelier\}/g,at)
      .replace(/\{adresse\}/g,atelierInfo?.adresse||"")
      .replace(/\{tel\}/g,atelierInfo?.tel||"");
    return encodeURIComponent(tpl);
  }
  // Format par défaut — clair, chaleureux, signature complète
  let msg=`Bonjour ${nom} 🫱🏽‍🫲🏾,\n`;
  if(IS_GROSSISTE){
    msg+=`merci pour votre achat (${o.cmdNum}) chez ${at} 🙏\n`;
    msg+=restant>0?`Reste à régler : ${restant.toLocaleString("fr-FR")} FCFA${o.dateEcheance?` (prévu le ${dateLongue(o.dateEcheance)})`:""}\n\n`:`Votre achat est intégralement réglé ✅\n\n`;
  }else if(o.statut==="Prêt"){
    msg+=`votre commande (${o.cmdNum}) est prête ✅\n`;
    if(restant>0)msg+=`Solde à régler à la livraison : ${restant.toLocaleString("fr-FR")} FCFA\n`;
    else msg+=`Votre commande est intégralement réglée 🙏\n`;
    msg+=`Merci de passer la récupérer à votre convenance.\n\n`;
  }else if(o.statut==="Livré"){
    msg+=`merci d'avoir choisi ${at} pour votre commande (${o.cmdNum}) 🙏\n`;
    if(restant>0)msg+=`Un solde de ${restant.toLocaleString("fr-FR")} FCFA reste à régler.\n\n`;
    else msg+=`Nous espérons que votre tenue vous plaît. À très bientôt 👗\n\n`;
  }else{
    msg+=`concernant votre commande (${o.cmdNum}) :\n`;
    msg+=`Statut : ${o.statut}\n`;
    if(o.dateLivraison)msg+=`Date de livraison prévue : ${o.dateLivraison}\n`;
    msg+=`\n`;
  }
  // Signature atelier
  msg+=`${at}`;
  if(atelierInfo?.adresse)msg+=`\n📍 ${atelierInfo.adresse}`;
  if(atelierInfo?.tel)msg+=`\n📞 ${atelierInfo.tel}`;
  return encodeURIComponent(msg);
}

// ── LIEN EMAIL CLIENT (mailto avec sujet + corps pré-remplis) ──────
function buildMailtoLink(o,client,atelierInfo){
  const email=client?.email||"";
  if(!email)return null;
  const nom=`${client.prenom||""} ${client.nom||""}`.trim()||"cher client";
  const restant=(parseFloat(o.prixTotal)||0)-(parseFloat(o.avanceRecue)||0);
  const at=atelierInfo?.nom||"Notre Atelier";
  const subject=`Votre commande ${o.cmdNum||""} — ${at}`;
  let body=`Bonjour ${nom},\n\n`;
  if(IS_GROSSISTE){
    body+=`Merci pour votre achat ${o.cmdNum||""} chez ${at}.\n`;
    body+=restant>0?`Reste à régler : ${restant.toLocaleString("fr-FR")} FCFA${o.dateEcheance?`, prévu le ${dateLongue(o.dateEcheance)}`:""}.\n\n`:`Votre achat est intégralement réglé.\n\n`;
  }else if(o.statut==="Prêt"){
    body+=`Votre commande ${o.cmdNum||""} est prête.\n`;
    if(restant>0)body+=`Solde à régler à la livraison : ${restant.toLocaleString("fr-FR")} FCFA.\n`;
    body+=`Merci de passer la récupérer à votre convenance.\n\n`;
  }else if(o.statut==="Livré"){
    body+=`Nous vous remercions d'avoir choisi ${at} pour votre commande ${o.cmdNum||""}.\n\n`;
    if(restant>0)body+=`Un solde de ${restant.toLocaleString("fr-FR")} FCFA reste à régler.\n\n`;
  }else{
    body+=`Concernant votre commande ${o.cmdNum||""} (statut : ${o.statut}) :\n`;
    if(o.dateLivraison)body+=`Date de livraison prévue : ${o.dateLivraison}.\n`;
    body+=`\n`;
  }
  body+=`Cordialement,\n\n${at}`;
  if(atelierInfo?.adresse)body+=`\n${atelierInfo.adresse}`;
  if(atelierInfo?.tel)body+=`\nTél. ${atelierInfo.tel}`;
  if(atelierInfo?.email)body+=`\n${atelierInfo.email}`;
  return`mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}
function waLink(tel,msg){const num=(tel||"").replace(/\D/g,"");if(!num)return null;return`https://wa.me/221${num}${msg?"?text="+msg:""}`;}

// ── REÇU HTML ─────────────────────────────────────────────────────
function buildReceiptHTML(o,info,isGrossiste){
  o=migrateTissus(o);
  const inf=info||{};
  const r=(parseFloat(o.prixTotal)||0)-(parseFloat(o.avanceRecue)||0);
  const today=new Date().toLocaleDateString("fr-FR",{day:"2-digit",month:"long",year:"numeric"});
  const recPrefix=isGrossiste?"FAC":"REÇU";
  const recNum=`${recPrefix}-${(o.cmdNum||"CMD-000").replace(/^[A-Z]+-/,"")}`;
  const nom=inf.nom||(isGrossiste?"Ma boutique":"Mon atelier");
  const logo=inf.logoImage||(inf.logoStyle?logoDataURL(nom,inf.logoStyle,inf.logoPalette):"");
  const adr=inf.adresse||"Dakar, Sénégal";
  const tel=inf.tel||"";
  const email=inf.email||"";
  const tissusFiltres=(o.tissus||[]).filter(t=>t.type||t.metres||t.couleur);
  const tissusList=tissusFiltres.map(t=>{const lbl=tissuLabel(t);return t.source==="atelier"?lbl+" (atelier)":lbl;}).join(", ")||"—";
  // Le nb de pièces du reçu = max entre tissus déposés et nombrePieces (cohérent avec le dashboard)
  const nbPieces=Math.max(tissusFiltres.length,parseInt(o.nombrePieces)||1,1);
  const categorie=o.categorie||"—";

  return`<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8"><title>${recNum}</title>
<style>*{margin:0;padding:0;box-sizing:border-box}
body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;background:#f0f0f0;color:#1a1a2e;font-size:13px}
.pg{max-width:740px;margin:16px auto;background:#fff;border-radius:12px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,0.1)}
.bar{height:5px;background:linear-gradient(90deg,#d4a017,#f5d060,#d4a017)}
.hdr{background:#1a1a2e;padding:22px 28px;display:flex;justify-content:space-between;align-items:flex-start}
.bn{font-size:24px;font-weight:900;color:#fff}.bn span{color:#d4a017}
.bs{font-size:8px;letter-spacing:3px;color:#666;font-weight:700;margin-top:2px}
.bi{font-size:11px;color:#999;margin-top:8px;line-height:1.8}
.dr{text-align:right}
.badge{display:inline-block;background:#10b981;color:#fff;padding:4px 12px;border-radius:20px;font-size:9px;font-weight:800;letter-spacing:2px;margin-bottom:6px}
.dn{font-size:20px;font-weight:900;color:#d4a017}
.dd{font-size:10px;color:#aaa;margin-top:3px}
.dc{display:inline-block;background:rgba(212,160,23,.15);color:#d4a017;border:1px solid rgba(212,160,23,.3);padding:2px 8px;border-radius:4px;font-size:9px;font-weight:700;margin-top:5px}
.bd{padding:22px 28px}
.sec{margin-bottom:18px}
.st{font-size:9px;font-weight:800;color:#ccc;text-transform:uppercase;letter-spacing:1.5px;margin-bottom:9px;padding-bottom:6px;border-bottom:1px solid #f5f5f5}
.cd{background:#f7f7f7;border-radius:8px;padding:11px 13px}
.rw{display:flex;justify-content:space-between;padding:4px 0;border-bottom:1px solid #efefef;font-size:12px}
.rw:last-child{border:none}.rl{color:#aaa}.rv{font-weight:700;text-align:right;max-width:56%}
.fg{display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin-bottom:10px}
.fi{background:#1a1a2e;border-radius:8px;padding:10px 12px;text-align:center}
.fil{font-size:9px;color:#666;margin-bottom:4px;font-weight:600}.fiv{font-size:13px;font-weight:800}
.sb{padding:9px 14px;border-radius:7px;font-size:12px;font-weight:700;text-align:center}
.nt{background:#fffbeb;border-left:3px solid #d4a017;padding:10px 14px;border-radius:0 7px 7px 0;font-size:12px;line-height:1.8;color:#555;white-space:pre-wrap}
.ft{background:#f9f7f0;padding:12px 28px;text-align:center;font-size:9px;color:#ccc;border-top:1px solid #eee;line-height:1.8}
.ft strong{color:#aaa}
@media print{body{background:#fff}.pg{box-shadow:none;margin:0;border-radius:0}@page{size:A4;margin:6mm}}</style>
</head><body><div class="pg">
<div class="bar"></div>
<div class="hdr">
  <div>
    ${logo?`<img src="${logo}" alt="" style="width:64px;height:64px;border-radius:50%;margin-bottom:8px;display:block">`:""}
    <div class="bn">${nom}</div>
    <div class="bs">${isGrossiste?"VENTE DE TISSUS":"ATELIER DE COUTURE PROFESSIONNEL"}</div>
    <div class="bi">📍 ${adr}${tel?`<br>📞 ${tel}`:""}${email?`<br>✉️ ${email}`:""}</div>
  </div>
  <div class="dr">
    <div class="badge">${isGrossiste?"FACTURE DE VENTE":"REÇU DE PAIEMENT"}</div>
    <div class="dn">${recNum}</div>
    <div class="dd">Émis le ${today}</div>
    <div class="dc">📋 ${o.cmdNum}</div>
  </div>
</div>
<div class="bd">
<div class="sec">
  <div class="st">👤 Identification</div>
  <div class="cd">
    <div class="rw" style="font-size:15px;padding:8px 0;border-bottom:2px solid #efefef">
      <span class="rl">Client</span>
      <span class="rv" style="font-size:16px;color:#1a1a2e">${((o.prenom||"")+" "+(o.nom||"")).trim()||"—"}</span>
    </div>
    <div class="rw" style="padding:7px 0">
      <span class="rl">${isGrossiste?"N° Vente":"N° Commande"}</span>
      <span class="rv" style="color:#d4a017;font-size:14px">${o.cmdNum}</span>
    </div>
    <div class="rw" style="padding:7px 0">
      <span class="rl">Catégorie</span>
      <span class="rv">${categorie}</span>
    </div>
    <div class="rw" style="padding:7px 0">
      <span class="rl">Nombre de pièces</span>
      <span class="rv" style="color:#1a1a2e;font-weight:800">${nbPieces} ${nbPieces>1?"pièces":"pièce"}</span>
    </div>
    <div class="rw" style="padding:7px 0">
      <span class="rl">Tissus déposés</span>
      <span class="rv" style="color:#1a1a2e">${tissusList}</span>
    </div>
    <div class="rw" style="padding:7px 0">
      <span class="rl">Date de dépôt</span>
      <span class="rv">${o.dateDepot||"—"}</span>
    </div>
    ${o.dateLivraison?`<div class="rw" style="padding:7px 0">
      <span class="rl">Date de livraison</span>
      <span class="rv" style="color:#d4a017">${o.dateLivraison}</span>
    </div>`:""}
  </div>
</div>
<div class="sec"><div class="st">💰 Récapitulatif</div>
<div class="fg" style="grid-template-columns:repeat(3,1fr)">
  <div class="fi"><div class="fil">Prix total</div><div class="fiv" style="color:#d4a017;font-size:16px">${fmt(o.prixTotal)} F</div></div>
  <div class="fi"><div class="fil">Montant versé</div><div class="fiv" style="color:#34d399;font-size:16px">${fmt(o.avanceRecue)} F</div></div>
  <div class="fi"><div class="fil">Solde restant</div><div class="fiv" style="color:${r>0?"#fca5a5":"#34d399"};font-size:16px">${r.toLocaleString("fr-FR")} F</div></div>
</div>
<div class="sb" style="background:${r>0?"#fffbeb":"#f0fdf4"};color:${r>0?"#92400e":"#065f46"};border:1px solid ${r>0?"#fde68a":"#86efac"};font-size:13px;padding:12px 16px">
  ${r>0?`📌 Solde de <strong>${r.toLocaleString("fr-FR")} FCFA</strong> à régler à la livraison`:`✅ Commande intégralement réglée — Merci pour votre confiance !`}
</div>
</div>
${o.remarques?`<div class="sec"><div class="st">📝 Remarques</div><div class="nt">${o.remarques.replace(/&/g,"&amp;").replace(/</g,"&lt;")}</div></div>`:""}
</div>
</div></body></html>`;
}

// ── FACTURE HTML GROSSISTE (B2B, layout commercial) ───────────────
const REGIMES={
  aucun:{l:"Non immatriculé (vente simple)",tva:false,mention:"TVA non applicable"},
  cgu:{l:"Contribution Globale Unique (CGU)",tva:false,mention:"TVA non applicable — régime de la Contribution Globale Unique"},
  reel:{l:"Réel — assujetti à la TVA",tva:true,mention:""},
};
function enLettres(n){
  n=Math.round(Math.abs(n));
  if(n===0)return"zéro";
  const u=["","un","deux","trois","quatre","cinq","six","sept","huit","neuf","dix","onze","douze","treize","quatorze","quinze","seize","dix-sept","dix-huit","dix-neuf"];
  const dz=["","","vingt","trente","quarante","cinquante","soixante","soixante","quatre-vingt","quatre-vingt"];
  const cent=(x,suivi)=>{
    let s="";const c=Math.floor(x/100),r=x%100;
    if(c>0)s+=(c>1?u[c]+" ":"")+"cent"+(c>1&&r===0&&!suivi?"s":"");
    if(r>0){
      if(s)s+=" ";
      if(r<20)s+=u[r];
      else{
        const t=Math.floor(r/10),v=r%10;
        if(t===7||t===9)s+=dz[t]+(v===1&&t===7?"-et-":"-")+u[10+v];
        else if(v===1&&t<8)s+=dz[t]+"-et-un";
        else if(v>0)s+=dz[t]+"-"+u[v];
        else s+=dz[t]+(t===8?"s":"");
      }
    }
    return s;
  };
  const parts=[];
  const tr=[[1000000000,"milliard"],[1000000,"million"],[1000,"mille"]];
  let reste=n;
  for(const [val,mot] of tr){
    const q=Math.floor(reste/val);
    if(q>0){
      if(mot==="mille")parts.push((q>1?cent(q,true)+" ":"")+"mille");
      else parts.push(cent(q,true)+" "+mot+(q>1?"s":""));
      reste-=q*val;
    }
  }
  if(reste>0)parts.push(cent(reste));
  return parts.join(" ");
}
function buildFactureHTML(o,info,stock){
  o=migrateTissus(o);
  const inf=info||{};
  const r=(parseFloat(o.prixTotal)||0)-(parseFloat(o.avanceRecue)||0);
  const prixTot=parseFloat(o.prixTotal)||0;
  const versé=parseFloat(o.avanceRecue)||0;
  const today=new Date().toLocaleDateString("fr-FR",{day:"2-digit",month:"long",year:"numeric"});
  const facNum=`FAC-${(o.cmdNum||"VTE-000").replace(/^[A-Z]+-/,"")}`;
  const nom=inf.nom||"Notre Boutique";
  const logo=inf.logoImage||(inf.logoStyle?logoDataURL(nom,inf.logoStyle,inf.logoPalette):"");
  const adr=inf.adresse||"Dakar, Sénégal";
  const tel=inf.tel||"";
  const email=inf.email||"";
  const ninea=inf.ninea||"";
  const rccm=inf.rccm||"";
  const forme=inf.formeJuridique||"";
  const capital=inf.capital||"";
  // Les prix saisis sont ceux que le client paye : la TVA s'en déduit, pour que la
  // facture et la caisse annoncent toujours le même montant.
  const reg=REGIMES[inf.regimeFiscal]||REGIMES.aucun;
  const taux=reg.tva?(parseFloat(inf.tauxTva)||18):0;
  const fF=n=>Math.round(n).toLocaleString("fr-FR");
  const articles=(o.tissus||[]).filter(t=>t.type||t.metres||t.couleur);
  // PRIX DE CHAQUE TISSU — celui retenu le jour de la vente (pour une vente plus
  // ancienne, celui du Stock). Avant, la facture divisait le total par les mètres :
  // un Getzner à 12 000 F/m et un autre à 10 500 F/m sortaient tous deux à 10 875 F.
  const mDe=t=>parseFloat(t.metres)||0;
  const pus=articles.map(t=>prixLigne(t,stock));
  const connu=articles.reduce((s,t,i)=>s+(pus[i]>0?mDe(t)*pus[i]:0),0);
  const mInconnus=articles.reduce((s,t,i)=>s+(pus[i]>0?0:mDe(t)),0);
  // Une ligne dont le prix n'est plus connu se partage ce qui reste du total.
  const puReste=mInconnus>0?Math.max(0,prixTot-connu)/mInconnus:0;
  const lignes=articles.map((t,i)=>{const pu=pus[i]>0?pus[i]:puReste;return{pu,total:mDe(t)*pu};});
  const sousTotal=Math.round(lignes.reduce((s,l)=>s+l.total,0));
  const ecart=sousTotal>0?prixTot-sousTotal:0;
  const rows=articles.map((t,i)=>{
    const label=t.type==="Autre"?(t.custom||"Autre"):(t.type||"—");
    const cou=t.couleur||"";
    const m=mDe(t);
    const pu=lignes[i].pu;
    const ligneTotal=lignes[i].total;
    return`<tr>
      <td style="padding:9px 10px;text-align:center;color:#888;font-size:11px">${i+1}</td>
      <td style="padding:9px 10px;font-weight:700">${label}${cou?` <span style="color:#888;font-weight:400">— ${cou}</span>`:""}</td>
      <td style="padding:9px 10px;text-align:center">${m>0?(t.unite==="yd"?(+(m/0.9144).toFixed(2))+" yd":m+" m"):"—"}</td>
      <td style="padding:9px 10px;text-align:right">${pu>0?fF(pu/(1+taux/100))+" F":"—"}</td>
      <td style="padding:9px 10px;text-align:right;font-weight:700;color:#1a1a2e">${ligneTotal>0?fF(ligneTotal/(1+taux/100))+" F":"—"}</td>
    </tr>`;
  }).join("");
  const soldStatus=r<=0&&prixTot>0;
  return`<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8"><title>${facNum}</title>
<style>*{margin:0;padding:0;box-sizing:border-box}
body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;background:#f0f0f0;color:#1a1a2e;font-size:12px}
.pg{max-width:820px;margin:14px auto;background:#fff;border-radius:6px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,0.1)}
.hdr{padding:24px 32px;display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #1a1a2e}
.brand{display:flex;flex-direction:column}
.brand-logo{font-size:22px;font-weight:900;color:#1a1a2e;letter-spacing:-0.5px}
.brand-logo span{color:#d4a017}
.brand-info{font-size:10px;color:#666;margin-top:8px;line-height:1.7}
.fac-info{text-align:right}
.fac-title{font-size:28px;font-weight:900;color:#d4a017;letter-spacing:1px}
.fac-meta{font-size:10px;color:#888;margin-top:6px;line-height:1.7}
.fac-meta strong{color:#1a1a2e}
.cli-bar{background:#f9f7f0;padding:14px 32px;display:flex;justify-content:space-between;align-items:center;border-bottom:1px solid #e8e0d4}
.cli-bar h3{font-size:9px;font-weight:900;color:#d4a017;letter-spacing:2px;margin-bottom:4px;text-transform:uppercase}
.cli-bar .cli-name{font-size:15px;font-weight:800;color:#1a1a2e}
.cli-bar .cli-meta{font-size:11px;color:#666;margin-top:2px}
.cli-bar .cli-right{text-align:right;font-size:10px;color:#888;line-height:1.6}
.body{padding:0}
table{width:100%;border-collapse:collapse;font-size:12px}
thead tr{background:#1a1a2e;color:#fff}
th{padding:11px 10px;text-align:left;font-size:10px;font-weight:800;letter-spacing:0.8px;text-transform:uppercase}
th.center{text-align:center}th.right{text-align:right}
tbody tr{border-bottom:1px solid #f0ece4}
tbody tr:nth-child(even){background:#fafafa}
.totals{padding:18px 32px;display:flex;justify-content:flex-end}
.totals-box{min-width:320px}
.tot-row{display:flex;justify-content:space-between;padding:5px 0;font-size:13px}
.tot-row.sub{color:#666}
.tot-row.tva{color:#888;border-bottom:1px solid #f0ece4;padding-bottom:9px}
.tot-row.ttc{font-weight:900;font-size:18px;color:#1a1a2e;padding-top:9px;border-top:2px solid #d4a017;margin-top:5px}
.tot-row.ttc span:first-child{color:#d4a017}
.paye{background:#1a1a2e;color:#fff;padding:16px 32px;display:flex;justify-content:space-between;align-items:center}
.paye-left{font-size:11px;color:#aaa}
.paye-left strong{display:block;color:#fff;font-size:14px;margin-top:3px}
.paye-right{text-align:right}
.paye-amount{font-size:22px;font-weight:900;color:${soldStatus?"#34d399":"#fca5a5"}}
.paye-label{font-size:10px;color:#888;margin-top:2px;letter-spacing:1px;text-transform:uppercase}
.cachet{display:inline-block;margin-top:10px;border:3px solid #34d399;color:#34d399;border-radius:10px;padding:5px 18px;font-size:20px;font-weight:900;letter-spacing:4px;transform:rotate(-7deg);opacity:.95}
.status-badge{display:inline-block;background:${soldStatus?"#10b981":"#f59e0b"};color:#fff;padding:5px 13px;border-radius:20px;font-size:11px;font-weight:900;letter-spacing:1.5px;margin-top:7px}
.lettres{padding:14px 32px 0;font-size:11px;color:#444;line-height:1.7}
.mentions{padding:8px 32px 0;font-size:10px;color:#888;line-height:1.6}
.sgs2{display:flex;gap:40px;padding:26px 32px 10px}
.sg2{flex:1;border-top:1px solid #ccc;padding-top:7px;font-size:10px;color:#888;text-align:center}
.ft{padding:16px 32px;background:#f9f7f0;font-size:10px;color:#888;text-align:center;line-height:1.6}
.ft strong{color:#1a1a2e}
@media print{body{background:#fff}.pg{box-shadow:none;margin:0;border-radius:0}@page{size:A4;margin:8mm}}</style>
</head><body><div class="pg">
<div class="hdr">
  <div class="brand">
    ${logo?`<img src="${logo}" alt="" style="width:60px;height:60px;border-radius:12px;margin-bottom:8px;display:block">`:""}<div class="brand-logo">${nom.replace(/</g,"&lt;")}</div>
    <div class="brand-info">
      ${forme?`${forme}${capital?` au capital de ${fF(parseFloat(capital)||0)} FCFA`:""}<br>`:""}📍 ${adr}${tel?`<br>📞 ${tel}`:""}${email?`<br>✉️ ${email}`:""}${ninea?`<br>NINEA : <strong style="color:#1a1a2e">${ninea}</strong>`:""}${rccm?`<br>RCCM : <strong style="color:#1a1a2e">${rccm}</strong>`:""}${reg.l&&inf.regimeFiscal?`<br>Régime : ${reg.l}`:""}
    </div>
  </div>
  <div class="fac-info">
    <div class="fac-title">FACTURE</div>
    <div class="fac-meta">
      <strong>N° ${facNum}</strong><br>
      Émise le ${today}<br>
      Réf. interne : ${o.cmdNum||""}
    </div>
  </div>
</div>
<div class="cli-bar">
  <div>
    <h3>👤 Doit</h3>
    <div class="cli-name">${((o.prenom||"")+" "+(o.nom||"")).trim()||"—"}</div>
    <div class="cli-meta">${o.telephone?"📞 "+o.telephone:""}${o.ville?" · "+o.ville:""}</div>
  </div>
  <div class="cli-right">
    Date de la vente : <strong style="color:#1a1a2e">${o.dateDepot||"—"}</strong>
  </div>
</div>
<div class="body">
  <table>
    <thead><tr><th class="center" style="width:40px">#</th><th>Désignation</th><th class="center" style="width:80px">Qté</th><th class="right" style="width:110px">${taux>0?"P.U. HT":"P.U."}</th><th class="right" style="width:130px">${taux>0?"Montant HT":"Montant"}</th></tr></thead>
    <tbody>${rows||'<tr><td colspan="5" style="padding:18px;text-align:center;color:#bbb">Aucun article</td></tr>'}</tbody>
  </table>
</div>
<div class="totals">
  <div class="totals-box">
    <div class="tot-row sub"><span>${taux>0?"Total HT":"Sous-total"}</span><span>${fF((sousTotal>0?sousTotal:prixTot)/(1+taux/100))} F</span></div>
    ${Math.abs(ecart)>=1?`<div class="tot-row sub"><span>${ecart<0?"Remise accordée":"Autres frais"}</span><span>${ecart<0?"−":"+"}${fF(Math.abs(ecart)/(1+taux/100))} F</span></div>`:""}
    ${taux>0
      ?`<div class="tot-row tva"><span>TVA ${taux} %</span><span>${fF(prixTot-prixTot/(1+taux/100))} F</span></div>`
      :`<div class="tot-row tva"><span>${reg.mention}</span><span>—</span></div>`}
    <div class="tot-row ttc"><span>${taux>0?"TOTAL TTC":"TOTAL À PAYER"}</span><span>${fF(prixTot)} F</span></div>
  </div>
</div>
<div class="paye">
  <div class="paye-left">
    Montant versé<strong>${versé.toLocaleString("fr-FR")} FCFA</strong>
    ${o.modePaiement?`<div style="font-size:11px;opacity:.8;margin-top:3px">Réglé en ${String(o.modePaiement).replace(/</g,"&lt;")}</div>`:""}
    ${soldStatus?`<div class="status-badge">✓ SOLDÉ</div>`:`<div class="status-badge" style="background:#f59e0b">⏳ RESTE À RÉGLER</div>`}
    ${!soldStatus&&o.dateEcheance?`<div style="font-size:11px;color:#fcd34d;margin-top:7px">À régler avant le ${dateLongue(o.dateEcheance)}</div>`:""}
  </div>
  <div class="paye-right">
    <div class="paye-amount">${r.toLocaleString("fr-FR")} F</div>
    <div class="paye-label">Solde dû</div>
    ${soldStatus?`<div class="cachet">PAYÉE</div>`:""}
  </div>
</div>
<div class="lettres">Arrêtée la présente facture à la somme de <strong>${enLettres(prixTot)} francs CFA</strong>${taux>0?" toutes taxes comprises":""}.</div>
${inf.mentionsFacture?`<div class="mentions">${String(inf.mentionsFacture).replace(/</g,"&lt;").split(String.fromCharCode(10)).join("<br>")}</div>`:""}
<div class="sgs2"><div class="sg2">Signature du client</div><div class="sg2">Cachet et signature de la boutique</div></div>
<div class="ft">
  <strong>${nom.replace(/</g,"&lt;")}</strong>${inf.slogan?` — ${String(inf.slogan).replace(/</g,"&lt;")}`:""}<br>
  ${[inf.adresse,inf.tel].filter(Boolean).map(x=>String(x).replace(/</g,"&lt;")).join(" · ")}<br>
  Merci pour votre confiance.
</div>
</div></body></html>`;
}

// REÇU DE PAIEMENT — remis au client qui vient régler tout ou partie de son crédit :
// le montant reçu, les ventes qu'il règle, et ce qu'il lui reste à payer.
function buildRecuPaiementHTML(p,info){
  const inf=info||{};
  const e=s=>String(s==null?"":s).replace(/&/g,"&amp;").replace(/</g,"&lt;");
  const nom=inf.nom||"Ma boutique";
  const logo=inf.logoImage||(inf.logoStyle?logoDataURL(nom,inf.logoStyle,inf.logoPalette):"");
  const num="PAI-"+String(p.date||"").replace(/-/g,"")+"-"+String(p.num||"").slice(-4);
  const lignes=(p.lignes||[]).map(l=>`<tr><td><strong>${e(l.cmdNum)}</strong></td><td>${e(dateCourte(l.dateDepot))}</td><td class="r" style="color:#059669;font-weight:800">${fmt(l.part)} F</td><td class="r">${l.reste>0?fmt(l.reste)+" F":"Soldée"}</td></tr>`).join("");
  const aJour=!(p.resteApres>0);
  return`<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8"><title>Reçu de paiement ${num}</title>
<style>*{margin:0;padding:0;box-sizing:border-box}
body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;background:#f0f0f0;color:#1a1a2e;font-size:12px}
.pg{max-width:620px;margin:14px auto;background:#fff;border-radius:6px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,0.1)}
.hdr{padding:22px 26px;display:flex;justify-content:space-between;gap:16px;align-items:flex-start;border-bottom:3px solid #1a1a2e;flex-wrap:wrap}
.bn{font-size:20px;font-weight:900}
.bi{font-size:10px;color:#666;margin-top:6px;line-height:1.7}
.tt{text-align:right}
.tt b{display:block;font-size:19px;font-weight:900;color:#d4a017;letter-spacing:1px}
.tt span{font-size:10px;color:#888;line-height:1.7}
.cli{background:#f9f7f0;padding:13px 26px;border-bottom:1px solid #e8e0d4}
.cli h3{font-size:9px;font-weight:900;color:#d4a017;letter-spacing:2px;text-transform:uppercase;margin-bottom:4px}
.cli .n{font-size:15px;font-weight:800}
.cli .t{font-size:11px;color:#666;margin-top:2px}
.mt{padding:22px 26px;text-align:center}
.mt .l{font-size:10px;color:#888;letter-spacing:1.5px;text-transform:uppercase;font-weight:800}
.mt .v{font-size:32px;font-weight:900;color:#059669;margin-top:4px}
.mt .m{font-size:11px;color:#666;margin-top:4px}
table{width:100%;border-collapse:collapse}
th{background:#1a1a2e;color:#fff;padding:9px 12px;font-size:10px;text-align:left;text-transform:uppercase;letter-spacing:.8px}
td{padding:9px 12px;border-bottom:1px solid #f0ece4}
.r{text-align:right}
.solde{background:#1a1a2e;color:#fff;padding:15px 26px;display:flex;justify-content:space-between;align-items:center;gap:12px}
.solde .l{font-size:11px;color:#aaa}
.solde .v{font-size:20px;font-weight:900}
.sgs{display:flex;gap:20px;padding:30px 26px 10px}
.sg{flex:1;border-top:1px solid #ccc;padding-top:6px;font-size:10px;color:#888;text-align:center}
.ft{padding:14px 26px;background:#f9f7f0;font-size:10px;color:#888;text-align:center;line-height:1.6}
@media print{body{background:#fff}.pg{box-shadow:none;margin:0;border-radius:0}@page{size:A5;margin:8mm}}</style>
</head><body><div class="pg">
<div class="hdr">
  <div>${logo?`<img src="${logo}" alt="" style="width:54px;height:54px;border-radius:12px;margin-bottom:8px;display:block;object-fit:contain">`:""}<div class="bn">${e(nom)}</div>
  <div class="bi">${[inf.adresse,inf.tel?"📞 "+inf.tel:""].filter(Boolean).map(x=>e(x)).join("<br>")}</div></div>
  <div class="tt"><b>REÇU DE PAIEMENT</b><span>N° ${num}<br>Le ${e(dateLongue(p.date))}</span></div>
</div>
<div class="cli"><h3>Reçu de</h3><div class="n">${e(p.nom)}</div>${p.telephone?`<div class="t">📞 ${e(p.telephone)}</div>`:""}</div>
<div class="mt"><div class="l">Montant reçu</div><div class="v">${fmt(p.montant)} F</div>${p.mode?`<div class="m">Réglé en ${e(p.mode)}</div>`:""}</div>
<table><thead><tr><th>Vente</th><th>Du</th><th class="r">Payé</th><th class="r">Reste</th></tr></thead><tbody>${lignes}</tbody></table>
<div class="solde"><div><div class="l">Reste à régler</div>${!aJour&&p.echeance?`<div style="font-size:11px;color:#fcd34d;margin-top:3px">Prévu le ${e(dateLongue(p.echeance))}</div>`:""}</div><div class="v" style="color:${aJour?"#34d399":"#fca5a5"}">${aJour?"✓ À JOUR":fmt(p.resteApres)+" F"}</div></div>
<div class="sgs"><div class="sg">Signature du client</div><div class="sg">Cachet de la boutique</div></div>
<div class="ft"><strong style="color:#1a1a2e">${e(nom)}</strong><br>Merci pour votre confiance.</div>
</div></body></html>`;
}

// ── REÇU EN IMAGE (partage WhatsApp pro : s'affiche direct dans la conversation) ──
const ReceiptModal=memo(({html,order,fileName,titre,atelierInfo,isGrossiste,onClose})=>{
  const ref=useRef();
  const doPrint=async()=>{
    if(window.electronAPI?.printReceipt){
      await window.electronAPI.printReceipt(html);
      return;
    }
    try{
      const blob=new Blob([html],{type:"text/html;charset=utf-8"});
      const url=URL.createObjectURL(blob);
      const w=window.open(url,"_blank");
      if(w){w.onload=()=>{w.print();};}
      else{
        const a=document.createElement("a");a.href=url;a.download="recu-couturepro.html";
        document.body.appendChild(a);a.click();document.body.removeChild(a);
        alert("Fichier téléchargé. Ouvrez-le dans un navigateur puis Ctrl+P.");
      }
      setTimeout(()=>URL.revokeObjectURL(url),30000);
    }catch(e){alert("Erreur d'impression. Essayez le bouton Télécharger.");}
  };
  const baseName=fileName||((isGrossiste?"Facture-":"Recu-")+((order?.cmdNum)||"document"));
  // Partage du REÇU COMPLET (format HTML original) — nommé "Recu-CMD-005" :
  // sur mobile la feuille de partage s'ouvre, WhatsApp l'envoie en document,
  // le client tape dessus et le reçu s'ouvre directement avec tous les détails.
  const doShare=async()=>{
    try{
      const file=new File([html],baseName+".html",{type:"text/html"});
      if(navigator.canShare&&navigator.canShare({files:[file]})){
        await navigator.share({files:[file],title:baseName});
      }else{
        const url=URL.createObjectURL(new Blob([html],{type:"text/html;charset=utf-8"}));
        const a=document.createElement("a");a.href=url;a.download=baseName+".html";
        document.body.appendChild(a);a.click();document.body.removeChild(a);
        setTimeout(()=>URL.revokeObjectURL(url),5000);
        alert("📄 Fichier téléchargé : "+baseName+".html — envoyez-le sur WhatsApp.");
      }
    }catch(e){/* partage annulé par l'utilisateur */}
  };
  const doDownload=()=>{
    try{
      const blob=new Blob([html],{type:"text/html;charset=utf-8"});
      const url=URL.createObjectURL(blob);
      const a=document.createElement("a");a.href=url;a.download=baseName+".html";
      document.body.appendChild(a);a.click();document.body.removeChild(a);
      setTimeout(()=>URL.revokeObjectURL(url),5000);
    }catch{}
  };
  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",zIndex:700,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:12}}>
      <div style={{background:"#fff",borderRadius:14,width:"100%",maxWidth:820,maxHeight:"92vh",display:"flex",flexDirection:"column",overflow:"hidden",boxShadow:"0 24px 80px rgba(0,0,0,0.25)"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 16px",background:D,borderRadius:"14px 14px 0 0",flexShrink:0,flexWrap:"wrap",gap:8}}>
          <div style={{display:"flex",gap:10,alignItems:"center"}}>
            <span style={{color:G,fontWeight:800,fontSize:15}}>🧾 {titre||(isGrossiste?"Facture":"Reçu")}</span>
          </div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            {<button onClick={doShare} style={{background:"#25D366",color:"#fff",border:"none",padding:"8px 16px",borderRadius:8,cursor:"pointer",fontWeight:800,fontSize:13,fontFamily:"inherit"}}>💬 Partager</button>}
            <button onClick={doPrint} style={{background:G,color:D,border:"none",padding:"8px 16px",borderRadius:8,cursor:"pointer",fontWeight:800,fontSize:13,fontFamily:"inherit"}}>🖨️ Imprimer</button>
            <button onClick={doDownload} style={{background:"#10b981",color:"#fff",border:"none",padding:"8px 14px",borderRadius:8,cursor:"pointer",fontWeight:700,fontSize:13,fontFamily:"inherit"}}>⬇️</button>
            <button onClick={onClose} style={{background:"rgba(255,255,255,0.12)",color:"#fff",border:"none",borderRadius:8,padding:"8px 14px",cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>✕</button>
          </div>
        </div>
        <iframe ref={ref} srcDoc={html} style={{flex:1,border:"none",minHeight:0}} title="Aperçu reçu"/>
      </div>
    </div>
  );
});

// ── COMPOSANTS FORM ───────────────────────────────────────────────
// CHIFFRES — on tape « 15000 », on lit « 15 000 ». La valeur gardée reste un nombre
// brut (« 15000 », « 2.5 ») : les calculs ne voient jamais d'espace. Et jamais de
// signe moins : un prix, un métrage, une quantité vont de 0 à l'infini.
function chiffreBrut(saisi){
  let s=String(saisi==null?"":saisi).replace(/,/g,".").replace(/[^0-9.]/g,"");
  const p=s.indexOf(".");
  if(p>=0)s=s.slice(0,p+1)+s.slice(p+1).replace(/[.]/g,"");
  return s;
}
function chiffreAffiche(brut){
  const s=chiffreBrut(brut);
  if(!s)return "";
  const [ent,dec]=s.split(".");
  const e=(ent||"").replace(/^0+(?=[0-9])/,"").replace(/[0-9](?=([0-9]{3})+$)/g,"$& ");
  return dec!==undefined?(e||"0")+","+dec:e;
}
const ChiffreInput=memo(({value,onChange,style,autoFocus})=>(
  <input type="text" inputMode="decimal" value={chiffreAffiche(value)} onChange={e=>onChange({target:{value:chiffreBrut(e.target.value)}})} style={style} autoFocus={autoFocus}/>
));
// ÉCRITURE — la première lettre en majuscule sans avoir à y penser ; le NOM de
// famille tout en majuscules, le prénom « Mame Diarra », comme sur une pièce d'identité.
function mettreEnForme(v,casse){
  const s=String(v==null?"":v);
  if(casse==="nom")return s.toUpperCase();
  if(casse==="prenom")return s.toLowerCase().replace(/(^|[ -])([a-zà-ÿ])/g,(m,a,b)=>a+b.toUpperCase());
  return s.charAt(0).toUpperCase()+s.slice(1);
}
// RÉPERTOIRE DU TÉLÉPHONE — Chrome sur Android sait ouvrir les contacts depuis une
// page web ; l'iPhone ne le permet à aucune application web. Le bouton n'apparaît
// donc que là où il fonctionne.
const CONTACTS_OK=typeof navigator!=="undefined"&&!!(navigator.contacts&&navigator.contacts.select);
async function choisirContact(){
  try{
    const [c]=await navigator.contacts.select(["name","tel"],{multiple:false});
    if(!c)return null;
    const tel=String((c.tel||[])[0]||"").replace(/[^0-9+]/g,"").replace(/^(00221|[+]221)/,"");
    const mots=String((c.name||[])[0]||"").trim().split(/[ ]+/).filter(Boolean);
    const nom=mots.length>1?mots.pop():"";
    return{tel:fmtTel(tel),prenom:mettreEnForme(mots.join(" "),"prenom"),nom:mettreEnForme(nom,"nom")};
  }catch{return null;}
}
const Row=memo(({children})=><div style={{display:"flex",gap:10,marginBottom:11,flexWrap:"wrap"}}>{children}</div>);
const Sec=memo(({title,children})=><div style={{marginBottom:18}}><div style={{fontWeight:700,color:D,fontSize:13,marginBottom:9,paddingBottom:6,borderBottom:"2px solid "+G}}>{title}</div>{children}</div>);
const Fld=memo(({label,value,onChange,type,suffix,onSuffixClick,casse,autoFocus})=>(
  <div style={{flex:1,minWidth:0}}>
    <label style={sl}>{label}</label>
    <div style={{display:"flex",alignItems:"center",gap:5}}>
      {type==="number"
        ?<ChiffreInput value={value} onChange={onChange} style={{...si,flex:1}} autoFocus={autoFocus}/>
        :<input type={type||"text"} value={value} onChange={(!type||type==="text")?(e=>onChange({target:{value:mettreEnForme(e.target.value,casse)}})):onChange} autoCapitalize={casse==="nom"?"characters":casse==="prenom"?"words":"sentences"} autoFocus={autoFocus} style={{...si,flex:1}}/>}
      {suffix&&(onSuffixClick
        ?<button type="button" onClick={onSuffixClick} title="Changer d'unité (mètres / yards)" style={{background:"#fffdf7",border:"1.5px solid "+G,padding:"8px 11px",borderRadius:7,fontSize:11,fontWeight:800,color:D,whiteSpace:"nowrap",cursor:"pointer",fontFamily:"inherit"}}>{suffix} ⇄</button>
        :<span style={{background:"#f0f0f0",padding:"9px 10px",borderRadius:7,fontSize:11,fontWeight:700,color:"#888",whiteSpace:"nowrap"}}>{suffix}</span>)}
    </div>
  </div>
));
const Slct=memo(({label,value,onChange,options})=>(
  <div style={{flex:1,minWidth:0}}>
    <label style={sl}>{label}</label>
    <select value={value} onChange={onChange} style={si}>{options.map(o=><option key={o}>{o}</option>)}</select>
  </div>
));
function AcField({label,value,onChange,suggestions}){
  const [open,setOpen]=useState(false);
  const f=suggestions.filter(s=>s.toLowerCase().includes((value||"").toLowerCase())).slice(0,8);
  return(<div style={{flex:1,minWidth:0,position:"relative"}}>
    <label style={sl}>{label}</label>
    <input value={value||""} onChange={e=>{onChange(mettreEnForme(e.target.value));setOpen(true);}} onFocus={()=>setOpen(true)} onBlur={()=>setTimeout(()=>setOpen(false),150)} style={si}/>
    {open&&f.length>0&&<div style={{position:"absolute",top:"100%",left:0,right:0,background:"#fff",border:"1.5px solid #e0e0e0",borderRadius:8,zIndex:300,boxShadow:"0 8px 24px rgba(0,0,0,0.10)",maxHeight:180,overflowY:"auto"}}>
      {f.map(s=><div key={s} onMouseDown={e=>{e.preventDefault();onChange(s);setOpen(false);}} style={{padding:"8px 13px",cursor:"pointer",fontSize:13,borderBottom:"1px solid #f5f5f5"}} onMouseEnter={e=>e.currentTarget.style.background="#f0f7ff"} onMouseLeave={e=>e.currentTarget.style.background=""}>→ {s}</div>)}
    </div>}
  </div>);
}

// ── LIGHTBOX AVEC ZOOM HD (Pro friendly) ──────────────────────────
const LightboxZoom=memo(({lightbox,setLightbox,colPhotos})=>{
  const items=lightbox.items;
  const cur=items[lightbox.index];
  const [zoom,setZoom]=useState(1);
  const [pan,setPan]=useState({x:0,y:0});
  const [dragging,setDragging]=useState(false);
  const dragStart=useRef({x:0,y:0,panX:0,panY:0});
  useEffect(()=>{setZoom(1);setPan({x:0,y:0});},[lightbox.index]);
  const prev=()=>setLightbox(l=>({...l,index:(l.index-1+items.length)%items.length}));
  const next=()=>setLightbox(l=>({...l,index:(l.index+1)%items.length}));
  // Clavier : flèches + Échap + zoom +/-
  useEffect(()=>{
    const h=e=>{
      if(e.key==="Escape")setLightbox(null);
      else if(e.key==="ArrowLeft")prev();
      else if(e.key==="ArrowRight")next();
      else if(e.key==="+"||e.key==="=")setZoom(z=>Math.min(z*1.4,5));
      else if(e.key==="-")setZoom(z=>{const n=z/1.4;if(n<=1.05){setPan({x:0,y:0});return 1;}return n;});
      else if(e.key==="0"){setZoom(1);setPan({x:0,y:0});}
    };
    window.addEventListener("keydown",h);return()=>window.removeEventListener("keydown",h);
  },[items.length]);
  // Zoom à la molette
  const onWheel=e=>{
    e.preventDefault();
    const delta=e.deltaY<0?1.2:1/1.2;
    setZoom(z=>{const n=z*delta;if(n<1.05){setPan({x:0,y:0});return 1;}return Math.min(n,5);});
  };
  // Drag pour pan quand zoomé
  const onMouseDown=e=>{if(zoom<=1)return;setDragging(true);dragStart.current={x:e.clientX,y:e.clientY,panX:pan.x,panY:pan.y};};
  const onMouseMove=e=>{if(!dragging)return;setPan({x:dragStart.current.panX+(e.clientX-dragStart.current.x),y:dragStart.current.panY+(e.clientY-dragStart.current.y)});};
  const onMouseUp=()=>setDragging(false);
  // Double-clic : toggle zoom 1x ↔ 2x
  const onDblClick=e=>{
    if(zoom>1){setZoom(1);setPan({x:0,y:0});}
    else setZoom(2);
  };
  return(<div onWheel={onWheel} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.96)",zIndex:800,display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden"}} onClick={e=>{if(e.target===e.currentTarget)setLightbox(null);}}>
    <button onClick={()=>setLightbox(null)} aria-label="Fermer" style={{position:"absolute",top:"calc(14px + env(safe-area-inset-top))",right:14,background:"rgba(0,0,0,0.55)",color:"#fff",border:"1px solid rgba(255,255,255,0.25)",borderRadius:50,width:46,height:46,cursor:"pointer",fontSize:24,fontFamily:"inherit",zIndex:5,display:"flex",alignItems:"center",justifyContent:"center",lineHeight:1}}>×</button>
    {items.length>1&&<button onClick={prev} style={{position:"absolute",left:18,top:"50%",transform:"translateY(-50%)",background:"rgba(255,255,255,0.1)",color:"#fff",border:"none",borderRadius:50,width:48,height:48,cursor:"pointer",fontSize:24,fontFamily:"inherit",zIndex:3}}>‹</button>}
    {items.length>1&&<button onClick={next} style={{position:"absolute",right:18,top:"50%",transform:"translateY(-50%)",background:"rgba(255,255,255,0.1)",color:"#fff",border:"none",borderRadius:50,width:48,height:48,cursor:"pointer",fontSize:24,fontFamily:"inherit",zIndex:3}}>›</button>}
    {/* Contrôles zoom */}
    <div style={{position:"absolute",top:"calc(14px + env(safe-area-inset-top))",left:14,display:"flex",gap:6,zIndex:3}}>
      <button onClick={()=>setZoom(z=>Math.min(z*1.4,5))} title="Zoom +" style={{background:"rgba(255,255,255,0.1)",color:"#fff",border:"none",borderRadius:8,width:38,height:38,cursor:"pointer",fontSize:18,fontFamily:"inherit",fontWeight:700}}>＋</button>
      <button onClick={()=>setZoom(z=>{const n=z/1.4;if(n<=1.05){setPan({x:0,y:0});return 1;}return n;})} title="Zoom −" style={{background:"rgba(255,255,255,0.1)",color:"#fff",border:"none",borderRadius:8,width:38,height:38,cursor:"pointer",fontSize:18,fontFamily:"inherit",fontWeight:700}}>−</button>
      <button onClick={()=>{setZoom(1);setPan({x:0,y:0});}} title="Reset zoom" style={{background:"rgba(255,255,255,0.1)",color:"#fff",border:"none",borderRadius:8,padding:"0 12px",height:38,cursor:"pointer",fontSize:11,fontFamily:"inherit",fontWeight:700}}>{Math.round(zoom*100)}%</button>
    </div>
    <div style={{maxWidth:"96vw",maxHeight:"96vh",display:"flex",flexDirection:"column",alignItems:"center",gap:12,pointerEvents:dragging?"none":"auto"}}>
      <div onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp} onDoubleClick={onDblClick} style={{overflow:"hidden",borderRadius:8,cursor:zoom>1?(dragging?"grabbing":"grab"):"zoom-in",maxWidth:"96vw",maxHeight:"82vh",display:"flex",alignItems:"center",justifyContent:"center"}}>
        {colPhotos[cur.id]?<img src={colPhotos[cur.id]} alt={cur.titre||""} draggable={false} style={{maxWidth:"96vw",maxHeight:"82vh",objectFit:"contain",display:"block",boxShadow:"0 10px 50px rgba(0,0,0,0.5)",transform:`translate(${pan.x}px,${pan.y}px) scale(${zoom})`,transformOrigin:"center center",transition:dragging?"none":"transform 0.15s ease-out",imageRendering:zoom>1.5?"auto":"auto",userSelect:"none"}}/>:<div style={{width:400,height:400,background:"linear-gradient(135deg,#1a1a2e,#0f0f1e)",borderRadius:12,display:"flex",alignItems:"center",justifyContent:"center",fontSize:100,color:G}}>👗</div>}
      </div>
      <div style={{background:"rgba(0,0,0,0.65)",backdropFilter:"blur(10px)",borderRadius:12,padding:"10px 18px",color:"#fff",textAlign:"center",minWidth:280}}>
        <div style={{fontSize:16,fontWeight:800,marginBottom:3}}>{cur.titre||"Sans titre"}</div>
        <div style={{display:"flex",gap:10,justifyContent:"center",alignItems:"center",fontSize:11,color:"#aaa",flexWrap:"wrap"}}>
          <span>{cur.categorie}</span>
          {cur.prixIndicatif&&<span style={{color:G,fontWeight:800}}>· {(parseFloat(cur.prixIndicatif)||0).toLocaleString("fr-FR")} FCFA</span>}
          <span>· {lightbox.index+1} / {items.length}</span>
        </div>
        <div style={{fontSize:9,color:"#888",marginTop:4}}>💡 Molette ou ＋/− pour zoomer · double-clic · glisser pour déplacer</div>
      </div>
    </div>
  </div>);
});

// ── PANNEAU DETAIL COMMANDE ───────────────────────────────────────
const DetailPanel=memo(({detail,onClose,onEdit,onDelete,onUpdSt,onPrint,onEncaisser,photoDe,prixDe,clients,onOpenClient,atelierInfo,isPro})=>{
  const defileRef=useRef(null); // zone qui défile, pour la flèche « remonter »
  const o=detail;
  // Photos tissu/modèle (Premium) — chargées par id depuis le stockage photos
  const [tPhotos,setTPhotos]=useState({});
  const [zoomImg,setZoomImg]=useState(null);
  useEffect(()=>{let alive=true;(async()=>{
    const map={};
    for(const t of(o.tissus||[])){
      for(const id of[t.photoTissu,t.photoModele]){
        if(id&&!map[id]){const d=await photoStore.get(id);if(d)map[id]=d;}
      }
    }
    if(alive)setTPhotos(map);
  })();return()=>{alive=false;};},[o]);
  const photoRow=t=>{
    const pt=t.photoTissu&&tPhotos[t.photoTissu],pm=t.photoModele&&tPhotos[t.photoModele];
    // GROSSISTE : la photo de la balle vendue, prise dans le Stock
    const pv=photoDe?photoDe(t):null;
    if(pv)return(<div style={{marginTop:8}}><div style={{fontSize:9,fontWeight:800,color:"#bbb",marginBottom:3}}>🧵 TISSU VENDU</div><img src={pv} alt="" onClick={()=>setZoomImg(pv)} title="Cliquer pour agrandir" style={{width:"100%",height:130,objectFit:"cover",borderRadius:8,cursor:"zoom-in",display:"block",border:"1px solid #e8e8e8"}}/></div>);
    if(!pt&&!pm)return null;
    return(<div style={{display:"flex",gap:7,marginTop:8}}>
      {pt&&<div style={{flex:1,minWidth:0}}><div style={{fontSize:9,fontWeight:800,color:"#bbb",marginBottom:3}}>🧵 TISSU APPORTÉ</div><img src={pt} alt="" onClick={()=>setZoomImg(pt)} title="Cliquer pour agrandir" style={{width:"100%",height:95,objectFit:"cover",borderRadius:8,cursor:"zoom-in",display:"block",border:"1px solid #e8e8e8"}}/></div>}
      {pm&&<div style={{flex:1,minWidth:0}}><div style={{fontSize:9,fontWeight:800,color:"#bbb",marginBottom:3}}>👗 MODÈLE À FAIRE</div><img src={pm} alt="" onClick={()=>setZoomImg(pm)} title="Cliquer pour agrandir" style={{width:"100%",height:95,objectFit:"cover",borderRadius:8,cursor:"zoom-in",display:"block",border:"1px solid #e8e8e8"}}/></div>}
    </div>);
  };
  const r=(parseFloat(o.prixTotal)||0)-(parseFloat(o.avanceRecue)||0);
  // Inclut modèle/photos pour que les commandes express (sans type de tissu) affichent leur carte
  const tis=o.tissus?.filter(t=>t.type||t.metres||t.couleur||t.modele||t.photoTissu||t.photoModele)||[];
  const t0=tis[0];
  const mesH=[["Épaule",o.mEpaule],["Longueur",o.mLongueur],["Poitrine",o.mPoitrine],["Taille",o.mTaille],["Manche",o.mManche],["Poignet",o.mPoignet],["Tour bras",o.mBras],["Bassin",o.mBassinH],["Col",o.mCol]].filter(([,v])=>v&&String(v).trim());
  const mesB=[["Ceinture",o.mCeinture],["Longueur",o.mLongueurP],["Cuisse",o.mCuisse],["Bassin",o.mBassinP],["Bas",o.mBas]].filter(([,v])=>v&&String(v).trim());
  const mesP=(o.mesuresPlus||[]).filter(m=>m.l||m.v);
  const colH=COULEURS.find(c=>c.n===t0?.couleur)?.h;
  const late=SUIVI_LIVRAISON&&o.dateLivraison&&new Date(o.dateLivraison)<new Date()&&o.statut!=="Livré";
  const linkedClient=o.clientId?clients.find(c=>c.id===o.clientId):null;
  const sC={background:"#f5f5f5",borderRadius:10,padding:"12px 14px",marginBottom:10};
  const sT={fontSize:9,fontWeight:800,color:"#bbb",textTransform:"uppercase",letterSpacing:"1.2px",marginBottom:8};
  const sR={display:"flex",justifyContent:"space-between",padding:"5px 0",borderBottom:"1px solid #ebebeb",fontSize:12};
  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",zIndex:300,display:"flex",justifyContent:"flex-end"}} onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div style={{background:"#fff",width:"100%",maxWidth:520,display:"flex",flexDirection:"column",boxShadow:"-4px 0 32px rgba(0,0,0,0.12)"}}>
        <div style={{background:D,padding:"calc(14px + env(safe-area-inset-top)) 18px 14px",display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0}}>
          <div style={{display:"flex",gap:9,alignItems:"center",flex:1,minWidth:0}}>
            <span style={{background:G,color:D,fontWeight:800,fontSize:10,padding:"3px 9px",borderRadius:5,flexShrink:0}}>{o.cmdNum}</span>
            <span style={{color:"#fff",fontWeight:700,fontSize:14,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{o.prenom} {o.nom}</span>
            {o.priorite==="Urgente"&&o.statut!=="Livré"&&o.statut!=="Prêt"&&<span style={{background:"#ef444422",color:"#fca5a5",fontSize:10,fontWeight:700,padding:"2px 7px",borderRadius:7,flexShrink:0}}>🚨</span>}
          </div>
          <div style={{display:"flex",gap:7,alignItems:"center",flexShrink:0}}>
            {IS_GROSSISTE?(()=>{
              // Une vente de tissu ne se juge que sur son paiement : le client repart
              // avec son tissu, il n'y a rien à « livrer ».
              const sg=statutGrossiste(o);
              return(<span style={{background:SC_GROS[sg]+"22",color:SC_GROS[sg],border:"1px solid "+SC_GROS[sg],borderRadius:7,padding:"4px 8px",fontWeight:800,fontSize:10,fontFamily:"inherit",whiteSpace:"nowrap"}}>{sg==="Soldé"?"✓ Soldé":"⏳ Reste à régler"}</span>);
            })():<select value={o.statut} onChange={e=>onUpdSt(o.id,e.target.value)} style={{background:SC[o.statut]+"22",color:SC[o.statut],border:"1px solid "+SC[o.statut],borderRadius:7,padding:"4px 8px",fontWeight:700,fontSize:10,cursor:"pointer",fontFamily:"inherit"}}>
              {STATUTS.map(s=><option key={s}>{s}</option>)}
            </select>}
            <button onClick={onClose} style={{background:"rgba(255,255,255,0.1)",border:"none",borderRadius:7,width:30,height:30,cursor:"pointer",fontSize:16,color:"#fff",fontFamily:"inherit"}}>×</button>
          </div>
        </div>
        <div ref={defileRef} style={{flex:1,overflowY:"auto",padding:"14px 16px"}}>
          {linkedClient&&<div onClick={()=>onOpenClient(linkedClient)} style={{background:"#eff6ff",border:"1px solid #bae6fd",borderRadius:9,padding:"9px 12px",marginBottom:10,cursor:"pointer",display:"flex",alignItems:"center",gap:9}}>
            <span style={{background:"#0ea5e9",color:"#fff",fontWeight:800,fontSize:10,padding:"3px 8px",borderRadius:5}}>{linkedClient.cliNum}</span>
            <span style={{fontSize:12,fontWeight:700,color:"#0c4a6e"}}>👤 Fiche client liée</span>
            {linkedClient.vip&&<span>⭐</span>}
            <span style={{marginLeft:"auto",fontSize:11,color:"#0369a1"}}>Cliquer pour ouvrir →</span>
          </div>}
          {late&&<div style={{background:"#fef2f2",border:"1px solid #fca5a5",borderRadius:8,padding:"7px 11px",marginBottom:10,fontSize:12,color:"#991b1b",fontWeight:600}}>⚠️ Livraison en retard depuis le {o.dateLivraison}</div>}
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(210px,1fr))",gap:9,marginBottom:0}}>
            <div style={sC}>
              <div style={sT}>👤 CLIENT</div>
              {[["Téléphone",o.telephone],["Adresse",o.ville||o.paysOrigine],["Pays",o.nationalite]].filter(([,v])=>v).map(([l,v])=>(
                <div key={l} style={sR}><span style={{color:"#aaa"}}>{l}</span><span style={{fontWeight:700,color:D,textAlign:"right"}}>{v}</span></div>
              ))}
            </div>
            {t0?<div style={sC}>
              <div style={sT}>🧶 {tissuLabel(t0).toUpperCase()}</div>
              <div style={sR}><span style={{color:"#aaa"}}>Type</span><span style={{fontWeight:700,color:D}}>Tissu {tissuLabel(t0)}</span></div>
              <div style={sR}><span style={{color:"#aaa"}}>Mètres</span><span style={{fontWeight:700,color:D}}>{t0.metres?t0.metres+" m":"—"}</span></div>
              <div style={sR}><span style={{color:"#aaa"}}>Couleur</span><span style={{fontWeight:700,color:D,display:"flex",alignItems:"center",gap:5}}>{colH&&<span style={{width:11,height:11,borderRadius:"50%",background:colH,border:"1px solid rgba(0,0,0,0.1)",display:"inline-block"}}/>}{t0.couleur||"—"}</span></div>
              {IS_GROSSISTE
                ?(prixDe&&prixDe(t0)>0&&<div style={sR}><span style={{color:"#aaa"}}>Prix</span><span style={{fontWeight:700,color:D}}>{fmt(prixDe(t0))} F/m · {fmt((parseFloat(t0.metres)||0)*prixDe(t0))} F</span></div>)
                :<div style={sR}><span style={{color:"#aaa"}}>Priorité</span><span style={{fontWeight:700,color:o.priorite==="Urgente"?"#ef4444":D}}>{o.priorite}</span></div>}
              {t0.modele&&<div style={{marginTop:7}}><div style={{fontSize:10,color:"#bbb",marginBottom:2,fontWeight:700}}>Modèle :</div><div style={{fontSize:12,color:"#555",lineHeight:1.6}}>{t0.modele}</div></div>}
              {photoRow(t0)}
            </div>:<div style={{...sC,display:"flex",alignItems:"center",justifyContent:"center",color:"#ddd"}}>Aucun tissu</div>}
          </div>
          {tis.length>1&&tis.slice(1).map((t,i)=>{
            return(<div key={i} style={{...sC,border:"1px solid #eee"}}>
              <div style={sT}>🧶 TISSU N°{i+2} — {tissuLabel(t).toUpperCase()}</div>
              {[["Mètres",t.metres?t.metres+" m":"—"],["Couleur",t.couleur||"—"],...(prixDe&&prixDe(t)>0?[["Prix",fmt(prixDe(t))+" F/m · "+fmt((parseFloat(t.metres)||0)*prixDe(t))+" F"]]:[])].map(([l,v])=>(
                <div key={l} style={sR}><span style={{color:"#aaa"}}>{l}</span><span style={{fontWeight:700,color:D}}>{v}</span></div>
              ))}
              {t.modele&&<div style={{fontSize:11,color:"#666",marginTop:5}}>↳ {t.modele}</div>}
              {photoRow(t)}
            </div>);
          })}
          {(mesH.length>0||mesB.length>0||mesP.length>0)&&<div style={{background:"#f0f9ff",borderRadius:10,padding:"12px 14px",marginBottom:10,border:"1px solid #bae6fd"}}>
            <div style={{...sT,color:"#0369a1"}}>📐 MESURES</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
              {mesH.length>0&&<div>
                <div style={{fontSize:10,fontWeight:700,color:"#555",marginBottom:7}}>👔 Haut</div>
                {mesH.map(([l,v])=>(<div key={l} style={{display:"flex",justifyContent:"space-between",padding:"4px 0",borderBottom:"1px solid #e0f0ff",fontSize:12}}><span style={{color:"#888"}}>{l}</span><span style={{fontWeight:800,color:"#2563eb"}}>{v}</span></div>))}
              </div>}
              {mesB.length>0&&<div>
                <div style={{fontSize:10,fontWeight:700,color:"#555",marginBottom:7}}>👖 Bas</div>
                {mesB.map(([l,v])=>(<div key={l} style={{display:"flex",justifyContent:"space-between",padding:"4px 0",borderBottom:"1px solid #d1fae5",fontSize:12}}><span style={{color:"#888"}}>{l}</span><span style={{fontWeight:800,color:"#059669"}}>{v}</span></div>))}
              </div>}
            </div>
            {mesP.length>0&&<div style={{marginTop:10,paddingTop:8,borderTop:"1px solid #bae6fd"}}>
              <div style={{fontSize:10,fontWeight:700,color:"#555",marginBottom:7}}>➕ Autres</div>
              {mesP.map((m,i)=>(<div key={i} style={{display:"flex",justifyContent:"space-between",padding:"4px 0",borderBottom:"1px solid #e0f0ff",fontSize:12}}><span style={{color:"#888"}}>{m.l||"—"}</span><span style={{fontWeight:800,color:"#2563eb"}}>{m.v}</span></div>))}
            </div>}
          </div>}
          <div style={{background:"#fffbeb",borderRadius:10,padding:"12px 14px",marginBottom:10,border:"1px solid #fde68a"}}>
            <div style={{...sT,color:"#d97706"}}>💰 FINANCES ET DATES</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(110px,1fr))",gap:7,marginBottom:9}}>
              {[["Prix total",fmt(o.prixTotal)+" F",G],["Versé",fmt(o.avanceRecue)+" F","#10b981"],["Restant",r.toLocaleString("fr-FR")+" F",r>0?"#ef4444":"#10b981"],IS_GROSSISTE?["Date de la vente",o.dateDepot||"—","#888"]:["Dépôt → Livraison",(o.dateDepot||"?")+" → "+(o.dateLivraison||"?"),"#888"]].map(([l,v,vc])=>(
                <div key={l} style={{background:D,borderRadius:7,padding:"9px 8px",textAlign:"center"}}>
                  <div style={{fontSize:8,color:"#777",marginBottom:3,fontWeight:600,lineHeight:1.3}}>{l}</div>
                  <div style={{fontSize:11,fontWeight:800,color:vc,lineHeight:1.3}}>{v}</div>
                </div>
              ))}
            </div>
            {IS_GROSSISTE&&r>0&&<div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,flexWrap:"wrap"}}>
              <span style={{fontSize:11.5,fontWeight:700,color:o.dateEcheance&&o.dateEcheance<new Date().toISOString().slice(0,10)?"#dc2626":"#92400e"}}><IconeCredit t={15}/> {o.dateEcheance?"Paiement promis le "+dateLongue(o.dateEcheance):"Vente à crédit · pas de date promise"}</span>
              {onEncaisser&&<button onClick={()=>onEncaisser(o)} style={{background:"#10b981",color:"#fff",border:"none",padding:"7px 12px",borderRadius:7,cursor:"pointer",fontWeight:800,fontSize:11.5,fontFamily:"inherit"}}>💵 Encaisser</button>}
            </div>}
            {(o.paiements||[]).length>0&&<div style={{marginTop:9,borderTop:"1px dashed #fde68a",paddingTop:8}}>
              <div style={{fontSize:9,fontWeight:800,color:"#b45309",letterSpacing:1,marginBottom:4}}>PAYÉ APRÈS LA VENTE</div>
              {o.paiements.map((p,i)=>(<div key={i} style={{display:"flex",justifyContent:"space-between",fontSize:11.5,padding:"3px 0",color:"#555"}}><span>{dateLongue(p.date)}{p.mode?" · "+p.mode:""}</span><strong style={{color:"#059669"}}>+{fmt(p.montant)} F</strong></div>))}
            </div>}
          </div>
          {/* BÉNÉFICE DE LA COMMANDE (Premium) — prix − argent dépensé */}
          {IS_PREMIUM&&(o.couts||[]).some(c=>c.l||c.m)&&(()=>{
            const cout=(o.couts||[]).reduce((s,c)=>s+(parseFloat(c.m)||0),0);
            const benef=(parseFloat(o.prixTotal)||0)-cout;
            return(<div style={{background:benef>=0?"#f0fdf4":"#fef2f2",borderRadius:10,padding:"12px 14px",marginBottom:10,border:"1px solid "+(benef>=0?"#bbf7d0":"#fecaca")}}>
              <div style={{...sT,color:benef>=0?"#166534":"#991b1b"}}>💸 ARGENT DÉPENSÉ & BÉNÉFICE</div>
              {(o.couts||[]).filter(c=>c.l||c.m).map((c,i)=>(
                <div key={i} style={{display:"flex",justifyContent:"space-between",fontSize:12,padding:"3px 0",color:"#555"}}><span>{c.l||"—"}</span><span style={{color:"#ef4444"}}>−{(parseFloat(c.m)||0).toLocaleString("fr-FR")} F</span></div>
              ))}
              <div style={{display:"flex",justifyContent:"space-between",fontSize:12,padding:"5px 0",borderTop:"1px solid "+(benef>=0?"#bbf7d0":"#fecaca"),marginTop:4}}><span style={{color:"#666"}}>Total dépensé</span><strong style={{color:"#ef4444"}}>−{cout.toLocaleString("fr-FR")} F</strong></div>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:14,fontWeight:900,paddingTop:4}}><span>{benef>=0?"✅ Vous gagnez":"⚠️ Vous perdez"}</span><span style={{color:benef>=0?"#059669":"#dc2626"}}>{benef.toLocaleString("fr-FR")} F</span></div>
            </div>);
          })()}
          {o.remarques&&<div style={{background:"#fffbeb",borderRadius:10,padding:"12px 14px",marginBottom:10,border:"1px solid #fde68a"}}>
            <div style={{...sT,color:"#d97706"}}>📝 REMARQUES</div>
            <div style={{fontSize:13,color:"#555",lineHeight:1.75,whiteSpace:"pre-wrap"}}>{o.remarques}</div>
          </div>}
        </div>
        <FlecheHaut cible={defileRef} bas={92} z={320}/>
        <div style={{padding:"12px 16px calc(12px + env(safe-area-inset-bottom))",borderTop:"1px solid #f0f0f0",display:"flex",gap:7,flexShrink:0,background:"#fafafa",flexWrap:"wrap"}}>
          {!IS_GROSSISTE&&o.statut!=="Livré"&&(()=>{const nx=STATUTS[STATUTS.indexOf(o.statut)+1];return nx?<button onClick={()=>onUpdSt(o.id,nx)} title={"Passer la commande à « "+nx+" »"} style={{flex:"1 1 100px",background:"#dcfce7",color:"#166534",border:"1px solid #86efac",padding:"10px",borderRadius:8,cursor:"pointer",fontWeight:800,fontSize:12,fontFamily:"inherit"}}>➡️ {nx}</button>:null;})()}
          <button onClick={()=>onPrint(o)} style={{flex:"1 1 90px",background:"#10b981",color:"#fff",border:"none",padding:"10px",borderRadius:8,cursor:"pointer",fontWeight:800,fontSize:12,fontFamily:"inherit"}}>🧾 Reçu</button>
          {isPro&&o.telephone&&(()=>{const url=waLink(o.telephone,buildWaMessage(o,atelierInfo));return url?<a href={url} target="_blank" rel="noreferrer" style={{flex:"1 1 90px",background:"#25D366",color:"#fff",border:"none",padding:"10px",borderRadius:8,cursor:"pointer",fontWeight:700,fontFamily:"inherit",fontSize:12,textDecoration:"none",textAlign:"center",display:"flex",alignItems:"center",justifyContent:"center",gap:4}} title="Envoyer un message WhatsApp avec message pré-rempli">💬 {o.statut==="Prêt"?"Notifier":"WhatsApp"}</a>:null;})()}
          {isPro&&linkedClient?.email&&(()=>{const mailUrl=buildMailtoLink(o,linkedClient,atelierInfo);return mailUrl?<a href={mailUrl} style={{flex:"1 1 90px",background:"#3b82f6",color:"#fff",border:"none",padding:"10px",borderRadius:8,cursor:"pointer",fontWeight:700,fontFamily:"inherit",fontSize:12,textDecoration:"none",textAlign:"center",display:"flex",alignItems:"center",justifyContent:"center",gap:4}} title="Envoyer un email avec sujet et corps pré-remplis">📧 Email</a>:null;})()}
          <button onClick={()=>onEdit(o)} style={{flex:"1 1 90px",background:"#fffbeb",color:"#f59e0b",border:"1px solid #fde68a",padding:"10px",borderRadius:8,cursor:"pointer",fontWeight:700,fontFamily:"inherit",fontSize:12}}>✏️ Modifier</button>
          <button onClick={()=>onDelete(o.id)} style={{background:"#fef2f2",color:"#ef4444",border:"1px solid #fca5a5",padding:"10px 13px",borderRadius:8,cursor:"pointer",fontWeight:700,fontFamily:"inherit"}}>🗑</button>
        </div>
      </div>
      {zoomImg&&<div onClick={e=>{e.stopPropagation();setZoomImg(null);}} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.9)",zIndex:400,display:"flex",alignItems:"center",justifyContent:"center",cursor:"zoom-out",padding:18}}>
        <img src={zoomImg} alt="" style={{maxWidth:"94vw",maxHeight:"88vh",objectFit:"contain",borderRadius:10,boxShadow:"0 20px 60px rgba(0,0,0,0.6)"}}/>
        <button onClick={()=>setZoomImg(null)} style={{position:"fixed",top:16,right:16,background:"rgba(255,255,255,0.14)",color:"#fff",border:"none",borderRadius:9,width:38,height:38,cursor:"pointer",fontSize:19,fontFamily:"inherit"}}>×</button>
      </div>}
    </div>
  );
});

// ── PANNEAU CLIENT ────────────────────────────────────────────────
const ClientPanel=memo(({client,orders,onClose,onEdit,onDelete,onNewOrder,onOpenOrder,atelierInfo,isPro,isGrossiste})=>{
  const defileRef=useRef(null); // zone qui défile, pour la flèche « remonter »
  const c=client;
  const clientOrders=orders.filter(o=>o.clientId===c.id).sort((a,b)=>b.id-a.id);
  const totalCA=clientOrders.reduce((s,o)=>s+(parseFloat(o.prixTotal)||0),0);
  const mesH=[["Épaule",c.mEpaule],["Longueur",c.mLongueur],["Poitrine",c.mPoitrine],["Taille",c.mTaille],["Manche",c.mManche],["Poignet",c.mPoignet],["Tour bras",c.mBras],["Bassin",c.mBassinH],["Col",c.mCol]].filter(([,v])=>v&&String(v).trim());
  const mesB=[["Ceinture",c.mCeinture],["Longueur",c.mLongueurP],["Cuisse",c.mCuisse],["Bassin",c.mBassinP],["Bas",c.mBas]].filter(([,v])=>v&&String(v).trim());
  const mesP=(c.mesuresPlus||[]).filter(m=>m.l||m.v);
  // En Pro : prend la dernière commande "Prêt" pour pré-remplir le message
  const cmdPrete=clientOrders.find(o=>o.statut==="Prêt");
  const waMsg=isPro&&cmdPrete?buildWaMessage(cmdPrete,atelierInfo):"";
  const waUrl=waLink(c.telephone,waMsg);
  const today=new Date();
  let bdayAlert=false,bdayDays=null;
  if(c.anniversaire){
    try{
      const parts=c.anniversaire.split("-");
      if(parts.length===3){
        const bd=new Date(today.getFullYear(),parseInt(parts[1])-1,parseInt(parts[2]));
        if(bd<today)bd.setFullYear(today.getFullYear()+1);
        bdayDays=Math.ceil((bd-today)/(1000*60*60*24));
        bdayAlert=bdayDays>=0&&bdayDays<=14;
      }
    }catch{}
  }
  const lastOrder=clientOrders[0];
  const daysSinceLast=lastOrder?Math.floor((today-new Date(lastOrder.id))/(1000*60*60*24)):null;
  const sC={background:"#f5f5f5",borderRadius:10,padding:"12px 14px",marginBottom:10};
  const sT={fontSize:9,fontWeight:800,color:"#bbb",textTransform:"uppercase",letterSpacing:"1.2px",marginBottom:8};
  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",zIndex:300,display:"flex",justifyContent:"flex-end"}} onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div style={{background:"#fff",width:"100%",maxWidth:540,display:"flex",flexDirection:"column",boxShadow:"-4px 0 32px rgba(0,0,0,0.12)"}}>
        <div style={{background:D,padding:"calc(14px + env(safe-area-inset-top)) 18px 14px",display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0}}>
          <div style={{display:"flex",gap:9,alignItems:"center",flex:1,minWidth:0}}>
            <span style={{background:"#0ea5e9",color:"#fff",fontWeight:800,fontSize:10,padding:"3px 9px",borderRadius:5,flexShrink:0}}>{c.cliNum}</span>
            <span style={{color:"#fff",fontWeight:700,fontSize:14,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.prenom} {c.nom}</span>
            {c.vip&&<span style={{fontSize:14}}>⭐</span>}
          </div>
          <button onClick={onClose} style={{background:"rgba(255,255,255,0.1)",border:"none",borderRadius:7,width:30,height:30,cursor:"pointer",fontSize:16,color:"#fff",fontFamily:"inherit"}}>×</button>
        </div>
        <div ref={defileRef} style={{flex:1,overflowY:"auto",padding:"14px 16px"}}>
          {bdayAlert&&<div style={{background:"#fef3c7",border:"1px solid #fde68a",borderRadius:8,padding:"9px 12px",marginBottom:10,fontSize:12,color:"#92400e",fontWeight:700,display:"flex",alignItems:"center",gap:8}}>
            🎂 Anniversaire {bdayDays===0?"aujourd'hui !":`dans ${bdayDays} jour${bdayDays>1?"s":""}`}
          </div>}
          {daysSinceLast!==null&&daysSinceLast>180&&<div style={{background:"#fef2f2",border:"1px solid #fca5a5",borderRadius:8,padding:"9px 12px",marginBottom:10,fontSize:12,color:"#991b1b",fontWeight:600}}>
            ⏰ Client inactif depuis {Math.floor(daysSinceLast/30)} mois — Pensez à le relancer
          </div>}
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:9,marginBottom:11}}>
            <div style={{background:D,borderRadius:9,padding:"10px",textAlign:"center"}}>
              <div style={{fontSize:9,color:"#777",marginBottom:3,fontWeight:600}}>📋 Commandes</div>
              <div style={{fontSize:18,fontWeight:900,color:G}}>{clientOrders.length}</div>
            </div>
            <div style={{background:D,borderRadius:9,padding:"10px",textAlign:"center"}}>
              <div style={{fontSize:9,color:"#777",marginBottom:3,fontWeight:600}}>💰 Total CA</div>
              <div style={{fontSize:13,fontWeight:900,color:"#34d399"}}>{fmt(totalCA)} F</div>
            </div>
            <div style={{background:D,borderRadius:9,padding:"10px",textAlign:"center"}}>
              <div style={{fontSize:9,color:"#777",marginBottom:3,fontWeight:600}}>📅 Dernière</div>
              <div style={{fontSize:11,fontWeight:800,color:"#fff"}}>{daysSinceLast!==null?(daysSinceLast===0?"Aujourd'hui":daysSinceLast<30?`Il y a ${daysSinceLast}j`:`Il y a ${Math.floor(daysSinceLast/30)}m`):"—"}</div>
            </div>
          </div>
          <div style={sC}>
            <div style={sT}>📞 CONTACT</div>
            {[["Téléphone",c.telephone],["Email",c.email],["Adresse",c.ville],["Nationalité",c.nationalite],["Anniversaire",c.anniversaire]].filter(([,v])=>v).map(([l,v])=>(
              <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"5px 0",borderBottom:"1px solid #ebebeb",fontSize:12}}><span style={{color:"#aaa"}}>{l}</span><span style={{fontWeight:700,color:D}}>{v}</span></div>
            ))}
            <div style={{marginTop:8,fontSize:10,color:"#aaa"}}>Client depuis le {c.dateCreation||"—"}</div>
          </div>
          {!isGrossiste&&((mesH.length>0||mesB.length>0||mesP.length>0)?<div style={{background:"#f0f9ff",borderRadius:10,padding:"12px 14px",marginBottom:10,border:"1px solid #bae6fd"}}>
            <div style={{...sT,color:"#0369a1"}}>📐 MESURES ENREGISTRÉES</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
              {mesH.length>0&&<div>
                <div style={{fontSize:10,fontWeight:700,color:"#555",marginBottom:7}}>👔 Haut</div>
                {mesH.map(([l,v])=>(<div key={l} style={{display:"flex",justifyContent:"space-between",padding:"4px 0",borderBottom:"1px solid #e0f0ff",fontSize:12}}><span style={{color:"#888"}}>{l}</span><span style={{fontWeight:800,color:"#2563eb"}}>{v}</span></div>))}
              </div>}
              {mesB.length>0&&<div>
                <div style={{fontSize:10,fontWeight:700,color:"#555",marginBottom:7}}>👖 Bas</div>
                {mesB.map(([l,v])=>(<div key={l} style={{display:"flex",justifyContent:"space-between",padding:"4px 0",borderBottom:"1px solid #d1fae5",fontSize:12}}><span style={{color:"#888"}}>{l}</span><span style={{fontWeight:800,color:"#059669"}}>{v}</span></div>))}
              </div>}
            </div>
            {mesP.length>0&&<div style={{marginTop:10,paddingTop:8,borderTop:"1px solid #bae6fd"}}>
              <div style={{fontSize:10,fontWeight:700,color:"#555",marginBottom:7}}>➕ Autres</div>
              {mesP.map((m,i)=>(<div key={i} style={{display:"flex",justifyContent:"space-between",padding:"4px 0",borderBottom:"1px solid #e0f0ff",fontSize:12}}><span style={{color:"#888"}}>{m.l||"—"}</span><span style={{fontWeight:800,color:"#2563eb"}}>{m.v}</span></div>))}
            </div>}
          </div>:<div style={{background:"#fff7ed",border:"1px dashed #fed7aa",borderRadius:9,padding:"11px 14px",marginBottom:10,fontSize:12,color:"#9a3412",textAlign:"center"}}>
            ⚠️ Aucune mesure enregistrée — Cliquez sur "Modifier" pour les ajouter
          </div>)}
          {c.notes&&<div style={{background:"#fffbeb",borderRadius:10,padding:"12px 14px",marginBottom:10,border:"1px solid #fde68a"}}>
            <div style={{...sT,color:"#d97706"}}>📝 NOTES PERMANENTES</div>
            <div style={{fontSize:12,color:"#555",lineHeight:1.75,whiteSpace:"pre-wrap"}}>{c.notes}</div>
          </div>}
          <div style={sC}>
            <div style={sT}>📋 HISTORIQUE COMMANDES ({clientOrders.length})</div>
            {clientOrders.length===0?<div style={{textAlign:"center",padding:"14px",color:"#ccc",fontSize:12}}>Aucune commande pour ce client</div>
            :clientOrders.map(o=>{
              const r2=(parseFloat(o.prixTotal)||0)-(parseFloat(o.avanceRecue)||0);
              return(<div key={o.id} onClick={()=>onOpenOrder(migrateTissus(o))} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 0",borderBottom:"1px solid #ebebeb",cursor:"pointer",fontSize:12}}>
                <span style={{background:G+"22",color:D,fontWeight:800,fontSize:9,padding:"2px 6px",borderRadius:4,flexShrink:0}}>{o.cmdNum}</span>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{IS_GROSSISTE?((o.tissus||[]).filter(t=>t.type).map(t=>tissuLabel(t)).join(", ")||"Vente"):o.categorie} · {o.dateDepot}</div>
                  <div style={{fontSize:10,color:"#aaa"}}>{fmt(o.prixTotal)} F {r2>0?`· ${r2.toLocaleString("fr-FR")}F dû`:"· soldé"}</div>
                </div>
                {!IS_GROSSISTE&&<span style={{background:SC[o.statut]+"22",color:SC[o.statut],padding:"2px 7px",borderRadius:20,fontSize:9,fontWeight:700,flexShrink:0}}>{o.statut}</span>}
              </div>);
            })}
          </div>
        </div>
        <FlecheHaut cible={defileRef} bas={92} z={320}/>
        <div style={{padding:"12px 16px calc(12px + env(safe-area-inset-bottom))",borderTop:"1px solid #f0f0f0",display:"flex",gap:7,flexShrink:0,background:"#fafafa"}}>
          <button onClick={()=>onNewOrder(c)} style={{flex:1,background:G,color:D,border:"none",padding:"10px",borderRadius:8,cursor:"pointer",fontWeight:800,fontSize:12,fontFamily:"inherit"}}>➕ Nouvelle commande</button>
          <button onClick={()=>onEdit(c)} style={{flex:1,background:"#fffbeb",color:"#f59e0b",border:"1px solid #fde68a",padding:"10px",borderRadius:8,cursor:"pointer",fontWeight:700,fontFamily:"inherit",fontSize:12}}>✏️ Modifier</button>
          {waUrl&&<a href={waUrl} target="_blank" rel="noreferrer" style={{background:"#25D366",color:"#fff",border:"none",padding:"10px 13px",borderRadius:8,cursor:"pointer",fontWeight:700,fontFamily:"inherit",textDecoration:"none",display:"flex",alignItems:"center",fontSize:13}} title="WhatsApp">💬</a>}
          {c.email&&<a href={`mailto:${c.email}?subject=${encodeURIComponent("Bonjour "+(c.prenom||"")+" "+(c.nom||""))}&body=${encodeURIComponent("Bonjour "+(c.prenom||"")+" "+(c.nom||"")+",\n\n\n\nCordialement,\n"+(atelierInfo?.nom||""))}`} style={{background:"#3b82f6",color:"#fff",border:"none",padding:"10px 13px",borderRadius:8,cursor:"pointer",fontWeight:700,fontFamily:"inherit",textDecoration:"none",display:"flex",alignItems:"center",fontSize:13}} title="Email">📧</a>}
          <button onClick={()=>onDelete(c.id)} style={{background:"#fef2f2",color:"#ef4444",border:"1px solid #fca5a5",padding:"10px 13px",borderRadius:8,cursor:"pointer",fontWeight:700,fontFamily:"inherit"}}>🗑</button>
        </div>
      </div>
    </div>
  );
});

// ── SETTINGS ──────────────────────────────────────────────────────
// ÉCRAN DE VERROUILLAGE DE LA CAISSE — l'employé saisit les commandes, seul le patron voit l'argent
function PinLock({onUnlock,isGrossiste}){
  const [pin,setPin]=useState("");
  const [err,setErr]=useState("");
  const [busy,setBusy]=useState(false);
  const submit=async()=>{
    if(!pin){return;}
    setBusy(true);
    const ok=await onUnlock(pin);
    setBusy(false);
    if(!ok){setErr("❌ Code incorrect");setPin("");}
  };
  const tap=d=>{setErr("");setPin(p=>(p+d).slice(0,8));};
  return(
    <div style={{display:"flex",alignItems:"center",justifyContent:"center",padding:"30px 12px"}}>
      <div style={{background:"#fff",borderRadius:16,padding:"26px 24px",maxWidth:330,width:"100%",textAlign:"center",boxShadow:"0 4px 20px rgba(0,0,0,0.07)",border:"1px solid #f0ece4"}}>
        <div style={{fontSize:46,marginBottom:6}}>🔒</div>
        <div style={{fontSize:16,fontWeight:900,color:D,marginBottom:5}}>Caisse protégée</div>
        <div style={{fontSize:11.5,color:"#888",lineHeight:1.6,marginBottom:16}}>Saisissez le code secret pour voir {isGrossiste?"les ventes":"la caisse"} et les bénéfices.</div>
        <input value={pin} onChange={e=>{setErr("");setPin(e.target.value.replace(/\D/g,"").slice(0,8));}} onKeyDown={e=>e.key==="Enter"&&submit()} type="password" inputMode="numeric" autoComplete="off" style={{...si,textAlign:"center",fontSize:24,letterSpacing:"8px",padding:"12px",border:"2px solid "+(err?"#fca5a5":"#e8e8e8")}}/>
        {err&&<div style={{color:"#ef4444",fontSize:12,marginTop:7,fontWeight:600}}>{err}</div>}
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:7,marginTop:14}}>
          {["1","2","3","4","5","6","7","8","9"].map(d=>(
            <button key={d} onClick={()=>tap(d)} style={{background:"#f7f5f0",border:"none",borderRadius:10,padding:"13px 0",fontSize:18,fontWeight:800,color:D,cursor:"pointer",fontFamily:"inherit"}}>{d}</button>
          ))}
          <button onClick={()=>{setErr("");setPin("");}} style={{background:"#f7f5f0",border:"none",borderRadius:10,padding:"13px 0",fontSize:13,fontWeight:700,color:"#888",cursor:"pointer",fontFamily:"inherit"}}>C</button>
          <button onClick={()=>tap("0")} style={{background:"#f7f5f0",border:"none",borderRadius:10,padding:"13px 0",fontSize:18,fontWeight:800,color:D,cursor:"pointer",fontFamily:"inherit"}}>0</button>
          <button onClick={()=>{setErr("");setPin(p=>p.slice(0,-1));}} style={{background:"#f7f5f0",border:"none",borderRadius:10,padding:"13px 0",fontSize:15,cursor:"pointer",fontFamily:"inherit"}}>⌫</button>
        </div>
        <button onClick={submit} disabled={busy||!pin} style={{marginTop:13,width:"100%",background:pin?G:"#e8e8e8",color:pin?D:"#aaa",border:"none",padding:"12px",borderRadius:9,cursor:pin?"pointer":"default",fontWeight:800,fontSize:14,fontFamily:"inherit"}}>🔓 Déverrouiller</button>
        <div style={{fontSize:10,color:"#bbb",marginTop:11,lineHeight:1.6}}>
          Code oublié ? Le patron peut le changer dans ⚙️ Paramètres → Atelier.<br/>
          Sinon, contactez <strong style={{color:"#999"}}>TOBY au 77 753 31 53</strong> pour le réinitialiser à distance.
        </div>
      </div>
    </div>
  );
}

function SettingsModal({onClose,atelierInfo,onSaveAtelier,isPro,colMeta,colPhotos,trash,onRestoreTrash,onPurgeTrash,onBackupDone,initialTab,pinHash,onSetPin,onRemovePin,onVerifyPin,cloud,licKey,onLogout}){
  const [aF,setAF]=useState({...atelierInfo});
  const [tab,setTab]=useState(initialTab||"atelier");
  const [msg,setMsg]=useState("");
  // Les deux « Restaurer » sont rangés derrière un volet qu'il faut ouvrir
  // exprès : ce sont les seuls boutons de l'application qui peuvent effacer
  // un mois de travail, et un avertissement ne se lit pas.
  const [voletAvance,setVoletAvance]=useState(false);
  const [extFolder,setExtFolder]=useState(null);
  const [backupInfo,setBackupInfo]=useState(null);
  const [busy,setBusy]=useState(false);
  const [sz,setSz]=useState(null);
  const [quota,setQuota]=useState(null);
  const [persisted,setPersisted]=useState(null); // stockage protégé contre l'effacement auto ?
  const [lastBackup,setLastBackup]=useState(null); // date de dernière sauvegarde web/mobile
  const [pinCur,setPinCur]=useState(""),[pinNew,setPinNew]=useState(""),[pinConf,setPinConf]=useState(""); // code caisse
  const [cPwd,setCPwd]=useState(""),[cPwd2,setCPwd2]=useState(""),[cMode,setCMode]=useState("connexion"); // compte TOBY Cloud
  const [phInfo,setPhInfo]=useState(null);   // {locales, poids} photos sur cet appareil
  const [phEtat,setPhEtat]=useState("");     // message d avancement
  const [chgAnc,setChgAnc]=useState(""),[chgNouv,setChgNouv]=useState(""),[chgNouv2,setChgNouv2]=useState(""); // changement de mot de passe du compte
  const contenuRef=useRef(null); // zone qui défile, pour la flèche « remonter »
  const mobile=useIsMobile();
  useEffect(()=>{(async()=>{
    if(window.electronAPI?.backup?.getFolder){
      try{const f=await window.electronAPI.backup.getFolder();setExtFolder(f);}catch{}
      try{const l=await window.electronAPI.backup.list();setBackupInfo(l);}catch{}
    }
    try{const k=await window.storage.list();let s=0;for(const key of(k.keys||[])){try{const v=await window.storage.get(key);if(v)s+=(v.value||"").length;}catch{}}setSz(s);}catch{}
    // Quota RÉEL du navigateur (IndexedDB : centaines de Mo à plusieurs Go selon l'appareil)
    if(!window.electronAPI&&navigator.storage?.estimate){
      try{const est=await navigator.storage.estimate();if(est.quota)setQuota(est.quota);}catch{}
    }
    // Stockage persistant : (re)demande + affiche l'état ; date de dernière sauvegarde
    setPersisted(await requestPersistentStorage());
    try{const lb=await window.storage.get("cp_last_backup");if(lb)setLastBackup(Number(lb.value));}catch{}
    // Photos présentes sur l appareil (pour annoncer le poids avant envoi)
    try{const ids=await photosLocales();const p=await poidsPhotosLocales();setPhInfo({n:ids.length,poids:p});}catch{}
  })();},[tab]);
  // Plafond : quota réel du navigateur, ou 2 Go sur PC (limite disque symbolique)
  const capacite=quota||2*1024*1024*1024;
  const pct=sz?Math.min((sz/capacite)*100,100):0;
  const fmtSz=b=>b==null?"—":b<1024?b+" o":b<1048576?(b/1024).toFixed(1)+" Ko":(b/1048576).toFixed(2)+" Mo";
  const showMsg=(m,err)=>{setMsg({text:m,err:!!err});setTimeout(()=>setMsg(""),3500);};
  const chooseFolder=async()=>{
    if(!window.electronAPI?.backup?.chooseFolder)return;
    setBusy(true);
    try{
      const f=await window.electronAPI.backup.chooseFolder();
      if(f){setExtFolder(f);showMsg("✅ Dossier configuré : "+f);const l=await window.electronAPI.backup.list();setBackupInfo(l);}
    }catch(e){showMsg("Erreur : "+e.message,true);}
    setBusy(false);
  };
  const clearFolder=async()=>{
    if(!window.electronAPI?.backup?.clearFolder)return;
    await window.electronAPI.backup.clearFolder();
    setExtFolder(null);showMsg("Dossier externe désactivé");
    const l=await window.electronAPI.backup.list();setBackupInfo(l);
  };
  // (buildWebBackup / downloadJSON / shareOrDownloadBackup / markBackupDone : helpers au niveau module)
  const backupNow=async()=>{
    setBusy(true);
    try{
      if(window.electronAPI?.backup?.now){
        const r=await window.electronAPI.backup.now();
        if(r.success){await markBackupDone();showMsg(r.externalFolder?"✅ Sauvegarde locale + externe créée":"✅ Sauvegarde locale créée");const l=await window.electronAPI.backup.list();setBackupInfo(l);onBackupDone&&onBackupDone();}
        else showMsg("Erreur : "+r.error,true);
      }else{
        // Mobile/web : partage le fichier (WhatsApp, Drive, email…) ou le télécharge
        const res=await shareOrDownloadBackup("toby-sauvegarde");
        if(res==="cancel"){showMsg("Sauvegarde annulée",true);}
        else{setLastBackup(Date.now());onBackupDone&&onBackupDone();showMsg(res==="share"?"✅ Sauvegarde partagée — garde bien le fichier !":"✅ Fichier de sauvegarde enregistré");}
      }
    }catch(e){showMsg("Erreur : "+e.message,true);}
    setBusy(false);
  };
  const restoreFile=async()=>{
    if(!window.confirm("⚠️ ATTENTION : Restaurer va REMPLACER toutes vos données actuelles par celles du fichier choisi.\n\nContinuer ?"))return;
    if(window.electronAPI?.backup?.import){
      setBusy(true);
      try{
        const r=await window.electronAPI.backup.import();
        if(r.success){showMsg("✅ Restauration réussie ! Rechargement...");setTimeout(()=>window.location.reload(),1200);}
        else if(r.error)showMsg("Erreur : "+r.error,true);
      }catch(e){showMsg("Erreur : "+e.message,true);}
      setBusy(false);
      return;
    }
    // Mobile/web : sélecteur de fichier natif
    const input=document.createElement("input");
    input.type="file";input.accept="application/json,.json";
    input.onchange=async()=>{
      const f=input.files&&input.files[0];
      if(!f)return;
      setBusy(true);
      try{
        const content=JSON.parse(await f.text());
        const data=content.version===2?content.data:content;
        for(const[k,v]of Object.entries(data)){await window.storage.set(k,v);}
        // Photos embarquées (backup desktop v2) → rangées en clés cp_p_
        if(content.version===2&&content.photos){
          for(const[id,b64]of Object.entries(content.photos)){await window.storage.set("cp_p_"+id,b64);}
        }
        showMsg("✅ Restauration réussie ! Rechargement...");
        setTimeout(()=>window.location.reload(),1200);
      }catch(e){showMsg("Fichier invalide : "+e.message,true);}
      setBusy(false);
    };
    input.click();
  };
  const exportFile=async()=>{
    setBusy(true);
    try{
      if(window.electronAPI?.backup?.export){
        const r=await window.electronAPI.backup.export();
        if(r.success){await markBackupDone();showMsg("✅ Exporté !");onBackupDone&&onBackupDone();}
      }else{
        const backup=await buildWebBackup();
        downloadJSON(backup,backupFileName("toby-export"));
        await markBackupDone();setLastBackup(Date.now());onBackupDone&&onBackupDone();
        showMsg("✅ Export téléchargé");
      }
    }catch(e){showMsg("Erreur",true);}
    setBusy(false);
  };
  const fmtDate=d=>{try{return new Date(d).toLocaleString("fr-FR",{day:"2-digit",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"});}catch{return"—";}};
  const lastLocal=backupInfo?.local?.[0];
  const lastExt=backupInfo?.external?.[0];
  // PARAMÈTRES RANGÉS COMME UN LOGICIEL PROFESSIONNEL — chaque sujet a sa page, et
  // tout ce qui touche au compte (mot de passe, code de la caisse, déconnexion) est
  // au même endroit, au lieu d'être caché au fond de la sauvegarde.
  const RUBRIQUES=[
    {id:"atelier",ic:"🏪",l:IS_GROSSISTE?"Ma boutique":"Mon atelier",d:"Nom, coordonnées et logo repris sur vos factures"},
    {id:"compte",ic:"🔐",l:"Compte et sécurité",d:"Mot de passe, code de la caisse, déconnexion"},
    {id:"ventes",ic:"🧾",l:IS_GROSSISTE?"Ventes et caisse":"Commandes et caisse",d:"Stock, dépenses et messages aux clients"},
    {id:"facturation",ic:"🧾",l:"Facturation",d:"Statut, NINEA, RCCM et TVA sur vos factures"},
    ...(isPro?[{id:"site",ic:"🌐",l:"Site web",d:"Votre vitrine en ligne à partager"}]:[]),
    {id:"backup",ic:"🛡️",l:"Sauvegarde",d:"Copie en ligne, photos et fichiers"},
    {id:"abonnement",ic:"🎫",l:"Abonnement",d:"Votre formule et son renouvellement"},
    {id:"corbeille",ic:"🗑",l:"Corbeille",d:"Éléments supprimés, gardés 30 jours",n:trash?trash.length:0},
  ];
  const rub=RUBRIQUES.find(r=>r.id===tab)||RUBRIQUES[0];
  const AVEC_ENREGISTRER=["atelier","ventes","site","facturation"].includes(rub.id);
  const modifie=JSON.stringify(aF)!==JSON.stringify(atelierInfo||{});
  const carte={background:"#fff",border:"1px solid #ece7dc",borderRadius:12,padding:"15px 16px",marginBottom:14};
  const titreCarte={fontSize:13.5,fontWeight:800,color:D,marginBottom:4,display:"flex",alignItems:"center",gap:7};
  const aideCarte={fontSize:11.5,color:"#888",lineHeight:1.55,marginBottom:12};
  const logoSrc=aF.logoImage||(aF.logoStyle?logoDataURL(aF.nom||"Ma boutique",aF.logoStyle,aF.logoPalette):"");
  const lienOubli="https://wa.me/221777533153?text="+encodeURIComponent("Bonjour TobyTech, j ai oublié le mot de passe de mon compte "+(IS_GROSSISTE?"Tissi-App":"CouturePro")+". Merci de m en générer un nouveau.");
  const changerMdp=async()=>{
    if(chgNouv.length<6){showMsg("❌ Le nouveau mot de passe doit faire au moins 6 caractères",true);return;}
    if(chgNouv!==chgNouv2){showMsg("❌ Les deux nouveaux mots de passe ne correspondent pas",true);return;}
    if(chgNouv===chgAnc){showMsg("❌ Le nouveau mot de passe est identique à l'actuel",true);return;}
    setBusy(true);const r=await cloud.changerMdp(chgAnc,chgNouv);setBusy(false);
    if(r.ok){setChgAnc("");setChgNouv("");setChgNouv2("");showMsg("✅ Mot de passe changé. Notez-le bien : il sert à vous connecter.");}
    else showMsg("❌ "+(r.erreur||"Échec — vérifiez votre connexion internet"),true);
  };
  const pret=chgAnc&&chgNouv&&chgNouv2;
  return(
    <div onClick={e=>{if(e.target===e.currentTarget)onClose();}} style={{position:"fixed",inset:0,background:"rgba(15,15,30,0.6)",zIndex:400,display:"flex",alignItems:"center",justifyContent:"center",padding:mobile?0:16}}>
      <div style={{background:"#f7f5f0",borderRadius:mobile?0:16,width:"100%",maxWidth:900,height:mobile?"100%":"min(88vh,760px)",display:"flex",flexDirection:"column",overflow:"hidden",boxShadow:"0 24px 70px rgba(0,0,0,0.35)"}}>
        {/* EN-TÊTE — la boutique et l'état du compte, d'un coup d'œil */}
        <div style={{background:D,color:"#fff",padding:mobile?"calc(12px + env(safe-area-inset-top)) 14px 12px":"16px 20px",display:"flex",alignItems:"center",gap:12,flexShrink:0}}>
          {/* Pas de bobine de fil ni de ciseaux chez un vendeur de tissus :
              tant que la boutique n'a pas son propre logo, on montre le nôtre. */}
          {logoSrc?<img src={logoSrc} alt="" style={{width:40,height:40,borderRadius:10,objectFit:"contain",background:"#fff",flexShrink:0}}/>:<div style={{width:40,height:40,borderRadius:10,background:IS_GROSSISTE?"#fff":G,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0}}>{IS_GROSSISTE?<LogoTissi h={10}/>:"✂️"}</div>}
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:16,fontWeight:900,color:G}}>Paramètres</div>
            <div style={{fontSize:11,color:"#9ca3af",marginTop:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{aF.nom||(IS_GROSSISTE?"Ma boutique":"Mon atelier")} · {cloud&&cloud.token?"compte TobyTech connecté":"compte TobyTech non connecté"}</div>
          </div>
          <button onClick={onClose} aria-label="Fermer" style={{background:"rgba(255,255,255,0.1)",border:"none",borderRadius:9,width:36,height:36,cursor:"pointer",fontSize:18,color:"#fff",fontFamily:"inherit",flexShrink:0}}>×</button>
        </div>
        <div style={{flex:1,minHeight:0,display:"flex",flexDirection:mobile?"column":"row"}}>
          {/* MENU — à gauche sur ordinateur, en pastilles sur téléphone */}
          <nav style={mobile?{display:"flex",gap:6,overflowX:"auto",padding:"10px 12px",background:"#fff",borderBottom:"1px solid #ece7dc",flexShrink:0,WebkitOverflowScrolling:"touch"}:{width:230,flexShrink:0,background:"#fff",borderRight:"1px solid #ece7dc",padding:"12px 8px",overflowY:"auto"}}>
            {RUBRIQUES.map(r=>{const on=r.id===rub.id;return(<button key={r.id} onClick={()=>setTab(r.id)} style={mobile
              ?{flexShrink:0,display:"flex",alignItems:"center",gap:6,padding:"8px 13px",borderRadius:20,border:"none",cursor:"pointer",fontFamily:"inherit",fontSize:12.5,fontWeight:on?800:600,background:on?D:"#f1efe9",color:on?G:"#555",whiteSpace:"nowrap"}
              :{width:"100%",display:"flex",alignItems:"center",gap:10,padding:"10px 12px",borderRadius:9,border:"none",cursor:"pointer",fontFamily:"inherit",textAlign:"left",marginBottom:2,background:on?"#fdf6e3":"transparent",boxShadow:on?"inset 3px 0 0 "+G:"none"}}>
              <span style={{fontSize:mobile?14:17}}>{r.ic}</span>
              {mobile?<span>{r.l}{r.n?" ("+r.n+")":""}</span>:<span style={{flex:1,minWidth:0}}>
                <span style={{display:"block",fontSize:13,fontWeight:on?800:600,color:on?D:"#444"}}>{r.l}{r.n?<span style={{marginLeft:6,background:"#fee2e2",color:"#b91c1c",fontSize:10,fontWeight:800,padding:"1px 6px",borderRadius:8}}>{r.n}</span>:null}</span>
                <span style={{display:"block",fontSize:10.5,color:"#999",marginTop:1,lineHeight:1.35}}>{r.d}</span>
              </span>}
            </button>);})}
          </nav>
          {/* CONTENU DE LA RUBRIQUE */}
          <div ref={contenuRef} style={{flex:1,minWidth:0,overflowY:"auto",padding:mobile?"16px 14px 24px":"22px 26px 26px"}}>
            <div style={{marginBottom:16}}>
              <div style={{fontSize:19,fontWeight:900,color:D}}>{rub.ic} {rub.l}</div>
              <div style={{fontSize:12,color:"#999",marginTop:3}}>{rub.d}</div>
            </div>

            {rub.id==="atelier"&&<>
              <div style={carte}>
                <div style={titreCarte}>🪪 Identité</div>
                <div style={aideCarte}>Ces informations apparaissent sur vos factures, reçus, bilans et messages WhatsApp.</div>
                {[[IS_GROSSISTE?"Nom de la boutique":"Nom de l'atelier","nom"],["Slogan (bas de facture)","slogan"],["Adresse","adresse"],["Téléphone / WhatsApp","tel"],["Email (optionnel)","email"],...(isPro?[["NINEA (optionnel)","ninea"]]:[])].map(([lbl,key])=>(
                  <div key={key} style={{marginBottom:10}}><label style={sl}>{lbl}</label><input value={aF[key]||""} onChange={e=>setAF(f=>({...f,[key]:e.target.value}))} style={si}/></div>
                ))}
              </div>
              <div style={carte}>
                <div style={titreCarte}>🎨 Logo</div>
                <div style={aideCarte}>Affiché sur l'accueil, les factures, les bilans et le site web.</div>
                <div style={{marginBottom:12}}>
                  {aF.logoImage?<div style={{display:"flex",alignItems:"center",gap:12,background:"#fdfcf9",border:"1.5px solid #ece7dc",borderRadius:10,padding:10}}>
                    <img src={aF.logoImage} alt="" style={{width:56,height:56,objectFit:"contain",borderRadius:8,background:"#fff"}}/>
                    <div style={{flex:1,fontSize:11.5,color:"#166534",fontWeight:700}}>✓ Votre logo est utilisé partout</div>
                    <button type="button" onClick={()=>setAF(f=>({...f,logoImage:""}))} style={{background:"#fef2f2",color:"#ef4444",border:"none",borderRadius:7,padding:"6px 11px",cursor:"pointer",fontSize:11,fontFamily:"inherit",fontWeight:600}}>✕ Retirer</button>
                  </div>
                  :<label style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,border:"2px dashed #d6cfbf",borderRadius:10,padding:"14px",cursor:"pointer",background:"#fdfcf9",color:D,fontSize:12,fontWeight:700}}>
                    📤 Importer mon logo (si j'en ai déjà un)
                    <input type="file" accept="image/*" style={{display:"none"}} onChange={async e=>{const f=e.target.files&&e.target.files[0];if(!f)return;const d=await shrinkImg(f);setAF(x=>({...x,logoImage:d}));}}/>
                  </label>}
                </div>
                {!aF.logoImage&&<>
                  <div style={{fontSize:11,color:"#888",marginBottom:8}}>…ou choisissez un logo tout prêt :</div>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(88px,1fr))",gap:8,marginBottom:12}}>
                    {LOGO_STYLES.map(ls=>(
                      <div key={ls.id} onClick={()=>setAF(f=>({...f,logoStyle:f.logoStyle===ls.id?"":ls.id}))} style={{cursor:"pointer",background:"#fff",border:aF.logoStyle===ls.id?"3px solid "+G:"1.5px solid #e0e0e0",borderRadius:10,padding:8,textAlign:"center"}}>
                        <img src={logoDataURL(aF.nom||"Ma boutique",ls.id,aF.logoPalette)} alt={ls.l} style={{width:56,height:56,display:"block",margin:"0 auto 5px",borderRadius:8}}/>
                        <div style={{fontSize:9,fontWeight:700,color:aF.logoStyle===ls.id?D:"#888"}}>{ls.l}</div>
                      </div>
                    ))}
                  </div>
                  <label style={sl}>Couleurs du logo</label>
                  <div style={{display:"flex",gap:8,marginBottom:10,flexWrap:"wrap"}}>
                    {LOGO_PALETTES.map(p=>(
                      <div key={p.id} onClick={()=>setAF(f=>({...f,logoPalette:p.id}))} title={p.l} style={{cursor:"pointer",width:40,height:40,borderRadius:"50%",background:`linear-gradient(135deg,${p.c1},${p.c2})`,border:(aF.logoPalette||"classique")===p.id?"3px solid "+D:"2px solid #e0e0e0",display:"flex",alignItems:"center",justifyContent:"center"}}>
                        {(aF.logoPalette||"classique")===p.id&&<span style={{color:"#fff",fontWeight:900,fontSize:15,textShadow:"0 1px 3px rgba(0,0,0,0.5)"}}>✓</span>}
                      </div>
                    ))}
                  </div>
                  <div style={{fontSize:10.5,color:"#888"}}>{aF.logoStyle?`✓ Logo sélectionné (${LOGO_PALETTES.find(p=>p.id===(aF.logoPalette||"classique"))?.l||""})`:"Aucun logo choisi pour l'instant."}</div>
                </>}
              </div>
            </>}

            {rub.id==="compte"&&<>
              {cloud&&(cloud.token?<div style={carte}>
                <div style={titreCarte}>👤 Compte TobyTech</div>
                <div style={{fontSize:12,color:"#166534",fontWeight:700,marginBottom:10}}>✓ Connecté — vos données sont sauvegardées en ligne.</div>
                {[["Clé de licence",licKey||"—"],["Formule",cloud.info?.plan||"—"],["Valable jusqu'au",cloud.info?.dateExpiration||"—"]].map(([l,v])=>(
                  <div key={l} style={{display:"flex",justifyContent:"space-between",gap:10,fontSize:12,padding:"7px 0",borderTop:"1px solid #f3f0ea"}}><span style={{color:"#888"}}>{l}</span><strong style={{color:D,textAlign:"right",wordBreak:"break-all"}}>{v}</strong></div>
                ))}
              </div>
              :<div style={carte}>
                <div style={titreCarte}>👤 Compte TobyTech</div>
                <div style={aideCarte}>Créez votre compte ou connectez-vous : vos données sont sauvegardées en ligne et récupérables sur n'importe quel téléphone.</div>
                <div style={{display:"flex",gap:3,marginBottom:10,background:"#f1efe9",borderRadius:8,padding:3}}>
                  {[["connexion","J'ai déjà un compte"],["creation","Créer mon compte"]].map(([id,l])=>(
                    <button key={id} onClick={()=>setCMode(id)} style={{flex:1,padding:"8px 4px",borderRadius:6,border:"none",cursor:"pointer",fontWeight:cMode===id?800:500,fontSize:11.5,background:cMode===id?"#fff":"transparent",color:cMode===id?D:"#8a7f66",fontFamily:"inherit"}}>{l}</button>
                  ))}
                </div>
                <label style={sl}>Mot de passe (6 caractères minimum)</label><input value={cPwd} onChange={e=>setCPwd(e.target.value)} type="password" style={{...si,marginBottom:8}}/>
                {cMode==="creation"&&<><label style={sl}>Confirmer le mot de passe</label><input value={cPwd2} onChange={e=>setCPwd2(e.target.value)} type="password" style={{...si,marginBottom:8}}/></>}
                <button disabled={busy||!cPwd} onClick={async()=>{
                  if(cMode==="creation"&&cPwd!==cPwd2){showMsg("❌ Les deux mots de passe ne correspondent pas",true);return;}
                  if(cPwd.length<6){showMsg("❌ Le mot de passe doit faire au moins 6 caractères",true);return;}
                  setBusy(true);
                  const r=cMode==="creation"?await cloud.creerCompte(cPwd):await cloud.connexion(cPwd);
                  setBusy(false);
                  if(r.ok){setCPwd("");setCPwd2("");showMsg("✅ Compte TobyTech connecté — sauvegarde en ligne activée");}
                  else showMsg("❌ "+(r.erreur||"Échec"),true);
                }} style={{background:cPwd?"#2563eb":"#e8e8e8",color:cPwd?"#fff":"#aaa",border:"none",padding:"11px",borderRadius:9,cursor:cPwd?"pointer":"default",fontWeight:800,fontSize:13,fontFamily:"inherit",width:"100%"}}>
                  {cMode==="creation"?"🔐 Créer mon compte TOBY":"☁️ Me connecter"}
                </button>
                <div style={{fontSize:10.5,color:"#8a7f66",marginTop:8,lineHeight:1.6,textAlign:"center"}}>
                  Votre identifiant est votre clé de licence. Notez bien votre mot de passe.<br/>
                  <a href={lienOubli} target="_blank" rel="noreferrer" style={{color:"#2563eb",fontWeight:700}}>Mot de passe oublié ? Écrire à TOBY</a>
                </div>
              </div>)}
              {cloud&&cloud.token&&<div style={carte}>
                <div style={titreCarte}>🔑 Mot de passe du compte</div>
                <div style={aideCarte}>C'est le mot de passe qui ouvre votre compte sur un téléphone ou un ordinateur, avec votre numéro. Il faut connaître l'actuel pour le changer.</div>
                <label style={sl}>Mot de passe actuel</label><input value={chgAnc} onChange={e=>setChgAnc(e.target.value)} type="password" autoComplete="current-password" style={{...si,marginBottom:10}}/>
                <div style={{display:"grid",gridTemplateColumns:mobile?"1fr":"1fr 1fr",gap:10}}>
                  <div><label style={sl}>Nouveau mot de passe</label><input value={chgNouv} onChange={e=>setChgNouv(e.target.value)} type="password" autoComplete="new-password" style={si}/></div>
                  <div><label style={sl}>Confirmer le nouveau</label><input value={chgNouv2} onChange={e=>setChgNouv2(e.target.value)} type="password" autoComplete="new-password" style={si}/></div>
                </div>
                <div style={{fontSize:10.5,color:chgNouv&&chgNouv.length<6?"#b45309":"#aaa",marginTop:6}}>6 caractères minimum.</div>
                <button disabled={busy||!pret} onClick={changerMdp} style={{marginTop:12,width:"100%",background:pret?"#2563eb":"#e5e7eb",color:pret?"#fff":"#9ca3af",border:"none",padding:"11px",borderRadius:9,cursor:pret?"pointer":"default",fontWeight:800,fontSize:13,fontFamily:"inherit"}}>🔑 Changer le mot de passe</button>
                <div style={{textAlign:"center",marginTop:9}}><a href={lienOubli} target="_blank" rel="noreferrer" style={{fontSize:11,color:"#2563eb",fontWeight:700}}>Mot de passe oublié ? Écrire à TOBY</a></div>
              </div>}
              {/* CODE SECRET DE LA CAISSE — protège les chiffres des employés */}
              {isPro&&<div style={carte}>
                <div style={titreCarte}>🔒 Code secret de la caisse</div>
                <div style={aideCarte}>
                  {pinHash
                    ?"Un code est actif : la caisse, le crédit et les bénéfices sont masqués tant qu'il n'est pas saisi. Vos employés peuvent continuer à enregistrer des ventes."
                    :"Protégez vos chiffres : avec un code, vos employés enregistrent les ventes mais ne voient ni la caisse, ni le crédit, ni les bénéfices."}
                </div>
                {pinHash&&<div style={{marginBottom:10}}><label style={sl}>Code actuel</label>
                  <input value={pinCur} onChange={e=>setPinCur(e.target.value.replace(/\D/g,"").slice(0,8))} type="password" inputMode="numeric" style={{...si,letterSpacing:"4px"}}/></div>}
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                  <div><label style={sl}>{pinHash?"Nouveau code":"Choisir un code"}</label>
                    <input value={pinNew} onChange={e=>setPinNew(e.target.value.replace(/\D/g,"").slice(0,8))} type="password" inputMode="numeric" style={{...si,letterSpacing:"4px"}}/></div>
                  <div><label style={sl}>Confirmer</label>
                    <input value={pinConf} onChange={e=>setPinConf(e.target.value.replace(/\D/g,"").slice(0,8))} type="password" inputMode="numeric" style={{...si,letterSpacing:"4px"}}/></div>
                </div>
                <div style={{display:"flex",gap:8,marginTop:12,flexWrap:"wrap"}}>
                  <button onClick={async()=>{
                    if(pinHash&&!(await onVerifyPin(pinCur))){showMsg("❌ Code actuel incorrect",true);return;}
                    if(pinNew.length<4){showMsg("❌ Le code doit faire au moins 4 chiffres",true);return;}
                    if(pinNew!==pinConf){showMsg("❌ Les deux codes ne correspondent pas",true);return;}
                    await onSetPin(pinNew);setPinCur("");setPinNew("");setPinConf("");
                    showMsg("✅ Code enregistré — la caisse est protégée");
                  }} style={{flex:"1 1 160px",background:"#7c3aed",color:"#fff",border:"none",padding:"11px",borderRadius:9,cursor:"pointer",fontWeight:800,fontSize:12.5,fontFamily:"inherit"}}>{pinHash?"🔑 Changer le code":"🔒 Activer la protection"}</button>
                  {pinHash&&<button onClick={async()=>{
                    if(!(await onVerifyPin(pinCur))){showMsg("❌ Code actuel incorrect",true);return;}
                    await onRemovePin();setPinCur("");setPinNew("");setPinConf("");
                    showMsg("Protection désactivée");
                  }} style={{flex:"0 1 auto",background:"transparent",color:"#ef4444",border:"1px solid #fca5a5",padding:"11px 14px",borderRadius:9,cursor:"pointer",fontWeight:700,fontSize:12.5,fontFamily:"inherit"}}>Désactiver</button>}
                </div>
              </div>}
              {onLogout&&<div style={carte}>
                <div style={titreCarte}>🚪 Se déconnecter</div>
                <div style={aideCarte}>Ferme votre compte sur cet appareil. Vos données restent sur l'appareil ; vous vous reconnectez avec votre numéro et votre mot de passe.</div>
                <button onClick={onLogout} style={{background:"#fef2f2",color:"#b91c1c",border:"1px solid #fecaca",borderRadius:9,padding:"11px 18px",cursor:"pointer",fontWeight:800,fontSize:13,fontFamily:"inherit",width:"100%"}}>🚪 Se déconnecter</button>
              </div>}
            </>}

            {rub.id==="ventes"&&<>
              <div style={carte}>
                <div style={titreCarte}>📦 Stock</div>
                <label style={sl}>Alerte stock bas par défaut (mètres)</label>
                <input value={aF.alerteStock||""} onChange={e=>setAF(f=>({...f,alerteStock:e.target.value}))} inputMode="decimal" style={si}/>
                <div style={{fontSize:10.5,color:"#aaa",marginTop:6}}>Un article passe en « stock bas » sous ce nombre de mètres, sauf réglage propre à l'article.</div>
              </div>
              {IS_GROSSISTE&&<div style={carte}>
                <div style={titreCarte}>💸 Dépenses</div>
                <label style={{display:"flex",alignItems:"flex-start",gap:10,padding:"11px 12px",background:aF.achatsEnDepenses?"#fff7ed":"#faf8f3",border:"1.5px solid "+(aF.achatsEnDepenses?"#fdba74":"#eee"),borderRadius:9,cursor:"pointer",textAlign:"left"}}>
                  <input type="checkbox" checked={!!aF.achatsEnDepenses} onChange={e=>setAF(f=>({...f,achatsEnDepenses:e.target.checked}))} style={{width:17,height:17,marginTop:1,flexShrink:0}}/>
                  <span><strong style={{fontSize:12.5,color:D}}>🧵 Compter mes achats de tissus comme dépenses</strong><br/><span style={{fontSize:11,color:"#888",lineHeight:1.5}}>Chaque article ajouté au stock, et chaque arrivage, entre dans la Caisse à sa date (prix d'achat × quantité). Le bilan net montre alors ce qui vous reste vraiment. Laissez décoché si vous saisissez vos achats vous-même.</span></span>
                </label>
              </div>}
              {isPro&&<div style={carte}>
                <div style={titreCarte}>💬 Message WhatsApp aux clients</div>
                <div style={aideCarte}>Laissez vide pour utiliser le message prévu par l'application.</div>
                <textarea value={aF.waTemplate||""} onChange={e=>setAF(f=>({...f,waTemplate:e.target.value}))} rows={4} style={{...si,resize:"vertical",lineHeight:1.6,fontFamily:"inherit"}}/>
                <div style={{fontSize:10.5,color:"#15803d",marginTop:6,lineHeight:1.6}}>Variables : <strong>{"{client}"}</strong> · <strong>{"{cmdNum}"}</strong> · <strong>{"{restant}"}</strong> · <strong>{"{total}"}</strong> · <strong>{"{atelier}"}</strong> · <strong>{"{adresse}"}</strong> · <strong>{"{tel}"}</strong></div>
              </div>}
            </>}

            {rub.id==="facturation"&&(()=>{
              const reg=aF.regimeFiscal||"aucun";
              const assujetti=reg==="reel";
              const manque=[["NINEA","ninea"],["RCCM","rccm"]].filter(([,k])=>!String(aF[k]||"").trim()).map(([l])=>l);
              return(<>
                <div style={carte}>
                  <div style={titreCarte}>⚖️ Votre statut</div>
                  <div style={aideCarte}>C'est lui qui décide de ce qui doit figurer sur vos factures. Choisissez celui qui est écrit sur vos papiers.</div>
                  {[["aucun","Pas encore immatriculé","Vous vendez sans NINEA. Vos factures restent des reçus de vente, sans TVA."],
                    ["cgu","Contribution Globale Unique (CGU)","Petit commerce immatriculé, impôt unique. Vous ne facturez pas la TVA."],
                    ["reel","Réel — assujetti à la TVA","Vous collectez la TVA et la reversez. Elle apparaîtra sur chaque facture."]].map(([id,l,d])=>(
                    <label key={id} style={{display:"flex",alignItems:"flex-start",gap:10,padding:"11px 12px",marginBottom:8,background:reg===id?"#fffbeb":"#faf8f3",border:"1.5px solid "+(reg===id?"#fcd34d":"#eee"),borderRadius:9,cursor:"pointer"}}>
                      <input type="radio" name="regime" checked={reg===id} onChange={()=>setAF(f=>({...f,regimeFiscal:id}))} style={{marginTop:2,flexShrink:0}}/>
                      <span><strong style={{fontSize:12.5,color:D}}>{l}</strong><br/><span style={{fontSize:11,color:"#888",lineHeight:1.5}}>{d}</span></span>
                    </label>
                  ))}
                  {assujetti&&<div style={{marginTop:4}}>
                    <label style={sl}>Taux de TVA (%)</label>
                    <input value={aF.tauxTva===undefined?"18":aF.tauxTva} onChange={e=>setAF(f=>({...f,tauxTva:e.target.value.replace(/[^0-9.]/g,"")}))} inputMode="decimal" style={{...si,maxWidth:140}}/>
                    <div style={{fontSize:11,color:"#92400e",marginTop:8,background:"#fffbeb",border:"1px solid #fde68a",borderRadius:8,padding:"9px 11px",lineHeight:1.55}}>
                      Vos prix de vente sont considérés <strong>TTC</strong> : la facture fait apparaître le montant hors taxe et la TVA incluse. Le total de la facture reste donc égal au montant encaissé en caisse.
                    </div>
                  </div>}
                </div>
                <div style={carte}>
                  <div style={titreCarte}>🏢 Identification de la boutique</div>
                  <div style={aideCarte}>Ces numéros s'impriment en haut de chaque facture. Recopiez-les exactement comme sur vos documents.</div>
                  {[["NINEA","ninea","Numéro d'identification fiscale"],["RCCM","rccm","Registre du commerce"],["Forme juridique","formeJuridique","Entreprise individuelle, SARL, SUARL, GIE…"],["Capital social (FCFA)","capital","Seulement pour une société"]].map(([l,k,aide])=>(
                    <div key={k} style={{marginBottom:10}}>
                      <label style={sl}>{l}</label>
                      <input value={aF[k]||""} onChange={e=>setAF(f=>({...f,[k]:e.target.value}))} style={si}/>
                      <div style={{fontSize:10,color:"#aaa",marginTop:3}}>{aide}</div>
                    </div>
                  ))}
                  {reg!=="aucun"&&manque.length>0&&<div style={{fontSize:11.5,color:"#991b1b",background:"#fef2f2",border:"1px solid #fca5a5",borderRadius:8,padding:"9px 11px",fontWeight:600}}>⚠️ Il manque {manque.join(" et ")} : une facture sans ces numéros n'est pas valable pour un client qui tient une comptabilité.</div>}
                </div>
                <div style={carte}>
                  <div style={titreCarte}>📝 Mentions au bas de la facture</div>
                  <div style={aideCarte}>Conditions de règlement, pénalités de retard, coordonnées bancaires… Laissez vide si vous n'en avez pas.</div>
                  <textarea value={aF.mentionsFacture||""} onChange={e=>setAF(f=>({...f,mentionsFacture:e.target.value}))} rows={3} style={{...si,resize:"vertical",lineHeight:1.6,fontFamily:"inherit"}}/>
                </div>
                <div style={{...carte,background:"#eff6ff",border:"1px solid #bae6fd"}}>
                  <div style={{...titreCarte,color:"#0369a1"}}>ℹ️ À faire valider</div>
                  <div style={{fontSize:11.5,color:"#0c4a6e",lineHeight:1.65}}>
                    L'application met sur vos factures ce qu'on y attend : votre identité et vos numéros, ceux du client, le numéro et la date, le détail des tissus, les quantités et prix unitaires, le total, la TVA s'il y en a, la somme en toutes lettres, le mode de règlement et les signatures.
                    <br/><br/>Ce n'est pas un conseil fiscal : faites valider une facture par votre comptable ou votre centre des impôts avant de la remettre à une grande entreprise.
                  </div>
                </div>
              </>);
            })()}
            {rub.id==="site"&&isPro&&<div style={carte}>
              <div style={titreCarte}>🌐 Mon site web boutique</div>
              <div style={aideCarte}>Un site d'une page avec votre collection, à mettre en ligne et à partager. Le logo se règle dans « {IS_GROSSISTE?"Ma boutique":"Mon atelier"} ».</div>
              <button onClick={()=>{
                if(!colMeta||colMeta.length===0){showMsg("Ajoutez d'abord des tissus dans Collection",true);return;}
                const html=buildCatalogHTML(colMeta,colPhotos,aF);
                const blob=new Blob([html],{type:"text/html;charset=utf-8"});
                const url=URL.createObjectURL(blob);
                const a=document.createElement("a");a.href=url;a.download="index.html";
                document.body.appendChild(a);a.click();document.body.removeChild(a);
                setTimeout(()=>URL.revokeObjectURL(url),5000);
                showMsg("✅ Site généré (index.html) ! Glissez-le sur app.netlify.com/drop puis collez l'adresse ci-dessous");
              }} style={{background:"#3b82f6",color:"#fff",border:"none",padding:"11px",borderRadius:9,cursor:"pointer",fontWeight:800,fontSize:12.5,fontFamily:"inherit",width:"100%",marginBottom:12}}>📄 Générer mon site (index.html)</button>
              <label style={sl}>🔗 Adresse du site en ligne</label>
              <input value={aF.catalogUrl||""} onChange={e=>setAF(f=>({...f,catalogUrl:e.target.value}))} style={si}/>
              <div style={{fontSize:10.5,color:"#888",marginTop:5,marginBottom:10,lineHeight:1.5}}>Le QR du Mode Boutique pointera vers cette adresse : vos clients scannent et voient votre site.</div>
              {aF.catalogUrl&&/^https?:\/\//.test(aF.catalogUrl)&&<a href={"https://wa.me/?text="+encodeURIComponent(`✨ Découvrez la collection de ${aF.nom||"notre boutique"} :\n${aF.catalogUrl}`)} target="_blank" rel="noreferrer" style={{display:"block",background:"#25D366",color:"#fff",padding:"11px",borderRadius:9,fontWeight:800,fontSize:12.5,width:"100%",textAlign:"center",textDecoration:"none",boxSizing:"border-box"}}>💬 Partager mon site sur WhatsApp</a>}
            </div>}

            {rub.id==="backup"&&<>
              {/* ── L'ÉTAT, EN PREMIER ET EN GRAND ──────────────────────
                  Neuf clients sur dix n'ont besoin que de cette ligne : « est-ce
                  que mes chiffres sont à l'abri, oui ou non ». Une phrase verte,
                  un seul bouton, et qui ne peut rien effacer. */}
              {cloud&&(cloud.token?<div style={carte}>
                {(()=>{
                  const enCours=cloud.state==="envoi", horsLigne=cloud.state==="hors-ligne", jamais=!cloud.last;
                  const coul=enCours?"#f59e0b":horsLigne?"#f59e0b":jamais?"#ef4444":"#10b981";
                  const titre=enCours?"⏳ Envoi en cours…":horsLigne?"📴 Hors ligne"
                    :jamais?"⚠️ Jamais sauvegardé":"✅ Vos données sont à l'abri";
                  const phrase=enCours?"Vos données partent chez TobyTech, restez connecté un instant."
                    :horsLigne?"Pas de réseau pour le moment. Tout partira tout seul dès qu'il revient — vous n'avez rien à faire."
                    :jamais?"Touchez « Envoyer maintenant » pour mettre vos données à l'abri chez TobyTech."
                    :"Copiées chez TobyTech automatiquement. Téléphone perdu, volé ou cassé : vous retrouvez tout sur le suivant.";
                  return(<>
                    <div style={{fontSize:15.5,fontWeight:900,color:coul,marginBottom:4}}>{titre}</div>
                    <div style={{fontSize:12.5,color:"#6b7280",lineHeight:1.6,marginBottom:10}}>{phrase}</div>
                    {cloud.last&&<div style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:12,padding:"9px 0",borderTop:"1px solid #f3f0ea"}}>
                      <span style={{color:"#888"}}>Dernier envoi</span>
                      <strong style={{color:D}}>{fmtDate(cloud.last)}</strong>
                    </div>}
                  </>);
                })()}
                <button disabled={busy} onClick={async()=>{setBusy(true);const r=await cloud.envoyer();setBusy(false);
                  showMsg(r.ok?"✅ Sauvegarde envoyée chez TobyTech":"❌ "+(r.erreur||"Échec de l'envoi"),!r.ok);
                }} style={{background:"#2563eb",color:"#fff",border:"none",padding:"11px",borderRadius:9,cursor:"pointer",fontWeight:800,fontSize:12.5,fontFamily:"inherit",width:"100%",marginTop:4}}>☁️ Envoyer maintenant</button>
                {phInfo&&phInfo.n>0&&<div style={{marginTop:12,paddingTop:10,borderTop:"1px solid #f3f0ea"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:12,marginBottom:8}}>
                    <span style={{color:"#888"}}>📷 Photos sur cet appareil</span>
                    <strong style={{color:D}}>{phInfo.n} · {fmtSz(phInfo.poids)}</strong>
                  </div>
                  {phEtat&&<div style={{fontSize:11,color:"#1d4ed8",marginBottom:7,fontWeight:600}}>{phEtat}</div>}
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                    <button disabled={busy} onClick={async()=>{
                      if(!window.confirm("Envoyer vos "+phInfo.n+" photos chez TOBY ?\n\nPoids : "+fmtSz(phInfo.poids)+"\n\nÀ faire de préférence en WiFi — cela peut consommer votre forfait internet."))return;
                      setBusy(true);setPhEtat("Envoi en cours...");
                      const r=await cloud.envoiPhotos((n,t)=>setPhEtat("Envoi "+n+" / "+t));
                      setBusy(false);setPhEtat("");
                      if(r.ok){
                        const menage=r.effacees?" · "+r.effacees+" ancienne(s) photo(s) effacée(s) en ligne":"";
                        showMsg((r.total===0?"✅ Vos photos étaient déjà sauvegardées":"✅ "+r.envoyees+" photo(s) sauvegardée(s)")+menage);
                      }
                      else showMsg("❌ "+(r.erreur||"Échec"),true);
                    }} style={{background:"#2563eb",color:"#fff",border:"none",padding:"10px",borderRadius:8,cursor:"pointer",fontWeight:700,fontSize:12,fontFamily:"inherit"}}>📤 Sauvegarder mes photos</button>
                    <button disabled={busy} onClick={async()=>{
                      setBusy(true);setPhEtat("Récupération...");
                      const r=await cloud.recupPhotos((n,t)=>setPhEtat("Reçu "+n+" / "+t));
                      setBusy(false);setPhEtat("");
                      if(r.ok){
                        if(r.total===0)showMsg("✅ Toutes vos photos sont déjà sur cet appareil");
                        else{showMsg("✅ "+r.recuperees+" photo(s) récupérée(s) — rechargement...");setTimeout(()=>window.location.reload(),1400);}
                      }else showMsg("❌ "+(r.erreur||"Échec"),true);
                    }} style={{background:"#0ea5e9",color:"#fff",border:"none",padding:"10px",borderRadius:8,cursor:"pointer",fontWeight:700,fontSize:12,fontFamily:"inherit"}}>📥 Récupérer mes photos</button>
                  </div>
                </div>}
              </div>
              :<div style={carte}>
                <div style={{fontSize:15.5,fontWeight:900,color:"#ef4444",marginBottom:4}}>⚠️ Vos données ne sont pas à l'abri</div>
                <div style={{fontSize:12.5,color:"#6b7280",lineHeight:1.6,marginBottom:10}}>Elles ne vivent que dans ce téléphone. S'il est perdu, volé ou cassé, tout disparaît avec lui. Un compte TobyTech suffit : ensuite, la copie se fait toute seule.</div>
                <button onClick={()=>setTab("compte")} style={{background:"#2563eb",color:"#fff",border:"none",padding:"11px",borderRadius:9,cursor:"pointer",fontWeight:800,fontSize:12.5,fontFamily:"inherit",width:"100%"}}>🔐 Aller à « Compte et sécurité »</button>
              </div>)}
              {IS_WEB&&<div style={carte}>
                <div style={titreCarte}>{persisted?"🛡️ Stockage protégé":persisted===false?"⚠️ Stockage à sécuriser":"⏳ Vérification…"}</div>
                <div style={aideCarte}>{persisted?"L'appareil ne peut pas effacer vos données automatiquement.":"Sauvegardez régulièrement et gardez le fichier (WhatsApp, Drive). Installez l'appli sur l'écran d'accueil pour renforcer la protection."}</div>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:12,paddingTop:8,borderTop:"1px solid #f3f0ea"}}>
                  <span style={{color:"#888"}}>Dernière copie en fichier</span>
                  <strong style={{color:lastBackup?((Date.now()-lastBackup)/864e5>7?"#ef4444":"#10b981"):"#ef4444"}}>{lastBackup?fmtDate(lastBackup):"jamais"}</strong>
                </div>
              </div>}
              {(isPro&&!IS_WEB)&&<div style={carte}>
                <div style={titreCarte}>{extFolder?"☁️ Dossier cloud activé":"📂 Dossier cloud"}</div>
                <div style={{fontSize:11,color:"#888",marginBottom:10,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={extFolder||""}>{extFolder||"OneDrive · Google Drive · Dropbox"}</div>
                <div style={{display:"flex",gap:8}}>
                  {extFolder?<button onClick={clearFolder} disabled={busy} style={{background:"transparent",color:"#ef4444",border:"1px solid #fca5a5",borderRadius:8,padding:"8px 12px",cursor:"pointer",fontSize:12,fontFamily:"inherit",fontWeight:600}}>Désactiver</button>
                  :<button onClick={chooseFolder} disabled={busy} style={{background:G,color:D,border:"none",borderRadius:8,padding:"8px 14px",cursor:"pointer",fontWeight:800,fontSize:12,fontFamily:"inherit"}}>Configurer</button>}
                </div>
                <div style={{fontSize:11,color:"#888",marginTop:10}}>
                  <div style={{display:"flex",justifyContent:"space-between",padding:"2px 0"}}><span>💻 Locale</span><span style={{fontWeight:700,color:lastLocal?"#10b981":"#bbb"}}>{lastLocal?fmtDate(lastLocal.mtime):"—"}</span></div>
                  {extFolder&&<div style={{display:"flex",justifyContent:"space-between",padding:"2px 0"}}><span>☁️ Cloud</span><span style={{fontWeight:700,color:lastExt?"#10b981":"#f59e0b"}}>{lastExt?fmtDate(lastExt.mtime):"—"}</span></div>}
                </div>
              </div>}
              <div style={carte}>
                {/* Sortir une copie ne casse rien : ça reste visible. Un client
                    qui peut emporter ses chiffres quand il veut n'est pas
                    prisonnier — et c'est un argument de vente, pas une faille. */}
                <div style={titreCarte}>💾 Emporter une copie</div>
                <div style={aideCarte}>Un fichier avec tout votre travail, à vous, à garder sur WhatsApp ou Drive. Vos données ne sont enfermées nulle part.</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                  <button onClick={backupNow} disabled={busy} style={{background:"#10b981",color:"#fff",border:"none",padding:"10px",borderRadius:8,cursor:"pointer",fontWeight:700,fontSize:12,fontFamily:"inherit"}}>{IS_WEB?"💾 Sauvegarder / Partager":"💾 Sauvegarder"}</button>
                  <button onClick={exportFile} disabled={busy} style={{background:"#f5f5f5",color:"#444",border:"none",padding:"9px",borderRadius:8,cursor:"pointer",fontWeight:600,fontSize:12,fontFamily:"inherit"}}>📤 Exporter une copie</button>
                  {!IS_WEB&&<button onClick={()=>window.electronAPI?.backup?.openFolder?.()} style={{background:"#f5f5f5",color:"#444",border:"none",padding:"9px",borderRadius:8,cursor:"pointer",fontWeight:600,fontSize:12,fontFamily:"inherit"}}>📁 Ouvrir dossier</button>}
                </div>
              </div>

              {/* ── LE VOLET DES REMISES À ZÉRO ─────────────────────────
                  Les deux « Restaurer » sont les seuls boutons capables
                  d'effacer un mois de travail. Ils sont rangés ici, fermés,
                  et il faut ouvrir le volet exprès pour les atteindre. */}
              <div style={{...carte,borderColor:voletAvance?"#fecaca":undefined}}>
                <div onClick={()=>setVoletAvance(v=>!v)} style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
                  <span style={{fontSize:14,color:voletAvance?"#dc2626":"#bbb",transform:voletAvance?"rotate(90deg)":"",display:"inline-block",transition:"transform .15s"}}>▶</span>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:800,color:D}}>⚠️ Remettre d'anciennes données</div>
                    <div style={{fontSize:11.5,color:"#999",marginTop:1}}>À n'ouvrir qu'en cas de problème, ou sur demande de TobyTech</div>
                  </div>
                </div>
                {voletAvance&&<div style={{marginTop:14,paddingTop:13,borderTop:"1px solid #fee2e2"}}>
                  <div style={{background:"#fef2f2",border:"1px solid #fecaca",borderRadius:9,padding:"11px 13px",fontSize:12,color:"#991b1b",lineHeight:1.6,marginBottom:12}}>
                    <strong>Ces deux boutons effacent le travail de cet appareil</strong> et le remplacent par une copie plus ancienne. Si vous avez saisi des ventes depuis, elles seront perdues. En cas de doute, faites d'abord « 📤 Exporter une copie » ci-dessus.
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr",gap:8}}>
                    {cloud&&cloud.token&&<button disabled={busy} onClick={async()=>{
                      if(!window.confirm("⚠️ Cela REMPLACE les données de cet appareil par celles enregistrées en ligne.\n\nTout ce que vous avez saisi depuis la dernière sauvegarde sera perdu.\n\nContinuer ?"))return;
                      setBusy(true);const r=await cloud.restaurer();setBusy(false);
                      if(r.ok){showMsg("✅ Données récupérées ! Rechargement...");setTimeout(()=>window.location.reload(),1200);}
                      else showMsg("❌ "+(r.erreur||"Échec"),true);
                    }} style={{background:"#0ea5e9",color:"#fff",border:"none",padding:"11px",borderRadius:8,cursor:"pointer",fontWeight:700,fontSize:12.5,fontFamily:"inherit"}}>⬇️ Reprendre la copie en ligne</button>}
                    <button onClick={restoreFile} disabled={busy} style={{background:"transparent",color:"#dc2626",border:"1.5px solid #fca5a5",padding:"11px",borderRadius:8,cursor:"pointer",fontWeight:700,fontSize:12.5,fontFamily:"inherit"}}>♻️ Reprendre un fichier de sauvegarde</button>
                  </div>
                </div>}
              </div>
              <div style={carte}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:7,fontSize:12}}><span style={{color:"#777",fontWeight:700}}>💾 Espace utilisé</span><span style={{fontWeight:800,color:pct>80?"#ef4444":pct>50?"#f59e0b":"#10b981"}}>{fmtSz(sz)} <span style={{color:"#bbb",fontWeight:500}}>/ {fmtSz(capacite)}</span></span></div>
                <div style={{background:"#e5e7eb",borderRadius:20,height:8,overflow:"hidden"}}><div style={{background:pct>80?"#ef4444":pct>50?"#f59e0b":"#10b981",height:"100%",width:pct+"%",borderRadius:20,transition:"width 0.3s"}}/></div>
              </div>
            </>}

            {rub.id==="abonnement"&&<>
              {cloud&&cloud.info?.dateExpiration&&<div style={carte}>
                <div style={titreCarte}>🎫 Votre formule</div>
                {[["Formule",cloud.info.plan||"—"],["Valable jusqu'au",cloud.info.dateExpiration]].map(([l,v])=>(
                  <div key={l} style={{display:"flex",justifyContent:"space-between",gap:10,fontSize:12,padding:"7px 0",borderTop:"1px solid #f3f0ea"}}><span style={{color:"#888"}}>{l}</span><strong style={{color:D}}>{v}</strong></div>
                ))}
              </div>}
              <div style={carte}>
                <div style={titreCarte}>💳 Renouveler</div>
                <div style={aideCarte}>Payez avant la date de fin pour ne jamais être bloqué.</div>
                <PaiementBloc cle={licKey} atelier={aF.nom} compact/>
              </div>
            </>}

            {rub.id==="corbeille"&&<div style={carte}>
              <div style={aideCarte}>Les ventes et clients supprimés sont gardés <strong>30 jours</strong>, puis effacés définitivement. Restaurez-les avant si besoin.</div>
              {(!trash||trash.length===0)?<div style={{textAlign:"center",color:"#bbb",padding:26,fontSize:13}}>La corbeille est vide.</div>:trash.map(e=>{
                const dl=Math.max(0,30-Math.floor((Date.now()-e.deletedAt)/864e5));
                return(<div key={e.tid} style={{border:"1px solid #eee",borderRadius:10,padding:"10px 12px",marginBottom:9,display:"flex",alignItems:"center",gap:9}}>
                  <span style={{fontSize:19,flexShrink:0}}>{e.type==="order"?"📋":"👥"}</span>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:700,fontSize:13,color:D,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{(e.type==="order"?e.snap?.cmdNum:e.snap?.cliNum)||""} · {e.snap?.prenom||""} {e.snap?.nom||""}</div>
                    <div style={{fontSize:10,color:"#aaa",marginTop:2}}>Supprimé le {new Date(e.deletedAt).toLocaleDateString("fr-FR")} · reste {dl} j</div>
                  </div>
                  <button onClick={()=>onRestoreTrash(e)} style={{background:"#dcfce7",color:"#166534",border:"none",borderRadius:7,padding:"7px 10px",cursor:"pointer",fontWeight:700,fontSize:11,fontFamily:"inherit",flexShrink:0}}>↩️ Restaurer</button>
                  <button onClick={()=>{if(window.confirm("Supprimer définitivement ? Cette action est irréversible."))onPurgeTrash(e);}} title="Supprimer définitivement" style={{background:"#fef2f2",color:"#ef4444",border:"none",borderRadius:7,padding:"7px 9px",cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>🗑</button>
                </div>);
              })}
            </div>}
          </div>
        </div>
        {/* PIED — enregistrer (rubriques qui modifient la fiche de la boutique) et messages */}
        {(msg||AVEC_ENREGISTRER)&&<div style={{flexShrink:0,background:"#fff",borderTop:"1px solid #ece7dc",padding:mobile?"10px 14px calc(10px + env(safe-area-inset-bottom))":"12px 22px",display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
          {msg?<div style={{flex:"1 1 200px",fontSize:12.5,fontWeight:700,color:msg.err?"#991b1b":"#065f46"}}>{msg.text||msg}</div>
            :<div style={{flex:"1 1 200px",fontSize:11.5,color:modifie?"#b45309":"#aaa",fontWeight:600}}>{modifie?"● Modifications non enregistrées":"Tout est enregistré"}</div>}
          {AVEC_ENREGISTRER&&<button onClick={()=>{onSaveAtelier(aF);showMsg("✅ Enregistré");}} disabled={!modifie} style={{background:modifie?G:"#eee",color:modifie?D:"#aaa",border:"none",padding:"11px 22px",borderRadius:9,cursor:modifie?"pointer":"default",fontWeight:800,fontSize:13,fontFamily:"inherit"}}>💾 Enregistrer</button>}
        </div>}
        <FlecheHaut cible={contenuRef} z={460} bas={mobile?"calc(84px + env(safe-area-inset-bottom))":96}/>
      </div>
    </div>
  );
}

// ── DONNÉES VIDES ─────────────────────────────────────────────────
const emptyClient={nom:"",prenom:"",telephone:"",email:"",nationalite:"",ville:"",paysOrigine:"",notes:"",anniversaire:"",vip:false,mEpaule:"",mLongueur:"",mPoitrine:"",mTaille:"",mManche:"",mPoignet:"",mBras:"",mBassinH:"",mCol:"",mCeinture:"",mLongueurP:"",mCuisse:"",mBassinP:"",mBas:"",mesuresPlus:[]};

// Stock & Fournisseurs (Pro)
// Stock — hiérarchie : Balle → Pièce → Mètres (basée sur l'enquête grossistes Sénégal)
// Getzner : 1 balle = 20 pièces × 30 m = 600 m
// Hommes  : 1 balle = 6-10 pièces × 25 yards
// Le nom complet d'un article : le tissu, puis la précision du vendeur.
// « Getzner Cotton Swiss » et « Getzner brodé » ne sont pas le même tissu, et
// dans une boutique qui en tient cent, les confondre coûte cher.
const nomArticle=(a)=>{
  if(!a)return "";
  const base=(a.type==="Autre"||!a.type)?((a.custom||"").trim()||"Sans nom"):a.type;
  const v=(a.variante||"").trim();
  return v?base+" "+v:base;
};
const emptyStock={modeSaisie:"balle",photoData:null,type:"",custom:"",variante:"",couleur:"",couleurHex:"",metres:"",prixUnitaire:"",prixVente:"",fournisseurId:null,lowAlert:10,notes:"",etagere:"",nbBalles:"",piecesParBalle:"",metresParPiece:"",uniteMesure:"m",prixParPiece:""};
const emptyFournisseur={nom:"",telephone:"",email:"",marche:"",ville:"",specialite:"",notes:""};

// Dépenses (Pro — Premium + GrossistePro)
const CATEGORIES_DEPENSE=[
  {id:"salaires",l:"👥 Salaires",c:"#8b5cf6"},
  {id:"achats",l:"🧵 Achats de tissus",c:"#3b82f6"},
  {id:"investissements",l:"💰 Investissements",c:"#10b981"},
  {id:"charges",l:"🏠 Loyer",c:"#f59e0b"},
  {id:"transport",l:"🚗 Transport",c:"#06b6d4"},
  {id:"communication",l:"📞 WiFi / Téléphone",c:"#ec4899"},
  {id:"sante",l:"🏥 Santé/Social",c:"#ef4444"},
  {id:"autres",l:"📋 Autres",c:"#6b7280"}
];
const MODES_PAIEMENT=["Espèces","Wave","Orange Money","Free Money","Virement bancaire","Chèque"];
const emptyDepense={montant:"",categorie:"achats",description:"",fournisseur:"",modePaiement:"Espèces",date:new Date().toISOString().slice(0,10),recurrente:false,notes:""};

const emptyO={clientId:null,nom:"",prenom:"",nationalite:"",ville:"",paysOrigine:"",telephone:"",categorie:"Ensemble",nombrePieces:1,priorite:"Normale",statut:IS_GROSSISTE?"Livré":"En attente",tissus:[{type:"",custom:"",metres:"",couleur:"",modele:"",source:"client",photoTissu:"",photoModele:""}],dateDepot:new Date().toISOString().split("T")[0],dateLivraison:IS_GROSSISTE?new Date().toISOString().split("T")[0]:"",modePaiement:"",prixTotal:"",avanceRecue:"",mEpaule:"",mLongueur:"",mPoitrine:"",mTaille:"",mManche:"",mPoignet:"",mBras:"",mBassinH:"",mCol:"",mCeinture:"",mLongueurP:"",mCuisse:"",mBassinP:"",mBas:"",remarques:"",couts:[],mesuresPlus:[]};

// ── APP ────────────────────────────────────────────────────────────
export default function App(){
  const isMobile=useIsMobile();
  const [ok,setOk]=useState(false);
  const [keyIn,setKeyIn]=useState("");
  const [keyErr,setKeyErr]=useState("");
  const [orders,setOrders]=useState([]);
  const [clients,setClients]=useState([]);
  const [trash,setTrash]=useState([]); // corbeille 30 jours (commandes + clients supprimés)
  const [colMetaSaisi,setColMeta]=useState([]);   // ancienne collection saisie à la main
  const [colPhotosSaisi,setColPhotos]=useState({});
  const [stockPhotos,setStockPhotos]=useState({});
  const [cpStock,setCpStock]=useState(false); // nuancier du formulaire de stock
  // Une balle de Getzner, c'est toujours 20 pièces de 30 m : seule la couleur change
  // d'une balle à l'autre. On saisit donc la configuration une fois, puis les couleurs.
  const [couleursLot,setCouleursLot]=useState([]);
  const [pickerVente,setPickerVente]=useState(-1); // ligne de vente qui choisit en images
  const [conflitCloud,setConflitCloud]=useState(null); // le serveur détient plus que cet appareil
  const [alignement,setAlignement]=useState(null); // l'appareil contient les données d'une autre boutique
  // INSCRIPTION — une clé appartient à une boutique identifiée. Tant qu'on ne sait
  // pas qui l'utilise, elle circule de main en main et l'abonnement ne vaut rien.
  const [etapeActiv,setEtapeActiv]=useState(1);
  const [activBusy,setActivBusy]=useState(false);
  const [appareilsOccupes,setAppareilsOccupes]=useState(null);
  const [modeActiv,setModeActiv]=useState("connexion");
  const [insc,setInsc]=useState({prenom:"",nom:"",boutique:"",tel:"",ville:"",mdp:""});
  const [pickerQ,setPickerQ]=useState("");
  const [pickerFam,setPickerFam]=useState("Tous"); // famille de tissus montrée dans le choix en images
  const mainRef=useRef(null);
  const formRef=useRef(null);   // formulaire de vente qui défile // zone principale qui défile (flèche « remonter »)
  // Le jour courant : un ordinateur resté allumé toute la nuit doit voir au matin les
  // crédits arrivés à échéance, et dater les nouvelles ventes du bon jour.
  const [jour,setJour]=useState(()=>new Date().toISOString().slice(0,10));
  useEffect(()=>{const t=setInterval(()=>{const j=new Date().toISOString().slice(0,10);setJour(p=>p===j?p:j);},60000);return()=>clearInterval(t);},[]);
  // CRÉDIT
  const [creditOuvert,setCreditOuvert]=useState(null);   // client déplié
  const [creditMoisFermes,setCreditMoisFermes]=useState([]); // mois repliés dans Crédit
  const [remboursMoisFermes,setRemboursMoisFermes]=useState([]); // mois repliés dans les remboursements
  const [creditFiltre,setCreditFiltre]=useState("tous");
  const [creditQ,setCreditQ]=useState("");
  const [encaisser,setEncaisser]=useState(null);         // paiement en cours de saisie
  const [encaisseFait,setEncaisseFait]=useState(null);   // dernier paiement, à confirmer au client
  const [echeanceEdit,setEcheanceEdit]=useState(null);   // date promise en cours de modification
  // Le vendeur nomme ses propres teintes — « jaune poussin », « bleu Sénégal ».
  // Elles rejoignent la palette pour ne pas être à ressaisir à chaque balle.
  const [couleursPerso,setCouleursPerso]=useState([]);
  // Saisie libre en cours : tant qu'elle dure, le champ reste affiché quoi qu'on
  // tape. C'est ce qui manquait — le champ disparaissait dès que le texte
  // ressemblait à une couleur connue, et le curseur sautait.
  const [couleurLibre,setCouleurLibre]=useState(false);
  const PALETTE=useMemo(()=>[...COULEURS,...couleursPerso],[couleursPerso]); // photo de chaque balle, par id d'article
  const [stockApercu,setStockApercu]=useState(null); // photo ouverte en grand
  const [pont,setPont]=useState(null);   // pont photo : {etat,lien,dep,qr,erreur}
  const pontMinuteur=useRef(null);
  // Le mode est fixé par le BUILD (appli dédiée), plus de toggle runtime
  const isGrossiste=IS_GROSSISTE;
  const ns=useCallback((key)=>key,[]); // chaque appli a son propre userData → pas de namespace
  const [atelierInfo,setAtelierInfo]=useState({nom:"",adresse:"Dakar, Sénégal",tel:"",email:"",catalogUrl:"",modeActivite:isGrossiste?"grossiste":"couturier"});
  const [busy,setBusy]=useState(true);
  // Licence essai/abonnement à durée limitée (fichier licence.json optionnel)
  const [licenceInfo,setLicenceInfo]=useState(null);
  // Après une mise à jour reçue de l'autre appareil, on revient sur le même écran.
  const [view,setView]=useState(()=>{try{const v=sessionStorage.getItem("cp_vue");if(v){sessionStorage.removeItem("cp_vue");return v;}}catch{}return "dashboard";});
  const [showOF,setShowOF]=useState(false);
  const [editO,setEditO]=useState(null);
  const [form,setForm]=useState(emptyO);
  // Détecte si l'utilisateur a modifié manuellement le prix total (désactive l'auto-calc)
  const [prixManuel,setPrixManuel]=useState(false);
  const [versementManuel,setVersementManuel]=useState(false); // le vendeur a saisi lui-même le montant versé
  const [libelleManuel,setLibelleManuel]=useState(false);     // le vendeur a écrit son propre libellé
  const [express,setExpress]=useState(false); // formulaire commande en mode "rapide" (essentiel seul)
  const [fTab,setFTab]=useState("client");
  const [mTab,setMTab]=useState("haut");
  const [search,setSearch]=useState("");
  const [fSt,setFSt]=useState("all");
  const [detail,setDetail]=useState(null);
  const [showCF,setShowCF]=useState(false);
  const [editC,setEditC]=useState(null);
  const [cForm,setCForm]=useState({titre:"",categorie:COLLECTION_CATS[0],description:"",prixIndicatif:"",photoData:null});
  const [colLightbox,setColLightbox]=useState(null); // {index, items}
  const [colCat,setColCat]=useState("Tous");
  const [colDetail,setColDetail]=useState(null);
  const [colMenu,setColMenu]=useState(false); // menu 3 points de la fiche modele
  const [colCardMenu,setColCardMenu]=useState(null); // id de la vignette dont le menu est ouvert
  const [cpOpen,setCpOpen]=useState(-1);
  const [confirm,setConfirm]=useState(null);
  const [showSettings,setShowSettings]=useState(false);
  const [settingsTab,setSettingsTab]=useState(null); // onglet initial des Réglages (ex: "backup")
  const [pinHash,setPinHash]=useState(null); // code caisse actif (haché) — null = pas de protection
  const [caisseUnlocked,setCaisseUnlocked]=useState(false); // déverrouillé pour la session en cours
  // TOBY Cloud (Premium) : compte atelier, sauvegarde en ligne, licence pilotée par le serveur
  const [cloudToken,setCloudToken]=useState(null);
  const [cloudInfo,setCloudInfo]=useState(null);   // {atelier, plan, dateExpiration}
  const [cloudLast,setCloudLast]=useState(null);   // date du dernier envoi réussi
  const [cloudState,setCloudState]=useState("");   // "", "envoi", "ok", "hors-ligne", "erreur"
  const [dirty,setDirty]=useState(0);
  const [licKey,setLicKey]=useState("");
  const [voirOffres,setVoirOffres]=useState(false); // écran des formules, ouvert depuis une vitrine
  const [detailCouts,setDetailCouts]=useState(false); // détail des dépenses par commande
  const [moisCoutsOuvert,setMoisCoutsOuvert]=useState(null); // mois déplié dans ce détail // clé de licence du client (pour le message de paiement)              // compteur de modifications non encore envoyées
  const [nudgeBackup,setNudgeBackup]=useState(false); // rappel "pense à sauvegarder" (aucune sauvegarde depuis 7 j)
  const [receiptHtml,setReceiptHtml]=useState(null);
  const [showClientF,setShowClientF]=useState(false);
  const [editClient,setEditClient]=useState(null);
  const [clientForm,setClientForm]=useState(emptyClient);
  const [clTab,setClTab]=useState("info");
  const [clMTab,setClMTab]=useState("haut");
  const [clientDetail,setClientDetail]=useState(null);
  const [cliSearch,setCliSearch]=useState("");
  const [clientListSearch,setClientListSearch]=useState("");
  const [caisseOpenMonth,setCaisseOpenMonth]=useState(null); // mois ouvert
  const [ordersOpenMonth,setOrdersOpenMonth]=useState(new Date().toISOString().slice(0,7)); // mois ouvert dans Commandes (Premium)
  const [bilanOpenMonth,setBilanOpenMonth]=useState(new Date().toISOString().slice(0,7)); // mois ouvert dans Bilan Net
  const cliSearchRef=useRef(null); // auto-focus à l'ouverture nouvelle vente/commande
  // STOCK & FOURNISSEURS (Pro)
  const [stock,setStock]=useState([]);
  const [fournisseurs,setFournisseurs]=useState([]);
  // DÉPENSES (Pro)
  const [depenses,setDepenses]=useState([]);
  const [caisseTab,setCaisseTab]=useState("revenus"); // revenus | depenses | bilan
  const [showDepF,setShowDepF]=useState(false);
  const [editDep,setEditDep]=useState(null);
  const [depForm,setDepForm]=useState(emptyDepense);
  const [depFilterCat,setDepFilterCat]=useState("all");
  const [depFilterMonth,setDepFilterMonth]=useState("all");
  const [depGroupeOuvert,setDepGroupeOuvert]=useState(null); // type de tissu déplié dans les achats
  const [depMoisOuvert,setDepMoisOuvert]=useState(()=>new Date().toISOString().slice(0,7)); // mois déplié dans les dépenses
  const [stockTab,setStockTab]=useState("inventaire");
  const [stockOpenCat,setStockOpenCat]=useState(null); // accordéon grossiste
  const [showStockF,setShowStockF]=useState(false);
  const [editStock,setEditStock]=useState(null);
  const [stockForm,setStockForm]=useState(emptyStock);
  const [showFourn,setShowFourn]=useState(false);
  const [editFourn,setEditFourn]=useState(null);
  const [fournForm,setFournForm]=useState(emptyFournisseur);
  const [showRecep,setShowRecep]=useState(false);
  const [recepForm,setRecepForm]=useState({stockId:null,metres:"",prixUnitaire:"",fournisseurId:null,notes:"",date:new Date().toISOString().slice(0,10),nbBalles:"",piecesParBalle:"",metresParPiece:"",uniteMesure:"m",prixParPiece:""});
  const [showBoutique,setShowBoutique]=useState(false);
  const [boutiqueIdx,setBoutiqueIdx]=useState(0);
  const [qrData,setQrData]=useState(null);
  const fileRef=useRef();
  const stockGalRef=useRef();  // galerie / fichiers — le cas le plus fréquent
  const stockCamRef=useRef();  // appareil photo, quand la balle n'est pas encore prise en photo

  useEffect(()=>{(async()=>{
    try{const r=await window.storage.get("cp_lic");if(r){setLicKey(r.value);if(validKey(r.value))setOk(true);}}catch{}
    // VÉRIFICATION PÉRIODIQUE — l'application s'ouvre tout de suite, hors ligne
    // comme en ligne, puis demande discrètement au serveur si l'accès tient
    // toujours. C'est ce qui rend une révocation réelle : sans elle, un appareil
    // déjà activé fonctionnait pour toujours, quoi que TOBY fasse du compte.
    (async()=>{
      try{
        const lic=await window.storage.get("cp_lic");
        if(!lic||!validKey(lic.value))return;
        const tk=await window.storage.get("cp_cloud_token");
        if(!tk||!tk.value){
          // Activation d'avant les comptes : une connexion suffit, les données restent.
          setOk(false);
          setKeyErr("Connectez-vous une fois avec votre numéro pour continuer. Vos données restent sur cet appareil.");
          return;
        }
        const DELAI_GRACE=14*86400000; // deux semaines : un voyage ou une panne ne ferme pas une boutique
        const dv=await window.storage.get("cp_derniere_verif");
        let derniere=dv?parseInt(dv.value)||0:0;
        if(!derniere){derniere=Date.now();await window.storage.set("cp_derniere_verif",String(derniere));}
        const r=await cloudAppel("/api/licence",{jeton:tk.value});
        if(r.ok){
          await window.storage.set("cp_derniere_verif",String(Date.now()));
          // Appareil connecté avant qu'on retienne à qui sont ses données : on le vérifie une fois.
          let ci={};try{const x=await window.storage.get("cp_cloud_info");if(x)ci=JSON.parse(x.value)||{};}catch{}
          if(!(await alignerDonnees(lic.value,tk.value,ci.atelier||"")))setOk(false);
          return;
        }
        if(r.statut===401||r.statut===403){
          // Session expirée, compte suspendu ou révoqué : on ne dit pas lequel,
          // on demande de se reconnecter. Les données locales ne sont pas touchées.
          try{await window.storage.delete("cp_cloud_token");}catch{}
          setCloudToken(null);
          setOk(false);
          setKeyErr("Votre session a expiré ou votre accès a été retiré par TOBY. Reconnectez-vous.");
          return;
        }
        if(r.horsLigne&&Date.now()-derniere>DELAI_GRACE){
          setOk(false);
          setKeyErr("Plus de deux semaines sans connexion. Connectez-vous à internet pour continuer : vos données sont intactes.");
        }
      }catch{}
    })();
    // Lecture fichier licence.json (essai/abonnement)
    if(window.electronAPI?.licence){
      try{const lic=await window.electronAPI.licence.read();if(lic)setLicenceInfo(lic);}catch{}
    }else{
      // WEB/MOBILE : licence importée (cp_licence) OU essai 30 jours auto depuis la 1ère activation
      try{
        let lic=null;
        const lr=await window.storage.get("cp_licence");
        if(lr){try{lic=JSON.parse(lr.value);}catch{}}
        if(!lic){
          const st=await window.storage.get("cp_trial_start");
          if(st){
            const start=parseInt(st.value)||Date.now();
            lic={plan:"ESSAI",dateExpiration:new Date(start+30*86400000).toISOString().slice(0,10)};
          }
        }
        if(lic&&lic.dateExpiration){
          // Jours restants = différence de dates à minuit (jour d'activation → 30 pile, pas 31)
          const today=new Date();today.setHours(0,0,0,0);
          const expDay=new Date(lic.dateExpiration+"T00:00:00");
          const j=Math.max(0,Math.round((expDay.getTime()-today.getTime())/86400000));
          const expired=Date.now()>new Date(lic.dateExpiration+"T23:59:59").getTime();
          setLicenceInfo({plan:lic.plan||"ESSAI",dateExpiration:lic.dateExpiration,joursRestants:j,expired});
        }
      }catch{}
    }
    // Le mode est fixé par le build (appli dédiée). Données isolées par userData Windows.
    const mode=IS_GROSSISTE?"grossiste":"couturier";
    const _ns=(k)=>k;
    try{
      const r=await window.storage.get(_ns("cp_v5"));
      if(r){
        let arr=JSON.parse(r.value).map(migrateTissus);
        const expectedPfx=(mode==="grossiste")?"VTE-":"CMD-";
        // MIGRATION : doublons OU préfixe incorrect (ex: VTE-* dans Standard couturier)
        const seen=new Set();
        const hasDup=arr.some(o=>{if(seen.has(o.cmdNum))return true;seen.add(o.cmdNum);return false;});
        const hasWrongPfx=arr.some(o=>o.cmdNum&&!o.cmdNum.startsWith(expectedPfx));
        if(hasDup||hasWrongPfx){
          arr=[...arr].sort((a,b)=>a.id-b.id).map((o,i)=>({...o,cmdNum:expectedPfx+String(i+1).padStart(3,"0")}));
          window.storage.set(_ns("cp_v5"),JSON.stringify(arr));
        }
        setOrders(arr);
      }else setOrders([]);
    }catch{setOrders([]);}
    try{
      const r=await window.storage.get(_ns("cp_clients"));
      if(r){
        let arr=JSON.parse(r.value);
        const seen=new Set();
        const hasDup=arr.some(c=>{if(seen.has(c.cliNum))return true;seen.add(c.cliNum);return false;});
        if(hasDup){
          arr=[...arr].sort((a,b)=>a.id-b.id).map((c,i)=>({...c,cliNum:"CLI-"+String(i+1).padStart(3,"0")}));
          window.storage.set(_ns("cp_clients"),JSON.stringify(arr));
        }
        setClients(arr);
      }else setClients([]);
    }catch{setClients([]);}
    // CHARGEMENT PHOTOS COLLECTION (les 2 modes ont leur propre collection)
    try{
      const r=await window.storage.get(_ns("cp_col"));
      if(r){
        const m=JSON.parse(r.value);
        setColMeta(m);
        const ph={};
        for(const x of m){
          if(window.electronAPI?.photos?.get){
            try{const data=await window.electronAPI.photos.get(x.id);if(data)ph[x.id]=data;}catch{}
          }else{
            try{const p=await window.storage.get("cp_p_"+x.id);if(p)ph[x.id]=p.value;}catch{}
          }
        }
        setColPhotos(ph);
      }else{setColMeta([]);setColPhotos({});}
    }catch{setColMeta([]);setColPhotos({});}
    try{const r=await window.storage.get(_ns("cp_atelier"));if(r){const ai=JSON.parse(r.value);setAtelierInfo({...ai,modeActivite:mode});}else setAtelierInfo({nom:"",adresse:"Dakar, Sénégal",tel:"",email:"",catalogUrl:"",modeActivite:mode});}catch{}
    // Code caisse (haché) — si absent, la caisse reste librement accessible
    try{const r=await window.storage.get(_ns("cp_pin"));setPinHash(r?r.value:null);}catch{setPinHash(null);}
    // Compte TOBY Cloud (Premium) : jeton + dernière synchro
    try{const r=await window.storage.get("cp_cloud_token");if(r)setCloudToken(r.value);}catch{}
    try{const r=await window.storage.get("cp_cloud_info");if(r)setCloudInfo(JSON.parse(r.value));}catch{}
    try{const r=await window.storage.get("cp_cloud_last");if(r)setCloudLast(Number(r.value));}catch{}
    try{const r=await window.storage.get(_ns("cp_couleurs"));setCouleursPerso(r?JSON.parse(r.value):[]);}catch{setCouleursPerso([]);}
    try{
      const r=await window.storage.get(_ns("cp_stock"));
      const liste=r?JSON.parse(r.value):[];
      setStock(liste);
      // Les photos suivent le même chemin que celles de la Collection : fichiers sur
      // ordinateur, base du navigateur sur téléphone — donc reprises par la
      // synchronisation TOBY Cloud sans rien de plus à écrire.
      const ph={};
      for(const a of liste){
        const pid=photoStockId(a.id);
        try{const v=await photoStore.get(pid);if(v)ph[a.id]=v;}catch{}
      }
      setStockPhotos(ph);
    }catch{setStock([]);setStockPhotos({});}
    try{const r=await window.storage.get(_ns("cp_fournisseurs"));if(r)setFournisseurs(JSON.parse(r.value));else setFournisseurs([]);}catch{setFournisseurs([]);}
    try{const r=await window.storage.get(_ns("cp_depenses"));if(r)setDepenses(JSON.parse(r.value));else setDepenses([]);}catch{setDepenses([]);}
    // Corbeille : charge + purge auto au-delà de 30 jours (efface aussi les photos des commandes purgées)
    try{const r=await window.storage.get(_ns("cp_trash"));let tl=r?JSON.parse(r.value):[];const cut=Date.now()-30*864e5;const keep=tl.filter(e=>e.deletedAt>=cut);const gone=tl.filter(e=>e.deletedAt<cut);gone.forEach(e=>{if(e.type==="order")(e.snap?.tissus||[]).forEach(ti=>{if(ti.photoTissu)photoStore.del(ti.photoTissu);if(ti.photoModele)photoStore.del(ti.photoModele);});});if(gone.length)window.storage.set(_ns("cp_trash"),JSON.stringify(keep));setTrash(keep);}catch{setTrash([]);}
    setBusy(false);
  })();},[]);

  // Sécurité des données (web/mobile) : (re)demande le stockage persistant, et rappelle
  // de sauvegarder si aucune sauvegarde depuis 7 jours (ou jamais, 7 j après la 1ère utilisation).
  useEffect(()=>{
    if(busy||!ok||!IS_WEB)return;
    let alive=true;
    (async()=>{
      await requestPersistentStorage();
      try{
        if((orders.length+clients.length)===0){if(alive)setNudgeBackup(false);return;}
        const now=Date.now();
        const sn=await window.storage.get("cp_backup_snooze");
        if(sn&&Number(sn.value)>now){if(alive)setNudgeBackup(false);return;}
        const lb=await window.storage.get("cp_last_backup");
        let ref;
        if(lb)ref=Number(lb.value);
        else{const fu=await window.storage.get("cp_trial_start");ref=fu?Number(fu.value):now;}
        if(alive)setNudgeBackup((now-ref)/864e5>=7);
      }catch{}
    })();
    return()=>{alive=false;};
  },[busy,ok,orders.length,clients.length]);
  // Sauvegarde 1 clic depuis la bannière de rappel (partage WhatsApp/Drive ou téléchargement)
  const doQuickBackup=async()=>{
    try{const res=await shareOrDownloadBackup("toby-sauvegarde");if(res!=="cancel")setNudgeBackup(false);}catch{}
  };
  const snoozeBackup=async()=>{try{await window.storage.set("cp_backup_snooze",String(Date.now()+3*864e5));}catch{}setNudgeBackup(false);};

  // ── TOBY CLOUD ──
  const cloudEnregistre=useCallback(async(jeton,infos)=>{
    try{await window.storage.set("cp_cloud_token",jeton);}catch{}
    try{await window.storage.set("cp_cloud_info",JSON.stringify(infos||{}));}catch{}
    setCloudToken(jeton);setCloudInfo(infos||{});
  },[]);
  // Applique la licence décidée par le SERVEUR (remplace l'essai local → anti-piratage)
  const cloudAppliqueLicence=useCallback(d=>{
    if(!d||!d.dateExpiration)return;
    const today=new Date();today.setHours(0,0,0,0);
    const expDay=new Date(d.dateExpiration+"T00:00:00");
    const j=Math.max(0,Math.round((expDay.getTime()-today.getTime())/86400000));
    setLicenceInfo({plan:d.plan||"ABONNEMENT",dateExpiration:d.dateExpiration,joursRestants:j,
      expired:!!d.expire||Date.now()>new Date(d.dateExpiration+"T23:59:59").getTime()});
  },[]);
  const cloudCreerCompte=useCallback(async(motDePasse)=>{
    const r=await cloudAppel("/api/compte",{methode:"POST",corps:{
      cle:(await window.storage.get("cp_lic").catch(()=>null))?.value||"",
      motDePasse,atelier:atelierInfo.nom||"",responsable:atelierInfo.nom||"",
      tel:atelierInfo.tel||"",ville:atelierInfo.adresse||"",app:PLAN.toLowerCase(),
    }});
    if(!r.ok)return r;
    await cloudEnregistre(r.data.jeton,{atelier:atelierInfo.nom||"",plan:r.data.plan,dateExpiration:r.data.dateExpiration});
    cloudAppliqueLicence(r.data);
    return r;
  },[atelierInfo,cloudEnregistre,cloudAppliqueLicence]);
  const cloudConnexion=useCallback(async(motDePasse)=>{
    const r=await cloudAppel("/api/connexion",{methode:"POST",corps:{
      cle:(await window.storage.get("cp_lic").catch(()=>null))?.value||"",motDePasse,produit:"grossiste",
    }});
    if(!r.ok)return r;
    await cloudEnregistre(r.data.jeton,{atelier:r.data.atelier,plan:r.data.plan,dateExpiration:r.data.dateExpiration});
    cloudAppliqueLicence(r.data);
    return r;
  },[cloudEnregistre,cloudAppliqueLicence]);
  const cloudEnvoiPhotos=useCallback(async(avancement)=>{
    if(!cloudToken)return{ok:false,erreur:"Non connecté"};
    return await cloudEnvoyerPhotos(cloudToken,avancement);
  },[cloudToken]);
  const cloudRecupPhotos=useCallback(async(avancement)=>{
    if(!cloudToken)return{ok:false,erreur:"Non connecté"};
    return await cloudRecupererPhotos(cloudToken,avancement);
  },[cloudToken]);
  const cloudChangerMdp=useCallback(async(ancien,nouveau)=>{
    if(!cloudToken)return{ok:false,erreur:"Non connecté"};
    return await cloudAppel("/api/mot-de-passe",{methode:"POST",jeton:cloudToken,corps:{ancien,nouveau}});
  },[cloudToken]);
  const cloudDeconnexion=useCallback(async()=>{
    try{await window.storage.delete("cp_cloud_token");}catch{}
    setCloudToken(null);setCloudState("");
  },[]);
  // Envoi de la sauvegarde en ligne (léger, sans photos)
  const cloudEnvoyerBrut=useCallback(async(forcer=false,photosApres=false)=>{
    // Jamais les données d'une autre boutique dans ce compte, même en « forçant ».
    try{
      const p=await window.storage.get("cp_proprio"),l=await window.storage.get("cp_lic");
      if(!p||!l||p.value!==l.value)return{ok:false,erreur:"Les données de cet appareil ne sont pas encore rattachées à ce compte."};
    }catch{return{ok:false,erreur:"Stockage illisible"};}
    if(!cloudToken)return{ok:false,erreur:"Non connecté"};

    // GARDE-FOU — la sauvegarde en ligne est UNE case par clé, et l'envoi l'écrase
    // sans fusionner. Deux appareils sur la même clé peuvent donc s'effacer l'un
    // l'autre. Avant d'écraser, on demande au serveur ce qu'il détient : quelques
    // centaines d'octets. S'il en a nettement plus que cet appareil, on s'arrête
    // et on laisse le vendeur trancher.
    if(!forcer){
      const etat=await cloudAppel("/api/sauvegarde-etat",{jeton:cloudToken});
      const d=etat&&etat.ok?etat.data:null;
      if(d&&d.existe){
        const ici={stock:stock.length,clients:clients.length,ventes:orders.length};
        // Modifié sur l'autre appareil depuis notre dernière synchronisation : écraser
        // effacerait son travail. On s'arrête et on fait choisir.
        const version=await versionCloud();
        if(version&&d.date&&d.date>version){
          setConflitCloud({serveur:d,ici,autreAppareil:true});
          setCloudState("conflit");
          return{ok:false,conflit:true};
        }
        const marge=2; // une ou deux suppressions volontaires ne sont pas un accident
        if(d.nbStock>ici.stock+marge||d.nbClients>ici.clients+marge||d.nbCommandes>ici.ventes+marge){
          setConflitCloud({serveur:d,ici});
          setCloudState("conflit");
          return{ok:false,conflit:true};
        }
      }
    }

    setCloudState("envoi");
    const paquet=await buildLightBackup();
    // Les photos partent d'abord : quand l'autre appareil verra les nouvelles données,
    // leurs photos seront déjà en ligne. Seules celles qui manquent sont envoyées.
    // Sauf quand on quitte l'appli : le téléphone peut s'endormir d'une seconde à
    // l'autre, alors les données (légères) passent en premier, les photos ensuite.
    if(!photosApres){try{await cloudEnvoyerPhotos(cloudToken);}catch{}}
    const base=forcer?"":await versionCloud();
    // Compressée avant de partir : une boutique de milliers de ventes enverrait sinon
    // plus d'un Mo à chaque modification.
    const {corps:envoi,gz}=await compresser(JSON.stringify(paquet));
    const params=[base?"base="+encodeURIComponent(base):"",gz?"gz=1":""].filter(Boolean).join("&");
    const r=await cloudAppel("/api/sauvegarde"+(params?"?"+params:""),{methode:"POST",jeton:cloudToken,corps:envoi,brut:true});
    if(r.ok){
      const t=Date.now();
      try{await window.storage.set("cp_cloud_last",String(t));}catch{}
      try{if(r.data&&r.data.date)await window.storage.set("cp_cloud_version",r.data.date);await window.storage.delete("cp_a_envoyer");}catch{}
      if(photosApres)cloudEnvoyerPhotos(cloudToken).catch(()=>{});
      await markBackupDone();
      setCloudLast(t);setCloudState("ok");setNudgeBackup(false);
    }else if(r.statut===409&&r.data&&r.data.conflit){
      // L'autre appareil a envoyé entre-temps : même choix que ci-dessus.
      const e=await cloudAppel("/api/sauvegarde-etat",{jeton:cloudToken});
      setConflitCloud({serveur:e.ok&&e.data?e.data:{date:r.data.date,nbStock:0,nbClients:0,nbCommandes:0},ici:{stock:stock.length,clients:clients.length,ventes:orders.length},autreAppareil:true});
      setCloudState("conflit");
    }else setCloudState(r.horsLigne?"hors-ligne":"erreur");
    return r;
  },[cloudToken,stock,clients,orders]);
  // Un seul envoi à la fois : deux envois croisés se prendraient l'un l'autre pour
  // « l'autre appareil » et feraient apparaître un faux conflit.
  const envoiEnCours=useRef(false);
  const cloudEnvoyer=useCallback(async(forcer=false,photosApres=false)=>{
    if(envoiEnCours.current)return{ok:false,enCours:true};
    envoiEnCours.current=true;
    try{return await cloudEnvoyerBrut(forcer,photosApres);}finally{envoiEnCours.current=false;}
  },[cloudEnvoyerBrut]);
  // Restauration depuis le cloud (nouveau téléphone, données perdues…)
  // jetonDirect : juste après une connexion, l'état React n'a pas encore le jeton.
  // vider : on ouvre un AUTRE compte sur cet appareil ; ses données d'avant partent,
  // mais seulement une fois la sauvegarde en ligne bien reçue.
  const cloudRestaurer=useCallback(async(jetonDirect,vider=false)=>{
    const jeton=jetonDirect||cloudToken;
    if(!jeton)return{ok:false,erreur:"Non connecté"};
    const etatAvant=await cloudAppel("/api/sauvegarde-etat?leger=1",{jeton});
    const r=await cloudAppel("/api/sauvegarde",{jeton});
    if(!r.ok)return r;
    const contenu=r.data;
    const donnees=contenu&&contenu.version===2?contenu.data:contenu;
    if(!donnees||typeof donnees!=="object")return{ok:false,erreur:"Sauvegarde illisible"};
    if(vider)await viderDonneesLocales();
    for(const[k,v]of Object.entries(donnees)){
      if(PROPRES_APPAREIL.includes(k))continue; // session et identité de cet appareil : il garde les siennes
      try{await window.storage.set(k,v);}catch{}
    }
    // Cet appareil est désormais à jour de cette version-là, sans rien d'inédit à envoyer.
    if(etatAvant.ok&&etatAvant.data&&etatAvant.data.date){try{await window.storage.set("cp_cloud_version",etatAvant.data.date);await window.storage.delete("cp_a_envoyer");}catch{}}
    // Les photos suivent : sans elles le vendeur perdrait son catalogue
    let photos=null;
    try{photos=await cloudRecupererPhotos(jeton);}catch{}
    return{ok:true,photos};
  },[cloudToken]);

  // À QUI SONT CES DONNÉES — après une connexion, et au démarrage d'un appareil connecté
  // avant cette vérification. Renvoie true si l'application peut s'ouvrir telle quelle.
  const alignerDonnees=async(cle,jeton,compte)=>{
    let proprio="";try{const p=await window.storage.get("cp_proprio");proprio=p?p.value:"";}catch{}
    if(proprio===cle)return true;
    const etat=await cloudAppel("/api/sauvegarde-etat",{jeton});
    if(!etat.ok)return true; // hors ligne : on ouvre, mais rien ne part tant que ce n'est pas tranché
    const serveur=etat.data&&etat.data.existe?etat.data:null;
    const local=await contenuLocal();
    const adopter=async()=>{await window.storage.set("cp_proprio",cle);return true;};
    // Appareil vide (nouveau téléphone, données perdues) : il reçoit le compte.
    if(local.commandes+local.clients+local.stock===0){
      if(!serveur)return adopter();
      const r=await cloudRestaurer(jeton,true);
      if(r.ok){await window.storage.set("cp_proprio",cle);window.location.reload();return false;}
      return true; // restauration impossible : l'appareil reste vide et n'envoie rien
    }
    // Données d'avant les comptes, visiblement celles de ce compte : même nom
    // de boutique, ou à peu près le même contenu que la sauvegarde en ligne.
    if(!proprio){
      const memeNom=!!local.nom&&!!compte&&local.nom.trim().toLowerCase()===compte.trim().toLowerCase();
      const proche=(a,b)=>Math.abs(a-b)<=2;
      if(memeNom||(serveur&&proche(local.commandes,serveur.nbCommandes)&&proche(local.clients,serveur.nbClients)&&proche(local.stock,serveur.nbStock)))return adopter();
    }
    // Sinon c'est à la boutique de trancher : on ne devine jamais entre deux boutiques.
    setAlignement({cle,jeton,compte,serveur,local});
    return false;
  };
  const ouvrirCompte=async()=>{
    const a=alignement;if(!a)return;
    setActivBusy(true);setKeyErr("");
    if(a.serveur){
      const r=await cloudRestaurer(a.jeton,true);
      if(!r.ok){setActivBusy(false);setKeyErr(r.horsLigne?"Pas de connexion internet. Réessayez.":(r.erreur||"Impossible de récupérer le compte."));return;}
    }else{
      await viderDonneesLocales();
      await window.storage.set("cp_atelier",JSON.stringify({nom:a.compte||"",adresse:"Dakar, Sénégal",tel:"",email:""}));
    }
    await window.storage.set("cp_proprio",a.cle);
    window.location.reload();
  };
  const garderDonneesIci=async()=>{
    const a=alignement;if(!a)return;
    await window.storage.set("cp_proprio",a.cle);
    await window.storage.set("cp_a_envoyer","1"); // ces données deviennent celles du compte : elles partent
    setAlignement(null);setOk(true);
  };
  const annulerAlignement=async()=>{
    try{await window.storage.delete("cp_cloud_token");}catch{}
    setCloudToken(null);setAlignement(null);setOk(false);setKeyErr("");
  };
  // SE DÉCONNECTER — l'appareil garde ses données (il marche hors ligne) ; il faudra
  // le numéro et le mot de passe pour rouvrir. Si quelqu'un d'autre se connecte
  // ensuite ici, l'écran de choix l'empêche de voir ou d'écraser ces données.
  const seDeconnecter=async()=>{
    if(!window.confirm("Se déconnecter de "+APP_NAME+" sur cet appareil ?\n\nVos données restent enregistrées ici. Il faudra votre numéro et votre mot de passe pour revenir."))return;
    try{if(dirtyRef.current!==syncedRef.current)await cloudEnvoyerRef.current();}catch{}
    try{await window.storage.delete("cp_cloud_token");}catch{}
    setCloudToken(null);setShowSettings(false);setAlignement(null);setModeActiv("connexion");setInsc(f=>({...f,mdp:""}));
    setKeyErr("Vous êtes déconnecté. Vos données restent sur cet appareil.");setOk(false);
  };

  // SAUVEGARDE AUTOMATIQUE — groupée, jamais à chaque frappe :
  // 20 s après la DERNIÈRE modification (compteur `dirty` incrémenté à chaque
  // enregistrement réel, y compris une simple correction de prix), et à la fermeture
  // de l'appli s'il reste des changements non envoyés.
  // Objectif : protéger les données SANS manger le crédit internet du client.
  const cloudEnvoyerRef=useRef(cloudEnvoyer);
  useEffect(()=>{cloudEnvoyerRef.current=cloudEnvoyer;},[cloudEnvoyer]);
  const dirtyRef=useRef(0),syncedRef=useRef(0);
  // Des changements pas encore partis : l'appareil s'en souvient même après une
  // fermeture, pour ne jamais les écraser en recevant ceux de l'autre appareil.
  useEffect(()=>{dirtyRef.current=dirty;if(dirty>0&&dirty!==syncedRef.current){try{window.storage.set("cp_a_envoyer","1");}catch{}}},[dirty]);
  useEffect(()=>{
    if(!cloudToken||busy||!ok||dirty===0||dirty===syncedRef.current)return;
    const t=setTimeout(()=>{const n=dirtyRef.current;cloudEnvoyerRef.current().then(r=>{if(r&&r.ok)syncedRef.current=n;});},20*1000);
    return()=>clearTimeout(t);
  },[dirty,cloudToken,busy,ok]);
  // Envoi quand l'appli passe en arrière-plan / se ferme — seulement s'il y a du nouveau
  useEffect(()=>{
    if(!cloudToken)return;
    const quand=()=>{
      if(document.visibilityState!=="hidden")return;
      if(dirtyRef.current===syncedRef.current)return; // rien de neuf : on n'envoie rien
      const n=dirtyRef.current;
      cloudEnvoyerRef.current(false,true).then(r=>{if(r&&r.ok)syncedRef.current=n;}); // données d'abord : l'appli se ferme
    };
    document.addEventListener("visibilitychange",quand);
    return()=>document.removeEventListener("visibilitychange",quand);
  },[cloudToken]);

  // RECEVOIR CE QUE L'AUTRE APPAREIL A ENVOYÉ — le téléphone et l'ordinateur d'une même
  // boutique se tiennent à jour tout seuls : toutes les 30 s, et dès qu'on revient sur
  // l'appli, on demande au serveur la date de sa dernière sauvegarde (quelques octets).
  // Plus récente que la nôtre et rien d'inédit ici : on la prend, photos comprises.
  // Jamais pendant une saisie : un formulaire ouvert attend qu'on le ferme.
  const formOuvert=showOF||showClientF||showStockF||showRecep||showCF||showDepF||showFourn||showSettings||!!detail||!!conflitCloud||!!alignement;
  const formOuvertRef=useRef(false);formOuvertRef.current=formOuvert;
  const vueRef=useRef(view);vueRef.current=view;
  const recevoirEnCours=useRef(false),tourPhotos=useRef(0);
  const [recuFlash,setRecuFlash]=useState(()=>{try{if(sessionStorage.getItem("cp_recu")){sessionStorage.removeItem("cp_recu");return true;}}catch{}return false;});
  useEffect(()=>{if(!recuFlash)return;const t=setTimeout(()=>setRecuFlash(false),4000);return()=>clearTimeout(t);},[recuFlash]);
  const recevoirNouveautes=async()=>{
    if(!cloudToken||recevoirEnCours.current||formOuvertRef.current)return;
    try{
      const p=await window.storage.get("cp_proprio"),l=await window.storage.get("cp_lic");
      if(!p||!l||p.value!==l.value)return; // données pas encore rattachées : l'écran de choix d'abord
      // Des changements d'ici pas encore partis : ils partent d'abord.
      const aEnvoyer=await window.storage.get("cp_a_envoyer");
      if(aEnvoyer||dirtyRef.current!==syncedRef.current){const n=dirtyRef.current;const r=await cloudEnvoyerRef.current();if(r&&r.ok)syncedRef.current=n;return;}
    }catch{return;}
    const etat=await cloudAppel("/api/sauvegarde-etat?leger=1",{jeton:cloudToken});
    if(!etat.ok||!etat.data||!etat.data.existe)return;
    const recu=()=>{try{sessionStorage.setItem("cp_vue",vueRef.current);sessionStorage.setItem("cp_recu","1");}catch{}window.location.reload();};
    const version=await versionCloud();
    if(!version||etat.data.date>version){
      recevoirEnCours.current=true;
      const r=await cloudRestaurer(cloudToken);
      if(r.ok){recu();return;}
      recevoirEnCours.current=false;return;
    }
    // Photos, environ une fois par minute : on renvoie celles d'ici restées en route
    // (un envoi coupé par la mise en veille du téléphone), et on prend celles de
    // l'autre appareil arrivées après ses données.
    // La liste des photos n'est demandée au serveur que si quelque chose a bougé : une
    // photo d'ici pas encore partie (le nombre a changé), ou une photo reçue par le
    // serveur depuis la dernière fois (photosMaj). Sinon, rien ne transite.
    if(++tourPhotos.current%2===0){
      try{
        const nbIci=(await photosLocales()).length;
        const envoyees=Number((await window.storage.get("cp_photos_envoyees").catch(()=>null))?.value??-1);
        if(nbIci!==envoyees){const e=await cloudEnvoyerPhotos(cloudToken);if(e&&e.ok&&e.envoyees===e.total)await window.storage.set("cp_photos_envoyees",String(nbIci));}
        const vues=(await window.storage.get("cp_photos_vues").catch(()=>null))?.value||"";
        if(etat.data.photosMaj&&etat.data.photosMaj!==vues){
          const ph=await cloudRecupererPhotos(cloudToken);
          if(ph&&ph.ok&&ph.recuperees===ph.total)await window.storage.set("cp_photos_vues",etat.data.photosMaj);
          if(ph&&ph.recuperees>0&&!formOuvertRef.current)recu();
        }
      }catch{}
    }
  };
  const recevoirRef=useRef(recevoirNouveautes);recevoirRef.current=recevoirNouveautes;
  useEffect(()=>{
    if(!cloudToken||busy||!ok)return;
    const tick=()=>{if(document.visibilityState==="visible")recevoirRef.current();};
    const premier=setTimeout(tick,1500);
    const id=setInterval(tick,30000);
    document.addEventListener("visibilitychange",tick);
    return()=>{clearTimeout(premier);clearInterval(id);document.removeEventListener("visibilitychange",tick);};
  },[cloudToken,busy,ok]);
  // Au démarrage : demande au serveur l'état réel de la licence (renouvellement à distance)
  useEffect(()=>{
    if(!cloudToken||busy)return;
    (async()=>{
      const r=await cloudAppel("/api/licence",{jeton:cloudToken});
      if(r.ok){
        cloudAppliqueLicence(r.data);
        // TOBY a ordonné l'effacement du code caisse (patron ayant oublié son PIN)
        if(r.data.resetPin){
          try{await window.storage.delete("cp_pin");}catch{}
          setPinHash(null);setCaisseUnlocked(true);
          alert("🔓 TOBY a réinitialisé votre code caisse.\n\nLa caisse est de nouveau accessible. Vous pouvez définir un nouveau code dans ⚙️ Paramètres → Atelier.");
        }
      }
      else if(r.statut===401)cloudDeconnexion(); // compte suspendu ou session expirée
    })();
  },[cloudToken,busy,cloudAppliqueLicence,cloudDeconnexion]);

  // ── CODE CAISSE ──
  const verifyPin=useCallback(async pin=>{if(!pinHash)return true;try{return(await hashPin(pin))===pinHash;}catch{return false;}},[pinHash]);
  const setCaissePin=useCallback(async pin=>{const h=await hashPin(pin);try{await window.storage.set("cp_pin",h);}catch{}setPinHash(h);setCaisseUnlocked(true);},[]);
  const removeCaissePin=useCallback(async()=>{try{await window.storage.delete("cp_pin");}catch{}setPinHash(null);setCaisseUnlocked(true);},[]);
  const unlockCaisse=useCallback(async pin=>{const ok=await verifyPin(pin);if(ok)setCaisseUnlocked(true);return ok;},[verifyPin]);
  // Chiffres masqués tant que le code n'a pas été saisi
  const moneyLocked=!!pinHash&&!caisseUnlocked;
  const mfmt=v=>moneyLocked?"••••":v;

  // `touche()` marque qu'il y a du nouveau à sauvegarder en ligne (déclenche la synchro TOBY Cloud)
  const touche=useCallback(()=>setDirty(d=>d+1),[]);
  const saveO=useCallback(d=>{try{window.storage.set(ns("cp_v5"),JSON.stringify(d));}catch{}touche();},[ns,touche]);
  const saveC=useCallback(d=>{try{window.storage.set(ns("cp_col"),JSON.stringify(d));}catch{}touche();},[ns,touche]);
  const saveClients=useCallback(d=>{try{window.storage.set(ns("cp_clients"),JSON.stringify(d));}catch{}touche();},[ns,touche]);
  const saveCouleurs=useCallback(d=>{try{window.storage.set(ns("cp_couleurs"),JSON.stringify(d));}catch{}touche();},[ns,touche]);
  const saveStock=useCallback(d=>{try{window.storage.set(ns("cp_stock"),JSON.stringify(d));}catch{}touche();},[ns,touche]);
  const saveFourn=useCallback(d=>{try{window.storage.set(ns("cp_fournisseurs"),JSON.stringify(d));}catch{}touche();},[ns,touche]);
  const saveDepenses=useCallback(d=>{try{window.storage.set(ns("cp_depenses"),JSON.stringify(d));}catch{}touche();},[ns,touche]);
  const saveTrash=useCallback(d=>{try{window.storage.set(ns("cp_trash"),JSON.stringify(d));}catch{}touche();},[ns,touche]);
  // CRUD Dépenses
  const openNewDep=()=>{setDepForm(emptyDepense);setEditDep(null);setShowDepF(true);};
  const openEditDep=d=>{setDepForm({...d});setEditDep(d);setShowDepF(true);};
  const submitDep=()=>{
    const id=editDep?editDep.id:Date.now();
    const nd={...depForm,id,montant:parseFloat(depForm.montant)||0};
    const list=editDep?depenses.map(d=>d.id===editDep.id?nd:d):[...depenses,nd];
    setDepenses(list);saveDepenses(list);setShowDepF(false);
  };
  const delDep=id=>askDel("Supprimer cette dépense ?",()=>{setDepenses(p=>{const nd=p.filter(d=>d.id!==id);saveDepenses(nd);return nd;});setConfirm(null);});
  // CRUD Stock
  // ── PONT PHOTO ────────────────────────────────────────────────────
  // L'ordinateur demande un lien de dépôt, l'affiche en QR code, puis vient voir
  // toutes les deux secondes si le téléphone y a déposé la photo.
  const fermerPont=useCallback(()=>{
    if(pontMinuteur.current){clearInterval(pontMinuteur.current);pontMinuteur.current=null;}
    setPont(null);
  },[]);
  const ouvrirPont=useCallback(async()=>{
    if(!cloudToken){setPont({etat:"horsligne"});return;}
    setPont({etat:"demande"});
    const r=await cloudAppel("/api/photo-lien",{methode:"POST",jeton:cloudToken});
    if(!r.ok){setPont({etat:"erreur",erreur:r.erreur||"Impossible de créer le lien."});return;}
    const lien=r.data.lien,dep=r.data.dep;
    QRCode.toDataURL(lien,{margin:1,width:230,color:{dark:"#1a1a2e",light:"#ffffff"}},(e,url)=>{
      setPont({etat:"attente",lien,dep,qr:e?null:url});
    });
    let essais=0;
    if(pontMinuteur.current)clearInterval(pontMinuteur.current);
    pontMinuteur.current=setInterval(async()=>{
      essais++;
      if(essais>450){clearInterval(pontMinuteur.current);pontMinuteur.current=null;setPont(x=>x?{...x,etat:"expire"}:null);return;}
      try{
        const rep2=await fetch(CLOUD_URL+"/api/photo-lien/"+encodeURIComponent(dep),{headers:{Authorization:"Bearer "+cloudToken}});
        if(!rep2.ok)return;
        // Tant que le téléphone n'a rien déposé, le serveur répond du JSON.
        if((rep2.headers.get("Content-Type")||"").indexOf("image")!==0)return;
        const d=await blobVersDataURL(await rep2.blob());
        clearInterval(pontMinuteur.current);pontMinuteur.current=null;
        setStockForm(f=>({...f,photoData:d}));
        setPont(null);
      }catch{}
    },2000);
  },[cloudToken]);
  useEffect(()=>()=>{if(pontMinuteur.current)clearInterval(pontMinuteur.current);},[]);

  const openNewStock=()=>{
    // Le seuil d'alerte propre à la boutique : 10 m chez l'un, 50 chez l'autre.
    const seuil=parseFloat(atelierInfo.alerteStock);
    setStockForm({...emptyStock,lowAlert:seuil>0?seuil:10});
    setCouleursLot([]);setCouleurLibre(false);setEditStock(null);setShowStockF(true);};
  const openEditStock=s=>{setStockForm({...s,photoData:stockPhotos[s.id]||null});setCouleursLot([]);setCouleurLibre(false);setEditStock(s);setShowStockF(true);};
  // Une photo choisie est compressée avant d'être gardée : 1400 px, ~150 Ko.
  const handleStockPhoto=async e=>{
    const f=e.target.files&&e.target.files[0];
    e.target.value="";
    if(!f)return;
    const d=await shrinkImg(f);
    setStockForm(x=>({...x,photoData:d}));
    // La photo sait déjà de quelle couleur est le tissu : on la propose plutôt que
    // de la faire chercher. Seulement si le vendeur n'a rien choisi — on ne touche
    // jamais à sa décision — et il reste libre de corriger.
    try{
      const teinte=await teinteDominante(d);
      if(!teinte)return;
      setStockForm(x=>{
        if(x.couleur)return x;
        const proche=couleurLaPlusProche(teinte,[...COULEURS,...couleursPerso]);
        return{...x,couleur:proche?proche.n:"",couleurHex:proche?proche.h:rgbVersHex(teinte),couleurDevinee:true};
      });
    }catch{}
  };
  // Ce que coûte une entrée en stock, à sa date : sert au bilan quand le vendeur
  // compte ses achats de tissus comme dépenses.
  const entreeStock=a=>({date:new Date().toISOString().slice(0,10),montant:Math.round((parseFloat(a.metres)||0)*(parseFloat(a.prixUnitaire)||0))});
  const submitStock=async()=>{
    const {photoData,...champs}=stockForm;

    // Toute teinte nommée par le vendeur rejoint sa palette : il ne la ressaisira
    // plus jamais. Sans ça, « jaune poussin » serait à réinventer à chaque balle.
    const aRetenir=[...couleursLot,...(stockForm.couleur?[{n:stockForm.couleur,h:stockForm.couleurHex||""}]:[])]
      .filter(c=>c.n&&!COULEURS.some(x=>x.n===c.n)&&!couleursPerso.some(x=>x.n===c.n));
    if(aRetenir.length){
      const vues=new Set();
      const maj=[...couleursPerso,...aRetenir].filter(c=>vues.has(c.n)?false:(vues.add(c.n),true));
      setCouleursPerso(maj);saveCouleurs(maj);
    }

    // SAISIE D'UN LOT : une balle par couleur, tout le reste identique. C'est le
    // geste courant chez un grossiste — 5 balles de Getzner qui ne diffèrent que
    // par la teinte — et le resaisir cinq fois faisait perdre un temps fou.
    const lot=[...couleursLot,...(stockForm.couleur?[{n:stockForm.couleur,h:stockForm.couleurHex||""}]:[])];
    if(!editStock&&couleursLot.length>0){
      const base=Date.now();
      const nouveaux=lot.map((c,k)=>({...champs,couleur:c.n,couleurHex:c.h,id:base+k,entrees:[entreeStock(champs)]}));
      const nd2=[...stock,...nouveaux];
      setStock(nd2);saveStock(nd2);
      // Chaque balle garde la photo prise avec sa couleur — un gris clair ne
      // ressemble pas à un rouge, une photo commune n'aurait servi à personne.
      const maj={};
      for(let k=0;k<nouveaux.length;k++){
        const img=lot[k].photo||(k===lot.length-1?photoData:null);
        if(!img)continue;
        try{await photoStore.save(photoStockId(nouveaux[k].id),img);}catch{}
        maj[nouveaux[k].id]=img;
      }
      if(Object.keys(maj).length)setStockPhotos(p=>({...p,...maj}));
      setCouleursLot([]);setShowStockF(false);
      return;
    }

    const id=editStock?editStock.id:Date.now();
    const ns={...champs,id,...(editStock?{}:{entrees:[entreeStock(champs)]})};
    const nd=editStock?stock.map(s=>s.id===editStock.id?ns:s):[...stock,ns];
    setStock(nd);saveStock(nd);
    // La photo ne va pas dans le JSON de l'article : elle est écrite à part, comme
    // celles de la Collection, sinon la sauvegarde deviendrait vite énorme.
    const pid=photoStockId(id);
    if(photoData){
      try{await photoStore.save(pid,photoData);}catch{}
      setStockPhotos(p=>({...p,[id]:photoData}));
    }else if(editStock&&stockPhotos[id]){
      try{await photoStore.del(pid);}catch{}
      setStockPhotos(p=>{const c={...p};delete c[id];return c;});
    }
    setShowStockF(false);
  };
  const delStock=id=>askDel("Supprimer cet article de stock ?",()=>{setStock(p=>{const nd=p.filter(s=>s.id!==id);saveStock(nd);return nd;});setConfirm(null);});
  // CRUD Fournisseurs
  const openNewFourn=()=>{setFournForm(emptyFournisseur);setEditFourn(null);setShowFourn(true);};
  const openEditFourn=f=>{setFournForm({...f});setEditFourn(f);setShowFourn(true);};
  const submitFourn=()=>{
    const id=editFourn?editFourn.id:Date.now();
    const nf={...fournForm,id};
    const nd=editFourn?fournisseurs.map(f=>f.id===editFourn.id?nf:f):[...fournisseurs,nf];
    setFournisseurs(nd);saveFourn(nd);setShowFourn(false);
  };
  const delFourn=id=>askDel("Supprimer ce fournisseur ?",()=>{setFournisseurs(p=>{const nd=p.filter(f=>f.id!==id);saveFourn(nd);return nd;});setConfirm(null);});
  // Sortie/restitution AUTO du stock à partir des tissus d'une commande
  // sign = -1 : déduction (nouvelle commande) | +1 : restitution (suppression / édition)
  // GROSSISTE : toutes les tissus déduisent le stock automatiquement
  // COUTURIER : seuls les tissus marqués "source: atelier" déduisent
  const applyOrderStockMovement=(oldOrder,newOrder)=>{
    setStock(prev=>{
      const next=prev.map(s=>({...s}));
      const apply=(tissus,sign)=>{
        if(!tissus||!Array.isArray(tissus))return;
        tissus.forEach(t=>{
          if(!isGrossiste&&t.source!=="atelier")return;
          const m=parseFloat(t.metres)||0;
          if(m<=0)return;
          const tType=t.type==="Autre"?(t.custom||""):(t.type||"");
          if(!tType)return;
          const idx=next.findIndex(s=>{
            const sType=s.type==="Autre"?(s.custom||""):(s.type||"");
            return sType.toLowerCase()===tType.toLowerCase()&&(s.couleur||"").toLowerCase()===(t.couleur||"").toLowerCase();
          });
          if(idx>=0){
            const cur=parseFloat(next[idx].metres)||0;
            next[idx]={...next[idx],metres:String(Math.max(0,cur+sign*m).toFixed(2))};
          }
        });
      };
      if(oldOrder)apply(oldOrder.tissus,+1); // restitue ancien
      if(newOrder)apply(newOrder.tissus,-1); // déduit nouveau
      saveStock(next);
      return next;
    });
  };
  // Réception : ajoute des mètres au stock
  const openRecep=stockId=>{
    const s=stock.find(x=>x.id===stockId);
    // Pré-rempli avec la dernière composition connue (si y en a une)
    setRecepForm({stockId,metres:"",prixUnitaire:"",fournisseurId:s?.fournisseurId||null,notes:"",date:new Date().toISOString().slice(0,10),nbBalles:"",piecesParBalle:s?.piecesParBalle||"",metresParPiece:s?.metresParPiece||"",uniteMesure:s?.uniteMesure||"m",prixParPiece:""});
    setShowRecep(true);
  };
  const submitRecep=()=>{
    const add=parseFloat(recepForm.metres)||0;
    const addBalles=parseFloat(recepForm.nbBalles)||0;
    if(add<=0||!recepForm.stockId){setShowRecep(false);return;}
    // Ce que l'arrivage a coûté : à la pièce si le prix est à la pièce, sinon au mètre.
    const pieces=(parseFloat(recepForm.nbBalles)||0)*(parseFloat(recepForm.piecesParBalle)||0);
    const coutArrivage=s=>Math.round(parseFloat(recepForm.prixParPiece)>0&&pieces>0?pieces*parseFloat(recepForm.prixParPiece):add*(parseFloat(recepForm.prixUnitaire||s.prixUnitaire)||0));
    // Un article d'avant ce suivi : son entrée d'origine est reconstituée, pour ne pas la perdre.
    const entreesDe=s=>Array.isArray(s.entrees)?s.entrees:[{date:new Date(typeof s.id==="number"?s.id:Date.now()).toISOString().slice(0,10),montant:Math.round((parseFloat(s.metres)||0)*(parseFloat(s.prixUnitaire)||0))}];
    const nd=stock.map(s=>s.id===recepForm.stockId?{
      ...s,
      metres:String(((parseFloat(s.metres)||0)+add).toFixed(2)),
      nbBalles:addBalles>0?String((parseFloat(s.nbBalles)||0)+addBalles):s.nbBalles,
      // Mémorise la dernière composition pour pré-remplir la prochaine réception
      piecesParBalle:recepForm.piecesParBalle||s.piecesParBalle,
      metresParPiece:recepForm.metresParPiece||s.metresParPiece,
      uniteMesure:recepForm.uniteMesure||s.uniteMesure||"m",
      lastReceptionDate:recepForm.date,
      prixUnitaire:recepForm.prixUnitaire||s.prixUnitaire,
      prixParPiece:recepForm.prixParPiece||s.prixParPiece,
      fournisseurId:recepForm.fournisseurId||s.fournisseurId,
      entrees:[...entreesDe(s),{date:recepForm.date,montant:coutArrivage(s)}]
    }:s);
    setStock(nd);saveStock(nd);setShowRecep(false);
  };
  const F=useCallback(k=>e=>setForm(f=>({...f,[k]:e.target.value})),[]);
  const FC=useCallback(k=>v=>setForm(f=>({...f,[k]:v})),[]);
  const CF=useCallback(k=>e=>setCForm(f=>({...f,[k]:e.target.value})),[]);
  const CLF=useCallback(k=>e=>setClientForm(f=>({...f,[k]:e.target.value})),[]);
  const CLFC=useCallback(k=>v=>setClientForm(f=>({...f,[k]:v})),[]);
  // Mesures libres supplémentaires — commande (setForm) et fiche client (setClientForm)
  const addMesPlus=useCallback(()=>setForm(o=>({...o,mesuresPlus:[...(o.mesuresPlus||[]),{l:"",v:""}]})),[]);
  const upMesPlus=useCallback((i,f,v)=>setForm(o=>({...o,mesuresPlus:(o.mesuresPlus||[]).map((m,j)=>j===i?{...m,[f]:v}:m)})),[]);
  const remMesPlus=useCallback(i=>setForm(o=>({...o,mesuresPlus:(o.mesuresPlus||[]).filter((_,j)=>j!==i)})),[]);
  const clAddMesPlus=useCallback(()=>setClientForm(o=>({...o,mesuresPlus:[...(o.mesuresPlus||[]),{l:"",v:""}]})),[]);
  const clUpMesPlus=useCallback((i,f,v)=>setClientForm(o=>({...o,mesuresPlus:(o.mesuresPlus||[]).map((m,j)=>j===i?{...m,[f]:v}:m)})),[]);
  const clRemMesPlus=useCallback(i=>setClientForm(o=>({...o,mesuresPlus:(o.mesuresPlus||[]).filter((_,j)=>j!==i)})),[]);
  const rest=useCallback(o=>(parseFloat(o.prixTotal)||0)-(parseFloat(o.avanceRecue)||0),[]);
  const upT=useCallback((i,f,v)=>setForm(o=>({...o,tissus:o.tissus.map((t,j)=>j===i?{...sansPrixRetenu(t,[f]),[f]:v}:t)})),[]);
  // Choisir un article du stock renseigne plusieurs champs à la fois
  const majT=useCallback((i,maj)=>setForm(o=>({...o,tissus:o.tissus.map((t,j)=>j===i?{...sansPrixRetenu(t,Object.keys(maj)),...maj}:t)})),[]);
  const majQte=useCallback((i,v)=>setForm(o=>({...o,tissus:o.tissus.map((t,j)=>j===i?{...t,saisie:v,metres:v===""?"":String(+((parseFloat(v)||0)*(t.unite==="yd"?CONV_YD:1)).toFixed(2))}:t)})),[]);
  // Changer d'unité garde le chiffre tapé : « 10 » reste « 10 », en yards cette fois.
  const basculerUnite=useCallback(i=>setForm(o=>({...o,tissus:o.tissus.map((t,j)=>{
    if(j!==i)return t;
    const u=t.unite==="yd"?"m":"yd";
    const q=parseFloat(t.saisie!==undefined&&t.saisie!==""?t.saisie:qteDe(t))||0;
    return{...t,unite:u,saisie:q?String(q):"",metres:q?String(+(q*(u==="yd"?CONV_YD:1)).toFixed(2)):""};
  })})),[]);
  const addT=useCallback(()=>setForm(o=>({...o,tissus:[...o.tissus,{type:"",custom:"",metres:"",couleur:"",modele:"",source:"client",photoTissu:"",photoModele:""}]})),[]);
  const remT=useCallback(i=>setForm(o=>({...o,tissus:o.tissus.filter((_,j)=>j!==i)})),[]);
  // « Argent dépensé » pour une commande (Premium) — lignes {l:libellé, m:montant}
  const addCout=useCallback((l="")=>setForm(o=>({...o,couts:[...(o.couts||[]),{l,m:""}]})),[]);
  const upCout=useCallback((i,f,v)=>setForm(o=>({...o,couts:(o.couts||[]).map((c,j)=>j===i?{...c,[f]:v}:c)})),[]);
  const remCout=useCallback(i=>setForm(o=>({...o,couts:(o.couts||[]).filter((_,j)=>j!==i)})),[]);
  // Photos tissu/modèle (Premium) — un seul input file partagé, cible (tissu n°, emplacement) dans un ref
  const tissuPhotoRef=useRef(null);
  const tissuPhotoTarget=useRef(null);
  const pickTissuPhoto=useCallback((i,slot)=>{tissuPhotoTarget.current={i,slot};tissuPhotoRef.current&&tissuPhotoRef.current.click();},[]);
  const handleTissuPhoto=useCallback(async e=>{
    const f=e.target.files&&e.target.files[0];e.target.value="";
    const tgt=tissuPhotoTarget.current;
    if(!f||!tgt)return;
    const d=await shrinkImg(f);
    upT(tgt.i,tgt.slot+"Data",d);
  },[upT]);
  const handleSaveAtelier=useCallback(ai=>{
    try{window.storage.set(ns("cp_atelier"),JSON.stringify(ai));}catch{}
    setAtelierInfo(ai);
    touche();
  },[ns,touche]);

  const [boutiqueUrl,setBoutiqueUrl]=useState(null);
  // Référence courte de chaque article : deux lettres du tissu et un numéro
  // d'ordre. Elle se calcule seule, ne se saisit jamais, et ne bouge pas quand un
  // article s'écoule — le vendeur n'a aucun registre à tenir dans sa tête.
  const refsStock=useMemo(()=>{
    const compte={},out={};
    [...stock].sort((a,b)=>a.id-b.id).forEach(a=>{
      const nom=a.type==="Autre"?(a.custom||"Autre"):(a.type||"Divers");
      const p2=(nom.normalize("NFD").replace(/[^A-Za-z]/g,"").slice(0,2)||"XX").toUpperCase();
      compte[p2]=(compte[p2]||0)+1;
      out[a.id]=p2+"-"+String(compte[p2]).padStart(2,"0");
    });
    return out;
  },[stock]);
  // LA COLLECTION EST LA VITRINE DU STOCK
  // Un tissu enregistré au stock s'y montre avec son SEUL prix de vente — le prix
  // d'achat ne regarde pas le client. Il y reste même à zéro mètre : le client peut
  // le commander, et le grossiste le trouvera entre-temps.
  const colVitrine=useMemo(()=>{
    if(!isGrossiste)return colMetaSaisi;
    return [...stock].sort((a,b)=>b.id-a.id).map(a=>{
      const nom=a.type==="Autre"?(a.custom||"Autre"):(a.type||"Tissu");
      const reste=parseFloat(a.metres)||0;
      return{
        id:a.id,
        titre:nom+(a.couleur?" · "+a.couleur:""),
        // Un tissu créé par le vendeur (« Autre ») est sa propre famille dans la vitrine
        categorie:a.type==="Autre"&&a.custom?mettreEnForme(String(a.custom).trim()):(CAT_DE_TISSU[a.type]||""),
        // Le métrage exact ne regarde pas le client — c'est une donnée de gestion,
        // et l'annoncer invite à marchander sur ce qu'il reste.
        description:reste>0?"En stock":"Sur commande",
        prixIndicatif:a.prixVente||"",
        date:"",
        ref:refsStock[a.id]||"",
        reste,
        couleurHex:a.couleurHex||"",
      };
    });
  },[isGrossiste,colMetaSaisi,stock,refsStock]);
  const colMeta=colVitrine;
  const colPhotos=isGrossiste?stockPhotos:colPhotosSaisi;


  // PRO : démarre le serveur HTTP au démarrage de l'app et le garde actif (les 2 modes)
  // → l'URL est toujours disponible, le QR fonctionne dès qu'on ouvre Mode Boutique
  useEffect(()=>{
    if(!IS_PRO||!window.electronAPI?.boutique||busy)return;
    let cancelled=false;
    (async()=>{
      try{
        const html=buildCatalogHTML(colMeta,colPhotos,atelierInfo);
        const r=await window.electronAPI.boutique.startServer(html);
        if(!cancelled&&r?.success&&r.url)setBoutiqueUrl(r.url);
        else setBoutiqueUrl(null);
      }catch{setBoutiqueUrl(null);}
    })();
    return()=>{cancelled=true;};
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[busy,isGrossiste]);

  // Mise à jour dynamique du HTML servi quand collection/atelier changent
  useEffect(()=>{
    if(!boutiqueUrl||!window.electronAPI?.boutique?.updateServer)return;
    try{
      const html=buildCatalogHTML(colMeta,colPhotos,atelierInfo);
      window.electronAPI.boutique.updateServer(html);
    }catch{}
  },[boutiqueUrl,colMeta,colPhotos,atelierInfo]);

  // QR Code — priorité: serveur local Pro > URL configurée > vCard fallback
  useEffect(()=>{
    if(!showBoutique)return;
    let payload;
    // Normalise l'URL du site : accepte "monsite.netlify.app" sans le https://
    const cuRaw=(atelierInfo.catalogUrl||"").trim();
    const cu=cuRaw?(/^https?:\/\//.test(cuRaw)?cuRaw:"https://"+cuRaw):"";
    if(cu){
      // PRIORITÉ AU SITE EN LIGNE : accessible partout (4G), pas seulement sur le WiFi local
      payload=cu;
    }else if(boutiqueUrl){
      payload=boutiqueUrl;
    }else{
      const nom=atelierInfo.nom||"Mon Atelier";
      const tel=(atelierInfo.tel||"").replace(/\D/g,"");
      const adr=atelierInfo.adresse||"";
      const email=atelierInfo.email||"";
      payload=`BEGIN:VCARD\nVERSION:3.0\nFN:${nom}\nORG:${nom}\nTEL;TYPE=WORK,VOICE:+221${tel}\nADR;TYPE=WORK:;;${adr};Dakar;;;Sénégal\n${email?`EMAIL:${email}\n`:""}NOTE:Atelier de couture\nEND:VCARD`;
    }
    QRCode.toDataURL(payload,{margin:1,width:240,color:{dark:"#1a1a2e",light:"#ffffff"}},(e,url)=>{if(!e)setQrData(url);});
  },[showBoutique,atelierInfo,boutiqueUrl]);

  // Auto-slideshow toutes les 5 secondes en mode boutique
  useEffect(()=>{
    if(!showBoutique||colMeta.length<2)return;
    const t=setInterval(()=>setBoutiqueIdx(i=>(i+1)%colMeta.length),5000);
    return()=>clearInterval(t);
  },[showBoutique,colMeta.length]);

  // Sortie mode boutique avec Échap
  useEffect(()=>{
    if(!showBoutique)return;
    const h=e=>{if(e.key==="Escape")setShowBoutique(false);if(e.key==="ArrowRight")setBoutiqueIdx(i=>(i+1)%Math.max(1,colMeta.length));if(e.key==="ArrowLeft")setBoutiqueIdx(i=>(i-1+colMeta.length)%Math.max(1,colMeta.length));};
    window.addEventListener("keydown",h);
    return()=>window.removeEventListener("keydown",h);
  },[showBoutique,colMeta.length]);
  // MODE D'ACTIVITÉ — Pro uniquement : couturier (par défaut) ou grossiste
  const LBL=useMemo(()=>isGrossiste?{
    sales:"Ventes",sale:"vente",newSale:"Nouvelle vente",
    receipt:"Facture",receiptTitle:"FACTURE DE VENTE",prefix:"VTE-"
  }:{
    sales:"Commandes",sale:"commande",newSale:"Nouvelle commande",
    receipt:"Reçu",receiptTitle:"REÇU DE PAIEMENT",prefix:"CMD-"
  },[isGrossiste]);
  // Un compteur à part, dans sa propre clé : il part dans la sauvegarde comme
  // le reste, et TobyTech voit combien de factures la boutique a sorties sans
  // avoir à lire le détail des ventes. Rien n'est écrit dans les ventes
  // elles-mêmes : un compteur n'a pas à risquer d'abîmer une facture.
  const compterFacture=useCallback(async()=>{
    try{
      const r=await window.storage.get(ns("cp_factures"));
      const c=r?JSON.parse(r.value):{n:0};
      window.storage.set(ns("cp_factures"),JSON.stringify({n:(c.n||0)+1,dernier:new Date().toISOString().slice(0,10)}));
      touche();
    }catch{}
  },[ns,touche]);
  const showReceipt=useCallback((o)=>{
    if(isGrossiste)compterFacture();
    setReceiptHtml({html:isGrossiste?buildFactureHTML(o,atelierInfo,stock):buildReceiptHTML(o,atelierInfo,isGrossiste),order:o});
  },[atelierInfo,isGrossiste,stock,compterFacture]);
  // Photo de la balle vendue (Stock), pour une ligne de vente ou pour la vente entière
  const photoLigne=useCallback(t=>{const s=articleDeLigne(t,stock);return s?(stockPhotos[s.id]||null):null;},[stock,stockPhotos]);
  const photoVente=useCallback(o=>{for(const t of(o.tissus||[])){const p=photoLigne(t);if(p)return p;}return null;},[photoLigne]);
  // Imprimer bilan mensuel
  const printBilanMensuel=useCallback((monthKey,monthOrders)=>{
    const [y,m]=monthKey.split("-");
    const label=new Date(parseInt(y),parseInt(m)-1,1).toLocaleDateString("fr-FR",{month:"long",year:"numeric"});
    setReceiptHtml({html:buildBilanHTML(label,monthOrders,clients,atelierInfo,"mensuel"),order:null,name:"Bilan-"+label.replace(/s+/g,"-")});
  },[clients,atelierInfo]);
  // Imprimer bilan annuel
  const printBilanAnnuel=useCallback((year)=>{
    const yearOrders=orders.filter(o=>{const d=o.dateDepot||new Date(o.id).toISOString().slice(0,10);return d.startsWith(year+"-");});
    setReceiptHtml({html:buildBilanHTML(year,yearOrders,clients,atelierInfo,"annuel"),order:null,name:"Bilan-"+year});
  },[orders,clients,atelierInfo]);

  // Étape 1 : la clé est-elle bien formée ? On ne fait remplir l'identité qu'ensuite,
  // pour ne pas imposer un formulaire à quelqu'un qui s'est trompé de clé.
  const verifierCle=()=>{
    if(!validKey(keyIn)){setKeyErr("❌ Clé invalide. Une clé Tissi-App commence par GPRO.");return;}
    setKeyErr("");setEtapeActiv(2);
  };

  // CONNEXION — le vendeur donne son numéro et son mot de passe, comme sur
  // n'importe quelle plateforme. Il n'a plus besoin de connaître sa clé.
  // MOT DE PASSE OUBLIÉ — l'application n'envoie ni SMS ni e-mail : le vendeur écrit
  // à TOBY sur WhatsApp, avec son numéro déjà rempli. TOBY vérifie que c'est bien lui,
  // puis génère un mot de passe provisoire depuis l'administration (bouton « Mot de passe »).
  const mdpOublie=()=>{
    const tel=(insc.tel||"").trim();
    const msg="Bonjour TOBY, j'ai oublié mon mot de passe "+APP_NAME+".\n\nMon numéro de connexion : "+(tel||"(à préciser)")+"\n\nPouvez-vous m'envoyer un mot de passe provisoire ? Merci.";
    window.open("https://wa.me/"+PAIEMENT.whatsapp+"?text="+encodeURIComponent(msg),"_blank");
  };

  const seConnecter=async()=>{
    const ident=(insc.tel||"").trim();
    if(ident.replace(/[^0-9]/g,"").length<9&&!validKey(ident)){setKeyErr("Entrez votre numéro de téléphone.");return;}
    if((insc.mdp||"").length<1){setKeyErr("Entrez votre mot de passe.");return;}
    setActivBusy(true);setKeyErr("");
    const appareil=await idAppareil();
    const r=await cloudAppel("/api/connexion",{methode:"POST",corps:{identifiant:ident,motDePasse:insc.mdp,produit:"grossiste",appareil,appareilNom:nomAppareil()}});
    setActivBusy(false);
    if(!r.ok){
      if(r.horsLigne){setKeyErr("Pas de connexion internet. Réessayez une fois le réseau revenu.");return;}
      if(r.statut===409&&r.data&&r.data.appareils){setAppareilsOccupes(r.data);setKeyErr("");return;}
      setKeyErr(r.erreur||"Connexion refusée.");return;
    }
    // Dernier rempart : un compte d'un autre produit n'ouvre jamais cette application.
    if(r.data.cle&&!validKey(r.data.cle)){setKeyErr("Ce compte n'est pas un compte "+APP_NAME+".");return;}
    await cloudEnregistre(r.data.jeton,{atelier:r.data.atelier||"",plan:r.data.plan,dateExpiration:r.data.dateExpiration});
    cloudAppliqueLicence(r.data);
    // La clé n'est plus tapée par le vendeur : elle vient du serveur.
    if(r.data.cle)await window.storage.set("cp_lic",r.data.cle);
    // Se connecter ouvre CE compte : si l'appareil contient une autre boutique, on demande.
    if(!(await alignerDonnees(r.data.cle,r.data.jeton,r.data.atelier||"")))return;
    await window.storage.set("cp_derniere_verif",String(Date.now()));
    setOk(true);
  };

  // Libérer un appareil quand la limite est atteinte — téléphone cassé, revendu…
  const libererAppareil=async(id)=>{
    setActivBusy(true);
    const r=await cloudAppel("/api/appareil-liberer",{methode:"POST",corps:{identifiant:(insc.tel||"").trim(),motDePasse:insc.mdp,produit:"grossiste",appareil:id}});
    setActivBusy(false);
    if(!r.ok){setKeyErr(r.erreur||"Libération impossible.");return;}
    setAppareilsOccupes(null);
    await seConnecter();
  };

  const activate=async()=>{
    if(!validKey(keyIn)){setKeyErr("❌ Clé invalide. Une clé Tissi-App commence par GPRO.");return;}
    const tel=(insc.tel||"").replace(/[^0-9]/g,"");
    if(!insc.prenom.trim()||!insc.nom.trim()){setKeyErr("Indiquez votre prénom et votre nom.");return;}
    if(insc.boutique.trim().length<2){setKeyErr("Indiquez le nom de votre boutique.");return;}
    if(tel.length<9){setKeyErr("Le numéro de téléphone est obligatoire (9 chiffres).");return;}
    if((insc.mdp||"").length<6){setKeyErr("Choisissez un mot de passe d'au moins 6 caractères.");return;}

    // C'est ICI que se joue « une clé = une boutique » : la clé est rattachée chez
    // TOBY à cette boutique nommée, et le serveur refuse une clé déjà prise.
    setActivBusy(true);setKeyErr("");
    const cle=keyIn.toUpperCase().replace(/[^A-Z0-9-]/g,"");
    const responsable=(insc.prenom.trim()+" "+insc.nom.trim()).trim();
    const r=await cloudAppel("/api/compte",{methode:"POST",corps:{
      cle,motDePasse:insc.mdp,atelier:insc.boutique.trim(),responsable,
      tel,ville:insc.ville.trim(),app:"grossiste",appareil:await idAppareil(),appareilNom:nomAppareil(),
    }});
    setActivBusy(false);
    if(!r.ok){
      if(r.horsLigne){setKeyErr("Pas de connexion. La première activation demande internet — ensuite l'application fonctionne hors ligne.");return;}
      if(r.statut===409){setKeyErr(r.erreur||"Cette clé est déjà utilisée par une autre boutique. Contactez TOBY.");return;}
      setKeyErr(r.erreur||"Activation refusée. Réessayez.");return;
    }
    await cloudEnregistre(r.data.jeton,{atelier:insc.boutique.trim(),plan:r.data.plan,dateExpiration:r.data.dateExpiration});
    cloudAppliqueLicence(r.data);
    // La fiche boutique est déjà remplie : le vendeur ne resaisit rien.
    handleSaveAtelier({...atelierInfo,nom:insc.boutique.trim(),tel:insc.tel.trim(),adresse:insc.ville.trim()||atelierInfo.adresse,responsable});
    await window.storage.set("cp_lic",cle);
    await window.storage.set("cp_proprio",cle); // la nouvelle boutique part des données de cet appareil
    await window.storage.set("cp_derniere_verif",String(Date.now()));
    // WEB/MOBILE : démarre l'essai 30 jours à la première activation (pas de fichier licence.json ici)
    if(!window.electronAPI){
      const st=await window.storage.get("cp_trial_start");
      let start;
      if(!st){start=Date.now();await window.storage.set("cp_trial_start",String(start));}
      else start=parseInt(st.value)||Date.now();
      const lr=await window.storage.get("cp_licence");
      if(!lr){
        const dateExpiration=new Date(start+30*86400000).toISOString().slice(0,10);
        const today=new Date();today.setHours(0,0,0,0);
        const expDay=new Date(dateExpiration+"T00:00:00");
        const j=Math.max(0,Math.round((expDay.getTime()-today.getTime())/86400000));
        setLicenceInfo({plan:"ESSAI",dateExpiration,joursRestants:j,expired:Date.now()>new Date(dateExpiration+"T23:59:59").getTime()});
      }
    }
    setOk(true);
  };
  const openNewO=()=>{setForm({...emptyO,dateDepot:jour,dateLivraison:IS_GROSSISTE?jour:""});setEditO(null);setEssaiO(false);setFTab("client");setMTab("haut");setCliSearch("");setPrixManuel(false);setVersementManuel(false);setLibelleManuel(false);setExpress(false);setShowOF(true);};
  // COMMANDE EXPRESS : un seul écran avec l'essentiel (client, prix, livraison, modèle)
  const openNewExpress=()=>{setForm(emptyO);setEditO(null);setFTab("__express");setMTab("haut");setCliSearch("");setPrixManuel(false);setVersementManuel(false);setLibelleManuel(false);setExpress(true);setShowOF(true);};
  const openEditO=useCallback(o=>{
    const mo=migrateTissus(o);
    // Une vente déjà enregistrée garde ses montants et son libellé : sans ça, rouvrir
    // une vente à crédit la remplissait comme payée, et effaçait la dette en silence.
    setForm(mo);setEditO(o);setFTab("client");setMTab("haut");setCliSearch("");setPrixManuel(true);setVersementManuel(true);setLibelleManuel(true);setExpress(false);setEssaiO(false);setShowOF(true);setDetail(null);
    // Charge les photos tissu/modèle existantes dans le formulaire (aperçus)
    if(IS_PREMIUM&&(mo.tissus||[]).some(t=>t.photoTissu||t.photoModele)){
      (async()=>{
        const loads=await Promise.all(mo.tissus.map(async t=>({
          pt:t.photoTissu?await photoStore.get(t.photoTissu):null,
          pm:t.photoModele?await photoStore.get(t.photoModele):null
        })));
        setForm(f=>({...f,tissus:f.tissus.map((t,i)=>{
          const L=loads[i];if(!L)return t;
          const nt={...t};
          if(L.pt&&nt.photoTissuData===undefined)nt.photoTissuData=L.pt;
          if(L.pm&&nt.photoModeleData===undefined)nt.photoModeleData=L.pm;
          return nt;
        })}));
      })();
    }
  },[]);
  // CHAMPS OBLIGATOIRES — une vente n'existe qu'une fois son client, ses tissus, sa
  // date, ses montants et son moyen de paiement renseignés : un formulaire à moitié
  // rempli fausse la caisse.
  const [essaiO,setEssaiO]=useState(false);
  const manquantsO=useMemo(()=>{
    if(!showOF)return[];
    const f=form,m=[];
    if(!((f.nom||"").trim()||(f.prenom||"").trim()))m.push({tab:"client",l:"Nom du client"});
    const lignes=(f.tissus||[]).filter(t=>t.type||t.custom||t.couleur||t.metres);
    if(!lignes.length)m.push({tab:"tissu",l:"Au moins un tissu vendu"});
    lignes.forEach((t,i)=>{
      const n=lignes.length>1?" (tissu "+(i+1)+")":"";
      if(!(t.type==="Autre"?(t.custom||"").trim():t.type))m.push({tab:"tissu",l:"Type de tissu"+n});
      if(!(parseFloat(t.metres)>0))m.push({tab:"tissu",l:"Mètres vendus"+n});
    });
    if(!f.dateDepot)m.push({tab:"finances",l:"Date de la vente"});
    const prix=parseFloat(f.prixTotal)||0,verse=parseFloat(f.avanceRecue);
    if(!(prix>0))m.push({tab:"finances",l:"Prix total"});
    if(f.avanceRecue===""||f.avanceRecue==null||isNaN(verse))m.push({tab:"finances",l:"Montant versé (0 si rien payé)"});
    else{
      if(verse>prix&&prix>0)m.push({tab:"finances",l:"Montant versé plus grand que le prix"});
      if(verse>0&&!f.modePaiement)m.push({tab:"finances",l:"Mode de paiement"});
      if(prix>0&&verse<prix){
        if(!f.dateEcheance)m.push({tab:"finances",l:"Date promise par le client"});
        if(!(f.telephone||"").replace(/\D/g,""))m.push({tab:"client",l:"Téléphone (vente à crédit)"});
      }
    }
    return m;
  },[form,showOF]);
  const nextCmdNum=()=>{const nums=orders.map(o=>parseInt((o.cmdNum||"").replace(/^[A-Z]+-/,""))||0);const max=nums.length>0?Math.max(...nums):0;return LBL.prefix+String(max+1).padStart(3,"0");};
  const submitO=async()=>{
    // Rien n'est enregistré tant que l'essentiel manque : on montre ce qui reste à remplir.
    if(manquantsO.length){setEssaiO(true);setFTab(manquantsO[0].tab);return;}
    // PHOTOS TISSU/MODÈLE (Premium) : enregistre les nouvelles, supprime les retirées,
    // et ne garde dans la commande que l'id (jamais le base64 → stockage léger)
    const tissusClean=await Promise.all((form.tissus||[]).map(async t=>{
      const nt={...t};
      for(const slot of["photoTissu","photoModele"]){
        const dk=slot+"Data";
        if(IS_PREMIUM){
          if(typeof nt[dk]==="string"&&nt[dk].startsWith("data:")){
            const id=nt[slot]||("tp"+Date.now()+Math.random().toString(36).slice(2,8));
            try{await photoStore.save(id,nt[dk]);nt[slot]=id;}catch{}
          }else if(nt[dk]===""&&nt[slot]){
            try{await photoStore.del(nt[slot]);}catch{}
            nt[slot]="";
          }
        }
        delete nt[dk];
      }
      return nt;
    }));
    // Chaque ligne garde le prix au mètre du jour de la vente : la facture l'affiche tel
    // quel, même si le prix du Stock change ensuite.
    const fClean={...form,tissus:tissusClean.map(t=>{if(parseFloat(t.prixM)>0)return t;const pv=prixLigne(t,stock);return pv>0?{...t,prixM:pv}:t;})};
    const nd=editO?orders.map(o=>o.id===editO.id?{...fClean,id:editO.id,cmdNum:editO.cmdNum}:o):[...orders,{...fClean,id:Date.now(),cmdNum:nextCmdNum()}];
    setOrders(nd);saveO(nd);
    // SORTIE AUTO DU STOCK (Pro uniquement)
    if(IS_PRO)applyOrderStockMovement(editO?migrateTissus(editO):null,fClean);
    // Si lié à un client : on synchronise aussi les mesures sur la fiche client (source de vérité)
    if(form.clientId){
      const cIdx=clients.findIndex(c=>c.id===form.clientId);
      if(cIdx>=0){
        const uc={...clients[cIdx],mEpaule:form.mEpaule,mLongueur:form.mLongueur,mPoitrine:form.mPoitrine,mTaille:form.mTaille,mManche:form.mManche,mPoignet:form.mPoignet,mBras:form.mBras,mBassinH:form.mBassinH,mCol:form.mCol,mCeinture:form.mCeinture,mLongueurP:form.mLongueurP,mCuisse:form.mCuisse,mBassinP:form.mBassinP,mBas:form.mBas,mesuresPlus:form.mesuresPlus};
        const nc=clients.map((c,i)=>i===cIdx?uc:c);
        setClients(nc);saveClients(nc);
      }
    }
    setShowOF(false);
  };
  const askDel=(msg,fn)=>setConfirm({msg,fn});
  const delO=useCallback(id=>askDel("Mettre cette commande à la corbeille ? Vous pourrez la restaurer pendant 30 jours.",()=>{
    const deleted=orders.find(o=>o.id===id);
    // Corbeille : on garde un instantané (les photos sont conservées jusqu'à la purge)
    if(deleted){setTrash(t=>{const nt=[{tid:Date.now(),type:"order",snap:migrateTissus(deleted),deletedAt:Date.now()},...t];saveTrash(nt);return nt;});}
    setOrders(p=>{
      const filtered=p.filter(o=>o.id!==id);
      // Renumérotation auto : trier par création (id) puis réattribuer numéros séquentiels
      const pfx=LBL.prefix;
      const sorted=[...filtered].sort((a,b)=>a.id-b.id);
      const renumbered=sorted.map((o,i)=>({...o,cmdNum:pfx+String(i+1).padStart(3,"0")}));
      saveO(renumbered);
      return renumbered;
    });
    // RESTITUTION du stock pour les tissus "atelier" de la commande supprimée
    if(IS_PRO&&deleted)applyOrderStockMovement(migrateTissus(deleted),null);
    setDetail(null);setConfirm(null);
  }),[saveO,orders,LBL.prefix,saveTrash]);
  // Statut → Livré : la commande est considérée payée en totalité (couturier)
  const updSt=useCallback((id,st)=>{
    const paid=o=>(!IS_GROSSISTE&&st==="Livré"&&(parseFloat(o.prixTotal)||0)>0)?{avanceRecue:o.prixTotal}:{};
    setOrders(p=>{const nd=p.map(o=>o.id===id?{...o,statut:st,...paid(o)}:o);saveO(nd);return nd;});
    setDetail(d=>d&&d.id===id?{...d,statut:st,...paid(d)}:d);
  },[saveO]);
  // Catalogue papier : réservé à Maison de couture
  const imprimerCatalogue=()=>{
    if(!dispo("cataloguePdf")){setVoirOffres(true);return;}
    if(!colMeta.length){alert("Ajoutez d abord des modèles à votre collection.");return;}
    setReceiptHtml({html:buildCatalogueHTML(colMeta,colPhotos,atelierInfo),order:null,name:"Catalogue-"+(atelierInfo.nom||"collection").replace(/[^a-zA-Z0-9]/g,"-")});
  };
  const openNewC=()=>{
    // La vitrine n'a rien à saisir : un tissu s'ajoute au stock, et paraît ici aussitôt.
    if(isGrossiste){setView("stock");openNewStock();return;}
    // Catalogue plein : on ne bloque pas sèchement, on propose la suite
    if(colMeta.length>=LIMITE_COLLECTION){setVoirOffres(true);return;}
    setCForm({titre:"",categorie:COLLECTION_CATS[0],description:"",prixIndicatif:"",photoData:null});setEditC(null);setShowCF(true);};
  const openEditC=c=>{setColMenu(false);setCForm({titre:c.titre||"",categorie:c.categorie||"Ensemble",description:c.description||"",prixIndicatif:c.prixIndicatif||"",photoData:colPhotos[c.id]||null});setEditC(c);setShowCF(true);setColDetail(null);};
  const handlePhoto=async e=>{const f=e.target.files[0];if(!f)return;const d=await shrinkImg(f);setCForm(c=>({...c,photoData:d}));};

  // PHOTO FIX : enregistrer la photo sur disque via IPC (Electron) plutôt que dans le JSON
  const submitC=async()=>{
    const id=editC?editC.id:Date.now();
    const item={id,titre:cForm.titre,categorie:cForm.categorie,description:cForm.description,prixIndicatif:cForm.prixIndicatif||"",date:editC?editC.date:new Date().toLocaleDateString("fr-FR")};
    const nd=editC?colMeta.map(c=>c.id===id?item:c):[item,...colMeta];
    setColMeta(nd);saveC(nd);
    if(cForm.photoData){
      if(window.electronAPI?.photos){
        // Écriture sur disque dans userData/photos/{id}.jpg → persiste après redémarrage
        await window.electronAPI.photos.save(id,cForm.photoData);
      }else{
        try{await window.storage.set("cp_p_"+id,cForm.photoData);}catch{}
      }
      // On stocke directement le base64 en mémoire (déjà chargé dans cForm.photoData)
      setColPhotos(p=>({...p,[id]:cForm.photoData}));
    }
    setShowCF(false);
  };
  const delC=id=>askDel("Supprimer ce modèle ?",async()=>{
    setColMeta(p=>{const nd=p.filter(c=>c.id!==id);saveC(nd);return nd;});
    if(window.electronAPI?.photos){
      try{await window.electronAPI.photos.delete(id);}catch{}
    }else{
      try{await window.storage.delete("cp_p_"+id);}catch{}
    }
    setColPhotos(p=>{const np={...p};delete np[id];return np;});
    setColDetail(null);setConfirm(null);
  });

  // ── CLIENTS CRUD ──
  const openNewClient=()=>{setClientForm(emptyClient);setEditClient(null);setClTab("info");setClMTab("haut");setShowClientF(true);};
  const openEditClient=c=>{setClientForm({...c});setEditClient(c);setClTab("info");setClMTab("haut");setShowClientF(true);setClientDetail(null);};
  const nextCliNum=()=>{const nums=clients.map(c=>parseInt((c.cliNum||"").replace("CLI-",""))||0);const max=nums.length>0?Math.max(...nums):0;return"CLI-"+String(max+1).padStart(3,"0");};
  const submitClient=()=>{
    const id=editClient?editClient.id:Date.now();
    const cliNum=editClient?editClient.cliNum:nextCliNum();
    const nc={...clientForm,id,cliNum,dateCreation:editClient?.dateCreation||new Date().toISOString().split("T")[0]};
    const nd=editClient?clients.map(c=>c.id===editClient.id?nc:c):[...clients,nc];
    setClients(nd);saveClients(nd);
    // Propager les changements vers les commandes liées (nom, téléphone, etc.)
    if(editClient){
      const uo=orders.map(o=>o.clientId===editClient.id?{...o,nom:nc.nom,prenom:nc.prenom,telephone:nc.telephone,nationalite:nc.nationalite,ville:nc.ville,paysOrigine:nc.paysOrigine}:o);
      setOrders(uo);saveO(uo);
    }
    setShowClientF(false);
    if(clientDetail&&editClient&&clientDetail.id===editClient.id)setClientDetail(nc);
  };
  const delClient=id=>askDel("Mettre ce client à la corbeille ? Restaurable pendant 30 jours. Ses commandes sont conservées.",()=>{
    const deleted=clients.find(c=>c.id===id);
    const relinkIds=orders.filter(o=>o.clientId===id).map(o=>o.id); // commandes à relier si restauration
    if(deleted){setTrash(t=>{const nt=[{tid:Date.now(),type:"client",snap:deleted,deletedAt:Date.now(),relinkIds},...t];saveTrash(nt);return nt;});}
    const uo=orders.map(o=>o.clientId===id?{...o,clientId:null}:o);
    setOrders(uo);saveO(uo);
    setClients(p=>{
      const filtered=p.filter(c=>c.id!==id);
      // Renumérotation auto des CLI- séquentiellement
      const sorted=[...filtered].sort((a,b)=>a.id-b.id);
      const renumbered=sorted.map((c,i)=>({...c,cliNum:"CLI-"+String(i+1).padStart(3,"0")}));
      saveClients(renumbered);
      return renumbered;
    });
    setClientDetail(null);setConfirm(null);
  });
  // CORBEILLE — restaurer un élément (commande ou client) ou le supprimer définitivement
  const restoreTrash=useCallback(entry=>{
    if(entry.type==="order"){
      const restored={...entry.snap,id:Date.now()};
      setOrders(p=>{const arr=[...p,restored];const pfx=LBL.prefix;const sorted=[...arr].sort((a,b)=>a.id-b.id);const renumbered=sorted.map((o,i)=>({...o,cmdNum:pfx+String(i+1).padStart(3,"0")}));saveO(renumbered);return renumbered;});
      if(IS_PRO)applyOrderStockMovement(null,restored); // re-déduit le stock rendu à la suppression
    }else if(entry.type==="client"){
      const newId=Date.now();const restored={...entry.snap,id:newId};
      setClients(p=>{const arr=[...p,restored];const sorted=[...arr].sort((a,b)=>a.id-b.id);const renumbered=sorted.map((c,i)=>({...c,cliNum:"CLI-"+String(i+1).padStart(3,"0")}));saveClients(renumbered);return renumbered;});
      if(entry.relinkIds&&entry.relinkIds.length)setOrders(p=>{const uo=p.map(o=>entry.relinkIds.includes(o.id)?{...o,clientId:newId}:o);saveO(uo);return uo;});
    }
    setTrash(t=>{const nt=t.filter(e=>e.tid!==entry.tid);saveTrash(nt);return nt;});
  },[saveO,saveClients,saveTrash,LBL.prefix]);
  const purgeTrash=useCallback(entry=>{
    if(entry.type==="order")(entry.snap?.tissus||[]).forEach(ti=>{if(ti.photoTissu)photoStore.del(ti.photoTissu);if(ti.photoModele)photoStore.del(ti.photoModele);});
    setTrash(t=>{const nt=t.filter(e=>e.tid!==entry.tid);saveTrash(nt);return nt;});
  },[saveTrash]);
  const selectClientForOrder=c=>{
    setForm(f=>({...f,clientId:c.id,nom:c.nom,prenom:c.prenom,telephone:c.telephone,nationalite:c.nationalite,ville:c.ville,paysOrigine:c.paysOrigine,mEpaule:c.mEpaule,mLongueur:c.mLongueur,mPoitrine:c.mPoitrine,mTaille:c.mTaille,mManche:c.mManche,mPoignet:c.mPoignet,mBras:c.mBras,mBassinH:c.mBassinH,mCol:c.mCol,mCeinture:c.mCeinture,mLongueurP:c.mLongueurP,mCuisse:c.mCuisse,mBassinP:c.mBassinP,mBas:c.mBas,mesuresPlus:c.mesuresPlus}));
    setCliSearch("");
  };
  const newOrderFromClient=c=>{
    setClientDetail(null);
    setEditO(null);
    setFTab("commande");setMTab("haut");setCliSearch("");
    setForm({...emptyO,clientId:c.id,nom:c.nom,prenom:c.prenom,telephone:c.telephone,nationalite:c.nationalite,ville:c.ville,paysOrigine:c.paysOrigine,mEpaule:c.mEpaule,mLongueur:c.mLongueur,mPoitrine:c.mPoitrine,mTaille:c.mTaille,mManche:c.mManche,mPoignet:c.mPoignet,mBras:c.mBras,mBassinH:c.mBassinH,mCol:c.mCol,mCeinture:c.mCeinture,mLongueurP:c.mLongueurP,mCuisse:c.mCuisse,mBassinP:c.mBassinP,mBas:c.mBas,mesuresPlus:c.mesuresPlus});
    setShowOF(true);
  };

  const filtered=useMemo(()=>orders.filter(o=>{const q=search.toLowerCase();return(!q||[o.nom,o.prenom,o.telephone,o.cmdNum].some(v=>v&&v.toLowerCase().includes(q)))&&(fSt==="all"||(IS_GROSSISTE?statutGrossiste(o)===fSt:o.statut===fSt));}).sort((a,b)=>{const na=parseInt((a.cmdNum||"").replace(/^[A-Z]+-/,""))||0;const nb=parseInt((b.cmdNum||"").replace(/^[A-Z]+-/,""))||0;return nb-na;}),[orders,search,fSt]);
  const filtCol=useMemo(()=>colCat==="Tous"?colMeta:colMeta.filter(c=>(c.categorie||"Autres")===colCat),[colMeta,colCat]);
  // Les familles de tissus de la vitrine : d'abord celles que le vendeur a créées
  // (ses propres noms de tissus), puis les familles connues — seulement celles qui
  // ont au moins un tissu, pour que le filtre reste lisible.
  const catsVitrine=useMemo(()=>{
    const presentes=new Set(colMeta.map(c=>c.categorie||"Autres"));
    const perso=[...presentes].filter(c=>!COLLECTION_CATS.includes(c)&&c!=="Autres");
    return[...perso,...COLLECTION_CATS.filter(c=>presentes.has(c)),...(presentes.has("Autres")?["Autres"]:[])];
  },[colMeta]);
  // Au moment de ranger un tissu : ses propres types de tissus en tête, puis les familles connues.
  const catsCollection=useMemo(()=>{
    const perso=[...new Set([
      ...stock.filter(s=>s.type==="Autre"&&s.custom).map(s=>mettreEnForme(String(s.custom).trim())),
      ...colMeta.map(c=>c.categorie).filter(c=>c&&!COLLECTION_CATS.includes(c)&&c!=="Autres"),
    ])];
    return[...perso,...COLLECTION_CATS];
  },[stock,colMeta]);
  const filtClients=useMemo(()=>{
    const q=clientListSearch.toLowerCase();
    // Filtre puis tri : du client le plus récemment actif au plus ancien (dernière commande)
    return clients
      .filter(c=>!q||[c.nom,c.prenom,c.telephone,c.cliNum,c.ville].some(v=>v&&v.toLowerCase().includes(q)))
      .map(c=>{
        const cOrders=orders.filter(o=>o.clientId===c.id);
        const lastOrderId=cOrders.length>0?Math.max(...cOrders.map(o=>o.id)):c.id;
        return{...c,_lastActivity:lastOrderId};
      })
      .sort((a,b)=>b._lastActivity-a._lastActivity);
  },[clients,clientListSearch,orders]);
  // Nombre de pièces par commande : max(nb tissus valides, champ nombrePieces, 1)
  const piecesOf=useCallback(o=>{
    const tissusValides=(o.tissus||[]).filter(t=>t.type||t.metres||t.couleur).length;
    return Math.max(tissusValides,parseInt(o.nombrePieces)||1,1);
  },[]);
  // Total pièces (toutes commandes confondues)
  const totalPieces=useMemo(()=>orders.reduce((s,o)=>s+piecesOf(o),0),[orders,piecesOf]);
  // Pièces par statut — pour que la somme des statuts = totalPieces
  const piecesByStatus=useMemo(()=>{
    const r={};
    orders.forEach(o=>{r[o.statut]=(r[o.statut]||0)+piecesOf(o);});
    return r;
  },[orders,piecesOf]);
  // Pièces urgentes (hors prêtes & livrées)
  const urgentesPieces=useMemo(()=>orders.filter(o=>o.priorite==="Urgente"&&o.statut!=="Livré"&&o.statut!=="Prêt").reduce((s,o)=>s+piecesOf(o),0),[orders,piecesOf]);
  const stats=useMemo(()=>{const tP=orders.reduce((s,o)=>s+(parseFloat(o.prixTotal)||0),0);const tE=orders.reduce((s,o)=>s+(parseFloat(o.avanceRecue)||0),0);return{tP,tE,tR:tP-tE,nbC:new Set(orders.filter(o=>o.telephone).map(o=>o.telephone)).size,nbU:urgentesPieces};},[orders,urgentesPieces]);
  // CRÉDIT — ce que les clients doivent encore, par client. L'échéance d'un client est
  // la plus proche des dates promises sur ses ventes non soldées : le jour venu, il
  // remonte en tête sans que le vendeur ait à le chercher.
  const credits=useMemo(()=>{
    const auj=jour;
    const dans7=new Date(new Date(jour+"T00:00:00Z").getTime()+7*86400000).toISOString().slice(0,10);
    const par={};
    orders.forEach(o=>{
      const r=resteDe(o);if(r<=0)return;
      const k=cleCredit(o);
      if(!par[k]){
        const c=o.clientId?clients.find(x=>x.id===o.clientId):null;
        par[k]={cle:k,nom:[(c||o).prenom,(c||o).nom].filter(Boolean).join(" ").trim(),telephone:(c&&c.telephone)||o.telephone||"",ventes:[],reste:0,echeance:""};
      }
      const g=par[k];g.ventes.push(o);g.reste+=r;
      if(o.dateEcheance&&(!g.echeance||o.dateEcheance<g.echeance))g.echeance=o.dateEcheance;
    });
    const RANG={retard:0,aujourdhui:1,bientot:2,plusTard:3,sansDate:4};
    const liste=Object.values(par).map(g=>{
      g.nom=g.nom||"Client sans nom";
      g.ventes.sort((a,b)=>(a.dateDepot||"").localeCompare(b.dateDepot||"")||a.id-b.id);
      g.depuis=g.ventes[0].dateDepot||"";
      g.etat=!g.echeance?"sansDate":g.echeance<auj?"retard":g.echeance===auj?"aujourdhui":g.echeance<=dans7?"bientot":"plusTard";
      g.jours=g.echeance?Math.abs(joursEntre(auj,g.echeance)):0;
      return g;
    }).sort((a,b)=>RANG[a.etat]-RANG[b.etat]||(a.echeance||"").localeCompare(b.echeance||"")||b.reste-a.reste);
    const dus=liste.filter(g=>g.etat==="retard"||g.etat==="aujourdhui");
    // Remboursements reçus, regroupés par encaissement (un paiement peut régler plusieurs ventes).
    const lots={};
    orders.forEach(o=>(o.paiements||[]).forEach(p=>{
      const k=p.t||(p.date+"-"+o.id);
      const l=lots[k]||(lots[k]={t:p.t||0,date:p.date,mode:p.mode,nom:[o.prenom,o.nom].filter(Boolean).join(" ")||"Client sans nom",montant:0,ventes:[]});
      l.montant+=parseFloat(p.montant)||0;l.ventes.push(o.cmdNum);
    }));
    const paiements=Object.values(lots).sort((a,b)=>(b.date||"").localeCompare(a.date||"")||b.t-a.t);
    const mois=auj.slice(0,7);
    return{liste,dus,paiements,auj,
      total:liste.reduce((s,g)=>s+g.reste,0),
      totalDu:dus.reduce((s,g)=>s+g.reste,0),
      semaine:liste.filter(g=>g.etat==="bientot").reduce((s,g)=>s+g.reste,0),
      rembourseMois:paiements.filter(p=>(p.date||"").startsWith(mois)).reduce((s,p)=>s+p.montant,0)};
  },[orders,clients,jour]);
  const ouvrirEncaissement=useCallback(cle=>{
    const g=credits.liste.find(x=>x.cle===cle);if(!g)return;
    setDetail(null);
    setEncaisser({cle,montant:String(g.reste),mode:"Espèces",date:jour,echeance:g.echeance&&g.echeance>jour?g.echeance:""});
  },[credits,jour]);
  // ENCAISSER — l'argent reçu règle d'abord les ventes les plus anciennes. Chaque vente
  // garde la trace du paiement, et la Caisse le compte aussitôt dans l'encaissé.
  const validerEncaissement=useCallback(()=>{
    if(!encaisser)return;
    const g=credits.liste.find(x=>x.cle===encaisser.cle);if(!g)return;
    const montant=Math.min(parseFloat(encaisser.montant)||0,g.reste);
    if(montant<=0)return;
    const t=Date.now(),date=encaisser.date||jour,mode=encaisser.mode||"Espèces";
    const resteApres=g.reste-montant;
    const echeance=resteApres>0?(encaisser.echeance||""):"";
    const parts={};let aRepartir=montant;
    g.ventes.forEach(o=>{if(aRepartir<=0)return;const p=Math.min(aRepartir,resteDe(o));parts[o.id]=p;aRepartir-=p;});
    const ids=new Set(g.ventes.map(o=>o.id));
    const nd=orders.map(o=>{
      if(!ids.has(o.id))return o;
      const p=parts[o.id]||0;
      const n=p>0?{...o,avanceRecue:String((parseFloat(o.avanceRecue)||0)+p),paiements:[...(o.paiements||[]),{date,montant:p,mode,t}]}:{...o};
      if(resteDe(n)>0)n.dateEcheance=echeance;else delete n.dateEcheance;
      return n;
    });
    setOrders(nd);saveO(nd);
    setEncaisser(null);
    setEncaisseFait({nom:g.nom,telephone:g.telephone,date,montant,mode,echeance,resteApres,num:t,
      lignes:g.ventes.filter(o=>parts[o.id]>0).map(o=>({cmdNum:o.cmdNum,dateDepot:o.dateDepot,part:parts[o.id],reste:resteDe(o)-parts[o.id]}))});
  },[encaisser,credits,orders,saveO,jour]);
  const fixerEcheance=useCallback((cle,date)=>{
    const g=credits.liste.find(x=>x.cle===cle);if(!g)return;
    const ids=new Set(g.ventes.map(o=>o.id));
    const nd=orders.map(o=>ids.has(o.id)?{...o,dateEcheance:date||""}:o);
    setOrders(nd);saveO(nd);setEcheanceEdit(null);
  },[credits,orders,saveO]);
  // DÉPENSES — stats + bénéfice net
  // ACHATS DE TISSUS EN DÉPENSES (option) — chaque article entré en stock, et chaque
  // arrivage, compte à sa date comme une dépense « achats » : prix d'achat × quantité.
  // Le bilan net montre alors ce qui reste vraiment au vendeur. Ces lignes sont
  // calculées, jamais enregistrées : décocher l'option les fait disparaître.
  const achatsStock=useMemo(()=>{
    if(!atelierInfo.achatsEnDepenses)return[];
    const lignes=[];
    stock.forEach(s=>{
      const entrees=Array.isArray(s.entrees)&&s.entrees.length?s.entrees
        :[{date:new Date(typeof s.id==="number"?s.id:Date.now()).toISOString().slice(0,10),montant:(parseFloat(s.metres)||0)*(parseFloat(s.prixUnitaire)||0)}];
      entrees.forEach((e,k)=>{
        const m=parseFloat(e.montant)||0;
        if(m>0)lignes.push({id:"achat-"+s.id+"-"+k,auto:true,date:e.date,montant:String(Math.round(m)),categorie:"achats",description:"Achat de tissu · "+nomArticle(s),modePaiement:"",
          // pour les regrouper par type de tissu dans la liste des dépenses
          tissu:(s.type==="Autre"||!s.type)?((s.custom||"").trim()||"Autre"):s.type,
          article:nomArticle(s)+(s.couleur?" · "+s.couleur:"")+(k===0?"":" (arrivage)")});
      });
    });
    return lignes;
  },[stock,atelierInfo.achatsEnDepenses]);
  const toutesDepenses=useMemo(()=>[...depenses,...achatsStock],[depenses,achatsStock]);
  const depStats=useMemo(()=>{
    const total=toutesDepenses.reduce((s,d)=>s+(parseFloat(d.montant)||0),0);
    const parCategorie={};
    CATEGORIES_DEPENSE.forEach(c=>parCategorie[c.id]=0);
    toutesDepenses.forEach(d=>{const cat=d.categorie||"autres";parCategorie[cat]=(parCategorie[cat]||0)+(parseFloat(d.montant)||0);});
    const topCat=Object.entries(parCategorie).sort((a,b)=>b[1]-a[1])[0];
    const beneficeNet=stats.tP-total;
    const marge=stats.tP>0?((beneficeNet/stats.tP)*100):0;
    return{total,parCategorie,topCat,beneficeNet,marge};
  },[toutesDepenses,stats.tP]);
  const depensesByMonth=useMemo(()=>{
    const groups={};
    toutesDepenses.forEach(d=>{const date=d.date||new Date(d.id).toISOString().slice(0,10);const key=date.slice(0,7);if(!groups[key])groups[key]={key,depenses:[],total:0};groups[key].depenses.push(d);groups[key].total+=parseFloat(d.montant)||0;});
    return Object.values(groups).sort((a,b)=>b.key.localeCompare(a.key));
  },[toutesDepenses]);
  // ARGENT DÉPENSÉ SUR LES COMMANDES (Premium) — total + par mois, source = les couts saisis dans chaque commande
  const coutsCommande=useMemo(()=>{
    let total=0;const byMonth={};const parMois={};
    // « Argent dépensé sur les commandes », c'est le métier du couturier : ce qu'il
    // débourse pour réaliser une commande. Un grossiste, lui, achète son stock —
    // et cet achat est déjà suivi dans le Stock et les Dépenses. Le compter ici le
    // ferait apparaître deux fois et fausserait le bénéfice net.
    if(isGrossiste)return{total,byMonth,parMois};
    orders.forEach(o=>{
      const lignes=(o.couts||[]).filter(x=>(parseFloat(x.m)||0)>0);
      const c=lignes.reduce((s,x)=>s+(parseFloat(x.m)||0),0);
      if(c>0){
        total+=c;
        const k=(o.dateDepot||new Date(o.id).toISOString().slice(0,10)).slice(0,7);
        byMonth[k]=(byMonth[k]||0)+c;
        // Détail : de quelle commande vient cette dépense, et pour quel client
        (parMois[k]=parMois[k]||[]).push({
          id:o.id, cmdNum:o.cmdNum||"", nom:((o.prenom||"")+" "+(o.nom||"")).trim()||"Client non renseigné",
          date:o.dateDepot||"", montant:c, lignes:lignes.map(x=>({l:x.l||"Sans libellé",m:parseFloat(x.m)||0})), commande:o
        });
      }
    });
    Object.values(parMois).forEach(l=>l.sort((a,b)=>b.montant-a.montant));
    return{total,byMonth,parMois};
  },[orders,isGrossiste]);
  const filteredDep=useMemo(()=>{
    return toutesDepenses.filter(d=>{
      if(depFilterCat!=="all"&&d.categorie!==depFilterCat)return false;
      if(depFilterMonth!=="all"&&!((d.date||"").startsWith(depFilterMonth)))return false;
      return true;
    }).sort((a,b)=>(b.date||"").localeCompare(a.date||"")||b.id-a.id);
  },[toutesDepenses,depFilterCat,depFilterMonth]);
  // Clients inactifs : aucune commande depuis 6 mois
  const inactiveClients=useMemo(()=>{
    const sixMonthsAgo=Date.now()-1000*60*60*24*180;
    return clients.filter(c=>{
      const cOrders=orders.filter(o=>o.clientId===c.id);
      if(cOrders.length===0)return false;
      const lastId=Math.max(...cOrders.map(o=>o.id));
      return lastId<sixMonthsAgo;
    });
  },[clients,orders]);
  const fTabs=useMemo(()=>{
    const all=isGrossiste?[
      {id:"client",l:"👤 Client"},
      {id:"tissu",l:"🧶 Articles"},
      {id:"finances",l:"💰 Finances"}
    ]:[
      {id:"client",l:"👤 Client"},
      {id:"commande",l:"🏷️ Commande"},
      {id:"tissu",l:"🧶 Tissu"},
      {id:"mesures",l:"📐 Mesures"},
      {id:"finances",l:"💰 Finances"},
      {id:"notes",l:"📝 Notes"}
    ];
    return all;
  },[isGrossiste]);
  const cFI=fTabs.findIndex(t=>t.id===fTab);
  const linkedClient=form.clientId?clients.find(c=>c.id===form.clientId):null;
  const cliMatches=useMemo(()=>{if(!cliSearch||cliSearch.length<1)return[];const q=cliSearch.toLowerCase();return clients.filter(c=>[c.nom,c.prenom,c.telephone,c.cliNum].some(v=>v&&v.toLowerCase().includes(q))).slice(0,6);},[clients,cliSearch]);

  // Auto-focus sur la recherche client à l'ouverture d'une nouvelle vente/commande
  useEffect(()=>{
    if(showOF&&fTab==="client"&&cliSearchRef.current){
      setTimeout(()=>{try{cliSearchRef.current.focus();}catch{}},120);
    }
  },[showOF,fTab]);

  // Prix suggéré : somme des (mètres × prix_vente_stock) de chaque tissu
  const prixSuggere=useMemo(()=>{
    if(!form.tissus)return 0;
    let total=0;
    form.tissus.forEach(t=>{
      const m=parseFloat(t.metres)||0;
      if(m<=0)return;
      const pv=prixLigne(t,stock);
      if(pv>0)total+=m*pv;
    });
    return Math.round(total);
  },[form.tissus,stock]);

  // Le prix se déduit des articles vendus et remplit le total ET le montant versé :
  // au marché le client règle presque toujours la totalité. Le vendeur n'a plus qu'à
  // corriger dans le cas contraire — et dès qu'il corrige, on ne touche plus à sa saisie.
  useEffect(()=>{
    if(!showOF)return;
    if(prixSuggere<=0)return;
    const v=String(prixSuggere);
    setForm(f=>{
      const nf={...f};
      if(!prixManuel&&f.prixTotal!==v)nf.prixTotal=v;
      if(!versementManuel&&f.avanceRecue!==v)nf.avanceRecue=v;
      return (nf.prixTotal===f.prixTotal&&nf.avanceRecue===f.avanceRecue)?f:nf;
    });
  },[prixSuggere,showOF,prixManuel,versementManuel]);

  // Le libellé s'écrit tout seul à partir de ce qui est vendu : « 400 m Getzner rose
  // — Wave ». Le vendeur peut le réécrire, et dès qu'il y touche on n'y revient plus.
  const libelleAuto=useMemo(()=>{
    const morceaux=(form.tissus||[]).map(t=>{
      const type=t.type==="Autre"?(t.custom||""):(t.type||"");
      const m=parseFloat(t.metres)||0;
      const bouts=[];
      if(m>0)bouts.push(m+" m");
      if(type)bouts.push(type);
      if(t.couleur)bouts.push(String(t.couleur).toLowerCase());
      return bouts.join(" ").trim();
    }).filter(Boolean);
    if(!morceaux.length)return "";
    return morceaux.join(" + ")+(form.modePaiement?" — "+form.modePaiement:"");
  },[form.tissus,form.modePaiement]);
  useEffect(()=>{
    if(!showOF||libelleManuel||!libelleAuto)return;
    setForm(f=>f.remarques===libelleAuto?f:{...f,remarques:libelleAuto});
  },[libelleAuto,showOF,libelleManuel]);
  // STOCK : stats + alertes
  // En grossiste : seuil "bas" = 1 pièce restante (calculé depuis metresParPiece)
  // En couturier : seuil "bas" = lowAlert en mètres (défaut 10m)
  const stockLowThreshold=useCallback((s)=>{
    const propre=parseFloat(s.lowAlert)||0;
    if(propre>0)return propre;                       // seuil propre à cet article
    const general=parseFloat(atelierInfo.alerteStock)||0;
    return general>0?general:10;                     // sinon celui des Paramètres
  },[atelierInfo.alerteStock]);
  const stockStats=useMemo(()=>{
    let valeur=0,total=0,nbBas=0,nbRupture=0,nbPieces=0;
    const types=new Set();
    stock.forEach(s=>{
      const m=parseFloat(s.metres)||0;
      const p=parseFloat(s.prixUnitaire)||0;
      const low=stockLowThreshold(s);
      valeur+=m*p;total+=m;
      if(m<=0)nbRupture++;else if(m<=low)nbBas++;
      const nom=((s.type==="Autre"||!s.type)?(s.custom||""):s.type).trim().toLowerCase();
      if(nom)types.add(nom);
      const mpp=parseFloat(s.metresParPiece)||0;
      if(mpp>0&&m>0)nbPieces+=Math.ceil(m/mpp);
    });
    return{valeur,total,nbBas,nbRupture,nbPieces,nbTypes:types.size,nbArticles:stock.length};
  },[stock,stockLowThreshold]);
  const stockAlerts=useMemo(()=>{
    return stock.filter(s=>{const m=parseFloat(s.metres)||0;const low=stockLowThreshold(s);return m<=low;}).sort((a,b)=>(parseFloat(a.metres)||0)-(parseFloat(b.metres)||0));
  },[stock,stockLowThreshold]);
  // Label complet d'un article de stock
  const stockLabel=s=>{const t=s.type==="Autre"?(s.custom||"Autre"):(s.type||"—");return `${t}${s.couleur?" "+s.couleur:""}`;};
  // ANNÉES disponibles (pour bilan annuel)
  const yearsAvailable=useMemo(()=>{
    const years=new Set();
    orders.forEach(o=>{const d=o.dateDepot||new Date(o.id).toISOString().slice(0,10);years.add(d.slice(0,4));});
    return Array.from(years).sort().reverse();
  },[orders]);
  // CAISSE : groupement par mois (compteur pièces aligné avec le dashboard)
  const ordersByMonth=useMemo(()=>{
    const groups={};
    orders.forEach(o=>{
      const d=o.dateDepot||new Date(o.id).toISOString().slice(0,10);
      const key=d.slice(0,7); // "2026-05"
      if(!groups[key])groups[key]={key,orders:[],tP:0,tE:0,pieces:0};
      groups[key].orders.push(o);
      groups[key].tP+=parseFloat(o.prixTotal)||0;
      groups[key].tE+=parseFloat(o.avanceRecue)||0;
      const tV=(o.tissus||[]).filter(t=>t.type||t.metres||t.couleur).length;
      groups[key].pieces+=Math.max(tV,parseInt(o.nombrePieces)||1,1);
    });
    return Object.values(groups).sort((a,b)=>b.key.localeCompare(a.key));
  },[orders]);

  // Écran d'attente : le logo, pas des ciseaux — on ne vend pas de la couture ici.
  if(busy)return <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100vh",background:D,color:G,fontFamily:"sans-serif",fontSize:18,flexDirection:"column",gap:16}}>{IS_GROSSISTE?<LogoPastille h={26}/>:<div style={{fontSize:54}}>✂️</div>}Chargement...</div>;

  if(!ok)return(
    <div style={{display:"flex",alignItems:"flex-start",justifyContent:"center",height:"100vh",overflowY:"auto",padding:"20px 0",boxSizing:"border-box",background:D,fontFamily:"'Segoe UI',system-ui,sans-serif"}}>
      <div style={{margin:"auto",background:IS_GROSSISTE?"#fff":"#f9eddf",borderRadius:20,padding:IS_GROSSISTE?"40px 44px":"28px 32px 34px",maxWidth:400,width:"90%",textAlign:"center",boxShadow:"0 20px 60px rgba(0,0,0,0.3)"}}>
        {IS_GROSSISTE
          ?null
          :<img src="logo-complet.jpg" alt={APP_NAME} style={{width:"100%",maxWidth:250,height:"auto",display:"block",margin:"0 auto 4px"}}/>}
        <div style={{fontSize:26,fontWeight:900,marginTop:6,display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>{IS_GROSSISTE&&<LogoTissi h={24}/>}
          {APP_BADGE&&<span style={{background:"linear-gradient(135deg,#fbbf24,#f59e0b)",color:"#451a03",fontSize:11,fontWeight:900,padding:"3px 9px",borderRadius:5,letterSpacing:1}}>{APP_BADGE}</span>}
        </div>
        {/* Le fond de cette carte est blanc : un gris trop clair y serait illisible. */}
        <div style={{fontSize:10,color:IS_GROSSISTE?"#9ca3af":"#a99c88",marginBottom:22,marginTop:IS_GROSSISTE?6:2,letterSpacing:"3px"}}>{APP_SUBTITLE}</div>
        {/* Deux portes : se connecter (le cas courant) ou créer sa boutique. */}
        <div style={{display:alignement?"none":"flex",gap:4,background:"#f2f0ea",borderRadius:9,padding:3,marginBottom:16}}>
          {[["connexion","Se connecter"],["inscription","Créer ma boutique"]].map(([m,l])=>(
            <button key={m} onClick={()=>{setModeActiv(m);setKeyErr("");setAppareilsOccupes(null);setEtapeActiv(1);}} style={{flex:1,padding:"9px 6px",borderRadius:7,border:"none",cursor:"pointer",fontWeight:modeActiv===m?800:600,fontSize:12.5,background:modeActiv===m?"#fff":"transparent",color:modeActiv===m?D:"#8a8378",fontFamily:"inherit",boxShadow:modeActiv===m?"0 1px 4px rgba(0,0,0,0.08)":"none"}}>{l}</button>
          ))}
        </div>

        {alignement?<>
          {/* L'appareil contient les données d'une autre boutique : on montre les deux,
              et c'est le vendeur qui choisit. On ne devine jamais entre deux boutiques. */}
          <div style={{fontSize:15.5,fontWeight:900,color:D,marginBottom:6}}>Cet appareil contient une autre boutique</div>
          <div style={{fontSize:12.5,color:"#666",lineHeight:1.55,marginBottom:14}}>Vous vous connectez à <b>{alignement.compte||"votre compte"}</b>, mais les données enregistrées ici appartiennent à une autre boutique.</div>
          {[
            ["📱 Sur cet appareil",alignement.local.nom||"Sans nom",alignement.local.commandes,alignement.local.clients,alignement.local.stock,"#fffbeb","#fde68a"],
            ["☁️ Dans votre compte",alignement.compte||"Votre compte",alignement.serveur?alignement.serveur.nbCommandes:null,alignement.serveur?alignement.serveur.nbClients:0,alignement.serveur?alignement.serveur.nbStock:0,"#ecfdf5","#a7f3d0"],
          ].map(([t,nom,c,cl,st,bg,bd])=>(
            <div key={t} style={{textAlign:"left",background:bg,border:"1px solid "+bd,borderRadius:10,padding:"10px 13px",marginBottom:9}}>
              <div style={{fontSize:10.5,fontWeight:800,color:"#888",letterSpacing:.5,textTransform:"uppercase"}}>{t}</div>
              <div style={{fontSize:14,fontWeight:900,color:D,margin:"2px 0"}}>{nom}</div>
              <div style={{fontSize:12,color:"#555"}}>{c===null?"Aucune sauvegarde en ligne pour l'instant":c+(IS_GROSSISTE?" ventes · ":" commandes · ")+cl+" clients · "+st+" articles en stock"}</div>
            </div>
          ))}
          {keyErr&&<div style={{color:"#ef4444",fontSize:12,margin:"4px 0 8px",textAlign:"left"}}>{keyErr}</div>}
          <button onClick={ouvrirCompte} disabled={activBusy} style={{width:"100%",background:"#10b981",color:"#fff",border:"none",borderRadius:10,padding:"13px",cursor:"pointer",fontWeight:800,fontSize:14,fontFamily:"inherit",marginTop:6}}>{activBusy?"Chargement…":"☁️ Ouvrir "+(alignement.compte||"mon compte")}</button>
          <div style={{fontSize:11,color:"#999",margin:"6px 2px 12px",lineHeight:1.5}}>Les données de « {alignement.local.nom||"cet appareil"} » seront retirées de cet appareil. Gardez-en une copie si vous en avez besoin :</div>
          <button onClick={()=>shareOrDownloadBackup("copie-"+(alignement.local.nom||"appareil").toLowerCase().replace(/[^a-z0-9]+/g,"-"))} disabled={activBusy} style={{width:"100%",background:"#fff",color:D,border:"1.5px solid #ddd",borderRadius:10,padding:"11px",cursor:"pointer",fontWeight:700,fontSize:13,fontFamily:"inherit",marginBottom:14}}>💾 Enregistrer une copie de « {alignement.local.nom||"cet appareil"} »</button>
          <button onClick={garderDonneesIci} disabled={activBusy} style={{background:"transparent",border:"none",color:"#b45309",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit",textDecoration:"underline"}}>Ces données sont bien à {alignement.compte||"ce compte"}</button>
          {alignement.serveur&&<div style={{fontSize:10.5,color:"#aaa",marginTop:3}}>Elles remplaceront alors la sauvegarde en ligne.</div>}
          <div><button onClick={annulerAlignement} disabled={activBusy} style={{background:"transparent",border:"none",color:"#999",fontSize:12,cursor:"pointer",marginTop:12,fontFamily:"inherit"}}>← Annuler</button></div>
        </>:appareilsOccupes?<>
          {/* La limite d'appareils est atteinte : on montre lesquels, et on laisse
              en libérer un. Un téléphone cassé ne doit bloquer personne. */}
          <div style={{textAlign:"left",fontSize:12.5,color:"#92400e",background:"#fffbeb",border:"1px solid #fde68a",borderRadius:9,padding:"11px 13px",marginBottom:13,lineHeight:1.6}}>{appareilsOccupes.erreur}</div>
          {(appareilsOccupes.appareils||[]).map(ap=>(
            <div key={ap.id} style={{display:"flex",alignItems:"center",gap:9,border:"1.5px solid #eee",borderRadius:9,padding:"10px 12px",marginBottom:8,textAlign:"left"}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontWeight:700,fontSize:13,color:D}}>{ap.nom||"Appareil"}</div>
                <div style={{fontSize:10.5,color:"#999"}}>dernier accès : {(ap.dernierAcces||"").slice(0,10)}</div>
              </div>
              <button onClick={()=>libererAppareil(ap.id)} disabled={activBusy} style={{background:"#fef2f2",color:"#b91c1c",border:"1px solid #fca5a5",borderRadius:7,padding:"7px 11px",cursor:"pointer",fontWeight:800,fontSize:11.5,fontFamily:"inherit",flexShrink:0}}>Libérer</button>
            </div>
          ))}
          {keyErr&&<div style={{color:"#ef4444",fontSize:12,marginBottom:8,textAlign:"left"}}>{keyErr}</div>}
          <button onClick={()=>{setAppareilsOccupes(null);setKeyErr("");}} style={{background:"transparent",border:"none",color:"#999",fontSize:12,cursor:"pointer",marginTop:6,fontFamily:"inherit"}}>← Retour</button>
        </>:modeActiv==="connexion"?<>
          <div style={{textAlign:"left",marginBottom:9}}>
            <label style={sl}>Numéro de téléphone</label>
            <input value={insc.tel} onChange={e=>setInsc(f=>({...f,tel:e.target.value}))} style={si} inputMode="tel"/>
          </div>
          <div style={{textAlign:"left",marginBottom:9}}>
            <label style={sl}>Mot de passe</label>
            <input type="password" value={insc.mdp} onChange={e=>setInsc(f=>({...f,mdp:e.target.value}))} onKeyDown={e=>e.key==="Enter"&&seConnecter()} style={si}/>
          </div>
          {keyErr&&<div style={{color:"#ef4444",fontSize:12,marginBottom:8,textAlign:"left",lineHeight:1.55}}>{keyErr}</div>}
          <button onClick={seConnecter} disabled={activBusy} style={{background:activBusy?"#e8e0cc":G,color:D,border:"none",padding:13,borderRadius:10,cursor:activBusy?"default":"pointer",fontWeight:800,fontSize:15,width:"100%",marginTop:6,fontFamily:"inherit"}}>{activBusy?"Connexion…":"Se connecter"}</button>
          <button onClick={mdpOublie} style={{background:"transparent",border:"none",color:"#2563eb",fontSize:12.5,fontWeight:700,cursor:"pointer",marginTop:12,fontFamily:"inherit",textDecoration:"underline"}}>Mot de passe oublié ?</button>
          <div style={{fontSize:11,color:"#aaa",marginTop:11,lineHeight:1.55}}>Fonctionne sur votre téléphone comme sur votre ordinateur.</div>
        </>:etapeActiv===1?<>
          <label style={{...sl,textAlign:"left"}}>Clé de licence</label><input value={keyIn} onChange={e=>setKeyIn(e.target.value)} onKeyDown={e=>e.key==="Enter"&&verifierCle()} style={{...si,letterSpacing:"2px",textAlign:"center",fontSize:15,fontFamily:"monospace",border:"2px solid #e5e7eb",marginBottom:6}}/>
          {keyErr&&<div style={{color:"#ef4444",fontSize:12,marginBottom:7}}>{keyErr}</div>}
          <button onClick={verifierCle} style={{background:G,color:D,border:"none",padding:13,borderRadius:10,cursor:"pointer",fontWeight:800,fontSize:15,width:"100%",marginTop:6,fontFamily:"inherit"}}>Continuer →</button>
        </>:<>
          {/* La clé se rattache à une boutique nommée. Sans cette étape, une clé
              passée de main en main ouvrait autant d'applications qu'on voulait. */}
          <div style={{fontSize:12.5,color:"#666",lineHeight:1.65,marginBottom:15,textAlign:"left",background:"#f9f7f0",borderRadius:9,padding:"11px 13px"}}>
            Cette clé va être rattachée à votre boutique. Elle ne pourra plus servir ailleurs.
          </div>
          {[["prenom","Prénom","Awa"],["nom","Nom","Diop"],["boutique","Nom de la boutique","Dane & Frères"],["tel","Téléphone (obligatoire)","77 000 00 00"],["ville","Adresse (facultatif)","Marché HLM, Dakar"]].map(([k,l])=>(
            <div key={k} style={{textAlign:"left",marginBottom:9}}>
              <label style={sl}>{l}</label>
              <input value={insc[k]} onChange={e=>{const v=e.target.value;setInsc(f=>({...f,[k]:k==="tel"?fmtTel(v):mettreEnForme(v,k==="nom"?"nom":k==="prenom"?"prenom":"")}));}} inputMode={k==="tel"?"tel":undefined} style={si}/>
            </div>
          ))}
          <div style={{textAlign:"left",marginBottom:9}}>
            <label style={sl}>Mot de passe (6 caractères minimum)</label>
            <input type="password" value={insc.mdp} onChange={e=>setInsc(f=>({...f,mdp:e.target.value}))} style={si}/>
          </div>
          {keyErr&&<div style={{color:"#ef4444",fontSize:12,marginBottom:8,textAlign:"left",lineHeight:1.55}}>{keyErr}</div>}
          <button onClick={activate} disabled={activBusy} style={{background:activBusy?"#e8e0cc":G,color:D,border:"none",padding:13,borderRadius:10,cursor:activBusy?"default":"pointer",fontWeight:800,fontSize:15,width:"100%",marginTop:6,fontFamily:"inherit"}}>{activBusy?"Activation en cours…":"🔓 Activer ma boutique"}</button>
          <button onClick={()=>{setEtapeActiv(1);setKeyErr("");}} style={{background:"transparent",border:"none",color:"#999",fontSize:12,cursor:"pointer",marginTop:10,fontFamily:"inherit"}}>← Changer de clé</button>
        </>}
      </div>
    </div>
  );

  return(
    <div style={{fontFamily:"'Segoe UI',system-ui,sans-serif",background:"#f4f2ec",height:"100%",overflow:"hidden",display:"flex",fontSize:14}}>

      {confirm&&<div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:500,display:"flex",alignItems:"center",justifyContent:"center"}}>
        <div style={{background:"#fff",borderRadius:14,padding:"26px 30px",maxWidth:340,width:"90%",textAlign:"center",boxShadow:"0 10px 40px rgba(0,0,0,0.15)"}}>
          <div style={{fontSize:42,marginBottom:10}}>⚠️</div>
          <div style={{fontSize:14,fontWeight:600,color:D,marginBottom:18,lineHeight:1.5}}>{confirm.msg}</div>
          <div style={{display:"flex",gap:9,justifyContent:"center"}}>
            <button onClick={()=>setConfirm(null)} style={{padding:"9px 22px",borderRadius:8,border:"1.5px solid #e0e0e0",background:"#fff",cursor:"pointer",fontWeight:600,fontFamily:"inherit"}}>Annuler</button>
            <button onClick={confirm.fn} style={{padding:"9px 22px",borderRadius:8,border:"none",background:"#ef4444",color:"#fff",cursor:"pointer",fontWeight:700,fontFamily:"inherit"}}>Supprimer</button>
          </div>
        </div>
      </div>}
      {pont&&<div onClick={e=>{if(e.target===e.currentTarget)fermerPont();}} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:650,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
        <div style={{background:"#fff",borderRadius:16,width:"100%",maxWidth:370,padding:"24px 22px",textAlign:"center",boxShadow:"0 20px 60px rgba(0,0,0,0.25)"}}>
          <div style={{fontSize:16,fontWeight:900,color:D,marginBottom:6}}>📱 Photo depuis le téléphone</div>
          {pont.etat==="demande"&&<div style={{fontSize:13,color:"#888",padding:"26px 0"}}>Préparation du lien…</div>}
          {pont.etat==="horsligne"&&<div style={{fontSize:13,color:"#555",lineHeight:1.7,padding:"14px 0"}}>
            Cette fonction passe par votre sauvegarde en ligne TOBY.<br/><br/>
            Ouvrez <strong>⚙️ Paramètres → Sauvegarde en ligne</strong> et connectez-vous, puis revenez ici.
          </div>}
          {pont.etat==="erreur"&&<div style={{fontSize:13,color:"#991b1b",background:"#fef2f2",border:"1px solid #fca5a5",borderRadius:9,padding:"12px 14px",margin:"12px 0",lineHeight:1.6}}>{pont.erreur}</div>}
          {pont.etat==="expire"&&<div style={{fontSize:13,color:"#92400e",background:"#fffbeb",border:"1px solid #fde68a",borderRadius:9,padding:"12px 14px",margin:"12px 0",lineHeight:1.6}}>Le lien a expiré sans photo. Refermez et recommencez.</div>}
          {pont.etat==="attente"&&<>
            <div style={{fontSize:12.5,color:"#666",lineHeight:1.65,marginBottom:14}}>Scannez ce code avec l'appareil photo de votre téléphone, puis choisissez la photo dans votre galerie.</div>
            {pont.qr
              ?<img src={pont.qr} alt="QR code" style={{width:230,height:230,display:"block",margin:"0 auto",borderRadius:10,border:"1px solid #eee"}}/>
              :<div style={{fontSize:11,color:"#888",wordBreak:"break-all",padding:12,background:"#f7f4ee",borderRadius:9}}>{pont.lien}</div>}
            <div style={{marginTop:14,fontSize:12,color:"#0369a1",background:"#eff6ff",border:"1px solid #bae6fd",borderRadius:9,padding:"10px 13px",fontWeight:600}}>⏳ J'attends la photo…</div>
            <div style={{marginTop:9,fontSize:10.5,color:"#aaa",lineHeight:1.6}}>Le lien vaut un quart d'heure et ne donne accès à rien d'autre.</div>
          </>}
          <button onClick={fermerPont} style={{marginTop:16,width:"100%",padding:"11px",borderRadius:9,border:"1.5px solid #e0e0e0",background:"#fff",cursor:"pointer",fontWeight:700,fontFamily:"inherit",fontSize:13}}>Fermer</button>
        </div>
      </div>}
      {stockApercu&&<div onClick={()=>setStockApercu(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.82)",zIndex:700,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:14,padding:18,cursor:"zoom-out"}}>
        <img src={stockApercu.src} alt="" style={{maxWidth:"94vw",maxHeight:"80vh",objectFit:"contain",borderRadius:10,boxShadow:"0 18px 60px rgba(0,0,0,0.5)"}}/>
        <div style={{color:"#fff",fontWeight:800,fontSize:15}}>{stockApercu.titre}</div>
      </div>}
      {voirOffres&&<OffresBloc cle={licKey} atelier={atelierInfo.nom} onFermer={()=>setVoirOffres(false)}/>}

      {/* DÉTAIL DES DÉPENSES SUR COMMANDES — par mois, puis par commande */}
      {detailCouts&&(()=>{
        const mois=Object.keys(coutsCommande.parMois).sort().reverse();
        const nomMois=(k)=>{try{return new Date(k+"-01T12:00:00").toLocaleDateString("fr-FR",{month:"long",year:"numeric"});}catch{return k;}};
        const ouvert=moisCoutsOuvert||mois[0];
        return(<div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:600,display:"flex",alignItems:"center",justifyContent:"center",padding:14}} onClick={e=>{if(e.target===e.currentTarget)setDetailCouts(false);}}>
          <div style={{background:"#fff",borderRadius:16,width:"100%",maxWidth:560,maxHeight:"90vh",display:"flex",flexDirection:"column",overflow:"hidden",boxShadow:"0 20px 60px rgba(0,0,0,0.25)"}}>
            {/* En-tête */}
            <div style={{background:"linear-gradient(135deg,#9a3412,#c2410c)",color:"#fff",padding:"18px 20px"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12}}>
                <div>
                  <div style={{fontSize:16,fontWeight:900}}>💸 Argent dépensé sur les commandes</div>
                  <div style={{fontSize:11,color:"#fed7aa",marginTop:3}}>Ce que vous avez déboursé pour réaliser chaque commande</div>
                </div>
                <button onClick={()=>setDetailCouts(false)} style={{background:"rgba(255,255,255,0.18)",border:"none",borderRadius:9,width:34,height:34,cursor:"pointer",color:"#fff",fontSize:17,fontFamily:"inherit",flexShrink:0}}>×</button>
              </div>
              <div style={{fontSize:26,fontWeight:900,marginTop:10}}>{fmt(coutsCommande.total)} F</div>
            </div>

            {/* Les mois */}
            <div style={{overflowY:"auto",padding:"12px 14px 16px"}}>
              {mois.length===0&&<div style={{textAlign:"center",color:"#bbb",padding:36,fontSize:13}}>Aucune dépense saisie sur les commandes.</div>}
              {mois.map(k=>{
                const lignes=coutsCommande.parMois[k];
                const totalMois=lignes.reduce((t,x)=>t+x.montant,0);
                const estOuvert=ouvert===k;
                return(<div key={k} style={{marginBottom:9,border:"1px solid #f0ece4",borderRadius:11,overflow:"hidden"}}>
                  <button onClick={()=>setMoisCoutsOuvert(estOuvert?"__aucun":k)} style={{width:"100%",background:estOuvert?"#fff7ed":"#fafafa",border:"none",padding:"12px 14px",cursor:"pointer",display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,fontFamily:"inherit",textAlign:"left"}}>
                    <div>
                      <div style={{fontSize:13,fontWeight:800,color:D,textTransform:"capitalize"}}>{nomMois(k)}</div>
                      <div style={{fontSize:10.5,color:"#aaa",marginTop:2}}>{lignes.length} commande{lignes.length>1?"s":""}</div>
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:9}}>
                      <span style={{fontSize:15,fontWeight:900,color:"#ea580c"}}>{fmt(totalMois)} F</span>
                      <span style={{color:"#ccc",fontSize:13}}>{estOuvert?"▲":"▼"}</span>
                    </div>
                  </button>

                  {estOuvert&&<div style={{background:"#fff"}}>
                    {lignes.map(c=>(
                      <div key={c.id} style={{borderTop:"1px solid #f5f2ec",padding:"11px 14px"}}>
                        <div onClick={()=>{setDetailCouts(false);setDetail(c.commande);}} style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,cursor:"pointer"}}>
                          <div style={{minWidth:0}}>
                            <div style={{fontSize:13,fontWeight:700,color:D}}>{c.nom}</div>
                            <div style={{fontSize:10.5,color:"#aaa",marginTop:2}}>
                              <span style={{background:"#f0ece4",padding:"1px 7px",borderRadius:5,fontWeight:700,color:"#8a7f66"}}>{c.cmdNum}</span>
                              {c.date&&<span style={{marginLeft:7}}>{c.date}</span>}
                            </div>
                          </div>
                          <div style={{fontSize:14,fontWeight:900,color:"#ea580c",whiteSpace:"nowrap"}}>{fmt(c.montant)} F</div>
                        </div>
                        {/* Le détail des postes de dépense */}
                        <div style={{marginTop:7,paddingLeft:4}}>
                          {c.lignes.map((x,i)=>(
                            <div key={i} style={{display:"flex",justifyContent:"space-between",fontSize:11.5,color:"#777",padding:"3px 0"}}>
                              <span>• {x.l}</span><span style={{fontWeight:600}}>{fmt(x.m)} F</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>}
                </div>);
              })}
              <div style={{fontSize:10.5,color:"#bbb",textAlign:"center",marginTop:10,lineHeight:1.6}}>Touchez une commande pour l'ouvrir.<br/>Ces montants sont déjà retirés de votre bénéfice net.</div>
            </div>
          </div>
        </div>);
      })()}
      {showSettings&&<SettingsModal onClose={()=>{setShowSettings(false);setSettingsTab(null);}} initialTab={settingsTab} onBackupDone={()=>setNudgeBackup(false)} atelierInfo={atelierInfo} onSaveAtelier={handleSaveAtelier} isPro={IS_PRO} colMeta={colMeta} colPhotos={colPhotos} trash={trash} onRestoreTrash={restoreTrash} onPurgeTrash={purgeTrash} pinHash={pinHash} onSetPin={setCaissePin} onRemovePin={removeCaissePin} onVerifyPin={verifyPin} licKey={licKey} onLogout={seDeconnecter}
        cloud={{token:cloudToken,info:cloudInfo,last:cloudLast,state:cloudState,creerCompte:cloudCreerCompte,connexion:cloudConnexion,deconnexion:cloudDeconnexion,envoyer:cloudEnvoyer,restaurer:cloudRestaurer,changerMdp:cloudChangerMdp,envoiPhotos:cloudEnvoiPhotos,recupPhotos:cloudRecupPhotos}}/>}
      {detail&&<DetailPanel detail={detail} onClose={()=>setDetail(null)} onEdit={openEditO} onDelete={delO} onUpdSt={updSt} onPrint={showReceipt} photoDe={IS_GROSSISTE?photoLigne:null} prixDe={IS_GROSSISTE?(t=>prixLigne(t,stock)):null} onEncaisser={IS_GROSSISTE?(o=>ouvrirEncaissement(cleCredit(o))):null} clients={clients} onOpenClient={c=>{setDetail(null);setClientDetail(c);}} atelierInfo={atelierInfo} isPro={IS_PRO}/>}
      {clientDetail&&<ClientPanel client={clientDetail} orders={orders} onClose={()=>setClientDetail(null)} onEdit={openEditClient} onDelete={delClient} onNewOrder={newOrderFromClient} onOpenOrder={o=>{setClientDetail(null);setDetail(o);}} atelierInfo={atelierInfo} isPro={IS_PRO} isGrossiste={isGrossiste}/>}
      {receiptHtml&&<ReceiptModal html={receiptHtml.html} order={receiptHtml.order} fileName={receiptHtml.name} titre={receiptHtml.titre} atelierInfo={atelierInfo} isGrossiste={isGrossiste} onClose={()=>setReceiptHtml(null)}/>}
      {/* BANDEAU LICENCE ESSAI/ABONNEMENT */}
      {licenceInfo&&(()=>{
        const j=licenceInfo.joursRestants;
        const expired=licenceInfo.expired;
        const couleur=expired?"#ef4444":j<=3?"#ef4444":j<=7?"#f59e0b":"#10b981";
        if(expired)return(<div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:9000,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{background:"#fff",border:"3px solid #ef4444",borderRadius:16,padding:"28px 36px",boxShadow:"0 20px 60px rgba(0,0,0,0.4)",maxWidth:480,textAlign:"center"}}>
            <div style={{fontSize:54,marginBottom:10}}>⛔</div>
            <div style={{fontSize:18,fontWeight:900,color:"#991b1b",marginBottom:10}}>Licence expirée</div>
            <div style={{fontSize:13,color:"#555",lineHeight:1.7,marginBottom:14}}>Votre {licenceInfo.plan==="ESSAI"?"période d'essai gratuite":"abonnement"} a expiré le <strong>{licenceInfo.dateExpiration}</strong>.<br/><strong style={{color:"#166534"}}>Vos données sont intactes.</strong> Enregistrez-les d'abord ci-dessous, puis contactez TOBY pour renouveler.</div>
            <button onClick={async()=>{
              try{
                if(window.electronAPI?.backup?.export){const r=await window.electronAPI.backup.export();if(r&&r.success)alert("✅ Données exportées avec succès.");}
                else{const res=await shareOrDownloadBackup("toby-sauvegarde");if(res!=="cancel")alert("✅ Sauvegarde enregistrée. Gardez bien ce fichier — il contient toutes vos données.");}
              }catch{alert("❌ Erreur pendant la sauvegarde. Réessayez.");}
            }} style={{display:"block",width:"100%",marginBottom:12,background:"#166534",color:"#fff",border:"none",padding:"13px 22px",borderRadius:9,cursor:"pointer",fontWeight:800,fontSize:14,fontFamily:"inherit"}}>💾 Enregistrer mes données (recommandé)</button>
            {/* Paiement de l'abonnement — Wave, Orange Money, confirmation WhatsApp */}
            <div style={{marginBottom:12}}><PaiementBloc cle={licKey} atelier={atelierInfo.nom} compact/></div>
            <a href={"https://wa.me/221777533153?text="+encodeURIComponent("Bonjour TOBY, j'ai une question sur mon abonnement "+APP_NAME+".")} target="_blank" rel="noreferrer" style={{display:"block",marginTop:10,color:"#888",fontSize:11.5,fontFamily:"inherit",textAlign:"center",textDecoration:"underline"}}>Une question ? Écrire à TOBY sur WhatsApp</a>
            {/* Compte TOBY Cloud : après paiement, le client se débloque lui-même */}
            {cloudToken&&<button onClick={async()=>{
              const r=await cloudAppel("/api/licence",{jeton:cloudToken});
              if(r.ok&&!r.data.expire){cloudAppliqueLicence(r.data);alert("✅ Licence renouvelée jusqu'au "+r.data.dateExpiration+" ! Bon travail.");}
              else if(r.ok)alert("⏳ Votre licence n'est pas encore renouvelée. Contactez TOBY après votre paiement.");
              else alert("❌ "+(r.erreur||"Vérification impossible"));
            }} style={{marginTop:10,background:"#2563eb",color:"#fff",border:"none",padding:"12px 22px",borderRadius:9,cursor:"pointer",fontWeight:800,fontSize:13,fontFamily:"inherit",width:"100%"}}>🔄 J'ai payé — vérifier ma licence</button>}
            {IS_WEB&&<button onClick={()=>{
              const input=document.createElement("input");
              input.type="file";input.accept="application/json,.json";
              input.onchange=async()=>{
                const f=input.files&&input.files[0];
                if(!f)return;
                try{
                  const lic=JSON.parse(await f.text());
                  if(!lic.dateExpiration||!lic.cle||!validKey(lic.cle)){alert("❌ Fichier de licence invalide.");return;}
                  const exp=new Date(lic.dateExpiration+"T23:59:59");
                  if(Date.now()>exp.getTime()){alert("❌ Cette licence est déjà expirée ("+lic.dateExpiration+").");return;}
                  await window.storage.set("cp_licence",JSON.stringify(lic));
                  alert("✅ Licence activée jusqu'au "+lic.dateExpiration+" !");
                  window.location.reload();
                }catch{alert("❌ Fichier illisible.");}
              };
              input.click();
            }} style={{marginTop:14,background:"#10b981",color:"#fff",border:"none",padding:"12px 22px",borderRadius:9,cursor:"pointer",fontWeight:800,fontSize:13,fontFamily:"inherit",width:"100%"}}>📂 Importer une licence de renouvellement</button>}
          </div>
        </div>);
        return(<div style={{position:"fixed",bottom:12,left:"50%",transform:"translateX(-50%)",zIndex:80,background:couleur+"15",border:`1px solid ${couleur}`,borderRadius:20,padding:"7px 14px",boxShadow:"0 4px 14px rgba(0,0,0,0.1)",display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontSize:14}}>{j<=3?"⚠️":j<=7?"⏳":"🎁"}</span>
          <div style={{fontSize:12,fontWeight:800,color:couleur}}>{j===0?`${licenceInfo.plan==="ESSAI"?"Essai":"Abonnement"} · expire aujourd'hui`:licenceInfo.plan==="ESSAI"?`Essai gratuit · ${j} jour${j>1?"s":""} restant${j>1?"s":""}`:`Abonnement · ${j} jours restants`}</div>
        </div>);
      })()}

      {/* NAVIGATION — sidebar (desktop) OU header + bottom nav + FAB (mobile/tablette) */}
      {(()=>{
        // Sur Stock et Caisse, les tableaux ont une colonne Actions a droite : les
        // boutons flottants la recouvraient. Ces pages ont leur propre bouton d ajout.
        const SANS_FAB=(view==="stock"||view==="caisse"||view==="credit");
        // Crédit juste avant Caisse : on regarde ce qui est dû, puis ce qui est encaissé.
        // Tissi-App porte les images choisies par le patron ; CouturePro garde ses émojis.
        const ic=(img,emo)=>IS_GROSSISTE?<IconeRubrique src={img}/>:emo;
        const navItems=[["dashboard","📊","Tableau de bord","Accueil"],["clients","👥","Clients","Clients"],["orders","🧾",LBL.sales,LBL.sales],["collection",ic("collection.png","👗"),"Collection","Collection"],["stock",ic("stock.jpg","📦"),"Stock","Stock"],...(IS_GROSSISTE?[["credit",<IconeCredit t={18}/>,"Crédit","Crédit"]]:[]),["caisse","💰","Caisse","Caisse"]];
        // Pastille rouge : les clients dont le jour de paiement promis est arrivé.
        const pastille=id=>id==="credit"?credits.dus.length:0;
        if(!isMobile)return(
          <div style={{width:195,background:D,display:"flex",flexDirection:"column",position:"fixed",top:0,left:0,bottom:0,zIndex:100}}>
            <div style={{padding:"20px 16px 14px",borderBottom:"1px solid #2a2a4a"}}>
              <div style={{fontSize:17,fontWeight:900,color:G,letterSpacing:"-0.5px",display:"flex",alignItems:"center",gap:6}}>
                {IS_GROSSISTE?<LogoPastille h={10}/>:<span>✂️ {APP_NAME}</span>}
                {APP_BADGE&&<span style={{background:"linear-gradient(135deg,#fbbf24,#f59e0b)",color:"#451a03",fontSize:8,fontWeight:900,padding:"2px 6px",borderRadius:4,letterSpacing:0.5}}>{APP_BADGE}</span>}
              </div>
              {/* Le logo est devenu une petite etiquette : un sous-titre ecrit en
                  dessous serait plus large que le nom lui-meme et volerait l'oeil.
                  « GESTION DE TISSUS » reste sur l'ecran de connexion, ou il y a
                  la place de le dire. */}
              {!IS_GROSSISTE&&<div style={{fontSize:9,color:"#444",marginTop:2,letterSpacing:"1.5px",fontWeight:700}}>{APP_SUBTITLE}</div>}
            </div>
            <nav style={{padding:"10px 0",flex:1}}>
              {navItems.map(([id,ic,lb])=>(
                <button key={id} onClick={()=>setView(id)} style={{background:view===id?G:"transparent",color:view===id?D:"#9ca3af",border:"none",padding:"10px 14px",textAlign:"left",cursor:"pointer",fontWeight:view===id?700:400,fontSize:13,borderRadius:7,margin:"2px 7px",width:"calc(100% - 14px)",fontFamily:"inherit",display:"flex",alignItems:"center",gap:9}}><span style={{fontSize:15}}>{ic}</span>{lb}{pastille(id)>0&&<span style={{marginLeft:"auto",background:"#dc2626",color:"#fff",fontSize:10,fontWeight:800,borderRadius:10,padding:"1px 7px",lineHeight:"16px"}}>{pastille(id)}</span>}</button>
              ))}
            </nav>
            <div style={{padding:"8px 9px 13px",borderTop:"1px solid #2a2a4a"}}>
              <button onClick={openNewO} style={{background:G,color:D,border:"none",padding:"10px",borderRadius:8,cursor:"pointer",fontWeight:800,width:"100%",fontSize:12,fontFamily:"inherit"}}>＋ {LBL.newSale}</button>
              {!isGrossiste&&<button onClick={openNewExpress} title="Créer une commande avec seulement l'essentiel" style={{background:"transparent",color:G,border:"1.5px dashed "+G,padding:"8px",borderRadius:8,cursor:"pointer",fontWeight:700,width:"100%",fontSize:11,fontFamily:"inherit",marginTop:7}}>⚡ Commande rapide</button>}
              <button onClick={()=>setShowSettings(true)} style={{background:"transparent",color:"#9ca3af",border:"none",padding:"7px 10px",cursor:"pointer",width:"100%",fontSize:11,fontFamily:"inherit",marginTop:6,textAlign:"left",borderRadius:6}}>⚙️ Paramètres</button>
            </div>
          </div>
        );
        return(<>
          {/* HEADER MOBILE */}
          {/* Bandeau bleu nuit, sous l'horloge du téléphone. 46 px et non 56 :
              la place prise par l'horloge et la batterie s'ajoute par-dessus et
              ne se récupère pas, autant ne pas en perdre davantage. */}
          <div style={{position:"fixed",top:0,left:0,right:0,height:"calc(46px + env(safe-area-inset-top))",background:D,display:"flex",alignItems:"center",justifyContent:"space-between",padding:"env(safe-area-inset-top) 14px 0",zIndex:100,boxShadow:"0 2px 12px rgba(0,0,0,0.25)"}}>
            <div style={{display:"flex",alignItems:"center",gap:7}}>
              {IS_GROSSISTE?<LogoPastille h={9}/>:<span style={{fontSize:16,fontWeight:900,color:G,letterSpacing:"-0.3px"}}>✂️ {APP_NAME}</span>}
              {APP_BADGE&&<span style={{background:"linear-gradient(135deg,#fbbf24,#f59e0b)",color:"#451a03",fontSize:8,fontWeight:900,padding:"2px 6px",borderRadius:4,letterSpacing:0.5}}>{APP_BADGE}</span>}
            </div>
            <button onClick={()=>setShowSettings(true)} style={{background:"rgba(255,255,255,0.08)",border:"none",borderRadius:9,width:34,height:34,cursor:"pointer",fontSize:16,fontFamily:"inherit",color:"#fff"}}>⚙️</button>
          </div>
          {/* BARRE DES RUBRIQUES — une pastille flottante, à la manière de WhatsApp.
              Elle ne touche plus les bords : elle se pose au-dessus du contenu,
              translucide, et laisse deviner ce qui défile dessous. Le flou
              derrière (backdrop-filter) est ce qui la rend lisible malgré la
              transparence ; là où il n'existe pas, le fond reste simplement
              plus opaque et tout fonctionne pareil.
              La rubrique ouverte est portée par sa propre pastille claire,
              au lieu d'un trait au-dessus : plus large, donc plus facile à
              viser du pouce. */}
          <div style={{position:"fixed",bottom:"calc(9px + "+BAS_BARRE+")",left:9,right:9,zIndex:100,
            background:"rgba(26,26,46,0.78)",backdropFilter:"blur(18px) saturate(150%)",WebkitBackdropFilter:"blur(18px) saturate(150%)",
            border:"1px solid rgba(255,255,255,0.09)",borderRadius:26,display:"flex",padding:"5px 4px",
            boxShadow:"0 8px 26px rgba(0,0,0,0.34)"}}>
            {navItems.map(([id,ic,,short])=>{
              const ouvert=view===id;
              return(
              <button key={id} onClick={()=>setView(id)} style={{flex:1,minWidth:0,background:ouvert?"rgba(255,255,255,0.13)":"transparent",border:"none",borderRadius:20,padding:"7px 1px 6px",cursor:"pointer",fontFamily:"inherit",display:"flex",flexDirection:"column",alignItems:"center",gap:3,transition:"background .18s"}}>
                <span style={{position:"relative",display:"inline-block",lineHeight:0}}>
                  <span style={{fontSize:22,lineHeight:1,display:"inline-block",filter:ouvert?"none":"grayscale(0.55) opacity(0.62)",transform:ouvert?"scale(1.06)":"none",transition:"transform .18s, filter .18s"}}>{ic}</span>
                  {pastille(id)>0&&<span style={{position:"absolute",top:-5,right:-11,background:"#dc2626",color:"#fff",fontSize:9,fontWeight:800,borderRadius:8,minWidth:15,height:15,lineHeight:"15px",textAlign:"center",padding:"0 3px",boxSizing:"border-box",border:"1.5px solid rgba(26,26,46,0.9)"}}>{pastille(id)}</span>}
                </span>
                <span style={{fontSize:9.5,fontWeight:ouvert?800:600,color:ouvert?G:"#aeb4c0",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",maxWidth:"100%"}}>{short}</span>
              </button>);
            })}
          </div>
          {/* FAB — bouton flottant nouvelle commande/vente */}
          {!isGrossiste&&!SANS_FAB&&<button onClick={openNewExpress} title="Commande rapide" style={{position:"fixed",right:22,bottom:"calc(156px + "+BAS_BARRE+")",width:46,height:46,borderRadius:"50%",background:"#fff",color:D,border:"2px solid "+G,fontSize:19,cursor:"pointer",zIndex:110,boxShadow:"0 4px 14px rgba(0,0,0,0.18)",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center"}}>⚡</button>}
          {!SANS_FAB&&<button onClick={openNewO} title={LBL.newSale} style={{position:"fixed",right:16,bottom:"calc(88px + "+BAS_BARRE+")",width:58,height:58,borderRadius:"50%",background:`linear-gradient(135deg,${G},#f5c542)`,color:D,border:"none",fontSize:28,fontWeight:900,cursor:"pointer",zIndex:110,boxShadow:"0 6px 20px rgba(212,160,23,0.45)",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center"}}>＋</button>}
        </>);
      })()}

      {/* MAIN */}
      <FlecheHaut cible={mainRef} fenetre bas={isMobile?"calc(158px + "+BAS_BARRE+")":28}/>
      <div ref={mainRef} style={{marginLeft:isMobile?0:195,flex:1,padding:isMobile?"calc(58px + env(safe-area-inset-top)) 12px calc(112px + "+BAS_BARRE+")":22,overflowY:"auto"}}>

        {/* ── DASHBOARD ── */}
        {view==="dashboard"&&<div>
          {/* INVITATION SAUVEGARDE EN LIGNE — Premium non connecté au cloud.
              Sans ça, le client ne découvrirait jamais la fonction (elle est dans les Réglages). */}
          {!cloudToken&&(orders.length+clients.length)>0&&<div style={{background:"linear-gradient(135deg,#eff6ff,#dbeafe)",border:"1px solid #93c5fd",borderRadius:12,padding:"13px 15px",marginBottom:16,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
            <span style={{fontSize:26,flexShrink:0}}>☁️</span>
            <div style={{flex:"1 1 200px",minWidth:0}}>
              <div style={{fontSize:13,fontWeight:800,color:"#1e40af"}}>Activez la sauvegarde en ligne</div>
              <div style={{fontSize:11,color:"#1d4ed8",marginTop:2,lineHeight:1.5}}>Vos données seront copiées automatiquement chez TOBY. Même téléphone perdu ou cassé, vous récupérez tout.</div>
            </div>
            <button onClick={()=>{setSettingsTab("backup");setShowSettings(true);}} style={{background:"#2563eb",color:"#fff",border:"none",padding:"9px 14px",borderRadius:8,cursor:"pointer",fontWeight:800,fontSize:12,fontFamily:"inherit",flexShrink:0}}>Activer</button>
          </div>}
          {/* RAPPEL SAUVEGARDE — aucune sauvegarde depuis 7 jours (protège contre la perte de données) */}
          {nudgeBackup&&<div style={{background:"linear-gradient(135deg,#fff7ed,#ffedd5)",border:"1px solid #fdba74",borderRadius:12,padding:"13px 15px",marginBottom:16,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
            <span style={{fontSize:26,flexShrink:0}}>💾</span>
            <div style={{flex:"1 1 200px",minWidth:0}}>
              <div style={{fontSize:13,fontWeight:800,color:"#9a3412"}}>Pense à sauvegarder tes données</div>
              <div style={{fontSize:11,color:"#9a3412",marginTop:2,lineHeight:1.5}}>Aucune sauvegarde depuis plus de 7 jours. Enregistre une copie (WhatsApp, Drive…) pour ne rien perdre.</div>
            </div>
            <div style={{display:"flex",gap:7,flexShrink:0}}>
              <button onClick={doQuickBackup} style={{background:"#ea580c",color:"#fff",border:"none",padding:"9px 14px",borderRadius:8,cursor:"pointer",fontWeight:800,fontSize:12,fontFamily:"inherit"}}>💾 Sauvegarder</button>
              <button onClick={snoozeBackup} style={{background:"transparent",color:"#9a3412",border:"1px solid #fdba74",padding:"9px 12px",borderRadius:8,cursor:"pointer",fontWeight:700,fontSize:12,fontFamily:"inherit"}}>Plus tard</button>
            </div>
          </div>}
          <div style={{background:"#fff",borderRadius:14,padding:"16px 20px",marginBottom:20,display:"flex",alignItems:"center",gap:16,boxShadow:"0 2px 12px rgba(0,0,0,0.06)",border:"1px solid #f0ece4"}}>
            {atelierInfo.logoImage
              ?<img src={atelierInfo.logoImage} alt="" style={{width:54,height:54,borderRadius:13,flexShrink:0,objectFit:"contain",background:"#fff",boxShadow:"0 2px 8px rgba(0,0,0,0.12)"}}/>
              :atelierInfo.logoStyle
              ?<img src={logoDataURL(atelierInfo.nom||"Mon Atelier",atelierInfo.logoStyle,atelierInfo.logoPalette)} alt="" style={{width:54,height:54,borderRadius:13,flexShrink:0,boxShadow:"0 2px 8px rgba(0,0,0,0.12)"}}/>
              :<div style={{width:52,height:52,background:D,borderRadius:13,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,color:G,fontSize:19,fontWeight:900,letterSpacing:1}}>{(atelierInfo.nom||"").trim().split(/s+/).filter(Boolean).slice(0,2).map(m=>m[0]).join("").toUpperCase()||"📦"}</div>}
            <div style={{flex:1}}>
              <div style={{fontSize:20,fontWeight:900,color:D,letterSpacing:"-0.3px"}}>{atelierInfo.nom||"Ma Boutique de Tissus"}</div>
              <div style={{display:"flex",gap:14,marginTop:4,flexWrap:"wrap"}}>
                <span style={{fontSize:12,color:"#aaa"}}>📍 {atelierInfo.adresse||"Dakar, Sénégal"}</span>
                {atelierInfo.tel&&<span style={{fontSize:12,color:"#aaa"}}>📞 {atelierInfo.tel}</span>}
                {atelierInfo.email&&<span style={{fontSize:12,color:"#aaa"}}>✉️ {atelierInfo.email}</span>}
              </div>
            </div>
          </div>
          {/* CRÉDITS À RELANCER — le jour promis, la dette remonte d'elle-même en tête */}
          {isGrossiste&&credits.dus.length>0&&<div onClick={()=>{setCreditFiltre("dus");setView("credit");}} style={{background:"#fef2f2",border:"1.5px solid #fca5a5",borderRadius:13,padding:"14px 16px",marginBottom:16,cursor:"pointer"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,flexWrap:"wrap",marginBottom:9}}>
              <div style={{fontSize:14,fontWeight:800,color:"#991b1b"}}><IconeCredit t={17}/> {credits.dus.length} paiement{credits.dus.length>1?"s":""} de crédit attendu{credits.dus.length>1?"s":""}</div>
              <div style={{fontSize:15,fontWeight:900,color:"#dc2626"}}>{mfmt(fmt(credits.totalDu)+" F")}</div>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {credits.dus.slice(0,4).map(g=>(<div key={g.cle} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",background:"#fff",borderRadius:8,fontSize:12,border:"1px solid #fecaca"}}>
                <span style={{fontWeight:700,flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{g.nom}</span>
                <span style={{color:g.etat==="retard"?"#dc2626":"#b45309",fontWeight:700,fontSize:10.5,flexShrink:0}}>{g.etat==="retard"?`en retard de ${g.jours} j`:"aujourd'hui"}</span>
                <span style={{fontWeight:800,color:D,flexShrink:0}}>{mfmt(fmt(g.reste)+" F")}</span>
              </div>))}
              {credits.dus.length>4&&<div style={{fontSize:10.5,color:"#991b1b",textAlign:"center",fontWeight:600}}>+ {credits.dus.length-4} autre{credits.dus.length-4>1?"s":""} — voir Crédit</div>}
            </div>
          </div>}
          {/* 📌 AUJOURD'HUI — livraisons du jour + retards, en tête pour savoir quoi coudre */}
          {!isGrossiste&&(()=>{
            const todayStr=new Date().toISOString().slice(0,10);
            const duJour=orders.filter(o=>o.dateLivraison===todayStr&&o.statut!=="Livré");
            const enRetard=SUIVI_LIVRAISON?orders.filter(o=>o.dateLivraison&&o.dateLivraison<todayStr&&o.statut!=="Livré").sort((a,b)=>a.dateLivraison.localeCompare(b.dateLivraison)):[];
            if(duJour.length===0&&enRetard.length===0)return null;
            const rowJ=(o,color,pfx)=>(<div key={o.id} onClick={()=>setDetail(migrateTissus(o))} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",background:"#fff",borderRadius:8,cursor:"pointer",fontSize:12,border:"1px solid "+color+"44"}}>
              <span style={{background:G+"22",color:D,fontWeight:800,fontSize:9,padding:"2px 7px",borderRadius:4,flexShrink:0}}>{o.cmdNum}</span>
              <span style={{fontWeight:700,flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{o.prenom} {o.nom}</span>
              <span style={{color,fontWeight:700,fontSize:10,flexShrink:0}}>{pfx}{o.dateLivraison}</span>
              <span style={{background:SC[o.statut]+"22",color:SC[o.statut],fontSize:9,fontWeight:700,padding:"2px 7px",borderRadius:5,flexShrink:0}}>{o.statut}</span>
            </div>);
            return(<div style={{background:enRetard.length?"#fef2f2":"#fffbeb",border:"1.5px solid "+(enRetard.length?"#fca5a5":"#fde68a"),borderRadius:13,padding:"14px 16px",marginBottom:16}}>
              <div style={{fontSize:14,fontWeight:800,color:enRetard.length?"#991b1b":"#92400e",marginBottom:9}}>
                📌 Aujourd'hui{duJour.length>0?` · ${duJour.length} livraison${duJour.length>1?"s":""} du jour`:""}{enRetard.length>0?` · ${enRetard.length} en retard`:""}
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {duJour.map(o=>rowJ(o,"#d97706","📅 "))}
                {enRetard.slice(0,5).map(o=>rowJ(o,"#ef4444","⚠️ "))}
                {enRetard.length>5&&<div style={{fontSize:10,color:"#991b1b",textAlign:"center",fontWeight:600}}>+ {enRetard.length-5} autre{enRetard.length-5>1?"s":""} en retard — voir Commandes</div>}
              </div>
            </div>);
          })()}
          {(()=>{
            const dashItems=isGrossiste?[
              // Chaque carte ouvre la rubrique qui en donne le détail
              {i:"🧾",l:"Ventes",v:orders.length,c:"#3b82f6",go:()=>{setFSt("all");setSearch("");setView("orders");}},
              {i:"📏",l:"Pièces vendues",v:totalPieces,c:"#8b5cf6",go:()=>{setFSt("all");setSearch("");setView("orders");}},
              {i:"👥",l:"Clients",v:clients.length,c:"#0ea5e9",go:()=>setView("clients")},
              {i:"💰",l:"Ventes soldées",v:orders.filter(o=>statutGrossiste(o)==="Soldé").length+" / "+orders.length,c:"#10b981",go:()=>{setFSt("Soldé");setSearch("");setView("orders");}},
              // Une vraie photo de balles empilées : on reconnaît le métier d'un coup d'œil.
              {i:<img src="tissus.jpg" alt="" style={{width:26,height:26,borderRadius:6,objectFit:"cover",display:"block"}}/>,l:"Types de tissus",v:stockStats.nbTypes,c:D,go:()=>{setStockTab("inventaire");setView("stock");}},
              {i:"📦",l:"Pièces en stock",v:fmt(stockStats.nbPieces)+" pièces",c:"#0ea5e9",go:()=>{setStockTab("inventaire");setView("stock");}},
              {i:"⚠️",l:"Stock bas",v:fmt(stockStats.nbBas)+" pièce"+(stockStats.nbBas>1?"s":""),c:"#f59e0b",go:()=>{setStockTab("inventaire");setView("stock");}},
              {i:"🔴",l:"Rupture de stock",v:fmt(stockStats.nbRupture)+" pièce"+(stockStats.nbRupture>1?"s":""),c:"#ef4444",go:()=>{setStockTab("inventaire");setView("stock");}}
            ]:[
              {i:"📋",l:"Commandes",v:orders.length,c:"#3b82f6"},
              {i:"🧵",l:"Pièces",v:totalPieces,c:"#8b5cf6"},
              {i:"👥",l:"Clients",v:clients.length,c:"#0ea5e9"},
              {i:"🛠️",l:"En cours",v:piecesByStatus["En cours"]||0,c:"#f59e0b"},
              {i:"✅",l:"Prêtes",v:piecesByStatus["Prêt"]||0,c:"#10b981"},
              {i:"📦",l:"Livrées",v:piecesByStatus["Livré"]||0,c:"#6b7280"},
              {i:"🚨",l:"Urgentes",v:stats.nbU,c:"#ef4444"}
            ];
            return(<div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:10,marginBottom:20}}>
              {dashItems.map(({i,l,v,c,go})=>(
                <div key={l} onClick={go} role={go?"button":undefined} title={go?"Voir le détail":undefined} style={{background:"#fff",borderRadius:12,padding:"13px 15px",boxShadow:"0 2px 8px rgba(0,0,0,0.05)",borderLeft:"3px solid "+c,cursor:go?"pointer":"default",position:"relative"}}>
                  <div style={{fontSize:20}}>{i}</div>
                  <div style={{fontSize:24,fontWeight:900,color:c,marginTop:6}}>{v}</div>
                  <div style={{fontSize:11,color:"#aaa",marginTop:2}}>{l}</div>
                  {go&&<span style={{position:"absolute",top:11,right:12,fontSize:14,color:"#cbd5e1",fontWeight:800}}>→</span>}
                </div>
              ))}
            </div>);
          })()}
          {/* GROSSISTE — détail pièces restantes par tissu (vue d'ensemble du stock) */}
          {isGrossiste&&stock.length>0&&<div style={{background:"#fff",border:"1px solid #f0f0f0",borderRadius:13,padding:"14px 17px",marginBottom:14,boxShadow:"0 2px 8px rgba(0,0,0,0.05)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:11}}>
              <div style={{fontSize:13,fontWeight:800,color:D}}>📦 Pièces restantes par tissu</div>
              <button onClick={()=>setView("stock")} style={{background:"transparent",border:"none",color:G,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Voir tout →</button>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",gap:9}}>
              {stock.slice().sort((a,b)=>{
                const mpa=parseFloat(a.metresParPiece)||1;
                const mpb=parseFloat(b.metresParPiece)||1;
                const pa=(parseFloat(a.metres)||0)/mpa;
                const pb=(parseFloat(b.metres)||0)/mpb;
                return pb-pa;
              }).slice(0,8).map(s=>{
                const m=parseFloat(s.metres)||0;
                const mpp=parseFloat(s.metresParPiece)||0;
                const pieces=mpp>0?Math.floor(m/mpp):null;
                const low=stockLowThreshold(s);
                const status=m<=0?"rupture":m<=low?"bas":"ok";
                const couleurHex=s.couleurHex||COULEURS.find(c=>c.n===s.couleur)?.h||"#ccc";
                const label=s.type==="Autre"?(s.custom||"Autre"):(s.type||"—");
                return(<div key={s.id} onClick={()=>setView("stock")} style={{background:status==="rupture"?"#fef2f2":status==="bas"?"#fffbeb":"#f9fafb",border:"1px solid "+(status==="rupture"?"#fca5a5":status==="bas"?"#fde68a":"#e5e7eb"),borderRadius:9,padding:"9px 11px",cursor:"pointer",transition:"transform 0.1s"}} onMouseEnter={e=>e.currentTarget.style.transform="translateY(-1px)"} onMouseLeave={e=>e.currentTarget.style.transform=""}>
                  <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:5}}>
                    {s.couleur&&<span style={{width:10,height:10,borderRadius:"50%",background:couleurHex,border:"1px solid rgba(0,0,0,0.12)",display:"inline-block",flexShrink:0}}/>}
                    <span style={{fontSize:12,fontWeight:800,color:D,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{label}{s.couleur?" "+s.couleur:""}</span>
                  </div>
                  {pieces!==null?<div style={{fontSize:11,color:status==="rupture"?"#991b1b":status==="bas"?"#92400e":"#166534",fontWeight:700}}>
                    <strong style={{fontSize:14}}>{pieces}</strong> pièce{pieces>1?"s":""} {status==="rupture"?"🔴":status==="bas"?"⚠️":""}
                    <div style={{fontSize:9,color:"#888",fontWeight:500,marginTop:2}}>{m.toFixed(0)} m restants</div>
                  </div>:<div style={{fontSize:11,color:status==="rupture"?"#991b1b":status==="bas"?"#92400e":"#166534",fontWeight:700}}>
                    <strong style={{fontSize:14}}>{m.toFixed(0)}</strong> m {status==="rupture"?"🔴":status==="bas"?"⚠️":""}
                  </div>}
                </div>);
              })}
            </div>
          </div>}
          {/* FINANCES DU MOIS EN COURS — repère rapide « où j'en suis ce mois-ci » */}
          {(()=>{
            const moisKey=new Date().toISOString().slice(0,7);
            const gM=ordersByMonth.find(g=>g.key===moisKey);
            const dM=depensesByMonth.find(g=>g.key===moisKey);
            const mCA=gM?gM.tP:0,mEnc=gM?gM.tE:0,mDep=(dM?dM.total:0)+(coutsCommande.byMonth[moisKey]||0),mNet=mCA-mDep;
            const moisLabel=new Date().toLocaleDateString("fr-FR",{month:"long",year:"numeric"});
            const cells=[
              {l:isGrossiste?"Ventes du mois":"CA du mois",v:mfmt(fmt(mCA)+" F"),c:D},
              {l:"Encaissé",v:mfmt(fmt(mEnc)+" F"),c:"#10b981"},
              ...(IS_PRO?[{l:"Dépenses",v:mfmt("−"+fmt(mDep)+" F"),c:"#ef4444"},{l:"Bénéfice net",v:mfmt((mNet>=0?"+":"")+fmt(mNet)+" F"),c:mNet>=0?"#059669":"#ef4444"}]:[]),
            ];
            return(<div onClick={()=>setView("caisse")} title="Voir la caisse" style={{background:"#fff",border:"1px solid #f0ece4",borderRadius:13,padding:"13px 16px",marginBottom:14,boxShadow:"0 2px 10px rgba(0,0,0,0.05)",cursor:"pointer"}}>
              <div style={{fontSize:12,fontWeight:800,color:D,marginBottom:10,textTransform:"capitalize"}}>📅 Ce mois-ci · {moisLabel} <span style={{color:"#cbd5e1",fontWeight:800}}>→</span></div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))",gap:8}}>
                {cells.map(c=>(<div key={c.l} style={{textAlign:"center"}}>
                  <div style={{fontSize:15,fontWeight:900,color:c.c}}>{c.v}</div>
                  <div style={{fontSize:9,color:"#aaa",marginTop:2}}>{c.l}</div>
                </div>))}
              </div>
            </div>);
          })()}
          {/* RÉSUMÉ CAISSE — aperçu financier direct sur l'accueil */}
          <div onClick={()=>setView("caisse")} style={{background:"#fff",border:"1px solid #f0ece4",borderRadius:13,padding:"14px 17px",marginBottom:14,cursor:"pointer",display:"flex",alignItems:"center",gap:12,flexWrap:"wrap",boxShadow:"0 2px 10px rgba(0,0,0,0.05)"}}>
            <div style={{display:"flex",alignItems:"center",gap:9,flex:"1 1 130px"}}>
              <span style={{fontSize:22}}>💰</span>
              <div>
                <div style={{fontSize:12,fontWeight:800,color:"#a16207"}}>Caisse {moneyLocked&&<span style={{fontSize:10}}>🔒</span>}</div>
                <div style={{fontSize:9,color:"#999"}}>{moneyLocked?"Code requis →":"Toucher pour le détail →"}</div>
              </div>
            </div>
            <div style={{display:"flex",gap:16,flexWrap:"wrap"}}>
              <div><div style={{fontSize:9,color:"#999",fontWeight:600}}>Revenus</div><div style={{fontSize:15,fontWeight:900,color:"#1d4ed8"}}>{mfmt(fmt(stats.tP)+" F")}</div></div>
              <div><div style={{fontSize:9,color:"#999",fontWeight:600}}>Encaissé</div><div style={{fontSize:15,fontWeight:900,color:"#059669"}}>{mfmt(fmt(stats.tE)+" F")}</div></div>
              <div><div style={{fontSize:9,color:"#999",fontWeight:600}}>À percevoir</div><div style={{fontSize:15,fontWeight:900,color:"#dc2626"}}>{mfmt(fmt(stats.tR)+" F")}</div></div>
              {IS_PRO&&depStats.total>0&&<div><div style={{fontSize:9,color:"#999",fontWeight:600}}>Dépenses</div><div style={{fontSize:15,fontWeight:900,color:"#b45309"}}>{mfmt("−"+fmt(depStats.total)+" F")}</div></div>}
            </div>
          </div>
          {IS_PRO&&stockAlerts.length>0&&<div style={{background:"#fffbeb",border:"1px solid #fde68a",borderRadius:13,padding:"14px 17px",marginBottom:14}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:9}}>
              <div style={{fontSize:13,fontWeight:700,color:"#92400e"}}>⚠️ Tissus en stock bas ou en rupture</div>
              <span style={{background:"#92400e",color:"#fff",padding:"2px 9px",borderRadius:20,fontSize:11,fontWeight:700}}>{stockAlerts.length}</span>
            </div>
            <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
              {stockAlerts.slice(0,10).map(s=>{const m=parseFloat(s.metres)||0;return(<span key={s.id} onClick={()=>{setView("stock");setStockTab("inventaire");}} style={{background:"#fff",border:"1px solid "+(m<=0?"#fca5a5":"#fde68a"),borderRadius:7,padding:"4px 10px",fontSize:11,cursor:"pointer",fontWeight:600,color:m<=0?"#991b1b":"#92400e"}}>{stockLabel(s)} · {m.toFixed(1)} m {m<=0?"🔴":"⚠️"}</span>);})}
            </div>
          </div>}
          {inactiveClients.length>0&&<div style={{background:"#fef2f2",border:"1px solid #fca5a5",borderRadius:13,padding:"14px 17px",marginBottom:14}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:9}}>
              <div style={{fontSize:13,fontWeight:700,color:"#991b1b"}}>⏰ Clients inactifs (+ 6 mois)</div>
              <span style={{background:"#991b1b",color:"#fff",padding:"2px 9px",borderRadius:20,fontSize:11,fontWeight:700}}>{inactiveClients.length}</span>
            </div>
            <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
              {inactiveClients.slice(0,8).map(c=>(<span key={c.id} onClick={()=>setClientDetail(c)} style={{background:"#fff",border:"1px solid #fca5a5",borderRadius:7,padding:"4px 10px",fontSize:11,cursor:"pointer",fontWeight:600,color:"#991b1b"}}>{c.cliNum} · {c.prenom} {c.nom}</span>))}
            </div>
          </div>}
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(300px,1fr))",gap:14}}>
            <div style={{background:"#fff",borderRadius:13,padding:"15px 17px",boxShadow:"0 2px 8px rgba(0,0,0,0.05)"}}>
              <h3 style={{margin:"0 0 11px",color:D,fontSize:14,fontWeight:700}}>{isGrossiste?"🧾 Dernières ventes":"📋 Dernières commandes"}</h3>
              {orders.length===0&&<div style={{color:"#ccc",textAlign:"center",padding:"20px 0",fontSize:12}}>{isGrossiste?"Aucune vente.":"Aucune commande."}</div>}
              {orders.slice().reverse().slice(0,5).map(o=>{
                const tissusV=(o.tissus||[]).filter(t=>t.type||t.metres||t.couleur).length;
                const nbP=Math.max(tissusV,parseInt(o.nombrePieces)||1,1);
                return(<div key={o.id} onClick={()=>setDetail(migrateTissus(o))} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"7px 0",borderBottom:"1px solid #f5f5f5",cursor:"pointer"}}>
                  <div style={{display:"flex",gap:8,alignItems:"center"}}>
                    <span style={{background:G+"22",color:D,fontWeight:800,fontSize:9,padding:"2px 6px",borderRadius:4}}>{o.cmdNum}</span>
                    <div style={{fontWeight:600,fontSize:12}}>{o.prenom} {o.nom}</div>
                    {nbP>1&&<span style={{background:"#dbeafe",color:"#1e40af",fontWeight:800,fontSize:9,padding:"2px 7px",borderRadius:10}}>× {nbP} pièces</span>}
                  </div>
                  <div style={{display:"flex",gap:6,alignItems:"center"}}>
                    {(()=>{const pt=parseFloat(o.prixTotal)||0;const av=parseFloat(o.avanceRecue)||0;const solde=pt>0&&av>=pt;return solde?<span style={{background:"#dcfce7",color:"#166534",padding:"2px 8px",borderRadius:20,fontSize:10,fontWeight:800,border:"1px solid #86efac"}}>✓ Soldé</span>:isGrossiste?<span style={{background:"#fef3c7",color:"#b45309",padding:"2px 8px",borderRadius:20,fontSize:10,fontWeight:700}}>⏳ Reste à régler</span>:<span style={{background:SC[o.statut]+"22",color:SC[o.statut],padding:"2px 8px",borderRadius:20,fontSize:10,fontWeight:700}}>{o.statut}</span>;})()}
                    <span style={{fontWeight:700,fontSize:12}}>{fmt(o.prixTotal)} F</span>
                  </div>
                </div>);
              })}
            </div>
            {isGrossiste?(()=>{
              // Top 5 tissus vendus pour grossiste
              const sold={};
              orders.forEach(o=>{(o.tissus||[]).forEach(t=>{if(!t.type)return;const k=t.type==="Autre"?(t.custom||"Autre"):t.type;const m=parseFloat(t.metres)||0;if(m>0)sold[k]=(sold[k]||0)+m;});});
              const top=Object.entries(sold).sort((a,b)=>b[1]-a[1]).slice(0,6);
              return(<div style={{background:"#fffbeb",borderRadius:13,padding:"15px 17px",border:"1px solid #fde68a"}}>
                <h3 style={{margin:"0 0 11px",color:"#92400e",fontSize:14,fontWeight:700}}>🏆 Top tissus vendus</h3>
                {top.length===0&&<div style={{color:"#e9c57a",textAlign:"center",padding:"20px 0",fontSize:12}}>Aucune vente enregistrée.</div>}
                {top.map(([n,m],i)=>(<div key={n} onClick={()=>{setStockOpenCat(TISSU_CAT_BY_NAME[n]||n);setStockTab("inventaire");setView("stock");}} title="Voir ce tissu dans le Stock" style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:"1px solid rgba(253,230,138,.5)",fontSize:12,cursor:"pointer"}}>
                  <span style={{fontWeight:600}}>{i+1}. {n}</span>
                  <span style={{color:"#92400e",fontWeight:700}}>{m.toFixed(1)} m vendus</span>
                </div>))}
              </div>);
            })():(<div style={{background:"#fffbeb",borderRadius:13,padding:"15px 17px",border:"1px solid #fde68a"}}>
              <h3 style={{margin:"0 0 11px",color:"#92400e",fontSize:14,fontWeight:700}}>⏰ Livraisons à venir</h3>
              {orders.filter(o=>o.dateLivraison&&o.statut!=="Livré").length===0&&<div style={{color:"#e9c57a",textAlign:"center",padding:"20px 0",fontSize:12}}>Aucune livraison planifiée.</div>}
              {orders.filter(o=>o.dateLivraison&&o.statut!=="Livré").sort((a,b)=>a.dateLivraison.localeCompare(b.dateLivraison)).slice(0,6).map(o=>{
                const late=SUIVI_LIVRAISON&&new Date(o.dateLivraison)<new Date();
                return(<div key={o.id} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:"1px solid rgba(253,230,138,.5)",fontSize:12}}>
                  <span style={{fontWeight:600,fontSize:12}}>{o.prenom} {o.nom}</span>
                  <span style={{color:late?"#ef4444":"#92400e",fontWeight:late?700:400}}>{late?"⚠️ ":""}{o.dateLivraison}</span>
                </div>);
              })}
            </div>)}
          </div>
        </div>}

        {/* ── CLIENTS ── */}
        {view==="clients"&&<div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
            <div><h2 style={{margin:"0 0 1px",color:D,fontSize:21,fontWeight:800}}>👥 Carnet clients</h2><div style={{color:"#aaa",fontSize:12}}>{filtClients.length} client(s) · CLI permanent</div></div>
            <button onClick={openNewClient} style={{background:G,color:D,border:"none",padding:"9px 18px",borderRadius:8,cursor:"pointer",fontWeight:800,fontSize:12,fontFamily:"inherit"}}>＋ Nouveau client</button>
          </div>
          <div style={{marginBottom:12}}>
            <label style={sl}>🔍 Rechercher un client (nom, téléphone, n°, ville)</label><input value={clientListSearch} onChange={e=>setClientListSearch(e.target.value)} style={{...si,padding:"9px 13px"}}/>
          </div>
          {filtClients.length===0
            ?<div style={{textAlign:"center",padding:"44px 16px",background:"#fff",borderRadius:12,color:"#ccc"}}><div style={{fontSize:44,marginBottom:10}}>👤</div>Aucun client.<div style={{fontSize:11,marginTop:7,color:"#bbb"}}>Créez votre premier client pour démarrer votre carnet</div></div>
            :<div style={{display:"grid",gridTemplateColumns:isMobile?"repeat(2,minmax(0,1fr))":"repeat(auto-fill,minmax(265px,1fr))",gap:isMobile?8:11}}>
              {filtClients.map(c=>{
                const cOrders=orders.filter(o=>o.clientId===c.id);
                const hasMes=[c.mEpaule,c.mLongueur,c.mPoitrine,c.mTaille,c.mCeinture,c.mLongueurP].some(v=>v&&String(v).trim())||(c.mesuresPlus||[]).some(m=>m.l||m.v);
                return(<div key={c.id} onClick={()=>setClientDetail(c)} style={{background:"#fff",borderRadius:11,padding:isMobile?"10px 11px":"13px 14px",boxShadow:"0 1px 6px rgba(0,0,0,0.07)",cursor:"pointer",border:c.vip?"2px solid "+G:"1px solid #f0f0f0",transition:"transform 0.12s",minWidth:0,overflow:"hidden"}} onMouseEnter={e=>e.currentTarget.style.transform="translateY(-2px)"} onMouseLeave={e=>e.currentTarget.style.transform=""}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                    <span style={{background:"#0ea5e9",color:"#fff",fontWeight:800,fontSize:10,padding:"3px 9px",borderRadius:5}}>{c.cliNum}</span>
                    {c.vip&&<span style={{fontSize:13}}>⭐</span>}
                    <span style={{fontSize:11,color:"#aaa",marginLeft:"auto"}}>{cOrders.length} cmd</span>
                  </div>
                  <div style={{fontWeight:700,color:D,fontSize:isMobile?12.5:14,marginBottom:3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.prenom} {c.nom}</div>
                  <div style={{fontSize:isMobile?10:11,color:"#888",marginBottom:6,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.telephone||"—"}{c.ville?" · "+c.ville:""}</div>
                  <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                    {!isGrossiste&&(hasMes?<span style={{background:"#dcfce7",color:"#166534",fontSize:9,fontWeight:700,padding:"2px 7px",borderRadius:5}}>📐 Mesures OK</span>:<span style={{background:"#fef3c7",color:"#92400e",fontSize:9,fontWeight:700,padding:"2px 7px",borderRadius:5}}>📐 À renseigner</span>)}
                    {cOrders.length>0&&<span style={{background:"#dbeafe",color:"#1e40af",fontSize:9,fontWeight:700,padding:"2px 7px",borderRadius:5}}>💰 {fmt(cOrders.reduce((s,o)=>s+(parseFloat(o.prixTotal)||0),0))} F</span>}
                  </div>
                </div>);
              })}
            </div>
          }
        </div>}

        {/* ── CRÉDIT ── ce que les clients doivent, qui relancer aujourd'hui, et
            l'encaissement qui met la dette et la Caisse à jour d'un seul geste. */}
        {view==="credit"&&moneyLocked&&<PinLock onUnlock={unlockCaisse} isGrossiste={isGrossiste}/>}
        {view==="credit"&&!moneyLocked&&(()=>{
          const ETAT={
            retard:{l:g=>`⚠️ En retard de ${g.jours} jour${g.jours>1?"s":""}`,c:"#dc2626",bg:"#fef2f2"},
            aujourdhui:{l:()=>"📅 Paiement prévu aujourd'hui",c:"#b45309",bg:"#fffbeb"},
            bientot:{l:g=>`📅 Prévu le ${dateCourte(g.echeance)}`,c:"#1d4ed8",bg:"#eff6ff"},
            plusTard:{l:g=>`📅 Prévu le ${dateCourte(g.echeance)}`,c:"#475569",bg:"#f1f5f9"},
            sansDate:{l:()=>"Pas de date promise",c:"#6b7280",bg:"#f3f4f6"},
          };
          const nbEtat=e=>credits.liste.filter(g=>g.etat===e).length;
          const FILTRES=[["tous","Tous",credits.liste.length],["dus","À relancer",credits.dus.length],["bientot","Cette semaine",nbEtat("bientot")],["sansDate","Sans date",nbEtat("sansDate")]];
          const q=creditQ.trim().toLowerCase();
          // Une recherche passe au-dessus du filtre : on cherche dans tous les crédits.
          const correspond=g=>!q||[g.nom,g.telephone,...g.ventes.map(o=>o.cmdNum)].some(v=>v&&String(v).toLowerCase().includes(q));
          const liste=credits.liste.filter(g=>(q||creditFiltre==="tous"||(creditFiltre==="dus"?(g.etat==="retard"||g.etat==="aujourdhui"):g.etat===creditFiltre))&&correspond(g));
          // Cherché un client qui a déjà tout payé : il n'est plus dans les crédits.
          const dejaPayes=q?[...new Set(credits.paiements.filter(p=>String(p.nom||"").toLowerCase().includes(q)).map(p=>p.nom))]:[];
          const nC=n=>`${n} client${n>1?"s":""}`;
          const kpis=[
            {i:<IconeCredit t={24}/>,l:"À encaisser",s:nC(credits.liste.length),v:fmt(credits.total)+" F",c:"#dc2626"},
            {i:"⚠️",l:"À relancer",s:nC(credits.dus.length),v:fmt(credits.totalDu)+" F",c:"#b45309"},
            {i:"📅",l:"Prévu cette semaine",s:nC(nbEtat("bientot")),v:fmt(credits.semaine)+" F",c:"#1d4ed8"},
            {i:"✅",l:"Remboursé ce mois",s:"Déjà dans la Caisse",v:fmt(credits.rembourseMois)+" F",c:"#059669"},
          ];
          return(<div>
            <div style={{marginBottom:14,display:"flex",alignItems:"center",gap:14,flexWrap:"wrap"}}>
              <h2 style={{margin:0,lineHeight:0}}><LogoCredit h={52}/></h2>
              <div style={{color:"#888",fontSize:12.5,lineHeight:1.5}}>L'argent que vos clients vous doivent encore</div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:11,marginBottom:16}}>
              {kpis.map(k=>(<div key={k.l} style={{background:"#fff",borderRadius:12,padding:"13px 15px",boxShadow:"0 2px 8px rgba(0,0,0,0.05)",borderTop:"3px solid "+k.c}}>
                <div style={{fontSize:20}}>{k.i}</div>
                <div style={{fontSize:17,fontWeight:900,color:k.c,marginTop:6}}>{k.v}</div>
                <div style={{fontSize:11,color:"#555",marginTop:2,fontWeight:700}}>{k.l}</div>
                <div style={{fontSize:10,color:"#aaa",marginTop:1}}>{k.s}</div>
              </div>))}
            </div>
            {credits.liste.length===0?<div style={{background:"#fff",borderRadius:12,textAlign:"center",padding:"38px 18px",boxShadow:"0 2px 6px rgba(0,0,0,0.04)"}}>
              <div style={{fontSize:42,marginBottom:8}}>✅</div>
              <div style={{fontSize:15,fontWeight:800,color:D}}>Aucun crédit en cours</div>
              <div style={{fontSize:12,color:"#999",marginTop:6,lineHeight:1.6,maxWidth:380,marginLeft:"auto",marginRight:"auto"}}>Quand un client repart sans tout payer, ce qu'il doit apparaît ici. Notez à la vente la date qu'il promet : le jour venu, l'application vous le rappelle.</div>
            </div>:<>
              <div style={{display:"flex",gap:6,marginBottom:10,overflowX:"auto",paddingBottom:2}}>
                {FILTRES.map(([id,l,n])=>{const on=creditFiltre===id;return(<button key={id} onClick={()=>setCreditFiltre(id)} style={{flexShrink:0,padding:"6px 13px",borderRadius:20,border:"none",cursor:"pointer",fontSize:12,fontFamily:"inherit",background:on?D:"#ececec",color:on?G:"#555",fontWeight:on?800:600,whiteSpace:"nowrap"}}>{l} <span style={{opacity:.7}}>{n}</span></button>);})}
              </div>
              <div style={{marginBottom:12}}>
                <label style={sl}>🔍 Chercher un client, un téléphone, une vente</label>
                <input value={creditQ} onChange={e=>setCreditQ(e.target.value)} style={{...si,padding:"9px 13px"}}/>
              </div>
              {liste.length===0&&<div style={{background:"#fff",borderRadius:12,textAlign:"center",padding:"26px 18px",fontSize:12.5}}>
                {dejaPayes.length>0
                  ?<><div style={{fontSize:30,marginBottom:8}}>✅</div><div style={{fontWeight:800,color:"#166534"}}>{dejaPayes.join(", ")} n'a plus rien à payer.</div><div style={{color:"#999",marginTop:5}}>Ce qu'il a versé est compté dans l'encaissé de la Caisse.</div></>
                  :<span style={{color:"#bbb"}}>{q?"Aucun crédit ne correspond à « "+creditQ.trim()+" ».":"Aucun client dans cette liste."}</span>}
              </div>}
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                {(()=>{
                  // Le mois retenu est celui de la première vente non soldée du client.
                  const parMois={};
                  liste.forEach(g=>{const k=(g.depuis||"").slice(0,7)||"0000-00";(parMois[k]=parMois[k]||[]).push(g);});
                  return Object.keys(parMois).sort().reverse().map(mk=>{
                    const groupe=parMois[mk];
                    const ouvert=!creditMoisFermes.includes(mk);
                    const [yy,mm]=mk.split("-");
                    const libMois=mk==="0000-00"?"Sans date":new Date(+yy,+mm-1,1).toLocaleDateString("fr-FR",{month:"long",year:"numeric"});
                    const totalMois=groupe.reduce((s,g)=>s+g.reste,0);
                    return(<div key={mk} style={{display:"flex",flexDirection:"column",gap:10}}>
                      <div onClick={()=>setCreditMoisFermes(f=>ouvert?[...f,mk]:f.filter(x=>x!==mk))} style={{display:"flex",alignItems:"center",gap:11,padding:"11px 14px",background:"#fff",borderRadius:11,border:"1px solid #f0f0f0",cursor:"pointer"}}>
                        <span style={{fontSize:15,color:ouvert?G:"#bbb",transform:ouvert?"rotate(90deg)":"",display:"inline-block",transition:"transform .15s"}}>▶</span>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:13.5,fontWeight:800,color:D,textTransform:"capitalize"}}>{libMois}</div>
                          <div style={{fontSize:11,color:"#888",marginTop:1}}>{groupe.length} client{groupe.length>1?"s":""} à encaisser</div>
                        </div>
                        <div style={{fontSize:14,fontWeight:900,color:"#dc2626",flexShrink:0}}>{fmt(totalMois)} F</div>
                      </div>
                      {ouvert&&groupe.map(g=>{
                  const e=ETAT[g.etat];
                  const open=creditOuvert===g.cle;
                  const wa=g.telephone?waLink(g.telephone,messageRelance(g,atelierInfo,credits.auj)):null;
                  const payes=g.ventes.flatMap(o=>(o.paiements||[]).map(p=>({...p,cmdNum:o.cmdNum}))).sort((a,b)=>(b.date||"").localeCompare(a.date||""));
                  return(<div key={g.cle} style={{background:"#fff",borderRadius:12,boxShadow:"0 2px 6px rgba(0,0,0,0.05)",border:"1px solid #f0f0f0",borderLeft:"4px solid "+e.c,overflow:"hidden"}}>
                    <div onClick={()=>setCreditOuvert(open?null:g.cle)} style={{display:"flex",alignItems:"flex-start",gap:12,padding:"13px 15px 10px",cursor:"pointer"}}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:14.5,fontWeight:800,color:D,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{g.nom}</div>
                        <div style={{fontSize:11,color:"#888",marginTop:2}}>{g.telephone||"Pas de téléphone"} · {g.ventes.length} vente{g.ventes.length>1?"s":""} · depuis le {dateCourte(g.depuis)}</div>
                        <span style={{display:"inline-block",marginTop:7,background:e.bg,color:e.c,fontSize:10.5,fontWeight:800,padding:"3px 9px",borderRadius:12}}>{e.l(g)}</span>
                      </div>
                      <div style={{textAlign:"right",flexShrink:0}}>
                        <div style={{fontSize:9,color:"#aaa",fontWeight:800,textTransform:"uppercase",letterSpacing:.6}}>Doit</div>
                        <div style={{fontSize:19,fontWeight:900,color:"#dc2626",whiteSpace:"nowrap"}}>{fmt(g.reste)} F</div>
                        <div style={{fontSize:10,color:open?G:"#bbb",marginTop:3,fontWeight:700}}>{open?"▲ Masquer":"▼ Détail"}</div>
                      </div>
                    </div>
                    <div style={{display:"flex",gap:7,padding:"0 15px 13px",flexWrap:"wrap"}}>
                      <button onClick={()=>ouvrirEncaissement(g.cle)} style={{flex:"1 1 100px",background:"#10b981",color:"#fff",border:"none",padding:"9px 12px",borderRadius:8,cursor:"pointer",fontWeight:800,fontSize:12.5,fontFamily:"inherit"}}>💵 Encaisser</button>
                      {wa&&<a href={wa} target="_blank" rel="noreferrer" style={{flex:"1 1 100px",background:"#25D366",color:"#fff",padding:"9px 12px",borderRadius:8,fontWeight:800,fontSize:12.5,textDecoration:"none",textAlign:"center"}}>💬 Relancer</a>}
                      <button onClick={()=>setEcheanceEdit({cle:g.cle,date:g.echeance||""})} style={{flex:"1 1 100px",background:"#fff",color:D,border:"1.5px solid #e5e5e5",padding:"8px 12px",borderRadius:8,cursor:"pointer",fontWeight:700,fontSize:12.5,fontFamily:"inherit"}}>📅 {g.echeance?"Changer la date":"Date promise"}</button>
                    </div>
                    {open&&<div style={{borderTop:"1px solid #f0ece4",background:"#fcfbf8",padding:"10px 15px 12px"}}>
                      <div style={{fontSize:9.5,fontWeight:800,color:"#a99a78",letterSpacing:1,marginBottom:6}}>VENTES NON SOLDÉES</div>
                      {g.ventes.map(o=>{
                        const tis=(o.tissus||[]).filter(t=>t.type).map(t=>tissuLabel(t)+(t.metres?" "+t.metres+" m":"")).join(", ");
                        return(<div key={o.id} onClick={()=>setDetail(migrateTissus(o))} style={{display:"flex",alignItems:"center",gap:9,padding:"8px 0",borderBottom:"1px solid #f0ece4",cursor:"pointer"}}>
                          <span style={{background:G+"22",color:D,fontWeight:800,fontSize:9,padding:"3px 7px",borderRadius:4,flexShrink:0}}>{o.cmdNum}</span>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:12,fontWeight:700,color:D,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{tis||"Vente de tissus"}</div>
                            <div style={{fontSize:10.5,color:"#999"}}>Le {dateCourte(o.dateDepot)} · {fmt(o.prixTotal)} F · versé {fmt(o.avanceRecue)} F</div>
                          </div>
                          <div style={{fontSize:12.5,fontWeight:800,color:"#dc2626",flexShrink:0,whiteSpace:"nowrap"}}>{fmt(resteDe(o))} F</div>
                        </div>);
                      })}
                      {payes.length>0&&<>
                        <div style={{fontSize:9.5,fontWeight:800,color:"#a99a78",letterSpacing:1,margin:"12px 0 6px"}}>DÉJÀ REMBOURSÉ</div>
                        {payes.map((p,i)=>(<div key={i} style={{display:"flex",justifyContent:"space-between",gap:8,fontSize:11.5,padding:"4px 0",color:"#555"}}><span>{dateCourte(p.date)} · {p.cmdNum}{p.mode?" · "+p.mode:""}</span><strong style={{color:"#059669",whiteSpace:"nowrap"}}>+{fmt(p.montant)} F</strong></div>))}
                      </>}
                    </div>}
                  </div>);
                      })}
                    </div>);
                  });
                })()}
              </div>
            </>}
            {/* Les remboursements reçus, mois par mois. Un client qui a fini de
                payer sort de la liste du dessus : sans cette section, l'argent
                encaissé en octobre ne se verrait plus nulle part. */}
            {credits.paiements.length>0&&<div style={{marginTop:20}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",gap:8,flexWrap:"wrap",marginBottom:9}}>
                <div style={{fontSize:13,fontWeight:800,color:D}}>💵 Remboursements reçus</div>
                <div style={{fontSize:10.5,color:"#999"}}>Comptés dans l'encaissé de la Caisse</div>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                {(()=>{
                  const parMois={};
                  credits.paiements.forEach(p=>{const k=(p.date||"").slice(0,7)||"0000-00";(parMois[k]=parMois[k]||[]).push(p);});
                  return Object.keys(parMois).sort().reverse().map(mk=>{
                    const groupe=parMois[mk];
                    const ouvert=!remboursMoisFermes.includes(mk);
                    const [yy,mm]=mk.split("-");
                    const libMois=mk==="0000-00"?"Sans date":new Date(+yy,+mm-1,1).toLocaleDateString("fr-FR",{month:"long",year:"numeric"});
                    const totalMois=groupe.reduce((s,p)=>s+p.montant,0);
                    return(<div key={mk} style={{display:"flex",flexDirection:"column",gap:10}}>
                      <div onClick={()=>setRemboursMoisFermes(f=>ouvert?[...f,mk]:f.filter(x=>x!==mk))} style={{display:"flex",alignItems:"center",gap:11,padding:"11px 14px",background:"#fff",borderRadius:11,border:"1px solid #f0f0f0",cursor:"pointer"}}>
                        <span style={{fontSize:15,color:ouvert?G:"#bbb",transform:ouvert?"rotate(90deg)":"",display:"inline-block",transition:"transform .15s"}}>▶</span>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:13.5,fontWeight:800,color:D,textTransform:"capitalize"}}>{libMois}</div>
                          <div style={{fontSize:11,color:"#888",marginTop:1}}>{groupe.length} remboursement{groupe.length>1?"s":""}</div>
                        </div>
                        <div style={{fontSize:14,fontWeight:900,color:"#059669",flexShrink:0}}>+{fmt(totalMois)} F</div>
                      </div>
                      {ouvert&&<div style={{background:"#fff",borderRadius:12,padding:"8px 16px",boxShadow:"0 2px 6px rgba(0,0,0,0.04)"}}>
                        {groupe.map((p,i)=>(<div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderTop:i?"1px solid #f5f5f5":"none",fontSize:12}}>
                          <span style={{color:"#999",width:52,flexShrink:0}}>{dateCourte(p.date)}</span>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontWeight:700,color:D,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.nom}</div>
                            <div style={{fontSize:10.5,color:"#aaa"}}>{[...new Set(p.ventes)].join(", ")}{p.mode?" · "+p.mode:""}</div>
                          </div>
                          <strong style={{color:"#059669",flexShrink:0,whiteSpace:"nowrap"}}>+{fmt(p.montant)} F</strong>
                        </div>))}
                      </div>}
                    </div>);
                  });
                })()}
              </div>
            </div>}
          </div>);
        })()}

        {/* ── CAISSE ── */}
        {view==="caisse"&&moneyLocked&&<PinLock onUnlock={unlockCaisse} isGrossiste={isGrossiste}/>}
        {view==="caisse"&&!moneyLocked&&<div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14,gap:14}}>
            <div>
              <h2 style={{margin:"0 0 2px",color:D,fontSize:21,fontWeight:800}}>💰 Caisse</h2>
              <div style={{color:"#aaa",fontSize:12}}>{caisseTab==="revenus"?"Revenus & encaissements":caisseTab==="depenses"?"Suivi des dépenses":"Bilan net (Revenus − Dépenses)"}</div>
            </div>
            {IS_PRO&&yearsAvailable.length>0&&<div style={{display:"flex",gap:7,alignItems:"center"}}>
              <span style={{fontSize:11,color:"#888",fontWeight:600}}>📑 Bilan annuel :</span>
              {yearsAvailable.slice(0,3).map(y=>(
                <button key={y} onClick={()=>printBilanAnnuel(y)} style={{background:D,color:G,border:"none",padding:"7px 13px",borderRadius:7,cursor:"pointer",fontSize:11,fontWeight:800,fontFamily:"inherit"}}>📑 {y}</button>
              ))}
            </div>}
          </div>
          {/* TABS Caisse (Pro uniquement) */}
          {IS_PRO&&<div style={{display:"flex",gap:4,marginBottom:18,background:"#f5f5f5",borderRadius:9,padding:3,maxWidth:500}}>
            {[["revenus","💵 Revenus"],["depenses","💸 Dépenses"],["bilan","💎 Bilan Net"]].map(([id,l])=>(
              <button key={id} onClick={()=>setCaisseTab(id)} style={{flex:1,padding:"8px 10px",borderRadius:7,border:"none",cursor:"pointer",fontWeight:caisseTab===id?700:500,fontSize:12,background:caisseTab===id?"#fff":"transparent",color:caisseTab===id?D:"#666",fontFamily:"inherit",boxShadow:caisseTab===id?"0 1px 4px rgba(0,0,0,0.08)":"none"}}>{l}</button>
            ))}
          </div>}
          {(!IS_PRO||caisseTab==="revenus")&&<>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:12,marginBottom:18}}>
            {[{i:"📊",l:"Revenus",v:stats.tP.toLocaleString("fr-FR")+" F",c:D},{i:"✅",l:"Total encaissé",v:stats.tE.toLocaleString("fr-FR")+" F",c:"#10b981"},{i:"⏳",l:"À percevoir",v:stats.tR.toLocaleString("fr-FR")+" F",c:"#ef4444"},isGrossiste?{i:"💰",l:"Ventes soldées",v:orders.filter(o=>statutGrossiste(o)==="Soldé").length+" / "+orders.length,c:"#0ea5e9"}:{i:"💰",l:"Soldé",v:orders.filter(o=>(parseFloat(o.prixTotal)||0)>0&&((parseFloat(o.avanceRecue)||0)>=(parseFloat(o.prixTotal)||0))).reduce((s,o)=>s+piecesOf(o),0)+" / "+totalPieces,c:"#0ea5e9"}].map(({i,l,v,c})=>(
              <div key={l} style={{background:"#fff",borderRadius:12,padding:"14px 16px",boxShadow:"0 2px 8px rgba(0,0,0,0.05)",borderTop:"3px solid "+c}}>
                <div style={{fontSize:22}}>{i}</div><div style={{fontSize:17,fontWeight:900,color:c,marginTop:7}}>{v}</div><div style={{fontSize:11,color:"#aaa",marginTop:3}}>{l}</div>
              </div>
            ))}
          </div>
          {/* LIEN AVEC LE CRÉDIT — l'« à percevoir » de la Caisse, ce sont des clients qui doivent. */}
          {isGrossiste&&credits.total>0&&<div onClick={()=>setView("credit")} style={{background:"#fff7ed",border:"1px solid #fed7aa",borderRadius:11,padding:"12px 15px",marginBottom:16,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap",cursor:"pointer"}}>
            <IconeCredit t={28}/>
            <div style={{flex:"1 1 200px",minWidth:0}}>
              <div style={{fontSize:13,fontWeight:800,color:"#9a3412"}}>{fmt(credits.total)} F à encaisser chez {credits.liste.length} client{credits.liste.length>1?"s":""}</div>
              <div style={{fontSize:11,color:"#c2410c",marginTop:2}}>{credits.dus.length>0?`${credits.dus.length} à relancer · `:""}Remboursé ce mois : {fmt(credits.rembourseMois)} F</div>
            </div>
            <span style={{fontSize:12,fontWeight:800,color:"#9a3412"}}>Voir Crédit →</span>
          </div>}
          {stats.tP>0&&<div style={{background:"#fff",borderRadius:11,padding:16,marginBottom:16,boxShadow:"0 2px 6px rgba(0,0,0,0.04)"}}>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:6,fontSize:12,fontWeight:600,color:D}}><span>Taux d'encaissement</span><span style={{color:"#10b981"}}>{((stats.tE/stats.tP)*100).toFixed(1)}%</span></div>
            <div style={{background:"#f0f0f0",borderRadius:20,height:13,overflow:"hidden"}}><div style={{background:"linear-gradient(90deg,#10b981,#34d399)",height:"100%",width:Math.min((stats.tE/stats.tP)*100,100)+"%",borderRadius:20}}/></div>
          </div>}

          {/* HISTORIQUE CA — graphique en barres (12 mois fixes, padded avec zéros) */}
          {IS_PRO&&(()=>{
            // Janvier → Décembre de l'année en cours (12 mois fixes)
            const today=new Date();
            const year=today.getFullYear();
            const monthKeys=[];
            for(let m=1;m<=12;m++){
              monthKeys.push(year+"-"+String(m).padStart(2,"0"));
            }
            const byKey=Object.fromEntries(ordersByMonth.map(g=>[g.key,g]));
            const series=monthKeys.map(k=>byKey[k]||{key:k,tP:0,pieces:0,orders:[]});
            const maxCA=Math.max(...series.map(g=>g.tP),1);
            const hasAnyData=series.some(g=>g.tP>0);
            const total12=series.reduce((s,g)=>s+g.tP,0);
            return(<div style={{background:"#fff",borderRadius:12,padding:"16px 20px",marginBottom:16,boxShadow:"0 2px 6px rgba(0,0,0,0.04)"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
                <div>
                  <div style={{fontSize:13,fontWeight:800,color:D}}>📈 Évolution des revenus</div>
                  <div style={{fontSize:10,color:"#aaa",marginTop:2}}>Année {(new Date()).getFullYear()} · Revenus de l’année <strong style={{color:D}}>{total12.toLocaleString("fr-FR")} F</strong></div>
                </div>
                <div style={{fontSize:10,color:"#888",textAlign:"right"}}>Pic mois<br/><strong style={{color:G,fontSize:13}}>{hasAnyData?maxCA.toLocaleString("fr-FR")+" F":"—"}</strong></div>
              </div>
              <HistoMois mois={series.map(g=>({key:g.key,ca:g.tP}))} barres={[{cle:"ca",label:"Revenus",couleur:"#1d4ed8",grad:"linear-gradient(180deg,#2563eb,#93c5fd)"}]}/>
              {!hasAnyData&&<div style={{textAlign:"center",fontSize:11,color:"#bbb",marginTop:10,fontStyle:"italic"}}>Aucune {isGrossiste?"vente":"commande"} enregistrée sur les 12 derniers mois</div>}
            </div>);
          })()}
          {/* CAISSE — regroupé par mois, repliable, click ligne → détail */}
          {orders.length===0?<div style={{background:"#fff",borderRadius:12,textAlign:"center",padding:36,color:"#ccc",fontSize:12,boxShadow:"0 2px 6px rgba(0,0,0,0.04)"}}>Aucune vente.</div>
          :<div style={{display:"flex",flexDirection:"column",gap:9}}>
            {ordersByMonth.map(grp=>{
              const open=caisseOpenMonth===grp.key;
              const [y,m]=grp.key.split("-");
              const monthLabel=new Date(parseInt(y),parseInt(m)-1,1).toLocaleDateString("fr-FR",{month:"long",year:"numeric"});
              const reste=grp.tP-grp.tE;
              return(<div key={grp.key} style={{background:"#fff",borderRadius:11,boxShadow:"0 2px 6px rgba(0,0,0,0.04)",overflow:"hidden",border:"1px solid #f0f0f0"}}>
                <div onClick={()=>setCaisseOpenMonth(open?null:grp.key)} style={{display:"flex",alignItems:"center",gap:12,padding:"13px 16px",cursor:"pointer",background:open?"#f9f7f0":"#fff",borderBottom:open?"1px solid #f0ece4":"none"}}>
                  <span style={{fontSize:18,color:open?G:"#bbb",transition:"transform 0.15s",transform:open?"rotate(90deg)":""}}>▶</span>
                  <div style={{flex:1}}>
                    <div style={{fontSize:14,fontWeight:800,color:D,textTransform:"capitalize"}}>{monthLabel}</div>
                    <div style={{fontSize:11,color:"#888",marginTop:1}}>{grp.orders.length} vente{grp.orders.length>1?"s":""}</div>
                  </div>
                  <div style={{display:"flex",gap:14,fontSize:11,fontWeight:700,alignItems:"center"}}>
                    <div style={{textAlign:"right"}}><div style={{color:"#aaa",fontSize:9,fontWeight:600,marginBottom:2}}>CA</div><div style={{color:D}}>{fmt(grp.tP)} F</div></div>
                    <div style={{textAlign:"right"}}><div style={{color:"#aaa",fontSize:9,fontWeight:600,marginBottom:2}}>Encaissé</div><div style={{color:"#10b981"}}>{fmt(grp.tE)} F</div></div>
                    <div style={{textAlign:"right"}}><div style={{color:"#aaa",fontSize:9,fontWeight:600,marginBottom:2}}>Restant</div><div style={{color:reste>0?"#ef4444":"#10b981"}}>{fmt(reste)} F</div></div>
                    {IS_PRO&&<button onClick={e=>{e.stopPropagation();printBilanMensuel(grp.key,grp.orders);}} title="Bilan financier du mois" style={{background:"#10b981",color:"#fff",border:"none",padding:"6px 10px",borderRadius:6,cursor:"pointer",fontSize:10,fontWeight:700,fontFamily:"inherit"}}>📑 Bilan</button>}
                  </div>
                </div>
                {open&&<div>
                  {grp.orders.slice().sort((a,b)=>(b.dateDepot||"").localeCompare(a.dateDepot||"")||((parseInt((b.cmdNum||"").replace(/[^0-9]/g,""))||0)-(parseInt((a.cmdNum||"").replace(/[^0-9]/g,""))||0))).map((o,i)=>{
                    const tV=(o.tissus||[]).filter(t=>t.type||t.metres||t.couleur).length;
                    const nbP=Math.max(tV,parseInt(o.nombrePieces)||1,1);
                    return(<div key={o.id} onClick={()=>setDetail(migrateTissus(o))} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 16px 9px 36px",borderBottom:i<grp.orders.length-1?"1px solid #f5f5f5":"none",cursor:"pointer",background:i%2?"#fafafa":"#fff",transition:"background 0.1s"}} onMouseEnter={e=>e.currentTarget.style.background="#f0f7ff"} onMouseLeave={e=>e.currentTarget.style.background=i%2?"#fafafa":"#fff"}>
                      <span style={{background:G+"22",color:D,fontWeight:800,fontSize:9,padding:"3px 8px",borderRadius:4,flexShrink:0}}>{o.cmdNum}</span>
                      {isGrossiste&&(()=>{const ph=photoVente(o);return ph?<img src={ph} alt="" loading="lazy" style={{width:36,height:36,objectFit:"cover",borderRadius:7,flexShrink:0,border:"1px solid #eee"}}/>:null;})()}
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontWeight:700,fontSize:12,display:"flex",alignItems:"center",gap:6}}>
                          <span>{o.prenom} {o.nom}</span>
                          {nbP>1&&<span style={{background:"#dbeafe",color:"#1e40af",fontWeight:800,fontSize:9,padding:"1px 7px",borderRadius:10}}>× {nbP} pièces</span>}
                        </div>
                        <div style={{fontSize:10,color:"#aaa"}}>{o.dateDepot} · {(o.tissus||[]).filter(t=>t.type).map(t=>tissuLabel(t)).join(", ")||o.categorie}</div>
                      </div>
                      <div style={{textAlign:"right",flexShrink:0,minWidth:80}}>
                        <div style={{fontSize:12,fontWeight:800,color:D}}>{fmt(o.prixTotal)} F</div>
                        <div style={{fontSize:10,color:rest(o)>0?"#ef4444":"#10b981",fontWeight:600}}>{rest(o)>0?fmt(rest(o))+" F dû":"✓ soldé"}</div>
                      </div>
                      {!isGrossiste&&<span style={{background:SC[o.statut]+"22",color:SC[o.statut],padding:"2px 8px",borderRadius:20,fontSize:9,fontWeight:700,flexShrink:0}}>{o.statut}</span>}
                      <button onClick={e=>{e.stopPropagation();showReceipt(o);}} style={{background:"#10b981",color:"#fff",border:"none",padding:"5px 10px",borderRadius:6,cursor:"pointer",fontSize:10,fontWeight:700,fontFamily:"inherit",flexShrink:0}}>🧾</button>
                    </div>);
                  })}
                </div>}
              </div>);
            })}
          </div>}
          </>}

          {/* ── ONGLET DÉPENSES (Pro) ── */}
          {IS_PRO&&caisseTab==="depenses"&&<>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:12,marginBottom:18}}>
              {[
                {i:"💸",l:"Total dépenses",v:depStats.total.toLocaleString("fr-FR")+" F",c:"#ef4444"},
                {i:"📋",l:"Nb dépenses",v:toutesDepenses.length,c:D},
                {i:"🏆",l:"Top catégorie",v:depStats.topCat&&depStats.topCat[1]>0?(CATEGORIES_DEPENSE.find(c=>c.id===depStats.topCat[0])?.l||"—"):"—",c:"#8b5cf6"},
                {i:"📅",l:"Ce mois",v:(depensesByMonth.find(g=>g.key===new Date().toISOString().slice(0,7))?.total||0).toLocaleString("fr-FR")+" F",c:"#f59e0b"}
              ].map(({i,l,v,c})=>(
                <div key={l} style={{background:"#fff",borderRadius:12,padding:"14px 16px",boxShadow:"0 2px 8px rgba(0,0,0,0.05)",borderTop:"3px solid "+c}}>
                  <div style={{fontSize:22}}>{i}</div><div style={{fontSize:15,fontWeight:900,color:c,marginTop:7}}>{v}</div><div style={{fontSize:11,color:"#aaa",marginTop:3}}>{l}</div>
                </div>
              ))}
            </div>
            {/* RÉCAP « ARGENT DÉPENSÉ SUR COMMANDES » (Premium) — regroupé ici, détail dans chaque commande */}
            {IS_PREMIUM&&coutsCommande.total>0&&<div onClick={()=>setDetailCouts(true)} title="Voir le détail par commande" style={{background:"#fff7ed",border:"1px solid #fed7aa",borderRadius:12,padding:"13px 16px",marginBottom:18,display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,flexWrap:"wrap",cursor:"pointer"}}>
              <div>
                <div style={{fontSize:13,fontWeight:800,color:"#9a3412"}}>💸 Argent dépensé sur les commandes</div>
                <div style={{fontSize:10,color:"#c2751a",marginTop:2}}>Coûts saisis dans chaque commande (tissu, fil, broderie…) · déjà compté dans le bilan net</div>
                <div style={{fontSize:11,color:"#ea580c",marginTop:5,fontWeight:700}}>Toucher pour voir le détail →</div>
              </div>
              <div style={{fontSize:20,fontWeight:900,color:"#ea580c"}}>−{coutsCommande.total.toLocaleString("fr-FR")} F</div>
            </div>}
            {/* ÉVOLUTION MENSUELLE DES DÉPENSES — Janvier → Décembre, comme le CA */}
            {(()=>{
              const year=(new Date()).getFullYear();
              const monthKeys=[];
              for(let m=1;m<=12;m++)monthKeys.push(year+"-"+String(m).padStart(2,"0"));
              const byKey=Object.fromEntries(depensesByMonth.map(g=>[g.key,g]));
              const series=monthKeys.map(k=>byKey[k]||{key:k,total:0});
              const maxD=Math.max(...series.map(g=>g.total),1);
              const hasAny=series.some(g=>g.total>0);
              const total12=series.reduce((s,g)=>s+g.total,0);
              return(<div style={{background:"#fff",borderRadius:12,padding:"16px 20px",marginBottom:16,boxShadow:"0 2px 6px rgba(0,0,0,0.04)"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:800,color:D}}>📉 Dépenses mensuelles</div>
                    <div style={{fontSize:10,color:"#aaa",marginTop:2}}>Année {year} · Total <strong style={{color:"#ef4444"}}>{total12.toLocaleString("fr-FR")} F</strong></div>
                  </div>
                  <div style={{fontSize:10,color:"#888",textAlign:"right"}}>Pic mois<br/><strong style={{color:"#ef4444",fontSize:13}}>{hasAny?maxD.toLocaleString("fr-FR")+" F":"—"}</strong></div>
                </div>
                <HistoMois mois={series.map(g=>({key:g.key,dep:g.total}))} barres={[{cle:"dep",label:"Dépenses",couleur:"#dc2626",grad:"linear-gradient(180deg,#ef4444,#fca5a5)"}]} hauteur={110}/>
                {!hasAny&&<div style={{textAlign:"center",fontSize:11,color:"#bbb",marginTop:10,fontStyle:"italic"}}>Aucune dépense enregistrée cette année</div>}
              </div>);
            })()}
            {/* Action button */}
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,gap:9,flexWrap:"wrap"}}>
              <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
                <select value={depFilterCat} onChange={e=>setDepFilterCat(e.target.value)} style={{...si,width:"auto",fontSize:11,padding:"7px 10px"}}>
                  <option value="all">Toutes catégories</option>
                  {CATEGORIES_DEPENSE.map(c=><option key={c.id} value={c.id}>{c.l}</option>)}
                </select>
                <select value={depFilterMonth} onChange={e=>setDepFilterMonth(e.target.value)} style={{...si,width:"auto",fontSize:11,padding:"7px 10px"}}>
                  <option value="all">Tous mois</option>
                  {depensesByMonth.map(g=>{const [y,m]=g.key.split("-");return<option key={g.key} value={g.key}>{new Date(parseInt(y),parseInt(m)-1,1).toLocaleDateString("fr-FR",{month:"long",year:"numeric"})}</option>;})}
                </select>
              </div>
              <button onClick={openNewDep} style={{background:G,color:D,border:"none",padding:"9px 18px",borderRadius:8,cursor:"pointer",fontWeight:800,fontSize:12,fontFamily:"inherit"}}>＋ Nouvelle dépense</button>
            </div>
            {/* Répartition par catégorie — barres */}
            {depStats.total>0&&<div style={{background:"#fff",borderRadius:11,padding:16,marginBottom:16,boxShadow:"0 2px 6px rgba(0,0,0,0.04)"}}>
              <div style={{fontSize:12,fontWeight:800,color:D,marginBottom:11}}>📊 Répartition par catégorie</div>
              {CATEGORIES_DEPENSE.filter(c=>depStats.parCategorie[c.id]>0).sort((a,b)=>depStats.parCategorie[b.id]-depStats.parCategorie[a.id]).map(c=>{
                const v=depStats.parCategorie[c.id];
                const pct=(v/depStats.total)*100;
                return(<div key={c.id} style={{marginBottom:9}}>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:11,marginBottom:3}}>
                    <span style={{fontWeight:700,color:D}}>{c.l}</span>
                    <span style={{fontWeight:600,color:"#666"}}>{v.toLocaleString("fr-FR")} F · {pct.toFixed(1)}%</span>
                  </div>
                  <div style={{background:"#f0f0f0",borderRadius:20,height:9,overflow:"hidden"}}>
                    <div style={{background:c.c,height:"100%",width:pct+"%",borderRadius:20,transition:"width 0.4s"}}/>
                  </div>
                </div>);
              })}
            </div>}
            {/* Liste des dépenses */}
            {filteredDep.length===0?<div style={{background:"#fff",borderRadius:12,textAlign:"center",padding:36,color:"#ccc",fontSize:12,boxShadow:"0 2px 6px rgba(0,0,0,0.04)"}}>
              <div style={{fontSize:48,marginBottom:10}}>💸</div>
              Aucune dépense enregistrée.
              <div style={{fontSize:11,marginTop:6,color:"#bbb"}}>Cliquez sur "+ Nouvelle dépense" pour démarrer</div>
            </div>:<div style={{display:"flex",flexDirection:"column",gap:9}}>
              {(()=>{
                // Rangées par mois, comme les revenus. Dans chaque mois : les achats de
                // tissus regroupés par type (dépliables), puis les dépenses saisies à la main.
                const parMois={};
                filteredDep.forEach(d=>{const k=(d.date||new Date(d.id).toISOString().slice(0,10)).slice(0,7);(parMois[k]=parMois[k]||[]).push(d);});
                return Object.keys(parMois).sort().reverse().map(mk=>{
                const liste=parMois[mk];
                const totalMois=liste.reduce((s,d)=>s+(parseFloat(d.montant)||0),0);
                const moisOuvert=depMoisOuvert===mk;
                const [yy,mm]=mk.split("-");
                const moisLib=new Date(+yy,+mm-1,1).toLocaleDateString("fr-FR",{month:"long",year:"numeric"});
                const groupes=Object.values(liste.filter(d=>d.auto).reduce((m,d)=>{const k=d.tissu||"Tissu";(m[k]=m[k]||{tissu:k,lignes:[],total:0}).lignes.push(d);m[k].total+=parseFloat(d.montant)||0;return m;},{})).sort((a,b)=>b.total-a.total);
                const manuelles=liste.filter(d=>!d.auto).sort((a,b)=>(b.date||"").localeCompare(a.date||""));
                const achats=groupes.reduce((s,g)=>s+g.total,0);
                return(<div key={mk} style={{background:"#fff",borderRadius:11,boxShadow:"0 2px 6px rgba(0,0,0,0.04)",overflow:"hidden",border:"1px solid #f0f0f0"}}>
                  <div onClick={()=>setDepMoisOuvert(moisOuvert?null:mk)} style={{display:"flex",alignItems:"center",gap:12,padding:"13px 16px",cursor:"pointer",background:moisOuvert?"#fef2f2":"#fff",borderBottom:moisOuvert?"1px solid #fee2e2":"none",flexWrap:"wrap"}}>
                    <span style={{fontSize:18,color:moisOuvert?"#ef4444":"#bbb",transition:"transform 0.15s",transform:moisOuvert?"rotate(90deg)":""}}>▶</span>
                    <div style={{flex:1,minWidth:120}}>
                      <div style={{fontSize:14,fontWeight:800,color:D,textTransform:"capitalize"}}>{moisLib}</div>
                      <div style={{fontSize:11,color:"#888",marginTop:1}}>{liste.length} dépense{liste.length>1?"s":""}</div>
                    </div>
                    <div style={{display:"flex",gap:14,fontSize:11,fontWeight:700,alignItems:"center"}}>
                      {achats>0&&<div style={{textAlign:"right"}}><div style={{color:"#aaa",fontSize:9,fontWeight:600,marginBottom:2}}>Achats tissus</div><div style={{color:"#b45309"}}>−{fmt(achats)} F</div></div>}
                      {totalMois-achats>0&&<div style={{textAlign:"right"}}><div style={{color:"#aaa",fontSize:9,fontWeight:600,marginBottom:2}}>Autres</div><div style={{color:"#ef4444"}}>−{fmt(totalMois-achats)} F</div></div>}
                      <div style={{textAlign:"right"}}><div style={{color:"#aaa",fontSize:9,fontWeight:600,marginBottom:2}}>Total</div><div style={{color:"#dc2626",fontSize:12.5}}>−{fmt(totalMois)} F</div></div>
                    </div>
                  </div>
                  {moisOuvert&&<>
                  {groupes.map(g=>{
                    const cleG=mk+"|"+g.tissu;
                    const ouvert=depGroupeOuvert===cleG;
                    return(<div key={"g-"+g.tissu} style={{borderBottom:"1px solid #f5f5f5",borderLeft:"3px solid #b45309"}}>
                      <div onClick={()=>setDepGroupeOuvert(ouvert?null:cleG)} style={{display:"flex",alignItems:"center",gap:11,padding:"11px 14px",cursor:"pointer",background:ouvert?"#fffbeb":"#fff"}}>
                        <span style={{fontSize:13,color:ouvert?G:"#bbb",transform:ouvert?"rotate(90deg)":"",transition:"transform 0.15s",flexShrink:0}}>▶</span>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:13,fontWeight:800,color:D}}>🧵 Achats · {g.tissu}</div>
                          <div style={{fontSize:10.5,color:"#999",marginTop:2}}>{g.lignes.length} entrée{g.lignes.length>1?"s":""} en stock · toucher pour le détail</div>
                        </div>
                        <div style={{fontSize:15,fontWeight:900,color:"#ef4444",flexShrink:0}}>−{g.total.toLocaleString("fr-FR")} F</div>
                      </div>
                      {ouvert&&<div style={{background:"#fffdf7",padding:"2px 14px 8px 38px"}}>
                        {g.lignes.map(d=>(<div key={d.id} style={{display:"flex",alignItems:"center",gap:10,padding:"7px 0",borderBottom:"1px dashed #f0e6cc",fontSize:12}}>
                          <span style={{flex:1,minWidth:0,color:D,fontWeight:600}}>{d.article||d.description}</span>
                          <span style={{color:"#999",fontSize:11,flexShrink:0}}>📅 {d.date}</span>
                          <span style={{color:"#ef4444",fontWeight:800,flexShrink:0}}>−{(parseFloat(d.montant)||0).toLocaleString("fr-FR")} F</span>
                        </div>))}
                      </div>}
                    </div>);
                  })}
                  {manuelles.map((d,i)=>{
                const cat=CATEGORIES_DEPENSE.find(c=>c.id===d.categorie)||CATEGORIES_DEPENSE[7];
                return(<div key={d.id} style={{display:"flex",alignItems:"center",gap:11,padding:"11px 14px",borderBottom:i<manuelles.length-1?"1px solid #f5f5f5":"none",background:i%2?"#fafafa":"#fff",borderLeft:"3px solid "+cat.c}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:9,marginBottom:3}}>
                      <span style={{background:cat.c+"22",color:cat.c,fontWeight:800,fontSize:10,padding:"2px 8px",borderRadius:5}}>{cat.l}</span>
                      <span style={{fontSize:11,color:"#999"}}>📅 {d.date}</span>
                      {d.modePaiement&&<span style={{fontSize:10,color:"#aaa"}}>· {d.modePaiement}</span>}
                    </div>
                    <div style={{fontSize:13,fontWeight:700,color:D}}>{d.description||"(sans description)"}</div>
                    {d.fournisseur&&<div style={{fontSize:10,color:"#888",marginTop:1}}>🏢 {d.fournisseur}</div>}
                  </div>
                  <div style={{textAlign:"right",flexShrink:0}}>
                    <div style={{fontSize:15,fontWeight:900,color:"#ef4444"}}>−{(parseFloat(d.montant)||0).toLocaleString("fr-FR")} F</div>
                  </div>
                  <div style={{display:"flex",gap:4,flexShrink:0}}>
                    {d.auto?<span title="Calculé depuis le stock — décochez l'option dans Paramètres pour le retirer" style={{fontSize:10,color:"#aaa",fontWeight:700,whiteSpace:"nowrap"}}>🧵 stock</span>:<><button onClick={()=>openEditDep(d)} style={{background:"#fffbeb",color:"#f59e0b",border:"none",padding:"4px 7px",borderRadius:5,cursor:"pointer",fontFamily:"inherit",fontSize:12}}>✏️</button>
                    <button onClick={()=>delDep(d.id)} style={{background:"#fef2f2",color:"#ef4444",border:"none",padding:"4px 7px",borderRadius:5,cursor:"pointer",fontFamily:"inherit",fontSize:12}}>🗑</button></>}
                  </div>
                </div>);
              })}
                  </>}
                </div>);
                });
              })()}
            </div>}
          </>}

          {/* ── ONGLET BILAN NET (Pro) ── */}
          {IS_PRO&&caisseTab==="bilan"&&<>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:14,marginBottom:18}}>
              <div style={{background:"linear-gradient(135deg,#1a1a2e,#2a2a4a)",color:"#fff",borderRadius:13,padding:"18px 20px"}}>
                <div style={{fontSize:11,color:"#aaa",fontWeight:600,letterSpacing:1,textTransform:"uppercase"}}>💵 Revenus (CA)</div>
                <div style={{fontSize:26,fontWeight:900,color:G,marginTop:7}}>{stats.tP.toLocaleString("fr-FR")} F</div>
                <div style={{fontSize:11,color:"#888",marginTop:4}}>Encaissé : {stats.tE.toLocaleString("fr-FR")} F</div>
              </div>
              <div style={{background:"linear-gradient(135deg,#7f1d1d,#991b1b)",color:"#fff",borderRadius:13,padding:"18px 20px"}}>
                <div style={{fontSize:11,color:"#fca5a5",fontWeight:600,letterSpacing:1,textTransform:"uppercase"}}>💸 Dépenses</div>
                <div style={{fontSize:26,fontWeight:900,color:"#fca5a5",marginTop:7}}>{(depStats.total+coutsCommande.total).toLocaleString("fr-FR")} F</div>
                <div style={{fontSize:11,color:"#fecaca",marginTop:4}}>{coutsCommande.total>0?`dont ${coutsCommande.total.toLocaleString("fr-FR")} F sur commandes`:`${toutesDepenses.length} dépense${toutesDepenses.length>1?"s":""}`}</div>
              </div>
              {(()=>{const netReel=stats.tP-depStats.total-coutsCommande.total;const marge=stats.tP>0?netReel/stats.tP*100:0;return(
              <div style={{background:`linear-gradient(135deg,${netReel>=0?"#065f46":"#7f1d1d"},${netReel>=0?"#10b981":"#dc2626"})`,color:"#fff",borderRadius:13,padding:"18px 20px"}}>
                <div style={{fontSize:11,color:"#a7f3d0",fontWeight:600,letterSpacing:1,textTransform:"uppercase"}}>💎 Bénéfice net</div>
                <div style={{fontSize:26,fontWeight:900,marginTop:7}}>{netReel.toLocaleString("fr-FR")} F</div>
                <div style={{fontSize:11,color:"#a7f3d0",marginTop:4}}>Marge : {marge.toFixed(1)}%</div>
              </div>);})()}
            </div>
            {/* TVA COLLECTÉE — pour une boutique assujettie : ce que la TVA représente
                dans le chiffre d'affaires de chaque mois, et ce qu'il faudra reverser. */}
            {atelierInfo.regimeFiscal==="reel"&&(()=>{
              const taux=parseFloat(atelierInfo.tauxTva)||18;
              const annee=new Date().getFullYear();
              const caBy=Object.fromEntries(ordersByMonth.map(g=>[g.key,g.tP]));
              const lignes=[];
              for(let m=1;m<=12;m++){
                const k=annee+"-"+String(m).padStart(2,"0");
                const ca=caBy[k]||0;
                if(ca>0)lignes.push({k,ca,ht:ca/(1+taux/100),tva:ca-ca/(1+taux/100)});
              }
              const totalTva=lignes.reduce((s,x)=>s+x.tva,0);
              const totalCA=lignes.reduce((s,x)=>s+x.ca,0);
              return(<div style={{background:"#fff",borderRadius:12,padding:"16px 18px",marginTop:16,boxShadow:"0 2px 6px rgba(0,0,0,0.04)"}}>
                <div style={{fontSize:13,fontWeight:800,color:D}}>🧾 TVA collectée · {taux} %</div>
                <div style={{fontSize:11,color:"#888",marginTop:3,lineHeight:1.6,marginBottom:12}}>Vos prix étant TTC, la TVA est déjà contenue dans vos ventes. Voici ce qu'elle représente mois par mois — c'est cet argent qui devra être reversé.</div>
                {lignes.length===0?<div style={{color:"#bbb",fontSize:12,textAlign:"center",padding:16}}>Aucune vente cette année.</div>:<>
                  <div style={{display:"grid",gridTemplateColumns:"1fr auto auto",gap:4,fontSize:10,fontWeight:800,color:"#aaa",textTransform:"uppercase",letterSpacing:.5,paddingBottom:6,borderBottom:"1px solid #f0ece4"}}>
                    <span>Mois</span><span style={{textAlign:"right"}}>Ventes TTC</span><span style={{textAlign:"right",minWidth:92}}>TVA collectée</span>
                  </div>
                  {lignes.map(x=>{const [y,m]=x.k.split("-");return(
                    <div key={x.k} style={{display:"grid",gridTemplateColumns:"1fr auto auto",gap:4,fontSize:12,padding:"7px 0",borderBottom:"1px solid #f7f5f0"}}>
                      <span style={{textTransform:"capitalize",color:"#555"}}>{new Date(parseInt(y),parseInt(m)-1,1).toLocaleDateString("fr-FR",{month:"long"})}</span>
                      <span style={{textAlign:"right",color:"#777"}}>{fmt(Math.round(x.ca))} F</span>
                      <span style={{textAlign:"right",fontWeight:800,color:"#b45309",minWidth:92}}>{fmt(Math.round(x.tva))} F</span>
                    </div>);})}
                  <div style={{display:"grid",gridTemplateColumns:"1fr auto auto",gap:4,fontSize:13,fontWeight:900,paddingTop:9,marginTop:2,borderTop:"2px solid "+D}}>
                    <span>Total {annee}</span>
                    <span style={{textAlign:"right"}}>{fmt(Math.round(totalCA))} F</span>
                    <span style={{textAlign:"right",color:"#b45309",minWidth:92}}>{fmt(Math.round(totalTva))} F</span>
                  </div>
                  <div style={{marginTop:12,background:"#fffbeb",border:"1px solid #fde68a",borderRadius:9,padding:"10px 12px",fontSize:11,color:"#92400e",lineHeight:1.6}}>
                    ⚠️ Mettez cet argent de côté : il ne vous appartient pas. Le rythme de versement est fixé par votre centre des impôts — le plus souvent chaque mois. Faites confirmer par votre comptable.
                  </div>
                </>}
              </div>);
            })()}
            {/* CA ET DÉPENSES CÔTE À CÔTE — 12 mois ; sous chaque mois, les deux montants
                en entier et le bénéfice net, pour voir d'un coup d'œil ce qui reste. */}
            {(()=>{
              const year=new Date().getFullYear();
              const keys=[];for(let m=1;m<=12;m++)keys.push(year+"-"+String(m).padStart(2,"0"));
              const caBy=Object.fromEntries(ordersByMonth.map(g=>[g.key,g.tP]));
              const depBy=Object.fromEntries(depensesByMonth.map(g=>[g.key,g.total]));
              const mois=keys.map(k=>{const ca=caBy[k]||0,dep=(depBy[k]||0)+(coutsCommande.byMonth[k]||0);return{key:k,ca,dep,net:ca-dep};});
              const hasData=mois.some(m=>m.ca||m.dep);
              const totalNet=mois.reduce((s,m)=>s+m.net,0);
              const puce=c=><span style={{width:10,height:10,borderRadius:3,background:c,display:"inline-block"}}/>;
              return(<div style={{background:"#fff",borderRadius:12,padding:"16px 20px",marginTop:16,boxShadow:"0 2px 6px rgba(0,0,0,0.04)"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10,flexWrap:"wrap",marginBottom:14}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:800,color:D}}>💎 Revenus et dépenses</div>
                    <div style={{fontSize:10,color:"#aaa",marginTop:2}}>Année {year} · Bénéfice net <strong style={{color:totalNet>=0?"#059669":"#ef4444"}}>{(totalNet>=0?"+":"")+totalNet.toLocaleString("fr-FR")} F</strong></div>
                  </div>
                  <div style={{display:"flex",gap:12,fontSize:10.5,fontWeight:700,color:"#555",flexWrap:"wrap"}}>
                    <span style={{display:"flex",alignItems:"center",gap:5}}>{puce("#2563eb")}Revenus</span>
                    <span style={{display:"flex",alignItems:"center",gap:5}}>{puce("#ef4444")}Dépenses</span>
                    <span style={{display:"flex",alignItems:"center",gap:5}}>{puce("#059669")}Net</span>
                  </div>
                </div>
                <HistoMois mois={mois} net="net" barres={[{cle:"ca",label:"Revenus",couleur:"#1d4ed8",grad:"linear-gradient(180deg,#2563eb,#93c5fd)"},{cle:"dep",label:"Dépenses",couleur:"#dc2626",grad:"linear-gradient(180deg,#ef4444,#fca5a5)"}]}/>
                {!hasData&&<div style={{textAlign:"center",fontSize:11,color:"#bbb",marginTop:10,fontStyle:"italic"}}>Pas encore de données cette année</div>}
              </div>);
            })()}
            {/* BILAN NET PAR MOIS — repliable comme les commandes/CA, détail par semaine */}
            {(()=>{
              const map={};
              ordersByMonth.forEach(g=>{map[g.key]={key:g.key,ca:g.tP,dep:0,orders:g.orders,deps:[]};});
              depensesByMonth.forEach(g=>{if(!map[g.key])map[g.key]={key:g.key,ca:0,dep:0,orders:[],deps:[]};map[g.key].dep=g.total;map[g.key].deps=g.depenses;});
              Object.keys(coutsCommande.byMonth).forEach(k=>{if(!map[k])map[k]={key:k,ca:0,dep:0,orders:[],deps:[]};map[k].dep+=coutsCommande.byMonth[k];});
              const keys=Object.keys(map).sort().reverse();
              if(keys.length===0)return null;
              return(<div style={{display:"flex",flexDirection:"column",gap:9,marginTop:16}}>
                <div style={{fontSize:13,fontWeight:800,color:D}}>📆 Bilan net par mois <span style={{fontWeight:400,color:"#aaa",fontSize:11}}>— cliquer un mois pour le détail par semaine</span></div>
                {keys.map(k=>{
                  const g=map[k];const net=g.ca-g.dep;const open=bilanOpenMonth===k;
                  const [y,m]=k.split("-");
                  const label=new Date(parseInt(y),parseInt(m)-1,1).toLocaleDateString("fr-FR",{month:"long",year:"numeric"});
                  const dim=new Date(parseInt(y),parseInt(m),0).getDate();
                  // Semaines : S1 = 1–7, S2 = 8–14, S3 = 15–21, S4 = 22–28, S5 = 29–fin
                  const weeks=open?(()=>{
                    const w=Array.from({length:Math.ceil(dim/7)},(_,i)=>({i,ca:0,dep:0}));
                    const wIdx=ds=>Math.min(w.length-1,Math.floor(((parseInt(ds.slice(8,10))||1)-1)/7));
                    g.orders.forEach(o=>{const d=o.dateDepot||new Date(o.id).toISOString().slice(0,10);const wi=wIdx(d);w[wi].ca+=parseFloat(o.prixTotal)||0;w[wi].dep+=(o.couts||[]).reduce((s,x)=>s+(parseFloat(x.m)||0),0);});
                    g.deps.forEach(dp=>{const d=dp.date||new Date(dp.id).toISOString().slice(0,10);w[wIdx(d)].dep+=parseFloat(dp.montant)||0;});
                    const mc=new Date(parseInt(y),parseInt(m)-1,1).toLocaleDateString("fr-FR",{month:"short"});
                    return w.map(x=>({...x,lbl:`Semaine ${x.i+1} (${String(x.i*7+1).padStart(2,"0")} ${mc} – ${String(Math.min(x.i*7+7,dim)).padStart(2,"0")} ${mc})`}));
                  })():[];
                  return(<div key={k} style={{background:"#fff",borderRadius:11,overflow:"hidden",border:"1px solid #f0f0f0",boxShadow:"0 2px 6px rgba(0,0,0,0.04)"}}>
                    <div onClick={()=>setBilanOpenMonth(open?null:k)} style={{display:"flex",alignItems:"center",gap:12,padding:"13px 16px",cursor:"pointer",background:open?"#f9f7f0":"#fff",borderBottom:open?"1px solid #f0ece4":"none",flexWrap:"wrap"}}>
                      <span style={{fontSize:18,color:open?G:"#bbb",transform:open?"rotate(90deg)":"",transition:"transform 0.15s"}}>▶</span>
                      <div style={{flex:1,minWidth:110}}>
                        <div style={{fontSize:14,fontWeight:800,color:D,textTransform:"capitalize"}}>{label}</div>
                      </div>
                      <div style={{display:"flex",gap:14,fontSize:11,fontWeight:700,alignItems:"center"}}>
                        <div style={{textAlign:"right"}}><div style={{color:"#aaa",fontSize:9,fontWeight:600,marginBottom:2}}>Revenus</div><div style={{color:D}}>{fmt(g.ca)} F</div></div>
                        <div style={{textAlign:"right"}}><div style={{color:"#aaa",fontSize:9,fontWeight:600,marginBottom:2}}>Dépenses</div><div style={{color:"#ef4444"}}>−{fmt(g.dep)} F</div></div>
                        <div style={{textAlign:"right"}}><div style={{color:"#aaa",fontSize:9,fontWeight:600,marginBottom:2}}>Bilan net</div><div style={{color:net>=0?"#10b981":"#ef4444",fontSize:12}}>{net>=0?"+":""}{fmt(net)} F</div></div>
                      </div>
                    </div>
                    {open&&<div style={{padding:"4px 16px 12px 40px"}}>
                      <div style={{display:"flex",gap:8,fontSize:9,color:"#bbb",fontWeight:700,padding:"6px 0",borderBottom:"1px solid #f0f0f0"}}>
                        <span style={{flex:1}}>SEMAINE</span>
                        <span style={{minWidth:78,textAlign:"right"}}>REVENUS</span>
                        <span style={{minWidth:78,textAlign:"right"}}>DÉPENSES</span>
                        <span style={{minWidth:86,textAlign:"right"}}>NET</span>
                      </div>
                      {weeks.map(wk=>{const wnet=wk.ca-wk.dep;const vide=wk.ca===0&&wk.dep===0;return(
                        <div key={wk.i} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 0",borderBottom:"1px solid #f5f5f5",fontSize:11,opacity:vide?0.45:1}}>
                          <span style={{flex:1,fontWeight:600,color:"#555"}}>{wk.lbl}</span>
                          <span style={{color:"#888",minWidth:78,textAlign:"right"}}>{fmt(wk.ca)} F</span>
                          <span style={{color:"#ef4444",minWidth:78,textAlign:"right"}}>−{fmt(wk.dep)} F</span>
                          <span style={{fontWeight:800,color:wnet>=0?"#10b981":"#ef4444",minWidth:86,textAlign:"right"}}>{wnet>=0?"+":""}{fmt(wnet)} F</span>
                        </div>);})}
                    </div>}
                  </div>);
                })}
              </div>);
            })()}
          </>}
        </div>}

        {/* ── COMMANDES ── */}
        {view==="orders"&&<div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
            <div><h2 style={{margin:"0 0 1px",color:D,fontSize:21,fontWeight:800,display:"flex",alignItems:"center",gap:9}}>🧾 {LBL.sales}</h2><div style={{color:"#aaa",fontSize:12}}>{filtered.length} résultat(s)</div></div>
            <div style={{display:"flex",gap:7}}>
              {!isGrossiste&&<button onClick={openNewExpress} title="Créer une commande avec seulement l'essentiel" style={{background:"#fff",color:D,border:"1.5px dashed "+G,padding:"9px 14px",borderRadius:8,cursor:"pointer",fontWeight:800,fontSize:12,fontFamily:"inherit"}}>⚡ Rapide</button>}
              <button onClick={openNewO} style={{background:G,color:D,border:"none",padding:"9px 18px",borderRadius:8,cursor:"pointer",fontWeight:800,fontSize:12,fontFamily:"inherit"}}>＋ Nouvelle</button>
            </div>
          </div>
          <div style={{display:"flex",gap:8,marginBottom:12,flexWrap:"wrap"}}>
            <div style={{flex:1,minWidth:160}}><label style={sl}>🔍 Rechercher (nom, téléphone, n°)</label><input value={search} onChange={e=>setSearch(e.target.value)} style={{...si,padding:"8px 12px"}}/></div>
            <select value={fSt} onChange={e=>setFSt(e.target.value)} style={{...si,width:"auto",minWidth:130,padding:"8px 10px"}}>
              <option value="all">Tous</option>{(isGrossiste?["Soldé","Reste à régler"]:STATUTS).map(s=><option key={s}>{s}</option>)}
            </select>
          </div>
          {(()=>{
            const renderRow=(o,i)=>{
              const late=SUIVI_LIVRAISON&&o.dateLivraison&&new Date(o.dateLivraison)<new Date()&&o.statut!=="Livré";
              const lc=o.clientId?clients.find(c=>c.id===o.clientId):null;
              const tissusV=(o.tissus||[]).filter(t=>t.type||t.metres||t.couleur).length;
              const nbP=Math.max(tissusV,parseInt(o.nombrePieces)||1,1);
              return isMobile?(
                <div key={o.id} style={{padding:"9px 12px",borderBottom:"1px solid #f5f5f5",background:i%2===0?"#fff":"#fafafa"}}>
                  <div style={{display:"flex",alignItems:"center",gap:7}}>
                    <span style={{background:G+"22",color:D,fontWeight:800,fontSize:9,padding:"2px 6px",borderRadius:4,flexShrink:0}}>{o.cmdNum}</span>
                    {lc&&<span style={{background:"#e0f2fe",color:"#0369a1",fontWeight:800,fontSize:9,padding:"2px 5px",borderRadius:4,flexShrink:0}}>{lc.cliNum}</span>}
                    {isGrossiste&&(()=>{const ph=photoVente(o);return ph?<img src={ph} alt="" loading="lazy" style={{width:34,height:34,objectFit:"cover",borderRadius:7,flexShrink:0,border:"1px solid #eee"}}/>:null;})()}
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:700,fontSize:12,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",display:"flex",alignItems:"center",gap:5}}>
                        <span style={{overflow:"hidden",textOverflow:"ellipsis"}}>{o.prenom} {o.nom}</span>
                        {nbP>1&&<span style={{background:"#dbeafe",color:"#1e40af",fontWeight:800,fontSize:9,padding:"1px 6px",borderRadius:10,flexShrink:0}}>×{nbP}</span>}
                        {o.priorite==="Urgente"&&o.statut!=="Livré"&&o.statut!=="Prêt"&&<span style={{color:"#ef4444",fontSize:10}}>🚨</span>}
                        {lc?.vip&&<span style={{fontSize:11}}>⭐</span>}
                      </div>
                      <div style={{fontSize:10,color:"#bbb",marginTop:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{isGrossiste?o.telephone:(o.categorie+" · "+o.telephone)}</div>
                    </div>
                    <div style={{textAlign:"right",flexShrink:0}}>
                      <div style={{fontWeight:800,fontSize:12}}>{fmt(o.prixTotal)} F</div>
                      <div style={{fontSize:9,color:rest(o)>0?"#ef4444":"#10b981",fontWeight:600}}>{rest(o).toLocaleString("fr-FR")} F rest.</div>
                    </div>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:7,marginTop:8,justifyContent:"space-between"}}>
                    <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap",minWidth:0}}>
                      <span style={{fontSize:10,color:late?"#ef4444":"#aaa",whiteSpace:"nowrap"}}>📅 {(isGrossiste?o.dateDepot:o.dateLivraison)||"—"}{late?" ⚠️":""}</span>
                      {isGrossiste?(()=>{const sg=statutGrossiste(o);return<span style={{background:SC_GROS[sg]+"22",color:SC_GROS[sg],border:"1px solid "+SC_GROS[sg],borderRadius:5,padding:"2px 7px",fontWeight:700,fontSize:9,fontFamily:"inherit",display:"inline-block"}}>{sg==="Soldé"?"✓ Soldé":"⏳ Reste"}</span>;})():<select value={o.statut} onChange={e=>updSt(o.id,e.target.value)} style={{background:SC[o.statut]+"22",color:SC[o.statut],border:"1px solid "+SC[o.statut],borderRadius:5,padding:"3px 6px",fontWeight:700,fontSize:9,cursor:"pointer",fontFamily:"inherit"}}>
                        {STATUTS.map(s=><option key={s}>{s}</option>)}
                      </select>}
                      {!isGrossiste&&o.statut!=="Livré"&&(()=>{const nx=STATUTS[STATUTS.indexOf(o.statut)+1];return nx?<button onClick={()=>updSt(o.id,nx)} title={"Passer à « "+nx+" »"} style={{background:"#dcfce7",color:"#166534",border:"none",borderRadius:5,padding:"3px 8px",cursor:"pointer",fontSize:9,fontWeight:800,fontFamily:"inherit"}}>➡️ {nx}</button>:null;})()}
                    </div>
                    <div style={{display:"flex",gap:5,flexShrink:0}}>
                      <button onClick={()=>setDetail(migrateTissus(o))} style={{background:"#eff6ff",color:"#3b82f6",border:"none",padding:"6px 10px",borderRadius:6,cursor:"pointer",fontFamily:"inherit",fontSize:13}}>👁</button>
                      <button onClick={()=>openEditO(o)} style={{background:"#fffbeb",color:"#f59e0b",border:"none",padding:"6px 10px",borderRadius:6,cursor:"pointer",fontFamily:"inherit",fontSize:13}}>✏️</button>
                      <button onClick={()=>delO(o.id)} style={{background:"#fef2f2",color:"#ef4444",border:"none",padding:"6px 10px",borderRadius:6,cursor:"pointer",fontFamily:"inherit",fontSize:13}}>🗑</button>
                    </div>
                  </div>
                </div>
              ):(<div key={o.id} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 14px",borderBottom:"1px solid #f5f5f5",background:i%2===0?"#fff":"#fafafa"}}>
                <span style={{background:G+"22",color:D,fontWeight:800,fontSize:9,padding:"2px 7px",borderRadius:4,flexShrink:0}}>{o.cmdNum}</span>
                {lc&&<span style={{background:"#e0f2fe",color:"#0369a1",fontWeight:800,fontSize:9,padding:"2px 6px",borderRadius:4,flexShrink:0}}>{lc.cliNum}</span>}
                {isGrossiste&&(()=>{const ph=photoVente(o);return ph?<img src={ph} alt="" loading="lazy" style={{width:38,height:38,objectFit:"cover",borderRadius:7,flexShrink:0,border:"1px solid #eee"}}/>:null;})()}
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:700,fontSize:12,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",display:"flex",alignItems:"center",gap:6}}>
                    <span>{o.prenom} {o.nom}</span>
                    {nbP>1&&<span style={{background:"#dbeafe",color:"#1e40af",fontWeight:800,fontSize:9,padding:"1px 7px",borderRadius:10,flexShrink:0}}>× {nbP} pièces</span>}
                    {o.priorite==="Urgente"&&o.statut!=="Livré"&&o.statut!=="Prêt"&&<span style={{color:"#ef4444",fontSize:10}}>🚨</span>}
                    {lc?.vip&&<span style={{fontSize:11}}>⭐</span>}
                  </div>
                  <div style={{fontSize:10,color:"#bbb",marginTop:1}}>{isGrossiste?o.telephone:(o.categorie+" · "+o.telephone)}</div>
                </div>
                <div style={{textAlign:"right",flexShrink:0}}>
                  <div style={{fontWeight:700,fontSize:12}}>{fmt(o.prixTotal)} F</div>
                  <div style={{fontSize:10,color:rest(o)>0?"#ef4444":"#10b981",fontWeight:600}}>{rest(o).toLocaleString("fr-FR")} F restant</div>
                </div>
                <div style={{textAlign:"right",flexShrink:0}}>
                  <div style={{fontSize:10,color:late?"#ef4444":"#aaa",whiteSpace:"nowrap"}}>{(isGrossiste?o.dateDepot:o.dateLivraison)||"—"}{late?" ⚠️":""}</div>
                  {isGrossiste?(()=>{const sg=statutGrossiste(o);return<span style={{background:SC_GROS[sg]+"22",color:SC_GROS[sg],border:"1px solid "+SC_GROS[sg],borderRadius:5,padding:"2px 7px",fontWeight:700,fontSize:9,fontFamily:"inherit",display:"inline-block",marginTop:2}}>{sg==="Soldé"?"✓ Soldé":"⏳ Reste à régler"}</span>;})():<select value={o.statut} onChange={e=>updSt(o.id,e.target.value)} style={{background:SC[o.statut]+"22",color:SC[o.statut],border:"1px solid "+SC[o.statut],borderRadius:5,padding:"2px 6px",fontWeight:700,fontSize:9,cursor:"pointer",fontFamily:"inherit",marginTop:2}}>
                    {STATUTS.map(s=><option key={s}>{s}</option>)}
                  </select>}
                  {/* STATUT EN UN CLIC : passe directement à l'étape suivante */}
                  {!isGrossiste&&o.statut!=="Livré"&&(()=>{const nx=STATUTS[STATUTS.indexOf(o.statut)+1];return nx?<button onClick={()=>updSt(o.id,nx)} title={"Passer à « "+nx+" »"} style={{display:"block",marginTop:3,marginLeft:"auto",background:"#dcfce7",color:"#166534",border:"none",borderRadius:5,padding:"2px 8px",cursor:"pointer",fontSize:9,fontWeight:800,fontFamily:"inherit"}}>➡️ {nx}</button>:null;})()}
                </div>
                <div style={{display:"flex",gap:4,flexShrink:0}}>
                  <button onClick={()=>setDetail(migrateTissus(o))} style={{background:"#eff6ff",color:"#3b82f6",border:"none",padding:"5px 7px",borderRadius:5,cursor:"pointer",fontFamily:"inherit",fontSize:12}}>👁</button>
                  <button onClick={()=>openEditO(o)} style={{background:"#fffbeb",color:"#f59e0b",border:"none",padding:"5px 7px",borderRadius:5,cursor:"pointer",fontFamily:"inherit",fontSize:12}}>✏️</button>
                  <button onClick={()=>delO(o.id)} style={{background:"#fef2f2",color:"#ef4444",border:"none",padding:"5px 7px",borderRadius:5,cursor:"pointer",fontFamily:"inherit",fontSize:12}}>🗑</button>
                </div>
              </div>);
            };
            if(filtered.length===0)return(<div style={{background:"#fff",borderRadius:12,boxShadow:"0 2px 6px rgba(0,0,0,0.04)",textAlign:"center",padding:36,color:"#ccc",fontSize:12}}>{isGrossiste?"Aucune vente trouvée.":"Aucune commande trouvée."}</div>);
            const isSearching=search.trim()!==""||fSt!=="all";
            // Liste plate : Standard, ou pendant une recherche/filtre (résultats directs).
            // Grossiste et Premium : un dossier par mois, le mois en cours ouvert.
            if((!IS_PREMIUM&&!isGrossiste)||isSearching)return(<div style={{background:"#fff",borderRadius:12,boxShadow:"0 2px 6px rgba(0,0,0,0.04)",overflow:"hidden"}}>{filtered.map(renderRow)}</div>);
            // PREMIUM : RACCOURCIS MENSUELS — un dossier par mois (comme le CA en Caisse)
            const groups={};
            filtered.forEach(o=>{const d=o.dateDepot||new Date(o.id).toISOString().slice(0,10);const k=d.slice(0,7);(groups[k]=groups[k]||[]).push(o);});
            const keys=Object.keys(groups).sort().reverse();
            return(<div style={{display:"flex",flexDirection:"column",gap:9}}>
              {keys.map(k=>{
                const list=groups[k];
                const open=ordersOpenMonth===k;
                const [y,m]=k.split("-");
                const monthLabel=new Date(parseInt(y),parseInt(m)-1,1).toLocaleDateString("fr-FR",{month:"long",year:"numeric"});
                const tot=list.reduce((s,o)=>s+(parseFloat(o.prixTotal)||0),0);
                const enCours=list.filter(o=>o.statut!=="Livré").length;
                const nbLate=SUIVI_LIVRAISON?list.filter(o=>o.dateLivraison&&new Date(o.dateLivraison)<new Date()&&o.statut!=="Livré").length:0;
                return(<div key={k} style={{background:"#fff",borderRadius:11,boxShadow:"0 2px 6px rgba(0,0,0,0.04)",overflow:"hidden",border:"1px solid #f0f0f0"}}>
                  <div onClick={()=>setOrdersOpenMonth(open?null:k)} style={{display:"flex",alignItems:"center",gap:12,padding:"13px 16px",cursor:"pointer",background:open?"#f9f7f0":"#fff",borderBottom:open?"1px solid #f0ece4":"none"}}>
                    <span style={{fontSize:18,color:open?G:"#bbb",transition:"transform 0.15s",transform:open?"rotate(90deg)":""}}>▶</span>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:14,fontWeight:800,color:D,textTransform:"capitalize"}}>📅 {monthLabel}</div>
                      <div style={{fontSize:11,color:"#888",marginTop:1}}>{list.length} {isGrossiste?"vente":"commande"}{list.length>1?"s":""} · {fmt(tot)} F</div>
                    </div>
                    <div style={{display:"flex",gap:6,alignItems:"center",flexShrink:0,flexWrap:"wrap",justifyContent:"flex-end"}}>
                      {isGrossiste?(()=>{
                        const dus=list.filter(o=>resteDe(o)>0);
                        return dus.length?<span style={{background:"#fef3c7",color:"#92400e",fontSize:10,fontWeight:800,padding:"3px 9px",borderRadius:11}}>⏳ {dus.length} à régler · {fmt(dus.reduce((s,o)=>s+resteDe(o),0))} F</span>:<span style={{background:"#dcfce7",color:"#166534",fontSize:10,fontWeight:800,padding:"3px 9px",borderRadius:11}}>✓ Tout soldé</span>;
                      })():<>
                        {nbLate>0&&<span style={{background:"#fef2f2",color:"#ef4444",fontSize:10,fontWeight:800,padding:"3px 9px",borderRadius:11}}>⚠️ {nbLate} en retard</span>}
                        {enCours>0?<span style={{background:"#fef3c7",color:"#92400e",fontSize:10,fontWeight:800,padding:"3px 9px",borderRadius:11}}>⏳ {enCours} en cours</span>:<span style={{background:"#dcfce7",color:"#166534",fontSize:10,fontWeight:800,padding:"3px 9px",borderRadius:11}}>✓ Tout livré</span>}
                      </>}
                    </div>
                  </div>
                  {open&&<div>{list.map(renderRow)}</div>}
                </div>);
              })}
            </div>);
          })()}
        </div>}

        {/* ── COLLECTION ── */}
        {view==="collection"&&!dispo("collection")&&<Vitrine fonction="collection" onVoirOffres={()=>setVoirOffres(true)}/>}
        {view==="collection"&&dispo("collection")&&<div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
            <div><h2 style={{margin:"0 0 1px",color:D,fontSize:21,fontWeight:800,display:"flex",alignItems:"center",gap:9}}>{IS_GROSSISTE?<img src="collection.png" alt="" style={{width:28,height:28,borderRadius:7,objectFit:"cover",display:"block"}}/>:<span>👗</span>}Collection</h2><div style={{color:"#aaa",fontSize:12}}>{isGrossiste?colMeta.length+" tissu(s) · la vitrine de votre stock":colMeta.length+" modèle(s)"}</div></div>
            <div style={{display:"flex",gap:7}}>
              {IS_PRO&&colMeta.length>0&&<button onClick={()=>{setBoutiqueIdx(0);setShowBoutique(true);}} style={{background:D,color:G,border:"none",padding:"9px 16px",borderRadius:8,cursor:"pointer",fontWeight:800,fontSize:12,fontFamily:"inherit"}}>📱 Mode Boutique</button>}
              <button onClick={openNewC} style={{background:G,color:D,border:"none",padding:"9px 18px",borderRadius:8,cursor:"pointer",fontWeight:800,fontSize:12,fontFamily:"inherit"}}>{isGrossiste?"＋ Ajouter un tissu":"＋ Ajouter"}</button>
              {LIMITE_COLLECTION!==Infinity&&<span style={{fontSize:11,color:colMeta.length>=LIMITE_COLLECTION?"#ef4444":"#aaa",fontWeight:700,marginLeft:9}}>{colMeta.length} / {LIMITE_COLLECTION} modèles</span>}
              <button onClick={imprimerCatalogue} title="Créer un catalogue à imprimer" style={{background:dispo("cataloguePdf")?"#1e3a5f":"transparent",color:dispo("cataloguePdf")?"#fff":"#999",border:dispo("cataloguePdf")?"none":"1px solid #ddd",padding:"9px 14px",borderRadius:8,cursor:"pointer",fontWeight:800,fontSize:12,fontFamily:"inherit",marginLeft:9}}>
                {dispo("cataloguePdf")?"📖 Catalogue papier":"🔒 Catalogue papier"}
              </button>
            </div>
          </div>
          <div style={{display:"flex",gap:6,marginBottom:14,flexWrap:"wrap"}}>
            {["Tous",...catsVitrine].map(cat=>(<button key={cat} onClick={()=>setColCat(cat)} style={{padding:"4px 11px",borderRadius:20,border:"none",cursor:"pointer",fontSize:11,fontFamily:"inherit",background:colCat===cat?D:"#e8e8e8",color:colCat===cat?G:"#666",fontWeight:colCat===cat?700:400}}>{cat}</button>))}
          </div>
          {filtCol.length===0
            ?<div style={{textAlign:"center",padding:"44px 16px",background:"#fff",borderRadius:12,color:"#ccc"}}><div style={{fontSize:44,marginBottom:10}}>📸</div>Aucun modèle.<div style={{fontSize:11,marginTop:6,color:"#bbb"}}>Constituez votre book photo — vos clients commanderont sur catalogue</div></div>
            :<div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))",gap:10}}>
              {filtCol.map((c,idx)=>{
                const nbCmd=orders.filter(o=>(o.tissus||[]).some(t=>(t.modele||"").toLowerCase().includes((c.titre||"").toLowerCase())&&c.titre)).length;
                return(<div key={c.id} style={{background:"#fff",borderRadius:10,overflow:"hidden",boxShadow:"0 1px 6px rgba(0,0,0,0.06)",cursor:"pointer",transition:"transform 0.15s, box-shadow 0.15s",border:"1px solid #f0f0f0"}} onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-3px)";e.currentTarget.style.boxShadow="0 6px 16px rgba(0,0,0,0.12)";}} onMouseLeave={e=>{e.currentTarget.style.transform="";e.currentTarget.style.boxShadow="0 1px 6px rgba(0,0,0,0.06)";}}>
                  <div onClick={()=>setColLightbox({index:idx,items:filtCol})} style={{position:"relative",cursor:"zoom-in"}}>
                    {colPhotos[c.id]?<img src={colPhotos[c.id]} alt="" style={{width:"100%",height:130,objectFit:"cover",display:"block"}} loading="lazy"/>:<div style={{height:130,background:"linear-gradient(135deg,#f0ece4,#e8e0d4)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:42}}>👗</div>}
                    {nbCmd>0&&<span style={{position:"absolute",top:6,right:6,background:"rgba(16,185,129,0.95)",color:"#fff",fontWeight:800,fontSize:9,padding:"2px 7px",borderRadius:12,boxShadow:"0 2px 5px rgba(0,0,0,0.2)"}}>🔥 {nbCmd}×</span>}
                    {c.prixIndicatif&&<span style={{position:"absolute",bottom:6,left:6,background:"rgba(26,26,46,0.92)",color:G,fontWeight:800,fontSize:10,padding:"2px 7px",borderRadius:5}}>{fmt(c.prixIndicatif)} F{IS_GROSSISTE?"/m":""}</span>}
                  </div>
                  <div onClick={()=>setColDetail(c)} style={{padding:"8px 10px 6px"}}>
                    <div style={{fontWeight:700,color:D,fontSize:12,marginBottom:3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.titre||"Sans titre"}</div>
                    <div style={{fontSize:9,color:"#888"}}>{c.categorie}</div>
                  </div>
                  {/* Menu discret ⋮ — remplace les gros boutons Modifier/Supprimer */}
                  <div style={{display:"flex",gap:5,padding:"0 8px 6px",position:"relative",alignItems:"center"}}>
                    {!isGrossiste&&<button onClick={e=>{e.stopPropagation();setColCardMenu(m=>m===c.id?null:c.id);}} aria-label="Options" style={{marginLeft:"auto",background:colCardMenu===c.id?"#f0ece4":"transparent",border:"none",borderRadius:7,width:30,height:30,cursor:"pointer",fontSize:17,color:"#aaa",fontFamily:"inherit",lineHeight:1}}>⋮</button>}
                    {colCardMenu===c.id&&<>
                      <div onClick={e=>{e.stopPropagation();setColCardMenu(null);}} style={{position:"fixed",inset:0,zIndex:5}}/>
                      <div style={{position:"absolute",bottom:34,right:6,background:"#fff",borderRadius:10,boxShadow:"0 8px 26px rgba(0,0,0,0.18)",border:"1px solid #eee",overflow:"hidden",zIndex:6,minWidth:150}}>
                        <button onClick={e=>{e.stopPropagation();setColCardMenu(null);openEditC(c);}} style={{display:"block",width:"100%",textAlign:"left",background:"transparent",border:"none",padding:"11px 14px",cursor:"pointer",fontSize:12.5,fontFamily:"inherit",color:D,fontWeight:600}}>✏️ Modifier</button>
                        <button onClick={e=>{e.stopPropagation();setColCardMenu(null);delC(c.id);}} style={{display:"block",width:"100%",textAlign:"left",background:"transparent",border:"none",borderTop:"1px solid #f5f2ec",padding:"11px 14px",cursor:"pointer",fontSize:12.5,fontFamily:"inherit",color:"#ef4444",fontWeight:600}}>🗑 Supprimer</button>
                      </div>
                    </>}
                  </div>
                </div>);
              })}
            </div>
          }
        </div>}

        {/* ── STOCK (Pro) ── */}
        {view==="stock"&&!dispo("stock")&&<Vitrine fonction="stock" onVoirOffres={()=>setVoirOffres(true)}/>}
        {view==="stock"&&dispo("stock")&&<div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
            <div><h2 style={{margin:"0 0 1px",color:D,fontSize:21,fontWeight:800,display:"flex",alignItems:"center",gap:9}}>{IS_GROSSISTE?<img src="stock.jpg" alt="" style={{width:28,height:28,borderRadius:7,objectFit:"cover",display:"block"}}/>:<span>🧵</span>}Stock &amp; Fournisseurs</h2><div style={{color:"#aaa",fontSize:12}}>Gestion des tissus et achats</div></div>
            <div style={{display:"flex",gap:7}}>
              {stockTab==="inventaire"&&<button onClick={openNewStock} style={{background:G,color:D,border:"none",padding:"9px 16px",borderRadius:8,cursor:"pointer",fontWeight:800,fontSize:12,fontFamily:"inherit"}}>＋ Article</button>}
              {stockTab==="fournisseurs"&&<button onClick={openNewFourn} style={{background:G,color:D,border:"none",padding:"9px 16px",borderRadius:8,cursor:"pointer",fontWeight:800,fontSize:12,fontFamily:"inherit"}}>＋ Fournisseur</button>}
            </div>
          </div>
          {/* KPIs stock */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:11,marginBottom:14}}>
            {[{i:"💰",l:"Valeur stock",v:fmt(stockStats.valeur)+" F",c:G},{i:"📦",l:"Total mètres",v:Math.round(stockStats.total)+" m",c:D},{i:"⚠️",l:"Stock bas",v:fmt(stockStats.nbBas)+" pièce"+(stockStats.nbBas>1?"s":""),c:"#f59e0b"},{i:"🔴",l:"Rupture de stock",v:fmt(stockStats.nbRupture)+" pièce"+(stockStats.nbRupture>1?"s":""),c:"#ef4444"}].map(({i,l,v,c})=>(
              <div key={l} style={{background:"#fff",borderRadius:11,padding:"12px 14px",boxShadow:"0 2px 6px rgba(0,0,0,0.04)",borderLeft:"3px solid "+c}}>
                <div style={{fontSize:18}}>{i}</div>
                <div style={{fontSize:18,fontWeight:900,color:c,marginTop:5}}>{v}</div>
                <div style={{fontSize:11,color:"#aaa",marginTop:2}}>{l}</div>
              </div>
            ))}
          </div>
          {/* Onglets */}
          <div style={{display:"flex",gap:3,marginBottom:14,background:"#f5f5f5",borderRadius:9,padding:3,maxWidth:360}}>
            {[["inventaire","📦 Inventaire"],["fournisseurs","🚚 Fournisseurs"]].map(([id,l])=>(
              <button key={id} onClick={()=>setStockTab(id)} style={{flex:1,padding:"7px 10px",borderRadius:7,border:"none",cursor:"pointer",fontWeight:stockTab===id?700:400,fontSize:11,background:stockTab===id?"#fff":"transparent",color:stockTab===id?D:"#888",fontFamily:"inherit",boxShadow:stockTab===id?"0 1px 4px rgba(0,0,0,0.07)":"none"}}>{l}</button>
            ))}
          </div>

          {/* ONGLET INVENTAIRE */}
          {stockTab==="inventaire"&&(()=>{
            if(stock.length===0)return<div style={{textAlign:"center",padding:"44px 16px",background:"#fff",borderRadius:12,color:"#ccc"}}><div style={{fontSize:44,marginBottom:10}}>🧵</div>Aucun article en stock.<div style={{fontSize:11,marginTop:6,color:"#bbb"}}>Cliquez sur "+ Article" pour démarrer votre inventaire</div></div>;
            const renderTable=(items)=>(
            <div style={{background:"#fff",borderRadius:12,overflowX:"auto",boxShadow:"0 2px 6px rgba(0,0,0,0.04)",WebkitOverflowScrolling:"touch"}}>
              <table style={{width:"100%",minWidth:isMobile?780:undefined,borderCollapse:"collapse",fontSize:12}}>
                <thead><tr style={{background:D,color:G}}>{["Tissu","Couleur","Stock","Rangement","Prix achat/m","Prix vente/m","Valeur","Fournisseur","Actions"].map(h=><th key={h} style={{padding:"10px 12px",textAlign:"left",fontWeight:700,fontSize:10}}>{h}</th>)}</tr></thead>
                <tbody>
                  {items.map((s,i)=>{
                    const m=parseFloat(s.metres)||0;
                    const p=parseFloat(s.prixUnitaire)||0;
                    const low=stockLowThreshold(s);
                    const status=m<=0?"rupture":m<=low?"bas":"ok";
                    const f=s.fournisseurId?fournisseurs.find(x=>x.id===s.fournisseurId):null;
                    const cSel=PALETTE.find(c=>c.n===s.couleur);
                    return(<tr key={s.id} style={{background:i%2?"#fafafa":"#fff",borderLeft:status==="rupture"?"3px solid #ef4444":status==="bas"?"3px solid #f59e0b":"3px solid transparent"}}>
                      <td style={{padding:"10px 12px",fontWeight:700,color:D}}>
                        <div style={{display:"flex",alignItems:"center",gap:9}}>
                          {stockPhotos[s.id]
                            ?<img src={stockPhotos[s.id]} alt="" loading="lazy" onClick={()=>setStockApercu({src:stockPhotos[s.id],titre:(s.type==="Autre"?s.custom:s.type||"Article")+(s.couleur?" · "+s.couleur:"")})} title="Agrandir la photo" style={{width:42,height:42,objectFit:"cover",borderRadius:7,border:"1px solid #e8e8e8",flexShrink:0,cursor:"zoom-in"}}/>
                            :<div style={{width:42,height:42,borderRadius:7,background:"#f5f2ea",border:"1px dashed #e0d8c8",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,color:"#c8bda8"}}>🧵</div>}
                          <span>
                            {nomArticle(s)}
                            {refsStock[s.id]&&<span style={{display:"block",fontSize:9.5,fontWeight:800,color:"#a99a78",letterSpacing:.6,marginTop:2}}>{refsStock[s.id]}</span>}
                          </span>
                        </div>
                      </td>
                      <td style={{padding:"10px 12px"}}>{s.couleur?<span style={{display:"inline-flex",alignItems:"center",gap:5}}><span style={{width:11,height:11,borderRadius:"50%",background:cSel?cSel.h:(s.couleurHex||"#d8d8d8"),border:"1px solid rgba(0,0,0,0.12)",display:"inline-block",flexShrink:0}}/>{s.couleur}</span>:"—"}</td>
                      <td style={{padding:"10px 12px",fontWeight:800,color:status==="rupture"?"#ef4444":status==="bas"?"#f59e0b":"#10b981",whiteSpace:"nowrap"}}>{m.toFixed(1)} m {status==="rupture"?"🔴":status==="bas"?"⚠️":""}</td>
                      <td style={{padding:"10px 12px",fontSize:11,color:"#666",whiteSpace:"nowrap"}}>
                        {s.etagere&&<div style={{display:"flex",alignItems:"center",gap:4,marginBottom:3}}><span style={{background:"#1a1a2e",color:G,fontSize:9,fontWeight:800,padding:"2px 6px",borderRadius:4}}>🗄️ {s.etagere}</span></div>}
                        {(()=>{
                          const mpp=parseFloat(s.metresParPiece)||0;
                          const ppb=parseFloat(s.piecesParBalle)||0;
                          const mRest=parseFloat(s.metres)||0;
                          if(mpp<=0)return s.etagere?null:<span style={{color:"#ccc"}}>—</span>;
                          // Calcul auto : pièces restantes (entamée compte pour 1) + balles
                          const piecesRest=mRest>0?Math.ceil(mRest/mpp):0;
                          const ballesRest=ppb>0&&piecesRest>0?Math.ceil(piecesRest/ppb):0;
                          return(<div style={{fontSize:10,color:"#666",lineHeight:1.5}}>
                            {ballesRest>0&&<div>📦 {ballesRest} balle{ballesRest>1?"s":""}{ppb>0?` (${ppb} pc/balle)`:""}</div>}
                            {piecesRest>0&&<div style={{color:"#888"}}>📐 {piecesRest} pièce{piecesRest>1?"s":""} × {mpp}{s.uniteMesure==="yd"?"yd":"m"}</div>}
                          </div>);
                        })()}
                        {!s.etagere&&!(parseFloat(s.metresParPiece)>0)&&<span style={{color:"#ccc"}}>—</span>}
                      </td>
                      <td style={{padding:"10px 12px",color:"#666",whiteSpace:"nowrap"}}>{p>0?fmt(p)+" F":"—"}</td>
                      <td style={{padding:"10px 12px",fontWeight:700,color:"#0369a1",whiteSpace:"nowrap"}}>{parseFloat(s.prixVente)>0?fmt(s.prixVente)+" F":<span style={{color:"#bbb",fontWeight:400}}>—</span>}</td>
                      <td style={{padding:"10px 12px",fontWeight:700,color:G,whiteSpace:"nowrap"}}>{fmt(m*p)} F</td>
                      <td style={{padding:"10px 12px",fontSize:11,color:"#666"}}>{f?f.nom:<span style={{color:"#bbb"}}>—</span>}</td>
                      <td style={{padding:"10px 12px"}}>
                        <div style={{display:"flex",gap:5}}>
                          <button onClick={()=>openRecep(s.id)} title="Une balle vient d'arriver du fournisseur : ajouter des mètres à cet article" style={{background:"#10b981",color:"#fff",border:"none",padding:"4px 9px",borderRadius:5,cursor:"pointer",fontSize:10,fontWeight:700,fontFamily:"inherit",whiteSpace:"nowrap"}}>📥 Arrivage</button>
                          <button onClick={()=>openEditStock(s)} style={{background:"#fffbeb",color:"#f59e0b",border:"none",padding:"4px 7px",borderRadius:5,cursor:"pointer",fontFamily:"inherit",fontSize:12}}>✏️</button>
                          <button onClick={()=>delStock(s.id)} style={{background:"#fef2f2",color:"#ef4444",border:"none",padding:"4px 7px",borderRadius:5,cursor:"pointer",fontFamily:"inherit",fontSize:12}}>🗑</button>
                        </div>
                      </td>
                    </tr>);
                  })}
                </tbody>
              </table>
            </div>
            );
            // COUTURIER : tableau plat classique
            if(!IS_GROSSISTE)return renderTable(stock);
            // GROSSISTE : accordéon par catégorie de tissu (liste courte, clic → détail)
            const groups={};
            // Un tissu que le vendeur a nommé lui-même mérite son propre groupe : le
            // ranger dans « Autre » avec tous les autres inconnus rend l'inventaire
            // illisible dès qu'il y en a plusieurs.
            stock.forEach(x=>{const cat=familleStock(x);(groups[cat]=groups[cat]||[]).push(x);});
            const orderedCats=ordreFamilles(Object.keys(groups));
            return(<div style={{display:"flex",flexDirection:"column",gap:9}}>
              {orderedCats.map(cat=>{
                const items=groups[cat];
                const open=stockOpenCat===cat;
                const totM=items.reduce((a,x)=>a+(parseFloat(x.metres)||0),0);
                const totV=items.reduce((a,x)=>a+((parseFloat(x.metres)||0)*(parseFloat(x.prixUnitaire)||0)),0);
                const nbAlert=items.filter(x=>(parseFloat(x.metres)||0)<=stockLowThreshold(x)).length;
                return(<div key={cat} style={{background:"#fff",borderRadius:11,boxShadow:"0 2px 6px rgba(0,0,0,0.04)",overflow:"hidden",border:"1px solid #f0f0f0"}}>
                  <div onClick={()=>setStockOpenCat(open?null:cat)} style={{display:"flex",alignItems:"center",gap:11,padding:"13px 15px",cursor:"pointer",background:open?"#f9f7f0":"#fff",borderBottom:open?"1px solid #f0ece4":"none"}}>
                    <span style={{fontSize:15,color:open?G:"#bbb",transition:"transform 0.15s",transform:open?"rotate(90deg)":"",display:"inline-block"}}>▶</span>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:14,fontWeight:800,color:D}}>{cat}</div>
                      <div style={{fontSize:10,color:"#888",marginTop:1}}>{items.length} article{items.length>1?"s":""}</div>
                    </div>
                    {nbAlert>0&&<span style={{background:"#fef3c7",color:"#92400e",fontSize:10,fontWeight:800,padding:"3px 9px",borderRadius:12,flexShrink:0}}>⚠️ {nbAlert}</span>}
                    <div style={{textAlign:"right",flexShrink:0}}>
                      <div style={{fontSize:13,fontWeight:900,color:"#10b981"}}>{Math.round(totM)} m</div>
                      <div style={{fontSize:9,color:"#888"}}>{fmt(totV)} F</div>
                    </div>
                  </div>
                  {open&&renderTable(items)}
                </div>);
              })}
            </div>);
          })()}

          {/* ONGLET FOURNISSEURS */}
          {stockTab==="fournisseurs"&&(fournisseurs.length===0
            ?<div style={{textAlign:"center",padding:"44px 16px",background:"#fff",borderRadius:12,color:"#ccc"}}><div style={{fontSize:44,marginBottom:10}}>🚚</div>Aucun fournisseur enregistré.<div style={{fontSize:11,marginTop:6,color:"#bbb"}}>Ajoutez vos fournisseurs pour passer commande en 1 clic</div></div>
            :<div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:11}}>
              {fournisseurs.map(f=>{
                const waNum=(f.telephone||"").replace(/\D/g,"");
                const subjPlain=`Demande de commande tissus — ${atelierInfo.nom||""}`;
                const bodyPlain=`Bonjour ${f.nom},\n\nNous souhaitons passer commande pour les tissus suivants :\n\n[À compléter]\n\nMerci de nous indiquer la disponibilité, les prix actuels et les délais de livraison.\n\nCordialement,\n${atelierInfo.nom||""}\n${atelierInfo.tel?"📞 "+atelierInfo.tel+"\n":""}${atelierInfo.adresse?"📍 "+atelierInfo.adresse:""}`;
                const waMsg=encodeURIComponent(`Bonjour ${f.nom}, ${atelierInfo.nom||"notre atelier"} souhaite passer commande. Pouvez-vous nous indiquer la disponibilité et le prix actuel ?\n\nMerci.\n— ${atelierInfo.nom||""}${atelierInfo.tel?"\n📞 "+atelierInfo.tel:""}`);
                const waUrl=waNum?`https://wa.me/221${waNum}?text=${waMsg}`:null;
                const mailto=f.email?`mailto:${f.email}?subject=${encodeURIComponent(subjPlain)}&body=${encodeURIComponent(bodyPlain)}`:null;
                const gmailUrl=f.email?`https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(f.email)}&su=${encodeURIComponent(subjPlain)}&body=${encodeURIComponent(bodyPlain)}`:null;
                const openLink=(url)=>{if(window.electronAPI?.openExternal)window.electronAPI.openExternal(url);else window.open(url,"_blank");};
                const onMail=()=>{
                  // Essaye d'abord le client mail par défaut ; si l'utilisateur veut Gmail web, clic droit sur l'app pour le configurer
                  if(mailto)openLink(mailto);
                  // Petit fallback automatique : ouvre aussi Gmail web après 800ms si possible (utilisateur peut fermer si déjà ouvert)
                  // → comportement simple : on ouvre mailto, l'OS gère
                };
                return(<div key={f.id} style={{background:"#fff",borderRadius:12,padding:"13px 14px",boxShadow:"0 2px 6px rgba(0,0,0,0.05)",border:"1px solid #f0f0f0"}}>
                  <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:6}}>
                    <div style={{minWidth:0,flex:1}}>
                      <div style={{fontWeight:800,color:D,fontSize:14}}>{f.nom||"Sans nom"}</div>
                      {f.specialite&&<div style={{fontSize:10,color:"#888",marginTop:2}}>{f.specialite}</div>}
                    </div>
                    <div style={{display:"flex",gap:4,flexShrink:0}}>
                      <button onClick={()=>openEditFourn(f)} style={{background:"#fffbeb",color:"#f59e0b",border:"none",padding:"4px 7px",borderRadius:5,cursor:"pointer",fontFamily:"inherit",fontSize:12}}>✏️</button>
                      <button onClick={()=>delFourn(f.id)} style={{background:"#fef2f2",color:"#ef4444",border:"none",padding:"4px 7px",borderRadius:5,cursor:"pointer",fontFamily:"inherit",fontSize:12}}>🗑</button>
                    </div>
                  </div>
                  <div style={{fontSize:11,color:"#666",lineHeight:1.7,marginBottom:9}}>
                    {f.telephone&&<div>📞 {f.telephone}</div>}
                    {f.email&&<div style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>📧 {f.email}</div>}
                    {(f.marche||f.ville)&&<div>📍 {[f.marche,f.ville].filter(Boolean).join(" · ")}</div>}
                  </div>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    {waUrl?<button onClick={()=>openLink(waUrl)} style={{flex:"1 1 100px",background:"#25D366",color:"#fff",border:"none",padding:"8px",borderRadius:7,cursor:"pointer",fontWeight:700,fontFamily:"inherit",fontSize:11}}>💬 WhatsApp</button>:<button disabled style={{flex:"1 1 100px",background:"#f5f5f5",color:"#ccc",border:"none",padding:"8px",borderRadius:7,fontWeight:700,fontSize:11,fontFamily:"inherit",cursor:"not-allowed"}}>💬 WhatsApp</button>}
                    {gmailUrl?<button onClick={()=>openLink(gmailUrl)} title="Ouvrir dans Gmail web" style={{flex:"1 1 100px",background:"#3b82f6",color:"#fff",border:"none",padding:"8px",borderRadius:7,cursor:"pointer",fontWeight:700,fontFamily:"inherit",fontSize:11}}>📧 Gmail</button>:<button disabled style={{flex:"1 1 100px",background:"#f5f5f5",color:"#ccc",border:"none",padding:"8px",borderRadius:7,fontWeight:700,fontSize:11,fontFamily:"inherit",cursor:"not-allowed"}}>📧 Gmail</button>}
                    {mailto&&<button onClick={onMail} title="Ouvrir avec le client mail par défaut" style={{flex:"0 0 36px",background:"#f5f5f5",color:"#3b82f6",border:"none",padding:"8px",borderRadius:7,cursor:"pointer",fontWeight:700,fontFamily:"inherit",fontSize:13}}>✉️</button>}
                  </div>
                </div>);
              })}
            </div>
          )}
        </div>}
      </div>

      {/* FORMULAIRE COMMANDE */}
      {showOF&&<div ref={formRef} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:200,display:"flex",alignItems:"flex-start",justifyContent:"center",overflowY:"auto",padding:14}}>
        <FlecheHaut cible={formRef} bas={24} z={220}/>
        <div style={{background:"#fff",borderRadius:15,width:"100%",maxWidth:660,padding:22,margin:"auto",boxShadow:"0 18px 56px rgba(0,0,0,0.14)"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
            <div style={{fontSize:16,fontWeight:800,color:D}}>{express?"⚡ Commande rapide":editO?"✏️ Modifier":"➕ "+LBL.newSale}</div>
            <button onClick={()=>setShowOF(false)} style={{background:"#f5f5f5",border:"none",borderRadius:7,width:31,height:31,cursor:"pointer",fontSize:17,fontFamily:"inherit"}}>×</button>
          </div>
          {!express&&<div style={{display:"flex",gap:3,marginBottom:18,background:"#f5f5f5",borderRadius:9,padding:3,overflowX:"auto"}}>
            {fTabs.map(t=>(<button key={t.id} onClick={()=>setFTab(t.id)} style={{padding:"6px 10px",borderRadius:7,border:"none",cursor:"pointer",fontWeight:fTab===t.id?700:400,fontSize:10,background:fTab===t.id?"#fff":"transparent",color:fTab===t.id?D:"#999",whiteSpace:"nowrap",fontFamily:"inherit",boxShadow:fTab===t.id?"0 1px 4px rgba(0,0,0,0.07)":"none"}}>{t.l}{essaiO&&manquantsO.some(x=>x.tab===t.id)&&<span style={{display:"inline-block",width:7,height:7,borderRadius:"50%",background:"#ef4444",marginLeft:5,verticalAlign:"middle"}}/>}</button>))}
          </div>}
          {IS_PREMIUM&&!isGrossiste&&<input ref={tissuPhotoRef} type="file" accept="image/*" style={{display:"none"}} onChange={handleTissuPhoto}/>}

          {express&&<Sec title="⚡ L'essentiel — le reste peut se compléter plus tard">
            {form.clientId?(()=>{const lc2=clients.find(c=>c.id===form.clientId);return lc2?<div style={{display:"flex",alignItems:"center",gap:9,background:"#eff6ff",border:"1px solid #bae6fd",borderRadius:8,padding:"9px 11px",marginBottom:11}}>
              <span style={{background:"#0ea5e9",color:"#fff",fontWeight:800,fontSize:10,padding:"3px 8px",borderRadius:5}}>{lc2.cliNum}</span>
              <div style={{flex:1,minWidth:0,fontWeight:700,fontSize:13,color:D}}>{lc2.prenom} {lc2.nom} {lc2.vip&&"⭐"}</div>
              <button type="button" onClick={()=>setForm(f=>({...f,clientId:null}))} style={{background:"#fef2f2",color:"#ef4444",border:"none",padding:"5px 10px",borderRadius:6,cursor:"pointer",fontSize:11,fontFamily:"inherit",fontWeight:600}}>✕</button>
            </div>:null;})()
            :<div style={{position:"relative",marginBottom:11}}>
              <label style={sl}>🔍 Client déjà connu ? Son nom ou son téléphone</label><input value={cliSearch} onChange={e=>setCliSearch(e.target.value)} style={{...si,fontSize:12}}/>
              {cliMatches.length>0&&<div style={{position:"absolute",top:"100%",left:0,right:0,background:"#fff",border:"1.5px solid #e0e0e0",borderRadius:8,zIndex:400,boxShadow:"0 8px 24px rgba(0,0,0,0.10)",maxHeight:220,overflowY:"auto",marginTop:3}}>
                {cliMatches.map(c=>(<div key={c.id} onMouseDown={e=>{e.preventDefault();selectClientForOrder(c);}} style={{padding:"9px 12px",cursor:"pointer",borderBottom:"1px solid #f5f5f5",display:"flex",alignItems:"center",gap:8}}>
                  <span style={{background:"#e0f2fe",color:"#0369a1",fontWeight:800,fontSize:9,padding:"2px 6px",borderRadius:4}}>{c.cliNum}</span>
                  <div style={{flex:1,minWidth:0}}><div style={{fontWeight:700,fontSize:12}}>{c.prenom} {c.nom} {c.vip&&<span>⭐</span>}</div><div style={{fontSize:10,color:"#aaa"}}>{c.telephone||"—"}</div></div>
                </div>))}
              </div>}
            </div>}
            <Row><Fld label="Prénom :" value={form.prenom} onChange={F("prenom")} casse="prenom"/><Fld label="Nom :" value={form.nom} onChange={F("nom")} casse="nom"/></Row>
            <Row>
              <div style={{flex:1,minWidth:0}}>
                <label style={sl}>Téléphone :</label>
                <div style={{display:"flex",gap:5}}>
                  <input value={form.telephone} onChange={e=>setForm(f=>({...f,telephone:fmtTel(e.target.value)}))} style={{...si,flex:1}} maxLength={12} inputMode="tel"/>
                  {CONTACTS_OK&&<button type="button" title="Choisir dans le répertoire du téléphone" onClick={async()=>{const c=await choisirContact();if(c)setForm(f=>({...f,telephone:c.tel||f.telephone,prenom:f.prenom||c.prenom,nom:f.nom||c.nom}));}} style={{background:"#eff6ff",border:"1.5px solid #bae6fd",borderRadius:8,padding:"0 11px",cursor:"pointer",fontSize:16,flexShrink:0}}>📇</button>}
                </div>
              </div>
              <Fld label="📅 Livraison :" value={form.dateLivraison} onChange={F("dateLivraison")} type="date"/>
            </Row>
            <Row>
              <div style={{flex:1,minWidth:0}}>
                <label style={sl}>Prix total (F) :</label>
                <ChiffreInput value={form.prixTotal} onChange={e=>{setPrixManuel(true);const v=e.target.value;setForm(f=>({...f,prixTotal:v}));}} style={si}/>
              </div>
              <Fld label="Avance reçue (F) :" value={form.avanceRecue} onChange={F("avanceRecue")} type="number"/>
            </Row>
            <div>
              <label style={sl}>Modèle souhaité :</label>
              <textarea value={(form.tissus[0]&&form.tissus[0].modele)||""} onChange={e=>upT(0,"modele",e.target.value)} rows={3} style={{...si,resize:"vertical",lineHeight:1.6}}/>
            </div>
            {/* Les 2 photos aussi en express : tissu apporté par le client + modèle voulu */}
            {IS_PREMIUM&&<div style={{display:"flex",gap:8,marginTop:9}}>
              {[["photoTissu","🧵 Photo du tissu apporté"],["photoModele","👗 Photo du modèle à faire"]].map(([slot,lbl])=>{
                const t0f=form.tissus[0]||{};const data=t0f[slot+"Data"];
                return(<div key={slot} style={{flex:1,minWidth:0}}>
                  <label style={sl}>{lbl}</label>
                  {data?<div style={{position:"relative"}}>
                    <img src={data} alt="" onClick={()=>pickTissuPhoto(0,slot)} title="Cliquer pour remplacer" style={{width:"100%",height:110,objectFit:"cover",borderRadius:9,cursor:"pointer",display:"block",border:"1.5px solid #e5e5e5"}}/>
                    <button type="button" onClick={()=>upT(0,slot+"Data","")} title="Retirer la photo" style={{position:"absolute",top:5,right:5,background:"rgba(0,0,0,0.55)",color:"#fff",border:"none",borderRadius:6,padding:"3px 8px",cursor:"pointer",fontSize:11,fontFamily:"inherit"}}>✕</button>
                  </div>
                  :<div onClick={()=>pickTissuPhoto(0,slot)} style={{border:"2px dashed #ddd",borderRadius:9,height:110,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",cursor:"pointer",background:"#fff",color:"#bbb",gap:4}}>
                    <div style={{fontSize:24}}>📷</div><div style={{fontSize:10,fontWeight:600}}>Ajouter une photo</div>
                  </div>}
                </div>);
              })}
            </div>}
          </Sec>}

          {fTab==="client"&&<Sec title="👤 Client">
            {/* LIAISON CLIENT */}
            <div style={{background:linkedClient?"#eff6ff":"#f9f7f0",border:linkedClient?"1.5px solid #bae6fd":"1.5px dashed "+G,borderRadius:10,padding:12,marginBottom:13}}>
              <div style={{fontSize:10,fontWeight:800,color:linkedClient?"#0369a1":"#92400e",marginBottom:8,textTransform:"uppercase",letterSpacing:1}}>🔗 Fiche client</div>
              {linkedClient?<div style={{display:"flex",alignItems:"center",gap:9,background:"#fff",borderRadius:8,padding:"9px 11px"}}>
                <span style={{background:"#0ea5e9",color:"#fff",fontWeight:800,fontSize:10,padding:"3px 8px",borderRadius:5}}>{linkedClient.cliNum}</span>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:700,fontSize:13,color:D}}>{linkedClient.prenom} {linkedClient.nom} {linkedClient.vip&&"⭐"}</div>
                  <div style={{fontSize:10,color:"#aaa"}}>{linkedClient.telephone||""}</div>
                </div>
                <button type="button" onClick={()=>setForm(f=>({...f,clientId:null}))} style={{background:"#fef2f2",color:"#ef4444",border:"none",padding:"5px 10px",borderRadius:6,cursor:"pointer",fontSize:11,fontFamily:"inherit",fontWeight:600}}>✕ Délier</button>
              </div>
              :<>
                <div style={{position:"relative"}}>
                  <label style={sl}>🔍 Chercher un client existant (nom, téléphone, n°)</label><input ref={cliSearchRef} value={cliSearch} onChange={e=>setCliSearch(e.target.value)} style={{...si,fontSize:12}}/>
                  {cliMatches.length>0&&<div style={{position:"absolute",top:"100%",left:0,right:0,background:"#fff",border:"1.5px solid #e0e0e0",borderRadius:8,zIndex:400,boxShadow:"0 8px 24px rgba(0,0,0,0.10)",maxHeight:240,overflowY:"auto",marginTop:3}}>
                    {cliMatches.map(c=>(<div key={c.id} onMouseDown={e=>{e.preventDefault();selectClientForOrder(c);}} style={{padding:"9px 12px",cursor:"pointer",borderBottom:"1px solid #f5f5f5",display:"flex",alignItems:"center",gap:8}} onMouseEnter={e=>e.currentTarget.style.background="#f0f7ff"} onMouseLeave={e=>e.currentTarget.style.background=""}>
                      <span style={{background:"#e0f2fe",color:"#0369a1",fontWeight:800,fontSize:9,padding:"2px 6px",borderRadius:4}}>{c.cliNum}</span>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontWeight:700,fontSize:12}}>{c.prenom} {c.nom} {c.vip&&<span>⭐</span>}</div>
                        <div style={{fontSize:10,color:"#aaa"}}>{c.telephone||"—"} · {c.ville||"—"}</div>
                      </div>
                    </div>))}
                  </div>}
                </div>
              </>}
            </div>
            <Row><Fld label="Nom :" value={form.nom} onChange={F("nom")} casse="nom"/><Fld label="Prénom :" value={form.prenom} onChange={F("prenom")} casse="prenom"/></Row>
            <Row>
              <div style={{flex:1,minWidth:0}}>
                <label style={sl}>Téléphone :</label>
                <div style={{display:"flex",gap:5}}>
                  <input value={form.telephone} onChange={e=>setForm(f=>({...f,telephone:fmtTel(e.target.value)}))} style={{...si,flex:1}} maxLength={12} inputMode="tel"/>
                  {CONTACTS_OK&&<button type="button" title="Choisir dans le répertoire du téléphone" onClick={async()=>{const c=await choisirContact();if(c)setForm(f=>({...f,telephone:c.tel||f.telephone,prenom:f.prenom||c.prenom,nom:f.nom||c.nom}));}} style={{background:"#eff6ff",border:"1.5px solid #bae6fd",borderRadius:8,padding:"0 11px",cursor:"pointer",fontSize:16,flexShrink:0}}>📇</button>}
                </div>
              </div>
              <AcField label="Nationalité :" value={form.nationalite} onChange={FC("nationalite")} suggestions={PAYS}/>
            </Row>
            <Row>
              <AcField label="📍 Adresse :" value={form.ville} onChange={FC("ville")} suggestions={VILLES}/>
              <div style={{flex:1,minWidth:0}}/>
            </Row>
          </Sec>}

          {fTab==="commande"&&<Sec title="🏷️ Commande">
            <Row><Slct label="Catégorie :" value={form.categorie} onChange={F("categorie")} options={CATEGORIES}/></Row>
            <Row><Fld label="Nombre de pièces :" value={form.nombrePieces} onChange={F("nombrePieces")} type="number"/><Slct label="Priorité :" value={form.priorite} onChange={F("priorite")} options={PRIORITES}/><Slct label="Statut :" value={form.statut} onChange={F("statut")} options={STATUTS}/></Row>
          </Sec>}

          {fTab==="tissu"&&<Sec title={`🧶 Tissus${form.tissus.length>1?" ("+form.tissus.length+")":""}`}>
            {form.tissus.map((t,i)=>{
              const cSel=COULEURS.find(c=>c.n===t.couleur);
              return(<div key={i} style={{background:"#f9f9f9",border:"1.5px solid #eee",borderRadius:10,padding:12,marginBottom:9}}>
                {form.tissus.length>1&&<div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:9}}>
                  <span style={{background:G,color:D,padding:"2px 9px",borderRadius:11,fontSize:10,fontWeight:700}}>Tissu n°{i+1}</span>
                  <button onClick={()=>remT(i)} style={{background:"#fef2f2",color:"#ef4444",border:"none",borderRadius:6,padding:"3px 9px",cursor:"pointer",fontSize:11,fontFamily:"inherit"}}>✕ Retirer</button>
                </div>}
                {/* SOURCE DU TISSU — Pro couturier seulement (pas grossiste) */}
                {IS_PRO&&!isGrossiste&&<div style={{display:"flex",gap:5,marginBottom:11,background:"#fff",padding:4,borderRadius:8,border:"1px solid #e8e8e8"}}>
                  <button type="button" onClick={()=>upT(i,"source","client")} style={{flex:1,padding:"6px 10px",borderRadius:6,border:"none",cursor:"pointer",fontSize:11,fontWeight:t.source!=="atelier"?700:500,background:t.source!=="atelier"?"#dbeafe":"transparent",color:t.source!=="atelier"?"#1e40af":"#777",fontFamily:"inherit"}}>🧳 Apporté par le client</button>
                  <button type="button" onClick={()=>upT(i,"source","atelier")} style={{flex:1,padding:"6px 10px",borderRadius:6,border:"none",cursor:"pointer",fontSize:11,fontWeight:t.source==="atelier"?700:500,background:t.source==="atelier"?"#fef3c7":"transparent",color:t.source==="atelier"?"#92400e":"#777",fontFamily:"inherit"}}>🏬 Vendu par l'atelier</button>
                </div>}
                {isGrossiste?<>
                  {/* On vend ce qu'on possède : la liste montre le stock réel, avec sa
                      pastille de couleur, ce qu'il en reste et son prix au mètre. Le
                      vendeur ne peut plus chercher une couleur qu'il n'a pas. */}
                  <div style={{marginBottom:9}}>
                    <label style={sl}>Article vendu :</label>
                    <div style={{display:"flex",gap:8,alignItems:"center"}}>
                      <span style={{width:28,height:28,borderRadius:8,flexShrink:0,background:t.couleurHex||(cSel?cSel.h:"#ececec"),border:"1px solid rgba(0,0,0,0.14)"}}/>
                      <select value={t.stockId||""} onChange={e=>{
                        const a=stock.find(x=>String(x.id)===e.target.value);
                        if(!a){majT(i,{stockId:"",type:"",custom:"",couleur:"",couleurHex:""});return;}
                        majT(i,{stockId:String(a.id),type:a.type||"",custom:a.custom||"",couleur:a.couleur||"",couleurHex:a.couleurHex||""});
                      }} style={{...si,flex:1,minWidth:0}}>
                        <option value="">-- Choisir dans le stock --</option>
                        {[...stock].sort((a,b)=>a.id-b.id).map(a=>{
                          const nom=nomArticle(a);
                          const reste=parseFloat(a.metres)||0;
                          const pv=parseFloat(a.prixVente)||0;
                          return <option key={a.id} value={a.id}>{(refsStock[a.id]||"")+" · "+nom+(a.couleur?" · "+a.couleur:"")+" · "+reste.toLocaleString("fr-FR")+" m"+(pv>0?" · "+pv.toLocaleString("fr-FR")+" F/m":"")}</option>;
                        })}
                      </select>
                      <button type="button" onClick={()=>{setPickerQ("");setPickerFam("Tous");setPickerVente(i);}} title="Choisir en regardant les photos — plus sûr quand la boutique tient des centaines de tissus" style={{flexShrink:0,width:42,height:38,borderRadius:8,border:"1.5px solid "+G,background:"#fffdf7",cursor:"pointer",fontSize:17,fontFamily:"inherit"}}>🖼️</button>
                    </div>
                    {stock.length===0&&<div style={{fontSize:11,color:"#92400e",background:"#fffbeb",border:"1px solid #fde68a",borderRadius:7,padding:"8px 10px",marginTop:7,lineHeight:1.5}}>Aucun article en stock. Enregistrez vos tissus dans la rubrique Stock : ils apparaîtront ici.</div>}
                    {(()=>{
                      const a=stock.find(x=>String(x.id)===String(t.stockId));
                      if(!a)return null;
                      const reste=parseFloat(a.metres)||0,dem=parseFloat(t.metres)||0;
                      if(dem>reste)return<div style={{fontSize:11.5,marginTop:6,padding:"7px 10px",borderRadius:7,background:"#fef2f2",border:"1px solid #fca5a5",color:"#991b1b",fontWeight:700}}>⚠️ Il ne reste que {reste.toLocaleString("fr-FR")} m de cet article</div>;
                      if(dem>0)return<div style={{fontSize:11.5,marginTop:6,color:"#166534",fontWeight:600}}>Il restera {(reste-dem).toLocaleString("fr-FR")} m après cette vente</div>;
                      return<div style={{fontSize:11.5,marginTop:6,color:"#888"}}>{reste.toLocaleString("fr-FR")} m disponibles</div>;
                    })()}
                  </div>
                  <Row>
                    <Fld label={uniteDe(t)==="yd"?"Yards vendus :":"Mètres vendus :"} value={qteDe(t)} onChange={e=>majQte(i,e.target.value)} type="number" suffix={uniteDe(t)} onSuffixClick={()=>basculerUnite(i)}/>
                    <div style={{flex:1,minWidth:0}}/>
                  </Row>
                </>:<>
                <Row>
                  <div style={{flex:1,minWidth:0}}>
                    <label style={sl}>Type :</label>
                    <select value={TISSUS.includes(t.type)?t.type:(t.type?"Autre":"")} onChange={e=>upT(i,"type",e.target.value)} style={si}>
                      <option value="">-- Choisir --</option>
                      {Object.entries(TISSUS_BY_CAT).map(([cat,list])=>(
                        <optgroup key={cat} label={cat}>
                          {list.map(x=><option key={x} value={x}>{x}</option>)}
                        </optgroup>
                      ))}
                    </select>
                    {(t.type==="Autre"||(t.type&&!TISSUS.includes(t.type)))&&<><label style={{...sl,marginTop:6}}>Nom du tissu</label><input value={t.custom||""} onChange={e=>upT(i,"custom",mettreEnForme(e.target.value))} style={si}/></>}
                  </div>
                  <Fld label={uniteDe(t)==="yd"?"Yards :":"Mètres :"} value={qteDe(t)} onChange={e=>majQte(i,e.target.value)} type="number" suffix={uniteDe(t)} onSuffixClick={()=>basculerUnite(i)}/>
                </Row>
                <div style={{marginBottom:9}}>
                  <label style={sl}>Couleur :</label>
                  <button type="button" onClick={()=>setCpOpen(cpOpen===i?-1:i)} style={{...si,display:"flex",alignItems:"center",gap:7,cursor:"pointer",background:"#fff",textAlign:"left"}}>
                    {t.couleur?<><span style={{width:16,height:16,borderRadius:"50%",background:cSel?cSel.h:"#ccc",border:"1px solid rgba(0,0,0,0.12)",display:"inline-block",flexShrink:0}}/><span style={{fontWeight:600}}>{t.couleur}</span></>:<span style={{color:"#bbb"}}>Choisir...</span>}
                    <span style={{marginLeft:"auto",color:"#ccc",fontSize:9}}>▼</span>
                  </button>
                  {cpOpen===i&&<div style={{background:"#fff",border:"1.5px solid #e0e0e0",borderRadius:10,padding:11,marginTop:3,boxShadow:"0 7px 22px rgba(0,0,0,0.09)"}}>
                    <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:4}}>
                      {COULEURS.map(c=>(<button key={c.n} type="button" onClick={()=>{upT(i,"couleur",c.n);setCpOpen(-1);}} style={{display:"flex",alignItems:"center",gap:4,padding:"5px 7px",border:t.couleur===c.n?"2px solid "+D:"1px solid #eee",borderRadius:7,cursor:"pointer",background:"#fff",fontFamily:"inherit"}}>
                        <span style={{width:15,height:15,borderRadius:"50%",background:c.h,border:"1px solid rgba(0,0,0,0.1)",display:"inline-block",flexShrink:0}}/><span style={{fontSize:10,color:"#666"}}>{c.n}</span>
                      </button>))}
                    </div>
                  </div>}
                </div>
                </>}
                {!isGrossiste&&<div><label style={sl}>Modèle :</label><textarea value={t.modele} onChange={e=>upT(i,"modele",e.target.value)} rows={2} style={{...si,resize:"vertical",lineHeight:1.6}}/></div>}
                {/* PHOTOS (Premium) : tissu apporté par le client + modèle à confectionner */}
                {IS_PREMIUM&&!isGrossiste&&<div style={{display:"flex",gap:8,marginTop:9}}>
                  {[["photoTissu","🧵 Photo du tissu apporté"],["photoModele","👗 Photo du modèle à faire"]].map(([slot,lbl])=>{
                    const data=t[slot+"Data"];
                    return(<div key={slot} style={{flex:1,minWidth:0}}>
                      <label style={sl}>{lbl}</label>
                      {data
                        ?<div style={{position:"relative"}}>
                          <img src={data} alt="" onClick={()=>pickTissuPhoto(i,slot)} title="Cliquer pour remplacer" style={{width:"100%",height:110,objectFit:"cover",borderRadius:9,cursor:"pointer",display:"block",border:"1.5px solid #e5e5e5"}}/>
                          <button type="button" onClick={()=>upT(i,slot+"Data","")} title="Retirer la photo" style={{position:"absolute",top:5,right:5,background:"rgba(0,0,0,0.55)",color:"#fff",border:"none",borderRadius:6,padding:"3px 8px",cursor:"pointer",fontSize:11,fontFamily:"inherit"}}>✕</button>
                        </div>
                        :<div onClick={()=>pickTissuPhoto(i,slot)} style={{border:"2px dashed #ddd",borderRadius:9,height:110,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",cursor:"pointer",background:"#fff",color:"#bbb",gap:4}}>
                          <div style={{fontSize:24}}>📷</div><div style={{fontSize:10,fontWeight:600}}>Ajouter une photo</div>
                        </div>}
                    </div>);
                  })}
                </div>}
                {/* PRIX CALCULÉ PAR TISSU (Pro) — auto à partir du stock prix de vente.
                    Couturier : uniquement si le tissu est vendu par l'atelier (un tissu apporté
                    par le client n'a rien à voir avec le stock → pas d'alerte inutile) */}
                {IS_PRO&&(()=>{
                  if(!isGrossiste&&t.source!=="atelier")return null;
                  const m=parseFloat(t.metres)||0;
                  if(m<=0||!t.type)return null;
                  const tType=t.type==="Autre"?(t.custom||""):(t.type||"");
                  const s=articleDeLigne(t,stock);
                  if(!s)return<div style={{marginTop:8,background:"#fef2f2",border:"1px dashed #fca5a5",borderRadius:7,padding:"7px 10px",fontSize:10,color:"#991b1b"}}>⚠️ Aucun stock correspondant ({tType} {t.couleur||""}) — pas de calcul auto</div>;
                  const pv=prixLigne(t,stock);
                  if(pv<=0)return<div style={{marginTop:8,background:"#fef3c7",border:"1px dashed #fde68a",borderRadius:7,padding:"7px 10px",fontSize:10,color:"#92400e"}}>💡 Renseignez "Prix de vente" pour {tType} {t.couleur||""} dans Stock pour activer le calcul auto</div>;
                  const stockRest=parseFloat(s.metres)||0;
                  const lineTotal=m*pv;
                  const insuffisant=stockRest<m;
                  return<div style={{marginTop:8,background:insuffisant?"#fef2f2":"#f0fdf4",border:"1px solid "+(insuffisant?"#fca5a5":"#86efac"),borderRadius:7,padding:"8px 11px",fontSize:11,display:"flex",justifyContent:"space-between",alignItems:"center",gap:8}}>
                    <span style={{color:insuffisant?"#991b1b":"#166534",fontWeight:600}}>{insuffisant?"⚠️ Stock insuffisant":"✓"} : {uniteDe(t)==="yd"?`${qteDe(t)} yd (${m} m)`:`${m} m`} × {pv.toLocaleString("fr-FR")} F/m</span>
                    <span style={{fontWeight:800,color:insuffisant?"#991b1b":"#166534",fontSize:13}}>= {lineTotal.toLocaleString("fr-FR")} F</span>
                  </div>;
                })()}
              </div>);
            })}
            <button type="button" onClick={addT} style={{background:"#f9f7f0",border:"2px dashed "+G,color:D,padding:"9px 14px",borderRadius:8,cursor:"pointer",fontWeight:700,width:"100%",fontFamily:"inherit",fontSize:12}}>＋ Ajouter un tissu</button>
            {/* Libellé — une ligne pour décrire la vente, à la place de l'ancien
                onglet Notes. Le vendeur note l'essentiel sans ouvrir un cadre de
                texte. Même champ de données qu'avant : rien n'est perdu. */}
            <div style={{marginTop:14,borderTop:"1px dashed #e5e5e5",paddingTop:12}}>
              <label style={sl}>🏷️ Libellé de la vente</label>
              <input value={form.remarques} onChange={e=>{setLibelleManuel(true);F("remarques")(e);}} style={si}/>
            </div>
          </Sec>}

          {fTab==="mesures"&&<Sec title="📐 Mesures (cm)">
            <div style={{display:"flex",gap:7,marginBottom:14}}>
              {[{id:"haut",l:"👔 Haut"},{id:"bas",l:"👖 Bas"}].map(t=>(<button key={t.id} onClick={()=>setMTab(t.id)} style={{padding:"8px 18px",borderRadius:8,border:"none",cursor:"pointer",fontWeight:700,fontSize:12,background:mTab===t.id?D:"#f0f0f0",color:mTab===t.id?G:"#777",fontFamily:"inherit"}}>{t.l}</button>))}
            </div>
            <div style={{fontSize:10,color:"#888",marginBottom:10,background:"#f9f7f0",borderRadius:7,padding:"7px 10px"}}>💡 Format libre : un chiffre (ex. 42) ou plusieurs valeurs (ex. 90/104/80), comme vous voulez.</div>
            {mTab==="haut"&&<><Row><Fld label="1. Épaule :" value={form.mEpaule} onChange={F("mEpaule")} type="text"/><Fld label="2. Longueur :" value={form.mLongueur} onChange={F("mLongueur")} type="text"/><Fld label="3. Poitrine :" value={form.mPoitrine} onChange={F("mPoitrine")} type="text"/></Row>
            <Row><Fld label="4. Taille :" value={form.mTaille} onChange={F("mTaille")} type="text"/><Fld label="5. Manche :" value={form.mManche} onChange={F("mManche")} type="text"/><Fld label="6. Poignet :" value={form.mPoignet} onChange={F("mPoignet")} type="text"/></Row>
            <Row><Fld label="7. Bras :" value={form.mBras} onChange={F("mBras")} type="text"/><Fld label="8. Bassin :" value={form.mBassinH} onChange={F("mBassinH")} type="text"/><Fld label="9. Col :" value={form.mCol} onChange={F("mCol")} type="text"/></Row></>}
            {mTab==="bas"&&<><Row><Fld label="1. Ceinture :" value={form.mCeinture} onChange={F("mCeinture")} type="text"/><Fld label="2. Longueur P :" value={form.mLongueurP} onChange={F("mLongueurP")} type="text"/><Fld label="3. Cuisse :" value={form.mCuisse} onChange={F("mCuisse")} type="text"/></Row>
            <Row><Fld label="4. Bassin :" value={form.mBassinP} onChange={F("mBassinP")} type="text"/><Fld label="5. Bas :" value={form.mBas} onChange={F("mBas")} type="text"/><div style={{flex:1}}/></Row></>}
            {/* AUTRES MESURES — libellé + valeur libres, ajoutables à volonté */}
            <div style={{marginTop:8,borderTop:"1px dashed #e5e5e5",paddingTop:12}}>
              <div style={{fontSize:11,fontWeight:800,color:D,marginBottom:8}}>➕ Autres mesures</div>
              {(form.mesuresPlus||[]).length>0&&<div style={{display:"flex",gap:7,marginBottom:4}}><span style={{...sl,flex:1,marginBottom:0}}>Nom de la mesure</span><span style={{...sl,width:110,flexShrink:0,marginBottom:0}}>Mesure</span><span style={{width:30,flexShrink:0}}/></div>}
              {(form.mesuresPlus||[]).map((m,i)=>(
                <div key={i} style={{display:"flex",gap:7,marginBottom:7,alignItems:"center"}}>
                  <input value={m.l} onChange={e=>upMesPlus(i,"l",e.target.value)} aria-label="Nom de la mesure" style={{...si,flex:1,minWidth:0}}/>
                  <input value={m.v} onChange={e=>upMesPlus(i,"v",e.target.value)} aria-label="Mesure" style={{...si,width:110,flexShrink:0}}/>
                  <button type="button" onClick={()=>remMesPlus(i)} style={{background:"#fef2f2",color:"#ef4444",border:"none",borderRadius:6,width:30,height:30,cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>✕</button>
                </div>
              ))}
              <button type="button" onClick={()=>addMesPlus()} style={{background:"transparent",border:"1.5px dashed "+G,color:D,padding:"8px",borderRadius:8,cursor:"pointer",fontWeight:700,width:"100%",fontFamily:"inherit",fontSize:12}}>＋ Ajouter une mesure</button>
            </div>
          </Sec>}

          {fTab==="finances"&&<Sec title="💰 Dates & Finances">
            {/* Une vente de tissus a une seule date : le jour où le client achète. */}
            {IS_GROSSISTE
              ?<Row><Fld label="📅 Date de la vente :" value={form.dateDepot} onChange={e=>{const v=e.target.value;setForm(f=>({...f,dateDepot:v,dateLivraison:v}));}} type="date"/><div style={{flex:1,minWidth:0}}/></Row>
              :<Row><Fld label="📅 Dépôt :" value={form.dateDepot} onChange={F("dateDepot")} type="date"/><Fld label="📅 Livraison :" value={form.dateLivraison} onChange={F("dateLivraison")} type="date"/></Row>}
            {IS_PRO&&prixSuggere>0&&<div style={{background:"#eff6ff",border:"1px solid #bae6fd",borderRadius:8,padding:"8px 11px",marginBottom:9,fontSize:11,color:"#0369a1",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span>💡 Prix suggéré (selon stock) : <strong>{prixSuggere.toLocaleString("fr-FR")} F</strong></span>
              {form.prixTotal!==String(prixSuggere)&&<button type="button" onClick={()=>{setForm(f=>({...f,prixTotal:String(prixSuggere)}));setPrixManuel(false);}} style={{background:"#0369a1",color:"#fff",border:"none",padding:"4px 10px",borderRadius:6,cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:"inherit"}}>↺ Appliquer</button>}
            </div>}
            <Row><Fld label="💵 Prix total (FCFA) :" value={form.prixTotal} onChange={e=>{setPrixManuel(true);setForm(f=>({...f,prixTotal:e.target.value}));}} type="number"/><Fld label="✅ Montant versé (FCFA) :" value={form.avanceRecue} onChange={e=>{const v=e.target.value;setVersementManuel(true);
              // Le plus souvent le client règle tout d'un coup. Tant que le prix total
              // n'a été ni saisi à la main ni calculé depuis le stock, il vaut ce qui
              // est versé : sinon le reste à payer s'affichait en négatif.
              const totalLibre=!prixManuel&&prixSuggere<=0;
              setForm(f=>totalLibre?{...f,avanceRecue:v,prixTotal:v}:{...f,avanceRecue:v});}} type="number"/></Row>
            <Row>
              <div style={{flex:1,minWidth:0}}>
                <label style={sl}>💳 Mode de paiement :</label>
                <select value={form.modePaiement||""} onChange={F("modePaiement")} style={si}>
                  <option value="">— Choisir —</option>
                  {MODES_PAIEMENT.map(m=><option key={m}>{m}</option>)}
                </select>
              </div>
              <div style={{flex:1,minWidth:0}}/>
            </Row>
            <div style={{background:"#f0fdf4",border:"1.5px solid #86efac",borderRadius:10,padding:14,textAlign:"center",marginBottom:14}}>
              <div style={{fontSize:11,color:"#16a34a",fontWeight:700,marginBottom:4}}>💸 Restant à percevoir</div>
              <div style={{fontSize:28,fontWeight:900,color:((parseFloat(form.prixTotal)||0)-(parseFloat(form.avanceRecue)||0))>0?"#dc2626":"#16a34a"}}>{((parseFloat(form.prixTotal)||0)-(parseFloat(form.avanceRecue)||0)).toLocaleString("fr-FR")} FCFA</div>
            </div>
            {/* VENTE À CRÉDIT — le reste part dans Crédit, avec la date que le client promet */}
            {IS_GROSSISTE&&resteDe(form)>0&&<div style={{background:"#fffbeb",border:"1.5px solid #fcd34d",borderRadius:10,padding:13,marginBottom:14}}>
              <div style={{fontSize:12.5,fontWeight:800,color:"#92400e",marginBottom:3,display:"flex",alignItems:"center",gap:6}}><IconeCredit t={18}/> Vente à crédit</div>
              <div style={{fontSize:11,color:"#a16207",marginBottom:10,lineHeight:1.5}}>Les {fmt(resteDe(form))} F restants apparaîtront dans Crédit. Le jour promis, l'application vous rappellera de relancer le client.</div>
              <label style={sl}>📅 Le client promet de payer le</label>
              <input type="date" value={form.dateEcheance||""} min={form.dateDepot||undefined} onChange={e=>{const v=e.target.value;setForm(f=>({...f,dateEcheance:v}));}} style={si}/>
              {!(form.telephone||"").replace(/\D/g,"")&&<div style={{fontSize:11,color:"#b45309",marginTop:8,fontWeight:600}}>⚠️ Notez le téléphone du client (onglet Client) pour pouvoir le relancer sur WhatsApp.</div>}
            </div>}
            {/* ARGENT DÉPENSÉ POUR LA COMMANDE (Premium) — pour connaître le vrai bénéfice */}
            {IS_PREMIUM&&<div style={{background:"#fff",border:"1.5px solid #eee",borderRadius:10,padding:13,marginBottom:14}}>
              <div style={{fontSize:12,fontWeight:800,color:D,marginBottom:3}}>💸 Argent dépensé pour cette commande</div>
              <div style={{fontSize:10,color:"#aaa",marginBottom:10}}>Ce que vous payez pour réaliser la tenue (tissu, fil, broderie…) — pour connaître votre vrai bénéfice.</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:10}}>
                {["Tissu","Fil","Boutons","Broderie","Fermeture","Main d'œuvre","Autre"].map(lbl=>(
                  <button key={lbl} type="button" onClick={()=>addCout(lbl)} style={{background:"#f9f7f0",border:"1px dashed "+G,color:D,padding:"5px 11px",borderRadius:20,cursor:"pointer",fontSize:11,fontWeight:600,fontFamily:"inherit"}}>+ {lbl}</button>
                ))}
              </div>
              {(form.couts||[]).length>0&&<div style={{display:"flex",gap:7,marginBottom:4}}><span style={{...sl,flex:1,marginBottom:0}}>Dépense</span><span style={{...sl,width:92,flexShrink:0,marginBottom:0}}>Montant (F)</span><span style={{width:30,flexShrink:0}}/></div>}
              {(form.couts||[]).map((c,i)=>(
                <div key={i} style={{display:"flex",gap:7,marginBottom:7,alignItems:"center"}}>
                  <input value={c.l} onChange={e=>upCout(i,"l",e.target.value)} aria-label="Dépense" style={{...si,flex:1,minWidth:0}}/>
                  <ChiffreInput value={c.m} onChange={e=>upCout(i,"m",e.target.value)} style={{...si,width:92,flexShrink:0}}/>
                  <span style={{fontSize:11,color:"#aaa",flexShrink:0}}>F</span>
                  <button type="button" onClick={()=>remCout(i)} style={{background:"#fef2f2",color:"#ef4444",border:"none",borderRadius:6,width:30,height:30,cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>✕</button>
                </div>
              ))}
              <button type="button" onClick={()=>addCout()} style={{background:"transparent",border:"1.5px dashed #ddd",color:"#888",padding:"7px",borderRadius:8,cursor:"pointer",fontWeight:600,width:"100%",fontFamily:"inherit",fontSize:11,marginTop:2}}>＋ Ajouter une dépense</button>
              {(()=>{
                const cout=(form.couts||[]).reduce((s,c)=>s+(parseFloat(c.m)||0),0);
                const prix=parseFloat(form.prixTotal)||0;
                if(cout<=0&&prix<=0)return null;
                const benef=prix-cout;
                return(<div style={{marginTop:12,background:benef>=0?"#f0fdf4":"#fef2f2",border:"1px solid "+(benef>=0?"#86efac":"#fca5a5"),borderRadius:9,padding:"11px 13px"}}>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:3}}><span style={{color:"#666"}}>Prix demandé</span><strong>{prix.toLocaleString("fr-FR")} F</strong></div>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:6}}><span style={{color:"#666"}}>Argent dépensé</span><strong style={{color:"#ef4444"}}>−{cout.toLocaleString("fr-FR")} F</strong></div>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:14,fontWeight:900,borderTop:"1px solid "+(benef>=0?"#bbf7d0":"#fecaca"),paddingTop:6}}><span>{benef>=0?"✅ Vous gagnez":"⚠️ Vous perdez"}</span><span style={{color:benef>=0?"#059669":"#dc2626"}}>{benef.toLocaleString("fr-FR")} F</span></div>
                </div>);
              })()}
            </div>}
            {editO&&<div style={{background:"#fffbeb",border:"1px solid #fde68a",borderRadius:9,padding:12,textAlign:"center"}}>
              <div style={{fontSize:10,color:"#92400e",fontWeight:700,marginBottom:9,textTransform:"uppercase",letterSpacing:.5}}>🧾 Générer le reçu</div>
              <button type="button" onClick={()=>showReceipt({...form,id:editO.id,cmdNum:editO.cmdNum})} style={{background:"#10b981",color:"#fff",border:"none",padding:"11px 26px",borderRadius:8,cursor:"pointer",fontWeight:800,fontSize:13,fontFamily:"inherit"}}>🧾 Aperçu &amp; Imprimer</button>
            </div>}
          </Sec>}

          {fTab==="notes"&&<Sec title="📝 Remarques">
            <textarea value={form.remarques} onChange={F("remarques")} rows={7} style={{...si,resize:"vertical",lineHeight:1.9}}/>
            {form.remarques&&<div style={{marginTop:9,background:"#f9f7f0",borderRadius:8,padding:12,border:"1px solid #e8e0d4"}}>
              {form.remarques.split('\n').filter(l=>l.trim()).map((l,i)=>(<div key={i} style={{display:"flex",gap:7,marginBottom:3,fontSize:12,color:"#666"}}><span style={{color:G,fontWeight:700}}>•</span>{l}</div>))}
            </div>}
          </Sec>}

          {essaiO&&manquantsO.length>0&&<div style={{background:"#fef2f2",border:"1.5px solid #fca5a5",borderRadius:10,padding:"11px 13px",marginTop:14}}>
            <div style={{fontSize:12.5,fontWeight:800,color:"#991b1b",marginBottom:7}}>⚠️ Pour enregistrer {isGrossiste?"la vente":"la commande"}, complétez :</div>
            <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
              {manquantsO.map((x,i)=>(<button key={i} type="button" onClick={()=>{setExpress(false);setFTab(x.tab);}} style={{background:"#fff",border:"1px solid #fca5a5",color:"#991b1b",padding:"5px 10px",borderRadius:16,cursor:"pointer",fontSize:11.5,fontWeight:700,fontFamily:"inherit"}}>{(fTabs.find(t=>t.id===x.tab)||{}).l} · {x.l}</button>))}
            </div>
          </div>}
          {/* La barre passe à la ligne sur un téléphone : trop large, elle faisait glisser
              tout le formulaire de gauche à droite pendant la saisie. */}
          <div style={{display:"flex",justifyContent:"space-between",flexWrap:"wrap",gap:8,marginTop:18,paddingTop:12,borderTop:"1px solid #f0f0f0"}}>
            <div style={{display:"flex",gap:7,flexWrap:"wrap",minWidth:0}}>
              {express?<button onClick={()=>{setExpress(false);setFTab("client");}} title="Ouvrir le formulaire complet (mesures, tissus...)" style={{padding:"9px 14px",borderRadius:8,border:"1.5px solid #e0e0e0",background:"#fff",cursor:"pointer",fontSize:12,fontFamily:"inherit",fontWeight:600}}>📋 Tout afficher</button>
              :<>
                {cFI>0&&<button onClick={()=>setFTab(fTabs[cFI-1].id)} style={{padding:"9px 14px",borderRadius:8,border:"1.5px solid #e0e0e0",background:"#fff",cursor:"pointer",fontSize:12,fontFamily:"inherit"}}>← Préc.</button>}
                {/* L'étape suivante se voit : sans ça, on croyait la vente finie au premier onglet. */}
                {cFI<fTabs.length-1&&<><span style={{fontSize:11,color:"#999",alignSelf:"center",whiteSpace:"nowrap"}}>Étape {cFI+1}/{fTabs.length}</span><button onClick={()=>setFTab(fTabs[cFI+1].id)} style={{padding:"10px 16px",borderRadius:8,border:"none",background:D,color:G,cursor:"pointer",fontSize:13,fontWeight:800,fontFamily:"inherit",boxShadow:"0 2px 8px rgba(26,26,46,0.25)"}}>Suivant : {fTabs[cFI+1].l} →</button></>}
              </>}
            </div>
            <div style={{display:"flex",gap:7,marginLeft:"auto"}}>
              <button onClick={()=>setShowOF(false)} style={{padding:"9px 16px",borderRadius:8,border:"1.5px solid #e0e0e0",background:"#fff",cursor:"pointer",fontWeight:600,fontFamily:"inherit",fontSize:12}}>Annuler</button>
              <button onClick={submitO} style={{padding:"9px 20px",borderRadius:8,border:"none",background:G,color:D,cursor:"pointer",fontWeight:800,fontSize:13,fontFamily:"inherit"}}>{editO?"💾 Enregistrer":"✅ Créer"}</button>
            </div>
          </div>
        </div>
      </div>}

      {/* FORMULAIRE CLIENT */}
      {showClientF&&<div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:200,display:"flex",alignItems:"flex-start",justifyContent:"center",overflowY:"auto",padding:14}}>
        <div style={{background:"#fff",borderRadius:15,width:"100%",maxWidth:660,padding:22,margin:"auto",boxShadow:"0 18px 56px rgba(0,0,0,0.14)"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
            <div style={{fontSize:16,fontWeight:800,color:D}}>{editClient?`✏️ Modifier client ${editClient.cliNum}`:"➕ Nouveau client"}</div>
            <button onClick={()=>setShowClientF(false)} style={{background:"#f5f5f5",border:"none",borderRadius:7,width:31,height:31,cursor:"pointer",fontSize:17,fontFamily:"inherit"}}>×</button>
          </div>
          <div style={{display:"flex",gap:3,marginBottom:18,background:"#f5f5f5",borderRadius:9,padding:3}}>
            {[["info","👤 Informations"],...(isGrossiste?[]:[["mesures","📐 Mesures"]]),["notes","📝 Notes"]].map(([id,l])=>(
              <button key={id} onClick={()=>setClTab(id)} style={{flex:1,padding:"7px 10px",borderRadius:7,border:"none",cursor:"pointer",fontWeight:clTab===id?700:400,fontSize:11,background:clTab===id?"#fff":"transparent",color:clTab===id?D:"#999",fontFamily:"inherit",boxShadow:clTab===id?"0 1px 4px rgba(0,0,0,0.07)":"none"}}>{l}</button>
            ))}
          </div>
          {clTab==="info"&&<Sec title="👤 Identité">
            <Row><Fld label="Nom :" value={clientForm.nom} onChange={CLF("nom")} casse="nom" autoFocus={!editClient}/><Fld label="Prénom :" value={clientForm.prenom} onChange={CLF("prenom")} casse="prenom"/></Row>
            <Row>
              <div style={{flex:1,minWidth:0}}>
                <label style={sl}>Téléphone :</label>
                <div style={{display:"flex",gap:5}}>
                  <input value={clientForm.telephone} onChange={e=>setClientForm(f=>({...f,telephone:fmtTel(e.target.value)}))} style={{...si,flex:1}} maxLength={12} inputMode="tel"/>
                  {CONTACTS_OK&&<button type="button" title="Choisir dans le répertoire du téléphone" onClick={async()=>{const c=await choisirContact();if(c)setClientForm(f=>({...f,telephone:c.tel||f.telephone,prenom:f.prenom||c.prenom,nom:f.nom||c.nom}));}} style={{background:"#eff6ff",border:"1.5px solid #bae6fd",borderRadius:8,padding:"0 11px",cursor:"pointer",fontSize:16,flexShrink:0}}>📇</button>}
                </div>
              </div>
              <Fld label="📧 Email :" value={clientForm.email} onChange={CLF("email")} type="email"/>
            </Row>
            <Row>
              <Fld label="🎂 Anniversaire :" value={clientForm.anniversaire} onChange={CLF("anniversaire")} type="date"/>
              <div style={{flex:1,minWidth:0}}/>
            </Row>
            <Row>
              <AcField label="📍 Adresse :" value={clientForm.ville} onChange={CLFC("ville")} suggestions={VILLES}/>
              <AcField label="Nationalité :" value={clientForm.nationalite} onChange={CLFC("nationalite")} suggestions={PAYS}/>
            </Row>
            <Row>
              <div style={{flex:1,minWidth:0,display:"flex",alignItems:"flex-end"}}>
                <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",padding:"9px 12px",background:clientForm.vip?"#fffbeb":"#f5f5f5",borderRadius:8,border:clientForm.vip?"1.5px solid "+G:"1.5px solid #e8e8e8",width:"100%",fontWeight:700,fontSize:12,color:clientForm.vip?"#92400e":"#666"}}>
                  <input type="checkbox" checked={clientForm.vip} onChange={e=>setClientForm(f=>({...f,vip:e.target.checked}))} style={{width:16,height:16,cursor:"pointer"}}/>
                  ⭐ Client VIP
                </label>
              </div>
            </Row>
          </Sec>}
          {!isGrossiste&&clTab==="mesures"&&<Sec title="📐 Mesures (cm) — saisie unique">
            <div style={{display:"flex",gap:7,marginBottom:14}}>
              {[{id:"haut",l:"👔 Haut"},{id:"bas",l:"👖 Bas"}].map(t=>(<button key={t.id} onClick={()=>setClMTab(t.id)} style={{padding:"8px 18px",borderRadius:8,border:"none",cursor:"pointer",fontWeight:700,fontSize:12,background:clMTab===t.id?D:"#f0f0f0",color:clMTab===t.id?G:"#777",fontFamily:"inherit"}}>{t.l}</button>))}
            </div>
            <div style={{fontSize:10,color:"#888",marginBottom:10,background:"#f9f7f0",borderRadius:7,padding:"7px 10px"}}>💡 Format libre : un chiffre (ex. 42) ou plusieurs valeurs (ex. 90/104/80).</div>
            {clMTab==="haut"&&<><Row><Fld label="1. Épaule :" value={clientForm.mEpaule} onChange={CLF("mEpaule")} type="text"/><Fld label="2. Longueur :" value={clientForm.mLongueur} onChange={CLF("mLongueur")} type="text"/><Fld label="3. Poitrine :" value={clientForm.mPoitrine} onChange={CLF("mPoitrine")} type="text"/></Row>
            <Row><Fld label="4. Taille :" value={clientForm.mTaille} onChange={CLF("mTaille")} type="text"/><Fld label="5. Manche :" value={clientForm.mManche} onChange={CLF("mManche")} type="text"/><Fld label="6. Poignet :" value={clientForm.mPoignet} onChange={CLF("mPoignet")} type="text"/></Row>
            <Row><Fld label="7. Bras :" value={clientForm.mBras} onChange={CLF("mBras")} type="text"/><Fld label="8. Bassin :" value={clientForm.mBassinH} onChange={CLF("mBassinH")} type="text"/><Fld label="9. Col :" value={clientForm.mCol} onChange={CLF("mCol")} type="text"/></Row></>}
            {clMTab==="bas"&&<><Row><Fld label="1. Ceinture :" value={clientForm.mCeinture} onChange={CLF("mCeinture")} type="text"/><Fld label="2. Longueur P :" value={clientForm.mLongueurP} onChange={CLF("mLongueurP")} type="text"/><Fld label="3. Cuisse :" value={clientForm.mCuisse} onChange={CLF("mCuisse")} type="text"/></Row>
            <Row><Fld label="4. Bassin :" value={clientForm.mBassinP} onChange={CLF("mBassinP")} type="text"/><Fld label="5. Bas :" value={clientForm.mBas} onChange={CLF("mBas")} type="text"/><div style={{flex:1}}/></Row></>}
            {/* AUTRES MESURES */}
            <div style={{marginTop:8,borderTop:"1px dashed #e5e5e5",paddingTop:12}}>
              <div style={{fontSize:11,fontWeight:800,color:D,marginBottom:8}}>➕ Autres mesures</div>
              {(clientForm.mesuresPlus||[]).length>0&&<div style={{display:"flex",gap:7,marginBottom:4}}><span style={{...sl,flex:1,marginBottom:0}}>Nom de la mesure</span><span style={{...sl,width:110,flexShrink:0,marginBottom:0}}>Mesure</span><span style={{width:30,flexShrink:0}}/></div>}
              {(clientForm.mesuresPlus||[]).map((m,i)=>(
                <div key={i} style={{display:"flex",gap:7,marginBottom:7,alignItems:"center"}}>
                  <input value={m.l} onChange={e=>clUpMesPlus(i,"l",e.target.value)} aria-label="Nom de la mesure" style={{...si,flex:1,minWidth:0}}/>
                  <input value={m.v} onChange={e=>clUpMesPlus(i,"v",e.target.value)} aria-label="Mesure" style={{...si,width:110,flexShrink:0}}/>
                  <button type="button" onClick={()=>clRemMesPlus(i)} style={{background:"#fef2f2",color:"#ef4444",border:"none",borderRadius:6,width:30,height:30,cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>✕</button>
                </div>
              ))}
              <button type="button" onClick={()=>clAddMesPlus()} style={{background:"transparent",border:"1.5px dashed "+G,color:D,padding:"8px",borderRadius:8,cursor:"pointer",fontWeight:700,width:"100%",fontFamily:"inherit",fontSize:12}}>＋ Ajouter une mesure</button>
            </div>
          </Sec>}
          {clTab==="notes"&&<Sec title="📝 Notes permanentes">
            <textarea value={clientForm.notes} onChange={CLF("notes")} rows={7} style={{...si,resize:"vertical",lineHeight:1.9}}/>
          </Sec>}
          <div style={{display:"flex",justifyContent:"flex-end",gap:7,marginTop:18,paddingTop:12,borderTop:"1px solid #f0f0f0"}}>
            <button onClick={()=>setShowClientF(false)} style={{padding:"9px 16px",borderRadius:8,border:"1.5px solid #e0e0e0",background:"#fff",cursor:"pointer",fontWeight:600,fontFamily:"inherit",fontSize:12}}>Annuler</button>
            <button onClick={submitClient} style={{padding:"9px 20px",borderRadius:8,border:"none",background:G,color:D,cursor:"pointer",fontWeight:800,fontSize:13,fontFamily:"inherit"}}>{editClient?"💾 Enregistrer":"✅ Créer le client"}</button>
          </div>
        </div>
      </div>}

      {/* FORMULAIRE COLLECTION */}
      {showCF&&<div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:200,display:"flex",alignItems:"flex-start",justifyContent:"center",overflowY:"auto",padding:14}}>
        <div style={{background:"#fff",borderRadius:15,width:"100%",maxWidth:480,padding:22,margin:"auto",boxShadow:"0 18px 56px rgba(0,0,0,0.14)"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}>
            <div style={{fontSize:16,fontWeight:800,color:D}}>{editC?"✏️ Modifier":"📸 Ajouter à la collection"}</div>
            <button onClick={()=>setShowCF(false)} style={{background:"#f5f5f5",border:"none",borderRadius:7,width:31,height:31,cursor:"pointer",fontSize:17,fontFamily:"inherit"}}>×</button>
          </div>
          <input ref={fileRef} type="file" accept="image/*" style={{display:"none"}} onChange={handlePhoto}/>
          {cForm.photoData?<div style={{position:"relative",marginBottom:14}}><img src={cForm.photoData} alt="" style={{width:"100%",height:200,objectFit:"cover",borderRadius:10}}/><button onClick={()=>setCForm(f=>({...f,photoData:null}))} style={{position:"absolute",top:7,right:7,background:"rgba(0,0,0,0.55)",color:"#fff",border:"none",borderRadius:7,padding:"4px 9px",cursor:"pointer",fontSize:11,fontFamily:"inherit"}}>✕</button></div>
          :<div onClick={()=>fileRef.current.click()} style={{border:"2px dashed #e0e0e0",borderRadius:10,padding:"36px 16px",textAlign:"center",cursor:"pointer",marginBottom:14,color:"#ccc",background:"#fafafa"}}><div style={{fontSize:38,marginBottom:7}}>📷</div><div style={{fontWeight:600,color:"#aaa",fontSize:12}}>Cliquer pour ajouter une photo</div></div>}
          <Sec title="📋 Détails">
            <Row><Fld label="Titre du modèle :" value={cForm.titre} onChange={CF("titre")}/></Row>
            <Row>
              <Slct label="Catégorie :" value={cForm.categorie} onChange={CF("categorie")} options={catsCollection}/>
              <Fld label={IS_GROSSISTE?"💰 Prix par mètre (FCFA/m) :":"💰 Prix indicatif (FCFA) :"} value={cForm.prixIndicatif} onChange={CF("prixIndicatif")} type="number" suffix="F"/>
            </Row>
            <div><label style={sl}>Description :</label><textarea value={cForm.description} onChange={CF("description")} rows={3} style={{...si,resize:"vertical"}}/></div>
          </Sec>
          <div style={{display:"flex",gap:7,justifyContent:"flex-end"}}>
            <button onClick={()=>setShowCF(false)} style={{padding:"9px 16px",borderRadius:8,border:"1.5px solid #e0e0e0",background:"#fff",cursor:"pointer",fontWeight:600,fontFamily:"inherit",fontSize:12}}>Annuler</button>
            <button onClick={submitC} style={{padding:"9px 20px",borderRadius:8,border:"none",background:G,color:D,cursor:"pointer",fontWeight:800,fontSize:13,fontFamily:"inherit"}}>{editC?"💾 Modifier":"✅ Ajouter"}</button>
          </div>
        </div>
      </div>}

      {/* FORMULAIRE STOCK ARTICLE */}
      {showStockF&&<div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:200,display:"flex",alignItems:"flex-start",justifyContent:"center",overflowY:"auto",padding:14}}>
        <div style={{background:"#fff",borderRadius:15,width:"100%",maxWidth:540,padding:22,margin:"auto",boxShadow:"0 18px 56px rgba(0,0,0,0.14)"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}>
            <div style={{fontSize:16,fontWeight:800,color:D}}>{editStock?"✏️ Modifier article":"➕ Nouvel article stock"}</div>
            <button onClick={()=>setShowStockF(false)} style={{background:"#f5f5f5",border:"none",borderRadius:7,width:31,height:31,cursor:"pointer",fontSize:17,fontFamily:"inherit"}}>×</button>
          </div>
          <Sec title="🧶 L'article">
            <Row>
              <div style={{flex:1,minWidth:0}}>
                <label style={sl}>Type :</label>
                <select value={TISSUS.includes(stockForm.type)?stockForm.type:(stockForm.type?"Autre":"")} onChange={e=>setStockForm(f=>({...f,type:e.target.value}))} style={si}>
                  <option value="">-- Choisir --</option>
                  {Object.entries(TISSUS_BY_CAT).map(([cat,list])=>(
                    <optgroup key={cat} label={cat}>
                      {list.map(x=><option key={x} value={x}>{x}</option>)}
                    </optgroup>
                  ))}
                </select>
                {(stockForm.type==="Autre"||(stockForm.type&&!TISSUS.includes(stockForm.type)))&&<><label style={{...sl,marginTop:6}}>Nom du tissu</label><input value={stockForm.custom||""} onChange={e=>setStockForm(f=>({...f,custom:mettreEnForme(e.target.value)}))} style={si}/></>}
                {/* Quel Getzner ? quel wax ? Deux tissus du même nom ne se valent pas. */}
                <label style={{...sl,marginTop:6}}>Précision (qualité, provenance…)</label><input value={stockForm.variante||""} onChange={e=>setStockForm(f=>({...f,variante:mettreEnForme(e.target.value)}))} style={{...si,fontSize:12}}/>
              </div>
              {/* PHOTO DE LA BALLE — placée avant la couleur : on reconnaît un tissu à
                  l'oeil bien avant de lire son nom. Le plus souvent elle existe déjà
                  dans le téléphone, donc la galerie vient en premier ; l'appareil photo
                  reste à côté, pour la balle qui n'a jamais été prise. */}
              <div style={{flexShrink:0,width:124}}>
                <label style={sl}>Photo</label>
                {stockForm.photoData?<div style={{position:"relative"}}>
                  <img src={stockForm.photoData} alt="" style={{width:124,height:80,objectFit:"cover",borderRadius:8,border:"1.5px solid #e8e8e8",display:"block"}}/>
                  <button type="button" onClick={()=>setStockForm(f=>({...f,photoData:null}))} title="Retirer la photo" style={{position:"absolute",top:-7,right:-7,background:"#fef2f2",color:"#ef4444",border:"1.5px solid #fca5a5",borderRadius:"50%",width:23,height:23,cursor:"pointer",fontSize:12,fontWeight:800,fontFamily:"inherit",lineHeight:1,padding:0}}>×</button>
                </div>:<div style={{display:"flex",gap:5}}>
                  <button type="button" onClick={()=>stockGalRef.current&&stockGalRef.current.click()} title="Choisir une photo déjà dans le téléphone ou l'ordinateur" style={{flex:1,height:78,borderRadius:8,border:"1.5px dashed "+G,background:"#fffdf7",cursor:"pointer",fontSize:19,fontFamily:"inherit",color:D,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:2}}>🖼️<span style={{fontSize:8.5,fontWeight:700,color:"#8a7f66"}}>Galerie</span></button>
                  {EST_TACTILE&&<button type="button" onClick={()=>stockCamRef.current&&stockCamRef.current.click()} title="Prendre la photo maintenant" style={{width:34,height:78,borderRadius:8,border:"1.5px solid #e8e8e8",background:"#fff",cursor:"pointer",fontSize:15,fontFamily:"inherit"}}>📷</button>}
                </div>}
                {!EST_TACTILE&&!stockForm.photoData&&<button type="button" onClick={ouvrirPont} title="Afficher un QR code : vous choisissez la photo sur votre téléphone, elle arrive ici" style={{width:"100%",marginTop:5,padding:"6px 4px",borderRadius:7,border:"1px solid #e8e8e8",background:"#fff",cursor:"pointer",fontSize:9.5,fontWeight:700,color:"#8a7f66",fontFamily:"inherit"}}>📱 Mon téléphone</button>}
                <input ref={stockGalRef} type="file" accept="image/*" style={{display:"none"}} onChange={handleStockPhoto}/>
                <input ref={stockCamRef} type="file" accept="image/*" capture="environment" style={{display:"none"}} onChange={handleStockPhoto}/>
              </div>
              <div style={{flex:1,minWidth:0}}>
                <label style={sl}>Couleur :</label>
                {/* Un nom de couleur ne se voit pas : on montre la teinte à côté de chacune. */}
                <button type="button" onClick={()=>setCpStock(o=>!o)} style={{...si,display:"flex",alignItems:"center",gap:8,cursor:"pointer",background:"#fff",textAlign:"left"}}>
                  <span style={{width:17,height:17,borderRadius:"50%",background:stockForm.couleurHex||(PALETTE.find(c=>c.n===stockForm.couleur)||{}).h||"#e8e8e8",border:"1px solid rgba(0,0,0,0.14)",display:"inline-block",flexShrink:0}}/>
                  <span style={{fontWeight:600,color:stockForm.couleur?D:"#bbb",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{stockForm.couleur||"Choisir…"}</span>
                  <span style={{marginLeft:"auto",color:"#ccc",fontSize:9}}>▼</span>
                </button>
                {cpStock&&<div onClick={e=>{if(e.target===e.currentTarget)setCpStock(false);}} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:400,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
                  <div style={{background:"#fff",borderRadius:14,width:"100%",maxWidth:430,maxHeight:"78vh",display:"flex",flexDirection:"column",overflow:"hidden",boxShadow:"0 20px 60px rgba(0,0,0,0.28)"}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"13px 15px",borderBottom:"1px solid #f0f0f0",flexShrink:0}}>
                    <span style={{fontWeight:800,color:D,fontSize:14}}>🎨 Choisir la couleur</span>
                    <button type="button" onClick={()=>setCpStock(false)} style={{background:"#f5f5f5",border:"none",borderRadius:7,width:29,height:29,cursor:"pointer",fontSize:16,fontFamily:"inherit",lineHeight:1}}>×</button>
                  </div>
                  <div style={{overflowY:"auto",padding:12,display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(122px,1fr))",gap:6,alignContent:"start"}}>
                    {PALETTE.map(c=>(<button key={c.n} type="button" onClick={()=>{setStockForm(f=>({...f,couleur:c.n,couleurHex:c.h,couleurDevinee:false}));setCouleurLibre(false);setCpStock(false);}} style={{display:"flex",alignItems:"center",gap:6,padding:"6px 8px",border:stockForm.couleur===c.n?"2px solid "+D:"1px solid #eee",borderRadius:7,cursor:"pointer",background:"#fff",fontFamily:"inherit",textAlign:"left"}}>
                      <span style={{width:15,height:15,borderRadius:"50%",background:c.h,border:"1px solid rgba(0,0,0,0.1)",display:"inline-block",flexShrink:0}}/><span style={{fontSize:11,color:"#555",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.n}</span>
                    </button>))}
                    <button type="button" onClick={()=>{setStockForm(f=>({...f,couleur:"",couleurHex:"#999999",couleurDevinee:false}));setCouleurLibre(true);setCpStock(false);}} title="Nommer soi-même une couleur qui n'est pas dans la liste" style={{display:"flex",alignItems:"center",gap:6,padding:"6px 8px",border:"1px dashed "+G,borderRadius:7,cursor:"pointer",background:"#fffdf7",fontFamily:"inherit",textAlign:"left"}}>
                      <span style={{fontSize:13}}>🎨</span><span style={{fontSize:11,color:"#8a7f66",fontWeight:700}}>Autre…</span>
                    </button>
                  </div>
                  </div>
                </div>}
                {stockForm.couleurDevinee&&stockForm.couleur&&<div style={{fontSize:10.5,color:"#0369a1",background:"#eff6ff",border:"1px solid #bae6fd",borderRadius:6,padding:"5px 8px",marginTop:5,lineHeight:1.45}}>💡 Couleur devinée d'après la photo — corrigez-la si besoin.</div>}
                {couleurLibre&&<>
                  <label style={{...sl,marginTop:6}}>Nom de la couleur</label><input value={stockForm.couleur} onChange={e=>setStockForm(f=>({...f,couleur:e.target.value}))} style={si} autoFocus/>
                  <div style={{display:"flex",alignItems:"center",gap:7,marginTop:6}}>
                    <span style={{fontSize:10,color:"#888",fontWeight:600}}>Teinte :</span>
                    <input type="color" value={stockForm.couleurHex||"#999999"} onChange={e=>setStockForm(f=>({...f,couleurHex:e.target.value}))} style={{width:42,height:30,border:"1px solid #ddd",borderRadius:6,cursor:"pointer",padding:0}}/>
                    <span style={{fontSize:10,color:"#666"}}>{stockForm.couleurHex}</span>
                  </div>
                </>}
              </div>
            </Row>
            {/* SAISIE RAPIDE D'UN LOT — une balle de Getzner, c'est toujours la même
                configuration : 20 pièces de 30 m au même prix. Seule la couleur change.
                On pose la configuration une fois, puis on enchaîne les couleurs. */}
            {!editStock&&<div style={{marginTop:12,borderTop:"1px dashed #e5e5e5",paddingTop:12}}>
              <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                <button type="button" disabled={!stockForm.couleur} onClick={()=>{
                  if(!stockForm.couleur)return;
                  setCouleursLot(l=>l.some(c=>c.n===stockForm.couleur)?l:[...l,{n:stockForm.couleur,h:stockForm.couleurHex||"",photo:stockForm.photoData||null}]);
                  // La photo part avec sa couleur : la suivante aura la sienne.
                  setStockForm(f=>({...f,couleur:"",couleurHex:"",photoData:null}));
                  setCouleurLibre(false);
                }} style={{background:stockForm.couleur?"#fffbeb":"#f5f5f5",color:stockForm.couleur?"#92400e":"#bbb",border:"1.5px dashed "+(stockForm.couleur?G:"#e0e0e0"),borderRadius:8,padding:"8px 13px",cursor:stockForm.couleur?"pointer":"default",fontWeight:800,fontSize:12,fontFamily:"inherit"}}>＋ Ajouter cette couleur au lot</button>
                <span style={{fontSize:11,color:"#999",lineHeight:1.5}}>Même tissu, même configuration, même prix — seules les couleurs changent.</span>
              </div>
              {couleursLot.length>0&&<div style={{marginTop:10}}>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {couleursLot.map((c,k)=>(<span key={c.n} style={{display:"inline-flex",alignItems:"center",gap:6,background:"#fff",border:"1.5px solid #e8e8e8",borderRadius:20,padding:"5px 9px 5px 7px",fontSize:12,fontWeight:600}}>
                    {c.photo
                      ?<img src={c.photo} alt="" style={{width:20,height:20,borderRadius:5,objectFit:"cover",border:"1px solid rgba(0,0,0,0.12)",flexShrink:0}}/>
                      :<span style={{width:14,height:14,borderRadius:"50%",background:c.h||"#ddd",border:"1px solid rgba(0,0,0,0.12)",display:"inline-block",flexShrink:0}}/>}
                    {c.n}
                    <button type="button" onClick={()=>setCouleursLot(l=>l.filter((_,j)=>j!==k))} title="Retirer" style={{background:"transparent",border:"none",color:"#ef4444",cursor:"pointer",fontSize:13,fontFamily:"inherit",lineHeight:1,padding:0,marginLeft:2}}>✕</button>
                  </span>))}
                </div>
                <div style={{fontSize:11.5,color:"#166534",background:"#f0fdf4",border:"1px solid #86efac",borderRadius:7,padding:"8px 11px",marginTop:9,fontWeight:700}}>
                  {couleursLot.length+(stockForm.couleur?1:0)} balle(s) seront enregistrées d'un coup. Chaque couleur garde la photo choisie avec elle.
                </div>
              </div>}
            </div>}
          </Sec>
          {/* COMBIEN EN STOCK — chez un grossiste le stock EST la boutique : cette
              section passe avant les prix, et n'est plus un volet repliable. */}
          <Sec title="📦 Combien en stock">
            {(() => {
              const mode = stockForm.modeSaisie || "balle";
              const conv = stockForm.uniteMesure === "yd" ? 0.9144 : 1;
              // En mode balle, le total mètres se déduit : balles × pièces × mètres/pièce.
              const recalc = form => {
                const b = parseFloat(form.nbBalles) || 0;
                const pp = parseFloat(form.piecesParBalle) || 0;
                const mp = parseFloat(form.metresParPiece) || 0;
                const out = { ...form };
                if ((out.modeSaisie || "balle") === "balle" && b > 0 && pp > 0 && mp > 0)
                  out.metres = String((b * pp * mp * (out.uniteMesure === "yd" ? 0.9144 : 1)).toFixed(2));
                return out;
              };
              const setRecalc = maj => setStockForm(f => recalc({ ...f, ...maj }));
              const totalM = (parseFloat(stockForm.nbBalles) || 0) * (parseFloat(stockForm.piecesParBalle) || 0) * (parseFloat(stockForm.metresParPiece) || 0) * conv;
              const choix = [["balle", "📦 Par balle", "Getzner, bazin — la balle est l'unité d'achat"], ["metre", "📏 Au mètre", "coupons, fins de rouleau"]];
              return (<>
                <div style={{display:"flex",gap:8,marginBottom:13}}>
                  {choix.map(([v,l,t])=>(
                    <button key={v} type="button" title={t} onClick={()=>setRecalc({modeSaisie:v})} style={{flex:1,padding:"11px 8px",borderRadius:9,border:"1.5px solid "+(mode===v?G:"#e5e5e5"),background:mode===v?"#fffbeb":"#fff",color:mode===v?D:"#888",fontWeight:800,fontSize:12.5,cursor:"pointer",fontFamily:"inherit"}}>{l}</button>
                  ))}
                </div>
                {mode==="balle"?<>
                  <Row>
                    <Fld label="📦 Nb de balles :" value={stockForm.nbBalles} onChange={e=>setRecalc({nbBalles:e.target.value})} type="number" suffix="balles"/>
                    <Fld label="📐 Pièces par balle :" value={stockForm.piecesParBalle} onChange={e=>setRecalc({piecesParBalle:e.target.value})} type="number" suffix="pièces"/>
                    <Fld label={`📏 ${stockForm.uniteMesure==="yd"?"Yards":"Mètres"} par pièce :`} value={stockForm.metresParPiece} onChange={e=>setRecalc({metresParPiece:e.target.value})} type="number" suffix={stockForm.uniteMesure==="yd"?"yd/pc":"m/pc"}/>
                  </Row>
                  {totalM>0
                    ?<div style={{background:"#f0fdf4",border:"1px solid #86efac",borderRadius:8,padding:"9px 12px",fontSize:12.5,color:"#166534",fontWeight:700}}>✓ Cela fait <strong>{totalM.toFixed(1)} m</strong> en stock{stockForm.uniteMesure==="yd"?` (${((parseFloat(stockForm.nbBalles)||0)*(parseFloat(stockForm.piecesParBalle)||0)*(parseFloat(stockForm.metresParPiece)||0)).toFixed(0)} yd)`:""}</div>
                    :<div style={{fontSize:11,color:"#888",lineHeight:1.6}}>💡 Exemple Getzner : <strong>1 balle × 20 pièces × 30 m = 600 m</strong></div>}
                </>:<>
                  <Row>
                    <Fld label="📏 Mètres en stock :" value={stockForm.metres} onChange={e=>setStockForm(f=>({...f,metres:e.target.value}))} type="number" suffix="m"/>
                    <div style={{flex:1,minWidth:0}}/>
                  </Row>
                </>}
                <Row>
                  <Fld label="🗄️ Référence étagère :" value={stockForm.etagere} onChange={e=>setStockForm(f=>({...f,etagere:e.target.value}))}/>
                  <div style={{flex:1,minWidth:0}}>
                    <label style={sl}>📐 Unité de mesure :</label>
                    <select value={stockForm.uniteMesure||"m"} onChange={e=>setRecalc({uniteMesure:e.target.value})} style={si}>
                      <option value="m">Mètres (m)</option>
                      <option value="yd">Yards (yd) — 1 yd = 0.9144 m</option>
                    </select>
                  </div>
                </Row>
              </>);
            })()}
          </Sec>
          <Sec title="💰 Prix et suivi">
            <Row>
              <Fld label="Prix d'achat / m :" value={stockForm.prixUnitaire} onChange={e=>setStockForm(f=>({...f,prixUnitaire:e.target.value}))} type="number" suffix="F"/>
              <Fld label="💵 Prix de vente / m :" value={stockForm.prixVente} onChange={e=>setStockForm(f=>({...f,prixVente:e.target.value}))} type="number" suffix="F"/>
            </Row>
            <Row>
              <Fld label="⚠️ Alerte stock bas :" value={stockForm.lowAlert} onChange={e=>setStockForm(f=>({...f,lowAlert:e.target.value}))} type="number" suffix="m"/>
              <div style={{flex:1,minWidth:0,display:"flex",alignItems:"flex-end",fontSize:11,color:"#666",fontWeight:600,paddingBottom:9}}>
                {(()=>{
                  const pa=parseFloat(stockForm.prixUnitaire)||0;
                  const pv=parseFloat(stockForm.prixVente)||0;
                  if(pa>0&&pv>0){
                    const marge=pv-pa;
                    const pct=Math.round((marge/pa)*100);
                    return<span style={{background:marge>=0?"#f0fdf4":"#fef2f2",border:"1px solid "+(marge>=0?"#86efac":"#fca5a5"),borderRadius:7,padding:"7px 11px",color:marge>=0?"#166534":"#991b1b",width:"100%"}}>📈 Marge : <strong>{marge.toLocaleString("fr-FR")} F/m ({pct>0?"+":""}{pct}%)</strong></span>;
                  }
                  return null;
                })()}
              </div>
            </Row>
            <Row>
              <div style={{flex:1,minWidth:0}}>
                <label style={sl}>Fournisseur :</label>
                <select value={stockForm.fournisseurId||""} onChange={e=>setStockForm(f=>({...f,fournisseurId:e.target.value?parseInt(e.target.value):null}))} style={si}>
                  <option value="">-- Aucun --</option>
                  {fournisseurs.map(f=><option key={f.id} value={f.id}>{f.nom}</option>)}
                </select>
              </div>
            </Row>
            <div><label style={sl}>Notes :</label><textarea value={stockForm.notes} onChange={e=>setStockForm(f=>({...f,notes:e.target.value}))} rows={2} style={{...si,resize:"vertical"}}/></div>
          </Sec>


          <div style={{display:"flex",gap:7,justifyContent:"flex-end"}}>
            <button onClick={()=>setShowStockF(false)} style={{padding:"9px 16px",borderRadius:8,border:"1.5px solid #e0e0e0",background:"#fff",cursor:"pointer",fontWeight:600,fontFamily:"inherit",fontSize:12}}>Annuler</button>
            <button onClick={submitStock} style={{padding:"9px 20px",borderRadius:8,border:"none",background:G,color:D,cursor:"pointer",fontWeight:800,fontSize:13,fontFamily:"inherit"}}>{editStock?"💾 Enregistrer":(couleursLot.length>0?"✅ Créer "+(couleursLot.length+(stockForm.couleur?1:0))+" articles":"✅ Ajouter")}</button>
          </div>
        </div>
      </div>}

      {/* FORMULAIRE FOURNISSEUR */}
      {showFourn&&<div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:200,display:"flex",alignItems:"flex-start",justifyContent:"center",overflowY:"auto",padding:14}}>
        <div style={{background:"#fff",borderRadius:15,width:"100%",maxWidth:520,padding:22,margin:"auto",boxShadow:"0 18px 56px rgba(0,0,0,0.14)"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}>
            <div style={{fontSize:16,fontWeight:800,color:D}}>{editFourn?"✏️ Modifier fournisseur":"➕ Nouveau fournisseur"}</div>
            <button onClick={()=>setShowFourn(false)} style={{background:"#f5f5f5",border:"none",borderRadius:7,width:31,height:31,cursor:"pointer",fontSize:17,fontFamily:"inherit"}}>×</button>
          </div>
          <Sec title="🚚 Coordonnées">
            <Row><Fld label="Nom :" value={fournForm.nom} onChange={e=>setFournForm(f=>({...f,nom:e.target.value}))}/></Row>
            <Row>
              <div style={{flex:1,minWidth:0}}>
                <label style={sl}>Téléphone WhatsApp :</label>
                <input value={fournForm.telephone} onChange={e=>setFournForm(f=>({...f,telephone:fmtTel(e.target.value)}))} style={si} maxLength={12}/>
              </div>
              <Fld label="Email :" value={fournForm.email} onChange={e=>setFournForm(f=>({...f,email:e.target.value}))} type="email"/>
            </Row>
            <Row>
              <Fld label="Marché :" value={fournForm.marche} onChange={e=>setFournForm(f=>({...f,marche:e.target.value}))}/>
              <AcField label="Ville :" value={fournForm.ville} onChange={v=>setFournForm(f=>({...f,ville:v}))} suggestions={VILLES}/>
            </Row>
            <Row><Fld label="Spécialité (tissus fournis) :" value={fournForm.specialite} onChange={e=>setFournForm(f=>({...f,specialite:e.target.value}))}/></Row>
            <div><label style={sl}>Notes :</label><textarea value={fournForm.notes} onChange={e=>setFournForm(f=>({...f,notes:e.target.value}))} rows={2} style={{...si,resize:"vertical"}}/></div>
          </Sec>
          <div style={{display:"flex",gap:7,justifyContent:"flex-end"}}>
            <button onClick={()=>setShowFourn(false)} style={{padding:"9px 16px",borderRadius:8,border:"1.5px solid #e0e0e0",background:"#fff",cursor:"pointer",fontWeight:600,fontFamily:"inherit",fontSize:12}}>Annuler</button>
            <button onClick={submitFourn} style={{padding:"9px 20px",borderRadius:8,border:"none",background:G,color:D,cursor:"pointer",fontWeight:800,fontSize:13,fontFamily:"inherit"}}>{editFourn?"💾 Enregistrer":"✅ Ajouter"}</button>
          </div>
        </div>
      </div>}

      {/* FORMULAIRE DÉPENSE */}
      {showDepF&&<div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:200,display:"flex",alignItems:"flex-start",justifyContent:"center",overflowY:"auto",padding:14}}>
        <div style={{background:"#fff",borderRadius:15,width:"100%",maxWidth:540,padding:22,margin:"auto",boxShadow:"0 18px 56px rgba(0,0,0,0.14)"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}>
            <div style={{fontSize:16,fontWeight:800,color:D}}>{editDep?"✏️ Modifier dépense":"💸 Nouvelle dépense"}</div>
            <button onClick={()=>setShowDepF(false)} style={{background:"#f5f5f5",border:"none",borderRadius:7,width:31,height:31,cursor:"pointer",fontSize:17,fontFamily:"inherit"}}>×</button>
          </div>
          <Sec title="💰 Montant & catégorie">
            <Row>
              <Fld label="Montant (FCFA) :" value={depForm.montant} onChange={e=>setDepForm(f=>({...f,montant:e.target.value}))} type="number" suffix="F"/>
              <Fld label="Date :" value={depForm.date} onChange={e=>setDepForm(f=>({...f,date:e.target.value}))} type="date"/>
            </Row>
            <Row>
              <div style={{flex:1,minWidth:0}}>
                <label style={sl}>Catégorie :</label>
                <select value={depForm.categorie} onChange={e=>setDepForm(f=>({...f,categorie:e.target.value}))} style={si}>
                  {CATEGORIES_DEPENSE.map(c=><option key={c.id} value={c.id}>{c.l}</option>)}
                </select>
              </div>
              <div style={{flex:1,minWidth:0}}>
                <label style={sl}>Mode de paiement :</label>
                <select value={depForm.modePaiement} onChange={e=>setDepForm(f=>({...f,modePaiement:e.target.value}))} style={si}>
                  {MODES_PAIEMENT.map(m=><option key={m}>{m}</option>)}
                </select>
              </div>
            </Row>
          </Sec>
          <Sec title="📝 Détails">
            <div style={{marginBottom:11}}>
              <label style={sl}>Description :</label>
              <input value={depForm.description} onChange={e=>setDepForm(f=>({...f,description:e.target.value}))} style={si}/>
            </div>
            <Row>
              <Fld label="Fournisseur / Bénéficiaire :" value={depForm.fournisseur} onChange={e=>setDepForm(f=>({...f,fournisseur:e.target.value}))}/>
            </Row>
            <div><label style={sl}>Notes :</label><textarea value={depForm.notes} onChange={e=>setDepForm(f=>({...f,notes:e.target.value}))} rows={2} style={{...si,resize:"vertical"}}/></div>
          </Sec>
          <div style={{display:"flex",gap:7,justifyContent:"flex-end"}}>
            <button onClick={()=>setShowDepF(false)} style={{padding:"9px 16px",borderRadius:8,border:"1.5px solid #e0e0e0",background:"#fff",cursor:"pointer",fontWeight:600,fontFamily:"inherit",fontSize:12}}>Annuler</button>
            <button onClick={submitDep} style={{padding:"9px 20px",borderRadius:8,border:"none",background:G,color:D,cursor:"pointer",fontWeight:800,fontSize:13,fontFamily:"inherit"}}>{editDep?"💾 Enregistrer":"✅ Ajouter"}</button>
          </div>
        </div>
      </div>}

      {/* FORMULAIRE RÉCEPTION */}
      {showRecep&&(()=>{
        const s=stock.find(x=>x.id===recepForm.stockId);
        if(!s)return null;
        return(<div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:14}}>
          <div style={{background:"#fff",borderRadius:15,width:"100%",maxWidth:460,maxHeight:"92vh",display:"flex",flexDirection:"column",overflow:"hidden",boxShadow:"0 18px 56px rgba(0,0,0,0.14)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"18px 20px 12px",flexShrink:0,borderBottom:"1px solid #f0ece4"}}>
              <div style={{fontSize:16,fontWeight:800,color:D}}>📥 Arrivage</div>
              <button onClick={()=>setShowRecep(false)} style={{background:"#f5f5f5",border:"none",borderRadius:7,width:31,height:31,cursor:"pointer",fontSize:17,fontFamily:"inherit"}}>×</button>
            </div>
            <div style={{flex:1,minWidth:0,overflowY:"auto",padding:"14px 20px 4px"}}>
            <div style={{background:"#f9f7f0",borderRadius:9,padding:"10px 12px",marginBottom:14,fontSize:12,color:"#666"}}>
              <strong style={{color:D}}>{stockLabel(s)}</strong> · stock actuel : <strong>{(parseFloat(s.metres)||0).toFixed(1)} m</strong>
              {s.etagere&&<span style={{marginLeft:8,fontSize:10,color:"#888"}}>🗄️ {s.etagere}</span>}
              <div style={{marginTop:8,paddingTop:8,borderTop:"1px dashed #e5ddc9",fontSize:11.5,color:"#92400e",lineHeight:1.6}}>
                ⚠️ N'utilisez l'arrivage que si <strong>le prix d'achat est le même qu'avant</strong>.
                S'il a changé, fermez cette fenêtre et enregistrez plutôt un <strong>nouvel article</strong> :
                vos deux prix resteront justes, et la référence les distinguera.
              </div>
            </div>
            {(()=>{
              // Auto-calcul : balles × pièces/balle × mètres/pièce (× conversion yd→m si yards)
              const recalc=(updated)=>{
                const b=parseFloat(updated.nbBalles)||0;
                const pp=parseFloat(updated.piecesParBalle)||0;
                const mp=parseFloat(updated.metresParPiece)||0;
                const conv=updated.uniteMesure==="yd"?0.9144:1;
                const totalM=b*pp*mp*conv;
                const out={...updated};
                if(b>0&&pp>0&&mp>0)out.metres=String(totalM.toFixed(2));
                return out;
              };
              const upd=patch=>setRecepForm(f=>recalc({...f,...patch}));
              const b=parseFloat(recepForm.nbBalles)||0;
              const pp=parseFloat(recepForm.piecesParBalle)||0;
              const mp=parseFloat(recepForm.metresParPiece)||0;
              const conv=recepForm.uniteMesure==="yd"?0.9144:1;
              const totalM=b*pp*mp*conv;
              return(<>
                <Sec title="📦 Composition de cette livraison">
                  <div style={{background:"#fffbeb",border:"1px solid #fde68a",borderRadius:8,padding:"8px 11px",marginBottom:9,fontSize:11,color:"#92400e"}}>
                    Chaque livraison peut avoir <strong>une composition différente</strong>. Saisissez ce qui correspond à CES balles précisément.
                  </div>
                  <Row>
                    <Fld label="📦 Nb balles :" value={recepForm.nbBalles} onChange={e=>upd({nbBalles:e.target.value})} type="number" suffix="balles"/>
                    <Fld label="📐 Pièces par balle :" value={recepForm.piecesParBalle} onChange={e=>upd({piecesParBalle:e.target.value})} type="number" suffix="pcs"/>
                    <Fld label={`📏 ${recepForm.uniteMesure==="yd"?"Yards":"Mètres"} / pièce :`} value={recepForm.metresParPiece} onChange={e=>upd({metresParPiece:e.target.value})} type="number" suffix={recepForm.uniteMesure==="yd"?"yd":"m"}/>
                  </Row>
                  <Row>
                    <div style={{flex:1,minWidth:0}}>
                      <label style={sl}>Unité :</label>
                      <select value={recepForm.uniteMesure||"m"} onChange={e=>upd({uniteMesure:e.target.value})} style={si}>
                        <option value="m">Mètres</option>
                        <option value="yd">Yards (1 yd = 0.9144 m)</option>
                      </select>
                    </div>
                    <Fld label="💰 Prix /pièce :" value={recepForm.prixParPiece} onChange={e=>{
                      const ppp=e.target.value;
                      const mp=parseFloat(recepForm.metresParPiece)||0;
                      const conv=recepForm.uniteMesure==="yd"?0.9144:1;
                      setRecepForm(f=>({...f,prixParPiece:ppp,prixUnitaire:ppp&&mp?String(Math.round(parseFloat(ppp)/(mp*conv))):f.prixUnitaire}));
                    }} type="number" suffix="F/pc"/>
                  </Row>
                  {totalM>0&&<div style={{background:"#f0fdf4",border:"1px solid #86efac",borderRadius:8,padding:"9px 12px",fontSize:12,color:"#166534",fontWeight:700}}>
                    ✓ Total cette livraison : <strong>{totalM.toFixed(1)} m</strong> · {b} balle{b>1?"s":""} × {pp} pc × {mp} {recepForm.uniteMesure==="yd"?"yd":"m"}
                    {recepForm.uniteMesure==="yd"&&<span style={{fontWeight:400,marginLeft:6,color:"#888"}}>({(b*pp*mp).toFixed(0)} yd)</span>}
                  </div>}
                </Sec>
                <Sec title="Ou saisie directe (sans balle)">
                  <Row>
                    <Fld label="Mètres reçus :" value={recepForm.metres} onChange={e=>setRecepForm(f=>({...f,metres:e.target.value}))} type="number" suffix="m"/>
                    <Fld label="Prix /m payé :" value={recepForm.prixUnitaire} onChange={e=>setRecepForm(f=>({...f,prixUnitaire:e.target.value}))} type="number" suffix="F"/>
                  </Row>
                </Sec>
              </>);
            })()}
            <Sec title="Fournisseur & date">
              <Row>
                <div style={{flex:1,minWidth:0}}>
                  <label style={sl}>Fournisseur :</label>
                  <select value={recepForm.fournisseurId||""} onChange={e=>setRecepForm(f=>({...f,fournisseurId:e.target.value?parseInt(e.target.value):null}))} style={si}>
                    <option value="">-- Choisir --</option>
                    {fournisseurs.map(f=><option key={f.id} value={f.id}>{f.nom}</option>)}
                  </select>
                </div>
                <Fld label="Date :" value={recepForm.date} onChange={e=>setRecepForm(f=>({...f,date:e.target.value}))} type="date"/>
              </Row>
              <div><label style={sl}>Notes :</label><textarea value={recepForm.notes} onChange={e=>setRecepForm(f=>({...f,notes:e.target.value}))} rows={2} style={{...si,resize:"vertical"}}/></div>
            </Sec>
            </div>
            <div style={{display:"flex",gap:7,justifyContent:"flex-end",padding:"12px 20px calc(12px + env(safe-area-inset-bottom))",borderTop:"1px solid #f0ece4",flexShrink:0,background:"#fafafa"}}>
              <button onClick={()=>setShowRecep(false)} style={{padding:"9px 16px",borderRadius:8,border:"1.5px solid #e0e0e0",background:"#fff",cursor:"pointer",fontWeight:600,fontFamily:"inherit",fontSize:12}}>Annuler</button>
              <button onClick={submitRecep} style={{padding:"9px 20px",borderRadius:8,border:"none",background:"#10b981",color:"#fff",cursor:"pointer",fontWeight:800,fontSize:13,fontFamily:"inherit"}}>✅ Valider la réception</button>
            </div>
          </div>
        </div>);
      })()}

      {/* DETAIL COLLECTION */}
      {colDetail&&<div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:14}} onClick={e=>{if(e.target===e.currentTarget)setColDetail(null);}}>
        <div style={{background:"#fff",borderRadius:15,maxWidth:520,width:"100%",overflow:"hidden",maxHeight:"88vh",display:"flex",flexDirection:"column",boxShadow:"0 18px 56px rgba(0,0,0,0.18)"}}>
          {colPhotos[colDetail.id]?<div onClick={()=>setColLightbox({index:filtCol.findIndex(x=>x.id===colDetail.id),items:filtCol})} style={{background:"#111",maxHeight:420,display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden",cursor:"zoom-in"}}><img src={colPhotos[colDetail.id]} alt="" style={{maxWidth:"100%",maxHeight:420,objectFit:"contain",display:"block"}}/></div>:<div style={{height:180,background:"linear-gradient(135deg,#f0ece4,#e8e0d4)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:64}}>👗</div>}
          <div style={{padding:18,overflowY:"auto"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8,gap:10}}>
              <div style={{fontSize:20,fontWeight:800,color:D}}>{colDetail.titre||"Sans titre"}</div>
              <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
                {colDetail.prixIndicatif&&<div style={{background:G,color:D,padding:"5px 12px",borderRadius:8,fontWeight:800,fontSize:14,whiteSpace:"nowrap"}}>{fmt(colDetail.prixIndicatif)} F{IS_GROSSISTE?"/m":""}</div>}
                {/* Menu discret : Modifier / Supprimer */}
                <div style={{position:"relative"}}>
                  {!isGrossiste&&<button onClick={e=>{e.stopPropagation();setColMenu(m=>!m);}} aria-label="Options" style={{background:colMenu?"#f0ece4":"transparent",border:"none",borderRadius:8,width:34,height:34,cursor:"pointer",fontSize:19,color:"#888",fontFamily:"inherit",lineHeight:1}}>⋮</button>}
                  {colMenu&&<>
                    <div onClick={()=>setColMenu(false)} style={{position:"fixed",inset:0,zIndex:5}}/>
                    <div style={{position:"absolute",top:38,right:0,background:"#fff",borderRadius:10,boxShadow:"0 8px 28px rgba(0,0,0,0.16)",border:"1px solid #eee",overflow:"hidden",zIndex:6,minWidth:170}}>
                      <button onClick={()=>{setColMenu(false);openEditC(colDetail);}} style={{display:"block",width:"100%",textAlign:"left",background:"transparent",border:"none",padding:"12px 15px",cursor:"pointer",fontSize:13,fontFamily:"inherit",color:D,fontWeight:600}}>✏️ Modifier</button>
                      <button onClick={()=>{setColMenu(false);delC(colDetail.id);}} style={{display:"block",width:"100%",textAlign:"left",background:"transparent",border:"none",borderTop:"1px solid #f5f2ec",padding:"12px 15px",cursor:"pointer",fontSize:13,fontFamily:"inherit",color:"#ef4444",fontWeight:600}}>🗑 Supprimer</button>
                    </div>
                  </>}
                </div>
              </div>
            </div>
            <div style={{display:"flex",gap:7,marginBottom:12,alignItems:"center",flexWrap:"wrap"}}>
              <span style={{background:"#f0f0f0",padding:"3px 10px",borderRadius:20,fontSize:11,fontWeight:600,color:"#666"}}>{colDetail.categorie}</span>
              <span style={{color:"#ccc",fontSize:11}}>📅 Ajouté le {colDetail.date}</span>
            </div>
            {colDetail.description&&<div style={{fontSize:13,color:"#555",lineHeight:1.75,marginBottom:14,background:"#f9f7f0",borderRadius:9,padding:13,border:"1px solid #f0ece4"}}>{colDetail.description}</div>}
            <div style={{display:"flex",gap:7}}>


              <button onClick={()=>setColDetail(null)} style={{flex:1,background:"#f5f2ec",color:"#666",border:"none",padding:"11px",borderRadius:9,cursor:"pointer",fontFamily:"inherit",fontWeight:700,fontSize:13}}>Fermer</button>
            </div>
          </div>
        </div>
      </div>}

      {/* MODE BOUTIQUE — plein écran présentation client avec QR code */}
      {/* CHOISIR L'ARTICLE EN IMAGES — dans une boutique qui tient des centaines de
          tissus, un nom dans une liste ne suffit pas : deux Getzner gris se lisent
          pareil et se vendent différemment. La photo, elle, ne trompe pas. */}
      {pickerVente>=0&&(()=>{
        const q=pickerQ.trim().toLowerCase();
        const liste=[...stock].sort((x,y)=>x.id-y.id).filter(x=>{
          if(!q)return true;
          return [nomArticle(x),x.couleur,refsStock[x.id],familleStock(x)].filter(Boolean).join(" ").toLowerCase().includes(q);
        });
        // Rangés par famille, comme dans le Stock : on va droit au Bazin ou au Getzner
        // au lieu de faire défiler toute la boutique.
        const parFam={};
        liste.forEach(x=>{const f=familleStock(x);(parFam[f]=parFam[f]||[]).push(x);});
        const familles=ordreFamilles(Object.keys(parFam));
        const fam=familles.includes(pickerFam)?pickerFam:"Tous";
        return(<div onClick={e=>{if(e.target===e.currentTarget)setPickerVente(-1);}} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:500,display:"flex",alignItems:"center",justifyContent:"center",padding:14}}>
          <div style={{background:"#fff",borderRadius:15,width:"100%",maxWidth:620,maxHeight:"86vh",display:"flex",flexDirection:"column",overflow:"hidden",boxShadow:"0 22px 66px rgba(0,0,0,0.3)"}}>
            <div style={{padding:"14px 16px",borderBottom:"1px solid #f0f0f0",flexShrink:0}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                <span style={{fontWeight:800,color:D,fontSize:15}}>🖼️ Choisir le tissu</span>
                <button type="button" onClick={()=>setPickerVente(-1)} style={{background:"#f5f5f5",border:"none",borderRadius:7,width:30,height:30,cursor:"pointer",fontSize:16,fontFamily:"inherit",lineHeight:1}}>×</button>
              </div>
              <label style={sl}>🔍 Chercher un tissu, une couleur, une référence</label><input value={pickerQ} onChange={e=>setPickerQ(e.target.value)} style={si} autoFocus/>
              {familles.length>1&&<div style={{display:"flex",gap:6,overflowX:"auto",marginTop:10,paddingBottom:2,WebkitOverflowScrolling:"touch"}}>
                {["Tous",...familles].map(f=>{const n=f==="Tous"?liste.length:parFam[f].length;const on=fam===f;return(<button key={f} type="button" onClick={()=>setPickerFam(f)} style={{flexShrink:0,padding:"5px 12px",borderRadius:20,border:"none",cursor:"pointer",fontSize:11.5,fontFamily:"inherit",background:on?D:"#eee",color:on?G:"#555",fontWeight:on?800:600,whiteSpace:"nowrap"}}>{f} <span style={{opacity:.7}}>{n}</span></button>);})}
              </div>}
            </div>
            <div style={{overflowY:"auto",padding:"0 12px 12px"}}>
              {liste.length===0&&<div style={{textAlign:"center",padding:"30px 10px",color:"#bbb",fontSize:13}}>Aucun tissu ne correspond.</div>}
              {(fam==="Tous"?familles:[fam]).map(f=>(<div key={f}>
              <div style={{position:"sticky",top:0,zIndex:1,background:"#fff",display:"flex",alignItems:"baseline",gap:7,padding:"12px 2px 7px",borderBottom:"1px solid #f0ece4",marginBottom:10}}>
                <span style={{fontSize:13,fontWeight:800,color:D}}>{f}</span>
                <span style={{fontSize:11,color:"#aaa"}}>{parFam[f].length} article{parFam[f].length>1?"s":""}</span>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(148px,1fr))",gap:10}}>
              {parFam[f].map(x=>{
                const reste=parseFloat(x.metres)||0;
                const pv=parseFloat(x.prixVente)||0;
                const teinte=x.couleurHex||(PALETTE.find(c=>c.n===x.couleur)||{}).h||"#e2e2e2";
                return(<button key={x.id} type="button" onClick={()=>{
                  majT(pickerVente,{stockId:String(x.id),type:x.type||"",custom:x.custom||"",variante:x.variante||"",couleur:x.couleur||"",couleurHex:x.couleurHex||""});
                  setPickerVente(-1);
                }} style={{background:"#fff",border:"1.5px solid #ececec",borderRadius:11,padding:0,cursor:"pointer",fontFamily:"inherit",textAlign:"left",overflow:"hidden",display:"flex",flexDirection:"column"}}>
                  {stockPhotos[x.id]
                    ?<img src={stockPhotos[x.id]} alt="" loading="lazy" style={{width:"100%",height:104,objectFit:"cover",display:"block"}}/>
                    :<div style={{height:104,background:"linear-gradient(135deg,#f5f2ea,#e9e2d4)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:30,color:"#c8bda8"}}>🧵</div>}
                  <div style={{padding:"8px 10px 10px"}}>
                    <div style={{fontSize:9,fontWeight:800,color:"#a99a78",letterSpacing:.6}}>{refsStock[x.id]||""}</div>
                    <div style={{fontSize:12.5,fontWeight:800,color:D,marginTop:2,lineHeight:1.3}}>{nomArticle(x)}</div>
                    {x.couleur&&<div style={{display:"flex",alignItems:"center",gap:5,marginTop:3}}>
                      <span style={{width:11,height:11,borderRadius:"50%",background:teinte,border:"1px solid rgba(0,0,0,0.12)",display:"inline-block",flexShrink:0}}/>
                      <span style={{fontSize:11,color:"#666"}}>{x.couleur}</span>
                    </div>}
                    <div style={{fontSize:11,marginTop:5,color:reste>0?"#166534":"#dc2626",fontWeight:700}}>{reste>0?reste.toLocaleString("fr-FR")+" m":"⚠️ Rupture de stock"}</div>
                    {pv>0&&<div style={{fontSize:12,fontWeight:900,color:G,marginTop:2}}>{pv.toLocaleString("fr-FR")} F/m</div>}
                  </div>
                </button>);
              })}
              </div></div>))}
            </div>
          </div>
        </div>);
      })()}

      {/* ENCAISSER UN CRÉDIT */}
      {encaisser&&(()=>{
        const g=credits.liste.find(x=>x.cle===encaisser.cle);
        if(!g)return null;
        const saisi=parseFloat(encaisser.montant)||0;
        const m=Math.min(saisi,g.reste);
        const resteApres=g.reste-m;
        let aRep=m;
        const reglees=g.ventes.filter(o=>{if(aRep<=0)return false;aRep-=Math.min(aRep,resteDe(o));return true;});
        const maj=(k,v)=>setEncaisser(x=>({...x,[k]:v}));
        const chip={background:"#ecfdf5",color:"#065f46",border:"1px solid #a7f3d0",padding:"5px 11px",borderRadius:20,cursor:"pointer",fontSize:11.5,fontWeight:700,fontFamily:"inherit"};
        return(<div onClick={ev=>{if(ev.target===ev.currentTarget)setEncaisser(null);}} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:600,display:"flex",alignItems:"center",justifyContent:"center",padding:14}}>
          <div style={{background:"#fff",borderRadius:15,width:"100%",maxWidth:440,maxHeight:"90vh",overflowY:"auto",boxShadow:"0 22px 66px rgba(0,0,0,0.3)"}}>
            <div style={{background:"linear-gradient(135deg,#065f46,#059669)",color:"#fff",padding:"16px 18px",display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10}}>
              <div style={{minWidth:0}}>
                <div style={{fontSize:16,fontWeight:900}}>💵 Encaisser un paiement</div>
                <div style={{fontSize:12,color:"#d1fae5",marginTop:3}}>{g.nom} doit {fmt(g.reste)} F</div>
              </div>
              <button type="button" onClick={()=>setEncaisser(null)} style={{background:"rgba(255,255,255,0.15)",border:"none",borderRadius:7,width:30,height:30,cursor:"pointer",fontSize:16,color:"#fff",fontFamily:"inherit",flexShrink:0}}>×</button>
            </div>
            <div style={{padding:"16px 18px"}}>
              <label style={sl}>Montant reçu (F)</label>
              <ChiffreInput value={encaisser.montant} onChange={ev=>maj("montant",ev.target.value)} style={{...si,fontSize:20,fontWeight:800,padding:"10px 13px"}} autoFocus/>
              <div style={{display:"flex",gap:6,marginTop:7,flexWrap:"wrap"}}>
                <button type="button" onClick={()=>maj("montant",String(g.reste))} style={chip}>Tout : {fmt(g.reste)} F</button>
                {g.ventes.length>1&&<button type="button" onClick={()=>maj("montant",String(resteDe(g.ventes[0])))} style={chip}>{g.ventes[0].cmdNum} : {fmt(resteDe(g.ventes[0]))} F</button>}
              </div>
              {saisi>g.reste&&<div style={{fontSize:11.5,color:"#b45309",marginTop:8,fontWeight:600}}>Le client ne doit que {fmt(g.reste)} F : c'est ce qui sera encaissé.</div>}
              <div style={{display:"flex",gap:10,marginTop:14,flexWrap:"wrap"}}>
                <div style={{flex:"1 1 150px",minWidth:0}}><label style={sl}>💳 Mode de paiement</label><select value={encaisser.mode} onChange={ev=>maj("mode",ev.target.value)} style={si}>{MODES_PAIEMENT.map(x=><option key={x}>{x}</option>)}</select></div>
                <div style={{flex:"1 1 150px",minWidth:0}}><label style={sl}>📅 Reçu le</label><input type="date" value={encaisser.date} max={jour} onChange={ev=>maj("date",ev.target.value)} style={si}/></div>
              </div>
              {m>0&&<div style={{marginTop:14,background:resteApres>0?"#fffbeb":"#f0fdf4",border:"1px solid "+(resteApres>0?"#fde68a":"#86efac"),borderRadius:10,padding:"11px 13px"}}>
                <div style={{fontSize:12.5,fontWeight:800,color:resteApres>0?"#92400e":"#166534"}}>{resteApres>0?`Il restera ${fmt(resteApres)} F à payer`:"✅ Le client sera à jour"}</div>
                <div style={{fontSize:11,color:"#777",marginTop:3,lineHeight:1.5}}>Ce paiement règle d'abord les ventes les plus anciennes : {reglees.map(o=>o.cmdNum).join(", ")}.</div>
                {resteApres>0&&<div style={{marginTop:10}}>
                  <label style={sl}>📅 Le client promet de payer le reste le</label>
                  <input type="date" value={encaisser.echeance} min={jour} onChange={ev=>maj("echeance",ev.target.value)} style={si}/>
                </div>}
              </div>}
              <div style={{display:"flex",gap:8,marginTop:16}}>
                <button type="button" onClick={()=>setEncaisser(null)} style={{flex:1,background:"#f5f2ec",color:"#666",border:"none",padding:"11px",borderRadius:9,cursor:"pointer",fontFamily:"inherit",fontWeight:700,fontSize:13}}>Annuler</button>
                <button type="button" disabled={m<=0} onClick={validerEncaissement} style={{flex:2,background:m>0?"#059669":"#cbd5e1",color:"#fff",border:"none",padding:"11px",borderRadius:9,cursor:m>0?"pointer":"default",fontFamily:"inherit",fontWeight:800,fontSize:13}}>✅ Encaisser{m>0?" "+fmt(m)+" F":""}</button>
              </div>
            </div>
          </div>
        </div>);
      })()}
      {/* PAIEMENT ENREGISTRÉ — le confirmer au client, ou lui remettre un reçu */}
      {encaisseFait&&(()=>{
        const p=encaisseFait;
        const wa=p.telephone?waLink(p.telephone,messagePaiement(p,atelierInfo)):null;
        return(<div onClick={ev=>{if(ev.target===ev.currentTarget)setEncaisseFait(null);}} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:600,display:"flex",alignItems:"center",justifyContent:"center",padding:14}}>
          <div style={{background:"#fff",borderRadius:15,width:"100%",maxWidth:400,padding:"24px 20px 18px",textAlign:"center",boxShadow:"0 22px 66px rgba(0,0,0,0.3)"}}>
            <div style={{fontSize:44,lineHeight:1}}>✅</div>
            <div style={{fontSize:20,fontWeight:900,color:"#059669",marginTop:10}}>{fmt(p.montant)} F encaissés</div>
            <div style={{fontSize:12.5,color:"#666",marginTop:4}}>{p.nom} · {p.mode}</div>
            <div style={{background:p.resteApres>0?"#fffbeb":"#f0fdf4",borderRadius:10,padding:"10px 12px",marginTop:14,fontSize:12.5,fontWeight:700,color:p.resteApres>0?"#92400e":"#166534",lineHeight:1.5}}>
              {p.resteApres>0?`Il reste ${fmt(p.resteApres)} F à régler${p.echeance?", prévu le "+dateLongue(p.echeance):""}.`:"Le client est à jour : il sort de la liste des crédits."}
            </div>
            <div style={{fontSize:10.5,color:"#aaa",marginTop:8}}>Déjà compté dans l'encaissé de la Caisse.</div>
            <div style={{display:"flex",flexDirection:"column",gap:8,marginTop:16}}>
              {wa&&<a href={wa} target="_blank" rel="noreferrer" style={{background:"#25D366",color:"#fff",padding:"11px",borderRadius:9,fontWeight:800,fontSize:13,textDecoration:"none"}}>💬 Confirmer au client sur WhatsApp</a>}
              <button type="button" onClick={()=>{setReceiptHtml({html:buildRecuPaiementHTML(p,atelierInfo),order:null,name:"Recu-paiement-"+p.date,titre:"Reçu de paiement"});setEncaisseFait(null);}} style={{background:G,color:D,border:"none",padding:"11px",borderRadius:9,cursor:"pointer",fontWeight:800,fontSize:13,fontFamily:"inherit"}}>🧾 Reçu de paiement</button>
              <button type="button" onClick={()=>setEncaisseFait(null)} style={{background:"#f5f2ec",color:"#666",border:"none",padding:"10px",borderRadius:9,cursor:"pointer",fontWeight:700,fontSize:13,fontFamily:"inherit"}}>Fermer</button>
            </div>
          </div>
        </div>);
      })()}
      {/* DATE PROMISE PAR LE CLIENT */}
      {echeanceEdit&&(()=>{
        const g=credits.liste.find(x=>x.cle===echeanceEdit.cle);
        if(!g)return null;
        const dans=n=>new Date(new Date(jour+"T00:00:00Z").getTime()+n*86400000).toISOString().slice(0,10);
        const finMois=(()=>{const d=new Date(jour+"T00:00:00Z");return new Date(Date.UTC(d.getUTCFullYear(),d.getUTCMonth()+1,0)).toISOString().slice(0,10);})();
        const RAPIDES=[["Dans 3 jours",dans(3)],["Dans 1 semaine",dans(7)],["Dans 2 semaines",dans(14)],["Fin du mois",finMois]];
        return(<div onClick={ev=>{if(ev.target===ev.currentTarget)setEcheanceEdit(null);}} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:600,display:"flex",alignItems:"center",justifyContent:"center",padding:14}}>
          <div style={{background:"#fff",borderRadius:15,width:"100%",maxWidth:400,padding:"18px 18px 16px",boxShadow:"0 22px 66px rgba(0,0,0,0.3)"}}>
            <div style={{fontSize:16,fontWeight:900,color:D}}>📅 Date promise</div>
            <div style={{fontSize:12,color:"#777",marginTop:4,lineHeight:1.5}}>Le jour où {g.nom} promet de payer les {fmt(g.reste)} F. Ce jour-là, l'application vous le rappellera.</div>
            <input type="date" value={echeanceEdit.date} min={jour} onChange={ev=>{const v=ev.target.value;setEcheanceEdit(x=>({...x,date:v}));}} style={{...si,marginTop:12}}/>
            <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:8}}>
              {RAPIDES.map(([l,d])=>(<button key={l} type="button" onClick={()=>setEcheanceEdit(x=>({...x,date:d}))} style={{background:echeanceEdit.date===d?D:"#f3f4f6",color:echeanceEdit.date===d?G:"#555",border:"none",padding:"5px 11px",borderRadius:20,cursor:"pointer",fontSize:11.5,fontWeight:700,fontFamily:"inherit"}}>{l}</button>))}
            </div>
            <div style={{display:"flex",gap:8,marginTop:16,flexWrap:"wrap",alignItems:"center"}}>
              {g.echeance&&<button type="button" onClick={()=>fixerEcheance(g.cle,"")} style={{background:"transparent",color:"#ef4444",border:"none",padding:"10px 4px",cursor:"pointer",fontWeight:700,fontSize:12,fontFamily:"inherit",marginRight:"auto"}}>Retirer la date</button>}
              <button type="button" onClick={()=>setEcheanceEdit(null)} style={{flex:"1 1 90px",background:"#f5f2ec",color:"#666",border:"none",padding:"10px",borderRadius:9,cursor:"pointer",fontWeight:700,fontSize:13,fontFamily:"inherit"}}>Annuler</button>
              <button type="button" disabled={!echeanceEdit.date} onClick={()=>fixerEcheance(g.cle,echeanceEdit.date)} style={{flex:"1 1 120px",background:echeanceEdit.date?D:"#cbd5e1",color:echeanceEdit.date?G:"#fff",border:"none",padding:"10px",borderRadius:9,cursor:echeanceEdit.date?"pointer":"default",fontWeight:800,fontSize:13,fontFamily:"inherit"}}>Enregistrer</button>
            </div>
          </div>
        </div>);
      })()}

      {recuFlash&&<div style={{position:"fixed",top:12,left:"50%",transform:"translateX(-50%)",background:"#065f46",color:"#fff",padding:"10px 18px",borderRadius:22,fontSize:13,fontWeight:700,zIndex:900,boxShadow:"0 6px 20px rgba(0,0,0,0.25)",whiteSpace:"nowrap"}}>🔄 Mis à jour avec votre autre appareil</div>}
      {/* CONFLIT DE SAUVEGARDE — on ne remplace jamais en silence un contenu plus
          riche que le sien. Le vendeur voit les deux comptes et tranche. */}
      {conflitCloud&&(()=>{
        const{serveur,ici}=conflitCloud;
        const lignes=[["🧵 Articles en stock",serveur.nbStock,ici.stock],["👥 Clients",serveur.nbClients,ici.clients],["🧾 Ventes",serveur.nbCommandes,ici.ventes]];
        const quand=(()=>{try{return new Date(serveur.date).toLocaleString("fr-FR",{day:"2-digit",month:"long",hour:"2-digit",minute:"2-digit"});}catch{return serveur.date||"";}})();
        return(<div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:700,display:"flex",alignItems:"center",justifyContent:"center",padding:14}}>
          <div style={{background:"#fff",borderRadius:16,width:"100%",maxWidth:470,maxHeight:"88vh",overflowY:"auto",boxShadow:"0 22px 66px rgba(0,0,0,0.3)"}}>
            <div style={{background:"linear-gradient(135deg,#92400e,#b45309)",color:"#fff",padding:"18px 20px"}}>
              <div style={{fontSize:17,fontWeight:900}}>{conflitCloud.autreAppareil?"⚠️ Modifié sur votre autre appareil":"⚠️ Sauvegarde en ligne plus complète"}</div>
              <div style={{fontSize:11.5,color:"#fed7aa",marginTop:4,lineHeight:1.55}}>Envoyer maintenant effacerait ce qui est en ligne. Rien n'a été envoyé.</div>
            </div>
            <div style={{padding:"18px 20px"}}>
              <div style={{fontSize:13,color:"#555",lineHeight:1.7,marginBottom:14}}>
                {conflitCloud.autreAppareil
                  ?<>Votre autre appareil a enregistré des changements le <strong>{quand}</strong>, et celui-ci en a aussi qui ne sont pas encore partis.</>
                  :<>La sauvegarde en ligne du <strong>{quand}</strong> contient plus d'enregistrements que cet appareil.</>}
                C'est le cas quand un autre téléphone ou un autre ordinateur travaille sur la même clé.
              </div>
              <div style={{border:"1px solid #eee",borderRadius:10,overflow:"hidden",marginBottom:16}}>
                <div style={{display:"grid",gridTemplateColumns:"1fr auto auto",background:"#f9f7f0",fontSize:10,fontWeight:800,color:"#8a7f66",textTransform:"uppercase",letterSpacing:.5}}>
                  <div style={{padding:"8px 11px"}}>&nbsp;</div>
                  <div style={{padding:"8px 11px",textAlign:"right"}}>En ligne</div>
                  <div style={{padding:"8px 11px",textAlign:"right"}}>Ici</div>
                </div>
                {lignes.map(([l,srv,loc])=>(
                  <div key={l} style={{display:"grid",gridTemplateColumns:"1fr auto auto",borderTop:"1px solid #f4f4f4",fontSize:12.5}}>
                    <div style={{padding:"9px 11px",color:"#555"}}>{l}</div>
                    <div style={{padding:"9px 11px",textAlign:"right",fontWeight:800,color:srv>loc?"#166534":"#666"}}>{srv}</div>
                    <div style={{padding:"9px 11px",textAlign:"right",fontWeight:800,color:srv>loc?"#b45309":"#666"}}>{loc}</div>
                  </div>
                ))}
              </div>
              <button onClick={async()=>{setConflitCloud(null);setCloudState("");const r=await cloudRestaurer();if(r&&r.ok){try{sessionStorage.setItem("cp_vue",view);sessionStorage.setItem("cp_recu","1");}catch{}window.location.reload();}}} style={{width:"100%",background:"#10b981",color:"#fff",border:"none",borderRadius:10,padding:"14px",cursor:"pointer",fontWeight:800,fontSize:14,fontFamily:"inherit",marginBottom:9}}>📥 Récupérer ce qui est en ligne</button>
              <div style={{fontSize:11,color:"#888",lineHeight:1.55,marginBottom:16}}>Recommandé : cet appareil reprend le contenu du serveur. Ce qui n'existe qu'ici sera remplacé.</div>
              <button onClick={async()=>{setConflitCloud(null);await cloudEnvoyer(true);}} style={{width:"100%",background:"#fff",color:"#b91c1c",border:"1.5px solid #fca5a5",borderRadius:10,padding:"12px",cursor:"pointer",fontWeight:800,fontSize:13,fontFamily:"inherit",marginBottom:9}}>📤 Envoyer quand même</button>
              <div style={{fontSize:11,color:"#991b1b",lineHeight:1.55,marginBottom:16}}>Le contenu en ligne sera écrasé par celui de cet appareil.</div>
              <button onClick={()=>{setConflitCloud(null);setCloudState("");}} style={{width:"100%",background:"#f5f5f5",color:"#666",border:"none",borderRadius:10,padding:"11px",cursor:"pointer",fontWeight:700,fontSize:13,fontFamily:"inherit"}}>Ne rien faire pour l'instant</button>
            </div>
          </div>
        </div>);
      })()}

      {showBoutique&&(()=>{
        const cur=colMeta[boutiqueIdx]||{};
        return(<div style={{position:"fixed",inset:0,background:"#0a0a14",zIndex:900,display:"flex",flexDirection:"column"}}>
          {/* Bandeau haut */}
          <div style={{padding:"calc(26px + env(safe-area-inset-top)) 24px 16px",display:"flex",justifyContent:"space-between",alignItems:"center",background:"linear-gradient(180deg,rgba(0,0,0,0.6),transparent)",position:"absolute",top:0,left:0,right:0,zIndex:2}}>
            <div>
              <div style={{fontSize:22,fontWeight:900,color:G,letterSpacing:"-0.3px"}}>{atelierInfo.nom||"Ma Boutique de Tissus"}</div>
              <div style={{fontSize:11,color:"#aaa",marginTop:2}}>{atelierInfo.adresse||"Dakar, Sénégal"} {atelierInfo.tel?`· 📞 ${atelierInfo.tel}`:""}</div>
            </div>
            <button onClick={()=>setShowBoutique(false)} style={{background:"rgba(255,255,255,0.1)",color:"#fff",border:"none",borderRadius:50,width:42,height:42,cursor:"pointer",fontSize:22,fontFamily:"inherit",flexShrink:0}}>×</button>
          </div>
          {/* Photo centrale */}
          <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",padding:"60px 24px 100px"}}>
            {colMeta.length===0?<div style={{textAlign:"center",color:"#666",fontSize:18}}>
              <div style={{fontSize:80,marginBottom:14}}>👗</div>
              Aucun modèle dans la collection.<br/>Ajoutez des photos pour utiliser le Mode Boutique.
            </div>:colPhotos[cur.id]?<img src={colPhotos[cur.id]} alt="" style={{maxWidth:"90vw",maxHeight:"75vh",objectFit:"contain",borderRadius:8,boxShadow:"0 20px 60px rgba(0,0,0,0.5)"}}/>
              :<div style={{width:400,height:400,background:"linear-gradient(135deg,#1a1a2e,#0f0f1e)",borderRadius:12,display:"flex",alignItems:"center",justifyContent:"center",fontSize:120,color:G}}>👗</div>}
          </div>
          {/* Bandeau bas : titre + nav + QR */}
          <div style={{position:"absolute",bottom:0,left:0,right:0,padding:"18px 24px",background:"linear-gradient(0deg,rgba(0,0,0,0.75),transparent)",display:"flex",alignItems:"flex-end",justifyContent:"space-between",gap:18,zIndex:2}}>
            <div style={{flex:1,color:"#fff"}}>
              <div style={{fontSize:22,fontWeight:800,marginBottom:5}}>{cur.titre||""}</div>
              <div style={{display:"flex",gap:10,alignItems:"center",fontSize:12,color:"#bbb"}}>
                {cur.categorie&&<span style={{background:"rgba(255,255,255,0.12)",padding:"3px 10px",borderRadius:12}}>{cur.categorie}</span>}
                {cur.prixIndicatif&&<span style={{color:G,fontWeight:800,fontSize:14}}>{fmt(cur.prixIndicatif)} FCFA</span>}
                {colMeta.length>0&&<span style={{marginLeft:"auto",color:"#888"}}>{boutiqueIdx+1} / {colMeta.length}</span>}
              </div>
              {colMeta.length>1&&<div style={{display:"flex",gap:9,marginTop:13}}>
                <button onClick={()=>setBoutiqueIdx(i=>(i-1+colMeta.length)%colMeta.length)} style={{background:"rgba(255,255,255,0.15)",color:"#fff",border:"none",borderRadius:8,width:44,height:44,cursor:"pointer",fontSize:18,fontFamily:"inherit"}}>‹</button>
                <button onClick={()=>setBoutiqueIdx(i=>(i+1)%colMeta.length)} style={{background:"rgba(255,255,255,0.15)",color:"#fff",border:"none",borderRadius:8,width:44,height:44,cursor:"pointer",fontSize:18,fontFamily:"inherit"}}>›</button>
                <span style={{color:"#666",fontSize:10,alignSelf:"center",marginLeft:10}}>← → ou Échap pour quitter</span>
              </div>}
            </div>
            {/* QR Code — Pro: serveur HTTP local | sinon URL configurée | sinon vCard */}
            {qrData&&<div style={{background:"#fff",borderRadius:14,padding:"14px 14px 10px",textAlign:"center",boxShadow:"0 12px 40px rgba(0,0,0,0.5)",border:"3px solid "+G}}>
              <div style={{fontSize:9,color:G,fontWeight:900,letterSpacing:1.2,marginBottom:6,textTransform:"uppercase"}}>{atelierInfo.catalogUrl?"Voir la boutique en ligne":boutiqueUrl?"Voir la collection":"Contact de la boutique"}</div>
              <img src={qrData} alt="QR Code" style={{width:160,height:160,display:"block",borderRadius:6,margin:"0 auto"}}/>
              <div style={{fontSize:10,color:D,fontWeight:700,marginTop:8,letterSpacing:0.5}}>📱 Scannez avec votre téléphone</div>
              {atelierInfo.catalogUrl?<div style={{fontSize:9,color:"#0369a1",marginTop:5,fontWeight:700,wordBreak:"break-all",background:"#eff6ff",padding:"3px 6px",borderRadius:4}}>{atelierInfo.catalogUrl}</div>:boutiqueUrl?<div style={{fontSize:9,color:"#0369a1",marginTop:5,fontWeight:700,wordBreak:"break-all",background:"#eff6ff",padding:"3px 6px",borderRadius:4}}>{boutiqueUrl}</div>:<div style={{fontSize:8.5,color:"#b45309",marginTop:4,lineHeight:1.5,maxWidth:170}}>Ce code enregistre votre contact.<br/>Pour qu'il ouvre votre boutique : Paramètres → Site web → adresse du site.</div>}
              <div style={{fontSize:8,color:"#aaa",marginTop:4,fontStyle:"italic"}}>{atelierInfo.catalogUrl?"Accessible partout (4G/WiFi)":boutiqueUrl?"Téléphone sur le même WiFi requis":""}</div>
            </div>}
          </div>
        </div>);
      })()}

      {/* LIGHTBOX COLLECTION — plein écran avec navigation + ZOOM HD */}
      {colLightbox&&<LightboxZoom lightbox={colLightbox} setLightbox={setColLightbox} colPhotos={colPhotos}/>}
    </div>
  );
}
